// Auto-generated documentation content

// Auto-generated from 3-auth.md
export const Doc3Auth = `
## Authentication and Authorization

### 1) Authentication

Authentication verifies the identity of the user.

Users can log in using:

- Email and password
- Single Sign-On (SSO)

Once authenticated, the system generates:

- Access token (JWT): used for API requests
- Refresh token: used to regenerate an access token after expiry

The following details are stored in local storage:

- Access token
- Refresh token
- User ID
- Organization name

For every API request:

- The access token is sent in the request header.
- If the access token expires, the refresh token API is called to issue a new access token.

### 2) Authorization

Authorization ensures that an authenticated user has the correct permissions to access resources.

The system performs these checks on each request:

- Validate that the user exists.
- Verify the user belongs to a valid organization.
- Check that the organization exists.
- Ensure the organization has access to the requested tool.
- Verify the organization has sufficient quota.

Additionally:

- Tool access is controlled at the organization level.
- Users can only use features/tools their organization is subscribed to.

## Login sequence diagram
![Login sequence diagram](/Documentation/login-seq-1.png)

## SSO login sequence diagram

![SSO login sequence diagram](/Documentation/SSO-login-seq-2.png)



`;

// Auto-generated from 7-security.md
export const Doc7Security = `## How security works on every request (\`security.py\` + token storage in \`database.py\`)

### 1) \`access_token\` is required for every protected API call
The client sends the access JWT on every request:
\`Authorization: Bearer <access_token>\`

\`security.py\` does this on each protected endpoint:
1. \`SecurityValidator.extract_jwt_token()\` extracts the token from the \`Authorization\` header.
2. \`SecurityValidator.validate_jwt_token()\` verifies the JWT:
   - signature using \`JWT_SECRET\` / \`JWT_ALGORITHM\`
   - \`iss\` (\`JWT_ISSUER\`) and \`aud\` (\`JWT_AUDIENCE\`)
   - expiration (\`exp\`)
3. \`perform_full_security_check()\` loads \`user_id\` and \`org_id\` from the JWT payload and verifies:
   - user exists and is active (\`db_handler.get_user_by_id\`)
   - organization exists and is active (\`db_handler.get_organization_by_id\`)

### 2) Expired \`access_token\` does not auto-refresh inside the API
If the access token is expired, the JWT validation fails and the protected request is rejected with \`401\` (\`error_code="invalid_token"\` and message like *JWT token has expired*).

Then the client calls:
\`POST /refresh\` using the stored \`refresh_token\`,
and retries the original API request with the newly returned \`access_token\`.

### 3) How \`tool_id\` is checked on every protected endpoint
Protected routes declare a FastAPI dependency with a fixed server-side tool id, for example:
\`Depends(require_tool(EXTENSION_TOOL_ID))\` or \`Depends(require_quota(EXTENSION_TOOL_ID))\`.

In \`security.py\`:
1. \`require_tool(tool_id)\` calls \`perform_full_security_check()\` (JWT + user/org checks).
2. It reads the organization’s enabled tools from the DB: \`context.org.get("tools_access", [])\`.
3. It checks \`tool_id\` is present in \`org.tools_access\`.
4. If not present, the server returns \`403\` with \`error_code="tool_denied"\`.

\`require_quota(tool_id)\` performs the same tool check, and then additionally enforces subscription/quota via \`validate_subscription_and_quota()\` before allowing heavy endpoints.

### 4) Refresh token storage + \`POST /refresh\` behavior
During login, the server stores refresh tokens in \`user_session_data\` via:
\`db_handler.create_user_session_data(...)\`

That row stores:
- \`refresh_token\`
- \`refresh_token_expiry\`
- \`is_revoke\` (starts as \`False\`)

When the client calls \`POST /refresh\`:
1. \`refresh_token\` JWT is validated (\`SecurityValidator.validate_jwt_token()\`).
2. The token must have \`type == "refresh"\`.
3. The server checks the refresh token exists in \`user_session_data\` and is not revoked (\`is_revoke == False\`).
4. It issues a new access token (\`create_custom_token(..., token_type="access")\`) using the user/org role, plan, and \`org.tools_access\`.

### 5) Logout revokes refresh tokens
\`POST /logout\` marks the refresh token revoked in \`user_session_data\` by setting \`is_revoke = True\`.`;

// Auto-generated from DEPENDENCY_ALL_TABLES.md
export const DependencyAllTables = `# Tool Dependency Summary

## Chat App


| Name | Version | License Type |
| --- | --- | --- |
| \`@testing-library/dom\` | \`10.4.0\` | \`MIT\` |
| \`@testing-library/jest-dom\` | \`6.6.3\` | \`MIT\` |
| \`@testing-library/react\` | \`16.2.0\` | \`MIT\` |
| \`@testing-library/user-event\` | \`13.5.0\` | \`MIT\` |
| \`@types/jest\` | \`27.5.2\` | \`MIT\` |
| \`@types/node\` | \`16.18.126\` | \`MIT\` |
| \`@types/react-dom\` | \`19.0.4\` | \`MIT\` |
| \`@types/react\` | \`19.0.12\` | \`MIT\` |
| \`axios\` | \`1.8.4\` | \`MIT\` |
| \`bootstrap-icons\` | \`1.11.3\` | \`MIT\` |
| \`bootstrap\` | \`5.3.3\` | \`MIT\` |
| \`firebase\` | \`11.5.0\` | \`Apache-2.0\` |
| \`react-bootstrap\` | \`2.10.9\` | \`MIT\` |
| \`react-dom\` | \`19.0.0\` | \`MIT\` |
| \`react-router-dom\` | \`7.4.0\` | \`MIT\` |
| \`react-scripts\` | \`5.0.1\` | \`MIT\` |
| \`react\` | \`19.0.0\` | \`MIT\` |
| \`typescript\` | \`4.9.5\` | \`Apache-2.0\` |
| \`uuid\` | \`11.1.0\` | \`MIT\` |
| \`web-vitals\` | \`2.1.4\` | \`Apache-2.0\` |

## Chrome Extension


| Name | Version | License Type |
| --- | --- | --- |
| @eslint/js | 9.18.0 | MIT |
| @supabase/auth-js | 2.98.0 | MIT |
| @supabase/functions-js | 2.98.0 | MIT |
| @supabase/postgrest-js | 2.98.0 | MIT |
| @supabase/realtime-js | 2.98.0 | MIT |
| @supabase/storage-js | 2.98.0 | MIT |
| @supabase/supabase-js | 2.98.0 | MIT |
| @types/phoenix | 1.6.7 | MIT |
| @types/react | 18.3.18 | MIT |
| @types/react-dom | 18.3.5 | MIT |
| @types/ws | 8.18.1 | MIT |
| @vitejs/plugin-react | 4.3.4 | MIT |
| bootstrap | 5.3.3 | MIT |
| eslint | 9.18.0 | MIT |
| eslint-plugin-react-hooks | 5.1.0 | MIT |
| eslint-plugin-react-refresh | 0.4.18 | MIT |
| firebase | 11.2.0 | Apache-2.0 |
| globals | 15.14.0 | MIT |
| iceberg-js | 0.8.1 | MIT |
| react | 18.3.1 | MIT |
| react-dom | 18.3.1 | MIT |
| typescript | 5.6.3 | Apache-2.0 |
| typescript-eslint | 8.21.0 | MIT |
| vite | 6.0.11 | MIT |
| ws | 8.19.0 | MIT |

## Figma Plugin


| Name | Version | License Type |
| --- | --- | --- |
| @figma/eslint-plugin-figma-plugins | 0.16.1 | MIT |
| @figma/plugin-typings | 1.109.0 | MIT License |
| @typescript-eslint/eslint-plugin | 6.21.0 | MIT |
| @typescript-eslint/parser | 6.21.0 | BSD-2-Clause |
| cross-env | 7.0.3 | MIT |
| dotenv | 16.6.1 | BSD-2-Clause |
| eslint | 8.57.1 | MIT |
| typescript | 5.8.3 | Apache-2.0 |

## Observatory Dashbaord


| Name | Version | License Type |
| --- | --- | --- |
| @emotion/react | 11.14.0 | MIT |
| @emotion/styled | 11.14.1 | MIT |
| @mui/icons-material | 7.3.5 | MIT |
| @mui/material | 7.3.5 | MIT |
| @nivo/bar | 0.99.0 | MIT |
| @nivo/circle-packing | 0.99.0 | MIT |
| @nivo/core | 0.99.0 | MIT |
| @nivo/funnel | 0.99.0 | MIT |
| @nivo/heatmap | 0.99.0 | MIT |
| @nivo/marimekko | 0.99.0 | MIT |
| @nivo/network | 0.99.0 | MIT |
| @nivo/pie | 0.99.0 | MIT |
| @nivo/radar | 0.99.0 | MIT |
| @nivo/sunburst | 0.99.0 | MIT |
| @nivo/swarmplot | 0.99.0 | MIT |
| @nivo/tree | 0.99.0 | MIT |
| @nivo/treemap | 0.99.0 | MIT |
| @popperjs/core | 2.11.8 | MIT |
| @radix-ui/react-accordion | 1.2.3 | MIT |
| @radix-ui/react-alert-dialog | 1.1.6 | MIT |
| @radix-ui/react-aspect-ratio | 1.1.2 | MIT |
| @radix-ui/react-avatar | 1.1.3 | MIT |
| @radix-ui/react-checkbox | 1.1.4 | MIT |
| @radix-ui/react-collapsible | 1.1.3 | MIT |
| @radix-ui/react-context-menu | 2.2.6 | MIT |
| @radix-ui/react-dialog | 1.1.6 | MIT |
| @radix-ui/react-dropdown-menu | 2.1.6 | MIT |
| @radix-ui/react-hover-card | 1.1.6 | MIT |
| @radix-ui/react-label | 2.1.2 | MIT |
| @radix-ui/react-menubar | 1.1.6 | MIT |
| @radix-ui/react-navigation-menu | 1.2.5 | MIT |
| @radix-ui/react-popover | 1.1.6 | MIT |
| @radix-ui/react-progress | 1.1.2 | MIT |
| @radix-ui/react-radio-group | 1.2.3 | MIT |
| @radix-ui/react-scroll-area | 1.2.3 | MIT |
| @radix-ui/react-select | 2.1.6 | MIT |
| @radix-ui/react-separator | 1.1.2 | MIT |
| @radix-ui/react-slider | 1.2.3 | MIT |
| @radix-ui/react-slot | 1.1.2 | MIT |
| @radix-ui/react-switch | 1.1.3 | MIT |
| @radix-ui/react-tabs | 1.1.3 | MIT |
| @radix-ui/react-toggle | 1.1.2 | MIT |
| @radix-ui/react-toggle-group | 1.1.2 | MIT |
| @radix-ui/react-tooltip | 1.1.8 | MIT |
| @supabase/supabase-js | 2.93.2 | MIT |
| @tailwindcss/vite | 4.1.12 | MIT |
| @vitejs/plugin-react | 4.7.0 | MIT |
| class-variance-authority | 0.7.1 | Apache-2.0 |
| clsx | 2.1.1 | MIT |
| cmdk | 1.1.1 | MIT |
| date-fns | 3.6.0 | MIT |
| embla-carousel-react | 8.6.0 | MIT |
| input-otp | 1.4.2 | MIT |
| lucide-react | 0.487.0 | ISC |
| motion | 12.23.24 | MIT |
| next-themes | 0.4.6 | MIT |
| react | 18.3.1 | MIT |
| react-day-picker | 8.10.1 | MIT |
| react-dnd | 16.0.1 | MIT |
| react-dnd-html5-backend | 16.0.1 | MIT |
| react-dom | 18.3.1 | MIT |
| react-force-graph-3d | 1.29.0 | MIT |
| react-hook-form | 7.55.0 | MIT |
| react-popper | 2.3.0 | MIT |
| react-resizable-panels | 2.1.7 | MIT |
| react-responsive-masonry | 2.7.1 | MIT |
| react-slick | 0.31.0 | MIT |
| recharts | 2.15.2 | MIT |
| sonner | 2.0.3 | MIT |
| tailwind-merge | 3.2.0 | MIT |
| tailwindcss | 4.1.12 | MIT |
| three | 0.182.0 | MIT |
| three-spritetext | 1.10.0 | MIT |
| tw-animate-css | 1.3.8 | MIT |
| vaul | 1.1.2 | MIT |
| vite | 6.3.5 | MIT |

## Report Application


| Name | Version | License Type |
| --- | --- | --- |
| @eslint/js | 9.39.1 | MIT |
| @types/react | 19.2.6 | MIT |
| @types/react-dom | 19.2.3 | MIT |
| @vitejs/plugin-react | 5.1.1 | MIT |
| axios | 1.13.2 | MIT |
| eslint | 9.39.1 | MIT |
| eslint-plugin-react-hooks | 7.0.1 | MIT |
| eslint-plugin-react-refresh | 0.4.24 | MIT |
| globals | 16.5.0 | MIT |
| react | 19.2.0 | MIT |
| react-dom | 19.2.0 | MIT |
| reactflow | 11.11.4 | MIT |
| react-router-dom | 7.12.0 | MIT |
| react-zoom-pan-pinch | 3.7.0 | MIT |
| vite | 7.2.2 | MIT |

## Review Tool Client


| Name | Version | License Type |
| --- | --- | --- |
| @testing-library/dom | 10.4.1 | MIT |
| @testing-library/jest-dom | 6.9.1 | MIT |
| @testing-library/react | 16.3.2 | MIT |
| @testing-library/user-event | 13.5.0 | MIT |
| airtable | 0.12.2 | MIT |
| bootstrap | 5.3.8 | MIT |
| react | 19.2.4 | MIT |
| react-dom | 19.2.4 | MIT |
| react-router-dom | 7.13.1 | MIT |
| react-scripts | 5.0.1 | MIT |
| react-toastify | 11.0.5 | MIT |
| reactflow | 11.11.4 | MIT |
| web-vitals | 2.1.4 | Apache-2.0 |

## Server 1


| Name | Version | License Type |
| --- | --- | --- |
| fastapi | 0.135.1 | MIT |
| uvicorn | 0.42.0 | BSD-3-Clause |
| python-dotenv | 1.2.2 | BSD-3-Clause |
| supabase | 2.28.2 | MIT |
| pydantic | 2.12.5 | MIT |
| python-jose | 3.5.0 | MIT |
| httpx | 0.28.1 | BSD-3-Clause |
| stripe | 14.4.1 | MIT License |
| langchain-openai | 1.1.11 | MIT |
| langchain-core | 1.2.19 | MIT |
| pypdf | 6.9.2 | BSD-3-Clause |
| python-docx | 1.2.0 | MIT |
| beautifulsoup4 | 4.14.3 | MIT License |
| langchain-community | 0.4.1 | MIT |
| firebase-admin | 7.2.0 | Apache License 2.0 |
| google-cloud-storage | 3.9.0 | Apache 2.0 |
| gspread | 6.2.1 | MIT License |
| google-cloud-aiplatform | 1.142.0 | Apache 2.0 |
| google-cloud-firestore | 2.25.0 | Apache 2.0 |
| google-cloud-tasks | 2.21.0 | Apache 2.0 |
| cachetools | 6.2.6 | MIT |
| python-multipart | 0.0.22 | Apache-2.0 |
| selenium | 4.41.0 | Apache-2.0 |

## Tool Application


| Name | Version | License Type |
| --- | --- | --- |
| @fortawesome/fontawesome-svg-core | 6.6.0 | MIT |
| @fortawesome/free-solid-svg-icons | 6.6.0 | (CC-BY-4.0 AND MIT) |
| @fortawesome/react-fontawesome | 0.2.2 | MIT |
| @nivo/pie | 0.99.0 | MIT |
| @supabase/supabase-js | 2.87.0 | MIT |
| @testing-library/jest-dom | 5.17.0 | MIT |
| @testing-library/react | 13.4.0 | MIT |
| @testing-library/user-event | 13.5.0 | MIT |
| airtable | 0.12.2 | MIT |
| axios | 1.7.7 | MIT |
| bootstrap | 5.3.3 | MIT |
| chart.js | 4.4.4 | MIT |
| d3 | 7.9.0 | ISC |
| dagre | 0.8.5 | MIT |
| firebase | 10.14.0 | Apache-2.0 |
| mermaid | 11.12.1 | MIT |
| papaparse | 5.5.3 | MIT |
| react | 18.3.1 | MIT |
| react-bootstrap | 2.10.4 | MIT |
| react-chartjs-2 | 5.2.0 | MIT |
| react-d3-tree | 3.6.2 | MIT |
| react-dom | 18.3.1 | MIT |
| react-firebase-hooks | 5.1.1 | Apache-2.0 |
| reactflow | 11.11.4 | MIT |
| react-flow-renderer | 10.3.17 | MIT |
| react-force-graph-2d | 1.27.1 | MIT |
| react-google-charts | 5.2.1 | MIT |
| react-markdown | 10.1.0 | MIT |
| react-router-dom | 6.28.0 | MIT |
| react-scripts | 5.0.1 | MIT |
| react-syntax-highlighter | 15.6.1 | MIT |
| react-toastify | 11.0.5 | MIT |
| remark-gfm | 4.0.1 | MIT |
| web-vitals | 2.1.4 | Apache-2.0 |
| xlsx | 0.18.5 | Apache-2.0 |
`;

// Auto-generated from api-documentation.md
export const ChatAgentDocumentationApiDocumentation = `# API Documentation

## Table of Contents

1. [Authentication & Security](#1-authentication--security)
2. [Endpoints & Methods](#2-endpoints--methods)
3. [Data Schemas](#3-data-schemas)
4. [Rate Limits & Quotas](#4-rate-limits--quotas)
5. [Error Codes & Responses](#5-error-codes--responses)
6. [Integration Examples](#6-integration-examples)
7. [Version Control & Lifecycle Management](#7-version-control--lifecycle-management)

---

## 1. Authentication & Security

### 1.1 Firebase Authentication Integration

The application uses Firebase Authentication for user authentication. All API requests require a valid Firebase ID token.

#### Obtaining a Token

**Frontend (JavaScript):**
\`\`\`javascript
import { getAuth, signInWithEmailAndPassword } from 'firebase/auth';

const auth = getAuth();
const userCredential = await signInWithEmailAndPassword(auth, email, password);
const idToken = await userCredential.user.getIdToken();
\`\`\`

#### Sending Token with Requests

**Headers:**
\`\`\`
Authorization: Bearer <firebase-id-token>
\`\`\`

**Example:**
\`\`\`javascript
const response = await fetch('https://api.example.com/endpoint', {
  method: 'POST',
  headers: {
    'Authorization': \`Bearer \${idToken}\`,
    'Content-Type': 'application/json'
  },
  body: JSON.stringify(data)
});
\`\`\`

### 1.2 Token Management

#### Token Refresh
Firebase tokens expire after 1 hour. Refresh tokens automatically:

\`\`\`javascript
// Token refresh is handled automatically by Firebase SDK
// But you can manually refresh:
const newToken = await user.getIdToken(true); // Force refresh
\`\`\`

#### Token Validation
Backend validates tokens on each request:
\`\`\`python
from firebase_admin import auth

def verify_token(id_token):
    try:
        decoded_token = auth.verify_id_token(id_token)
        return decoded_token
    except Exception as e:
        return None
\`\`\`

### 1.3 Privacy-Preserving Protocols

#### Payload Sanitization
Sensitive data is automatically sanitized before logging:
\`\`\`python
def sanitize_payload(payload):
    sensitive_fields = ['password', 'token', 'api_key', 'secret', 'auth']
    for key in payload:
        for sensitive in sensitive_fields:
            if sensitive in key.lower():
                payload[key] = "***REDACTED***"
    return payload
\`\`\`

#### Data Encryption
- **In Transit**: All API endpoints use HTTPS/TLS
- **At Rest**: Google Cloud Storage and Firestore use automatic encryption
- **API Keys**: Stored in environment variables, never in code or logs

### 1.4 API Key Security

#### Environment Variables
All API keys stored in environment variables:
\`\`\`bash
OPENAI_API_KEY=sk-...
GOOGLE_API_KEY=...
PERPLEXITY_API_KEY=pplx-...
\`\`\`

#### Task-Based Key Distribution
Different API keys for different tasks prevent rate limit exhaustion:
\`\`\`python
TASK_API_MAPPING = {
    "Page Name Extraction": "OPENAI_API_KEY_USER_INFO",
    "Coercing Scanner Analysis": "OPENAI_API_KEY_PARENT_CATEGORY",
    # ... more mappings
}
\`\`\`

---

## 2. Endpoints & Methods

### 2.1 Image Processing Endpoints

#### POST /upload_and_process_for_aiagents

Main endpoint for image upload and dark pattern analysis.

**Request:**
- **Method**: POST
- **Content-Type**: multipart/form-data
- **Authentication**: Required (Firebase token)

**Parameters:**
\`\`\`json
{
  "image": "<file>",              // Image file (optional if image_url provided)
  "image_url": "<string>",        // Image URL (optional if image provided)
  "url": "<string>",              // Webpage URL (required)
  "note": "<string>",             // User notes (optional)
  "session_id": "<string>",       // Session ID (required)
  "journey_id": "<string>",       // Journey ID (required)
  "image_doc_id": "<string>"      // Image document ID (required)
}
\`\`\`

**Response:**
\`\`\`json
{
  "status": "success",
  "data": {
    "public_url": "https://storage.googleapis.com/...",
    "processed_metadata": {
      "status": "completed",
      "response": [
        {
          "type": "outcome",
          "text": "Hidden costs in checkout",
          "coordinates": {"x": 100, "y": 200},
          "laws": [...],
          "fair_patterns": [...]
        }
      ]
    }
  }
}
\`\`\`

**Status Codes:**
- \`200\`: Success
- \`400\`: Invalid input
- \`401\`: Unauthorized
- \`500\`: Processing error

#### POST /upload_and_process_for_aiagents_figma

Figma-specific endpoint for design analysis (no database operations).

**Request:**
- **Method**: POST
- **Content-Type**: multipart/form-data OR application/json
- **Authentication**: Optional

**Parameters (Form Data):**
\`\`\`json
{
  "image": "<file>",              // Image file OR base64 string
  "image_url": "<string>",        // Image URL
  "filename": "<string>",         // Filename (for base64)
  "content_type": "<string>",      // Content type (for base64)
  "url": "<string>",              // Target URL (optional)
  "note": "<string>"              // User note (optional)
}
\`\`\`

**Parameters (JSON):**
\`\`\`json
{
  "image": "data:image/png;base64,...",  // Base64 encoded image
  "filename": "design.png",
  "content_type": "image/png",
  "url": "https://figma.com/...",
  "note": "Checkout page design"
}
\`\`\`

**Response:**
\`\`\`json
{
  "status": "success",
  "data": {
    "public_url": "https://storage.googleapis.com/...",
    "processed_metadata": {
      "figma_result": {...},
      "response": [...],
      "dark_pattern_analysis": "...",
      "ui_features": "..."
    }
  }
}
\`\`\`

#### POST /upload_and_process_step1

Process only step 1 (information extraction) of image analysis.

**Request:** Same as \`/upload_and_process_for_aiagents\`

**Response:**
\`\`\`json
{
  "status": "success",
  "data": {
    "public_url": "https://storage.googleapis.com/...",
    "response": {
      "ui_features": "...",
      "dark_pattern_analysis": "..."
    },
    "dark_pattern_analysis": "..."
  }
}
\`\`\`

#### POST /upload_and_process_step1_figma

Figma version of step 1 processing (no database).

**Request:** Same as \`/upload_and_process_for_aiagents_figma\`

**Response:** Same as \`/upload_and_process_step1\`

#### POST /get_image_metadata

Get processing status and metadata for an image.

**Request:**
\`\`\`json
{
  "image_url": "<string>",        // Image URL (required)
  "url": "<string>",              // Webpage URL (required)
  "session_id": "<string>",       // Session ID (required)
  "journey_id": "<string>",       // Journey ID (required)
  "image_doc_id": "<string>",     // Image document ID (required)
  "note": "<string>"              // Optional note
}
\`\`\`

**Response:**
\`\`\`json
{
  "status": "completed",
  "image_url": "https://storage.googleapis.com/...",
  "url": "https://example.com",
  "response": [...],
  "createdAt": "2025-11-26T10:00:00Z",
  "completedAt": "2025-11-26T10:01:00Z"
}
\`\`\`

### 2.2 Analysis & Interaction Endpoints

#### POST /get_context_based_questions

Generate 4 specific questions based on provided context.

**Request:**
\`\`\`json
{
  "context": "<string>"  // Context information (required)
}
\`\`\`

**Response:**
\`\`\`json
{
  "status": "success",
  "data": {
    "questions": [
      "Question 1?",
      "Question 2?",
      "Question 3?",
      "Question 4?"
    ]
  }
}
\`\`\`

#### POST /generate_recommendation

Generate recommendations based on analysis outcome.

**Request:**
\`\`\`json
{
  "outcome": "<string>"  // Analysis outcome (required)
}
\`\`\`

**Response:**
\`\`\`json
{
  "status": "success",
  "recommendation": "Recommendation text..."
}
\`\`\`

#### POST /regerate_based_on_feedback

Regenerate analysis based on user feedback.

**Request:**
\`\`\`json
{
  "feedback": "<string>",    // User feedback (required)
  "outcome": "<string>",     // Original outcome (required)
  "imageUrl": "<string>",    // Image URL (required)
  "imageId": "<string>"      // Image ID (required)
}
\`\`\`

**Response:**
\`\`\`json
{
  "status": "success",
  "revised_outcome": {
    "outcome": [...],
    "Hallucination": [...],
    "doubtful": [...]
  },
  "response": [
    {
      "type": "outcome",
      "text": "...",
      "coordinates": {...}
    }
  ]
}
\`\`\`

#### POST /chat_with_image

Chat with LLM about an image based on its context.

**Request:**
\`\`\`json
{
  "session_id": "<string>",   // Session ID (required)
  "journey_id": "<string>",   // Journey ID (required)
  "image_id": "<string>",     // Image ID (required)
  "question": "<string>"      // User's question (required)
}
\`\`\`

**Response:**
\`\`\`json
{
  "status": "success",
  "data": {
    "response": "LLM's response to the question (80-100 words)..."
  }
}
\`\`\`

---

## 3. Data Schemas

### 3.1 Request/Response JSON Structures

#### Image Upload Request
\`\`\`typescript
interface ImageUploadRequest {
  image?: File;              // Optional file upload
  image_url?: string;        // Optional image URL
  url: string;               // Required webpage URL
  note?: string;             // Optional user notes
  session_id: string;         // Required session ID
  journey_id: string;        // Required journey ID
  image_doc_id: string;      // Required image document ID
}
\`\`\`

#### Image Response
\`\`\`typescript
interface ImageResponse {
  type: 'outcome' | 'hallucination' | 'doubtful';
  text: string;
  coordinates: {
    x: number;
    y: number;
  };
  parent_type?: string;
  dark_pattern?: string;
  dark_pattern_details?: {
    name: string;
    type: string;
    context: string;
    source_urls: string[];
    reference_urls: string[];
  };
  laws?: Law[];
  fair_patterns?: FairPattern[];
}
\`\`\`

#### Law Schema
\`\`\`typescript
interface Law {
  law_name: string;
  validation_flag: string;
  availability: string;
  clause_name: string;
  type: string;
  jurisdiction?: string;
  link?: string;
  description?: string;
  validation_reasoning?: string;
}
\`\`\`

#### Fair Pattern Schema
\`\`\`typescript
interface FairPattern {
  name: string;
  description: string;
  implementation_guidance: string;
  examples: string[];
}
\`\`\`

### 3.2 Image Upload Formats

#### Supported Formats
- **Images**: PNG, JPG, JPEG, GIF, BMP, WEBP
- **Documents**: PDF, DOC, DOCX, TXT, RTF, PPT, PPTX
- **Videos**: MP4, AVI, MOV, WMV, FLV, WEBM
- **Audio**: MP3, WAV, AAC, OGG, FLAC
- **Archives**: ZIP, RAR, 7Z, TAR, GZ

#### File Size Limits
- **Maximum**: 10MB per file
- **Recommended**: < 5MB for faster processing

#### Base64 Format
\`\`\`json
{
  "image": "data:image/png;base64,iVBORw0KGgo...",
  "filename": "screenshot.png",
  "content_type": "image/png"
}
\`\`\`

### 3.3 Error Response Formats

#### Standard Error Response
\`\`\`json
{
  "status": "error",
  "message": "Error description",
  "error_code": "ERROR_CODE",
  "details": {
    "field": "Additional error details"
  }
}
\`\`\`

#### Validation Error
\`\`\`json
{
  "status": "error",
  "message": "Invalid input. 'image_url' and 'url' are required.",
  "error_code": "VALIDATION_ERROR",
  "missing_fields": ["image_url", "url"]
}
\`\`\`

### 3.4 Firebase Document Schemas

#### Image Document
\`\`\`typescript
interface ImageDocument {
  status: 'initial' | 'processing' | 'completed' | 'error';
  url: string;
  image_url: string;
  note?: string;
  response?: ImageResponse[];
  figma_result?: object;
  ui_features?: string;
  dark_pattern_analysis?: string;
  processing_metadata?: {
    processing_time?: number;
    model_used?: string;
    tokens_used?: number;
  };
  createdAt: Timestamp;
  completedAt?: Timestamp;
  error?: string;
}
\`\`\`

#### Session Document
\`\`\`typescript
interface SessionDocument {
  sessionName: string;
  createdBy: string;
  createdAt: Timestamp;
  domainName: string;
  adminId: string;
  userRole: string;
  type: string;  // 'dark_pattern' | 'compliance'
  demo?: boolean;
}
\`\`\`

---

## 4. Rate Limits & Quotas

### 4.1 LLM API Rate Limits

#### OpenAI
- **GPT-4o-mini**: 500 requests/minute per key
- **GPT-4o**: 40 requests/minute per key
- **Token Limits**: Varies by model

**Mitigation:**
- Task-based API key distribution
- Multiple keys for different task types
- Exponential backoff on rate limit errors

#### Google Gemini
- **Gemini 2.0 Flash**: 15 requests/minute (free tier)
- **Paid Tier**: Higher limits available

#### Perplexity AI
- **Free Tier**: 5 requests/day
- **Paid Tier**: Higher limits

### 4.2 Google Cloud Quotas

#### Cloud Storage
- **Upload**: 1000 requests/second per bucket
- **Download**: 1000 requests/second per bucket
- **Storage**: Based on project quota

#### Firestore
- **Reads**: 50,000/day (free tier)
- **Writes**: 20,000/day (free tier)
- **Deletes**: 20,000/day (free tier)

#### Cloud Tasks
- **Tasks**: 1000/second per queue
- **Queue**: 1000 queues per project

### 4.3 Firebase Limits

#### Authentication
- **Users**: Unlimited
- **API Calls**: Based on plan

#### Firestore
- **Document Size**: 1MB maximum
- **Collection Size**: Unlimited
- **Query Complexity**: Limited by indexes

### 4.4 Application-Level Limits

#### Image Processing
- **Concurrent**: 4 workers (Gunicorn)
- **Timeout**: 5 minutes per image
- **Queue**: Unlimited (Cloud Tasks)

#### API Requests
- **Rate Limiting**: Not currently implemented
- **Recommended**: < 10 requests/second per user

---

## 5. Error Codes & Responses

### 5.1 HTTP Status Codes

#### Success Codes
- \`200 OK\`: Request successful
- \`201 Created\`: Resource created successfully

#### Client Error Codes
- \`400 Bad Request\`: Invalid input or malformed request
- \`401 Unauthorized\`: Authentication required or invalid token
- \`403 Forbidden\`: Insufficient permissions
- \`404 Not Found\`: Resource not found
- \`413 Payload Too Large\`: File size exceeds limit
- \`415 Unsupported Media Type\`: File type not allowed

#### Server Error Codes
- \`500 Internal Server Error\`: Unexpected server error
- \`502 Bad Gateway\`: Upstream service error
- \`503 Service Unavailable\`: Service temporarily unavailable
- \`504 Gateway Timeout\`: Request timeout

### 5.2 Custom Error Messages

#### Validation Errors
\`\`\`json
{
  "status": "error",
  "message": "Invalid input. 'image_url' and 'url' are required.",
  "error_code": "VALIDATION_ERROR"
}
\`\`\`

#### Authentication Errors
\`\`\`json
{
  "status": "error",
  "message": "Authentication required. Please provide a valid Firebase token.",
  "error_code": "AUTH_REQUIRED"
}
\`\`\`

#### Processing Errors
\`\`\`json
{
  "status": "error",
  "message": "Failed to process image. Please try again.",
  "error_code": "PROCESSING_ERROR",
  "retry_available": true
}
\`\`\`

#### Timeout Errors
\`\`\`json
{
  "status": "error",
  "message": "Processing timeout. The image may be too complex.",
  "error_code": "TIMEOUT_ERROR",
  "suggestion": "Try uploading a smaller or simpler image"
}
\`\`\`

### 5.3 Error Logging Format

All errors are logged to Firebase with the following structure:

\`\`\`json
{
  "api_name": "upload_and_process_for_aiagents",
  "timestamp": "2025-11-26T10:30:00Z",
  "error": "TimeoutError: LLM call timed out",
  "error_code": "TIMEOUT_ERROR",
  "payload": {
    "url": "https://example.com",
    "session_id": "session_123"
  },
  "additional_info": {
    "url": "/upload_and_process_for_aiagents",
    "method": "POST",
    "remote_addr": "192.168.1.1",
    "user_agent": "Mozilla/5.0...",
    "user_id": "user_123"
  }
}
\`\`\`

---

## 6. Integration Examples

### 6.1 Legal Case Tools Integration

#### Example: Integration with Legal Research Platform

\`\`\`python
import requests

def analyze_for_legal_case(image_url, case_id):
    # Upload and analyze image
    response = requests.post(
        'https://api.example.com/upload_and_process_for_aiagents_figma',
        files={'image': open('screenshot.png', 'rb')},
        data={
            'url': 'https://example.com',
            'note': f'Case ID: {case_id}'
        }
    )
    
    results = response.json()
    
    # Extract laws and violations
    violations = []
    for item in results['data']['processed_metadata']['response']:
        if item['type'] == 'outcome':
            violations.append({
                'pattern': item['text'],
                'laws': item.get('laws', []),
                'case_id': case_id
            })
    
    # Send to legal research platform
    legal_platform_api.create_case_evidence(case_id, violations)
    
    return violations
\`\`\`

### 6.2 EMR Integration Patterns

#### Example: Healthcare Compliance Check

\`\`\`python
def check_emr_compliance(emr_screenshot_url):
    # Analyze EMR interface
    response = requests.post(
        'https://api.example.com/upload_and_process_for_aiagents',
        json={
            'image_url': emr_screenshot_url,
            'url': 'https://emr.example.com',
            'note': 'HIPAA compliance check'
        },
        headers={'Authorization': f'Bearer {token}'}
    )
    
    results = response.json()
    
    # Filter for HIPAA-relevant patterns
    hipaa_violations = []
    for item in results['data']['processed_metadata']['response']:
        laws = item.get('laws', [])
        if any('HIPAA' in law.get('law_name', '') for law in laws):
            hipaa_violations.append(item)
    
    return {
        'compliant': len(hipaa_violations) == 0,
        'violations': hipaa_violations
    }
\`\`\`

### 6.3 Finance Dashboard Integration

#### Example: Financial Compliance Monitoring

\`\`\`javascript
async function monitorFinancialDashboard(dashboardUrl) {
  // Capture dashboard screenshot
  const screenshot = await captureScreenshot(dashboardUrl);
  
  // Analyze for compliance issues
  const response = await fetch('https://api.example.com/upload_and_process_for_aiagents_figma', {
    method: 'POST',
    headers: {
      'Authorization': \`Bearer \${firebaseToken}\`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      image: screenshot,
      url: dashboardUrl,
      note: 'Financial compliance check'
    })
  });
  
  const results = await response.json();
  
  // Check for financial regulation violations
  const violations = results.data.processed_metadata.response
    .filter(item => item.type === 'outcome')
    .filter(item => 
      item.laws?.some(law => 
        law.law_name.includes('SEC') || 
        law.law_name.includes('FINRA') ||
        law.law_name.includes('MiFID')
      )
    );
  
  // Send alert if violations found
  if (violations.length > 0) {
    await sendComplianceAlert({
      dashboard: dashboardUrl,
      violations: violations,
      severity: 'high'
    });
  }
  
  return violations;
}
\`\`\`

### 6.4 Webhook Integration

#### Example: Webhook for Analysis Completion

\`\`\`python
# Configure webhook URL in environment
WEBHOOK_URL = os.getenv('WEBHOOK_URL')

def send_webhook_notification(image_id, results):
    payload = {
        'image_id': image_id,
        'status': 'completed',
        'results': results,
        'timestamp': datetime.now().isoformat()
    }
    
    requests.post(
        WEBHOOK_URL,
        json=payload,
        headers={'Content-Type': 'application/json'}
    )
\`\`\`

---

## 7. Version Control & Lifecycle Management

### 7.1 API Versioning Strategy

#### Current Version
- **Version**: v1.0
- **Base URL**: \`https://api.example.com/\`
- **No version prefix**: All endpoints are at root level

#### Future Versioning
Planned URL-based versioning:
- \`https://api.example.com/v1/upload_and_process_for_aiagents\`
- \`https://api.example.com/v2/upload_and_process_for_aiagents\`

#### Header-Based Versioning (Alternative)
\`\`\`
API-Version: 1.0
\`\`\`

### 7.2 Deprecation Policies

#### Deprecation Timeline
1. **Announcement**: 3 months before deprecation
2. **Deprecation**: Endpoint marked as deprecated
3. **Removal**: Endpoint removed after 6 months

#### Deprecation Response
Deprecated endpoints return:
\`\`\`json
{
  "status": "deprecated",
  "message": "This endpoint is deprecated. Use /v2/endpoint instead.",
  "deprecation_date": "2025-12-31",
  "removal_date": "2026-06-30",
  "replacement": "/v2/endpoint"
}
\`\`\`

### 7.3 Backward Compatibility

#### Guaranteed Compatibility
- Response schemas remain stable within major version
- New fields added without breaking existing clients
- Optional parameters remain optional

#### Breaking Changes
Breaking changes only in major version updates:
- Removing fields
- Changing field types
- Removing endpoints
- Changing authentication methods

### 7.4 Change Log

#### Version 1.0.0 (Current)
- Initial API release
- All endpoints stable
- No breaking changes

#### Planned Changes
- Webhook support for async notifications
- Batch processing endpoints
- Enhanced filtering and search
- GraphQL alternative (future)

---

## Conclusion

This API documentation provides comprehensive coverage of all endpoints, authentication, data schemas, and integration patterns. For implementation details, see the [Code Documentation](./code-documentation.md).

**Related Documentation:**
- [Architecture Documentation](./architecture.md) - System design
- [User Manual](./user-manual.md) - User guides
- [Functional Flows](./functional-flows.md) - Process flows

`;

// Auto-generated from architecture.md
export const ChatAgentDocumentationArchitecture = `# Architecture Documentation

## Table of Contents

1. [System Overview](#1-system-overview)
2. [Component-Level Design](#2-component-level-design)
3. [Data Flow & Storage](#3-data-flow--storage)
4. [Deployment Architecture](#4-deployment-architecture)
5. [Scalability & Resilience](#5-scalability--resilience)
6. [Security & Privacy Layers](#6-security--privacy-layers)
7. [Integration Points](#7-integration-points)
8. [Monitoring & Observability](#8-monitoring--observability)

---

## 1. System Overview

### 1.1 Architecture Layers

\`\`\`
┌─────────────────────────────────────────────────────────┐
│                    UI Layer (React)                      │
│  - ChatPanel, Sidebar, ProjectManagement, Login         │
│  - Bootstrap UI components                              │
│  - Real-time updates via Firestore listeners            │
└────────────────────┬────────────────────────────────────┘
                     │
                     │ HTTP/REST API
                     ▼
┌─────────────────────────────────────────────────────────┐
│                  API Layer (Flask)                      │
│  - REST endpoints for image processing                  │
│  - Authentication middleware                            │
│  - Request validation and sanitization                 │
│  - Error handling and logging                           │
└────────────────────┬────────────────────────────────────┘
                     │
                     │ Function calls
                     ▼
┌─────────────────────────────────────────────────────────┐
│                 Model Layer (Multi-Agent)                │
│  - Parent Category Scanners (6 types)                   │
│  - Sub-Agents (30+ specialized)                        │
│  - LLM integration (OpenAI, Gemini, Anthropic)         │
│  - Validation and reasoning engines                     │
└────────────────────┬────────────────────────────────────┘
                     │
                     │ Data operations
                     ▼
┌─────────────────────────────────────────────────────────┐
│              Database Layer (Firestore)                 │
│  - Sessions, Journeys, Images                           │
│  - Real-time synchronization                            │
│  - User management and authentication                   │
└────────────────────┬────────────────────────────────────┘
                     │
                     │ File operations
                     ▼
┌─────────────────────────────────────────────────────────┐
│          Storage Layer (Google Cloud Storage)           │
│  - Image storage (dd-media-files bucket)                │
│  - Public URLs for image access                         │
│  - CDN-ready distribution                               │
└─────────────────────────────────────────────────────────┘
\`\`\`

### 1.2 Compliance Pipeline

\`\`\`
Image Input
    │
    ▼
┌─────────────────┐
│ UI Feature       │
│ Extraction      │
└────────┬────────┘
         │
         ▼
┌─────────────────┐
│ Dark Pattern    │
│ Detection       │
│ (Multi-Agent)   │
└────────┬────────┘
         │
         ▼
┌─────────────────┐
│ Deep            │
│ Validation      │
│ (validation_    │
│  data.json)     │
└────────┬────────┘
         │
         ▼
┌─────────────────┐
│ Law Fetching    │
│ (Perplexity AI) │
└────────┬────────┘
         │
         ▼
┌─────────────────┐
│ Recommendations │
│ Generation      │
└────────┬────────┘
         │
         ▼
┌─────────────────┐
│ Coordinate      │
│ Generation      │
│ (AI Vision)     │
└────────┬────────┘
         │
         ▼
    Response
\`\`\`

### 1.3 Technology Stack Summary

**Frontend:**
- React 19 with TypeScript
- Bootstrap 5 for UI
- Firebase SDK for auth and database
- React Router for navigation
- Axios for HTTP requests

**Backend:**
- Flask (Python 3.11)
- Gunicorn WSGI server
- Firebase Admin SDK
- Google Cloud Storage client
- LangChain for LLM orchestration

**Database:**
- Firebase Firestore (primary)
- Supabase (reasoning storage)

**Storage:**
- Google Cloud Storage

**AI/ML:**
- OpenAI GPT models
- Google Gemini
- Anthropic Claude
- Perplexity AI (legal research)

**Infrastructure:**
- Docker for containerization
- Google Cloud Run for deployment
- Cloud Tasks for background jobs

---

## 2. Component-Level Design

### 2.1 Evaluation Engine

#### DarkPattern Module (\`DarkPattern/version3.py\`)

**Responsibilities:**
- Orchestrates dark pattern detection
- Manages multi-agent analysis pipeline
- Handles Figma-specific analysis
- Stores reasoning for learning

**Key Functions:**
\`\`\`python
async def analyze_dark_patterns_figma(image_url, image_id):
    """
    Analyze Figma design for dark patterns.
    Returns: UI features, dark pattern analysis, processing metadata
    """
    pass

def analyze_with_sub_agents(context, image_url, image_id):
    """
    Run sub-agent analysis pipeline.
    Returns: Structured analysis results
    """
    pass
\`\`\`

**Dependencies:**
- \`utility.LlmCalls\`: LLM API calls
- \`utility.reasoning_storage\`: Reasoning persistence
- \`DarkPattern.dp_sub_agents\`: Sub-agent implementations

#### DigitalViolation Module (\`DigitalViolation/compliance_analysis.py\`)

**Responsibilities:**
- Compliance violation detection
- Page type identification
- Structured compliance reporting
- Law mapping integration

**Key Functions:**
\`\`\`python
def analyze_digital_compliance(image_url, image_id):
    """
    Analyze image for digital compliance violations.
    Returns: Compliance structure with outcome, hallucination, doubtful items
    """
    pass

def identify_page_type(image_url, image_id):
    """
    Identify page type from 10 categories.
    Returns: Page type and justification
    """
    pass
\`\`\`

### 2.2 Benchmarking Module

#### Performance Tracking (\`utility/LlmCalls.py\`)

**Responsibilities:**
- LLM call timing and token tracking
- Cost calculation and logging
- Task-based API key routing
- Error handling and retries

**Key Functions:**
\`\`\`python
def call_llm(model_name, prompt, task_name, image_url=None, image_id=None):
    """
    Unified LLM call interface with tracking.
    Returns: LLM response text
    """
    # Track timing
    # Track tokens
    # Log to Firebase
    # Handle errors
    pass

def log_failure(api_name, payload, error, additional_info):
    """
    Log API failures to Firebase for analysis.
    """
    pass
\`\`\`

**Metrics Tracked:**
- Call duration
- Token usage (input/output/total)
- Cost per call
- Success/failure rates
- Error types and frequencies

### 2.3 Audit Trail Manager

#### Firebase Logging

**Error Logs:**
\`\`\`
firestore/
  error_logs/
    {logId}/
      - api_name: string
      - error: string
      - payload: object (sanitized)
      - timestamp: timestamp
      - user_id: string
      - additional_info: object
\`\`\`

#### User Action Tracking

**Tracked Actions:**
- Session creation
- Image upload
- Analysis requests
- Chat interactions
- Feedback submissions
- Project management actions

**Implementation:**
\`\`\`typescript
const trackUserAction = (action: string, resource: string) => {
  logAction({
    userId: userSession.userId,
    action,
    resource,
    timestamp: new Date(),
    ipAddress: getClientIP(),
    userAgent: navigator.userAgent
  });
};
\`\`\`

---

## 3. Data Flow & Storage

### 3.1 Image Upload Flow

\`\`\`
User Upload
    │
    ▼
┌─────────────────┐
│ Frontend        │
│ (React)         │
│ - File picker   │
│ - Validation    │
└────────┬────────┘
         │
         │ POST /upload_and_process_for_aiagents
         ▼
┌─────────────────┐
│ Backend API     │
│ (Flask)         │
│ - Validate file │
│ - Check size    │
└────────┬────────┘
         │
         │ Upload to GCS
         ▼
┌─────────────────┐
│ Google Cloud    │
│ Storage         │
│ - Store image   │
│ - Generate URL  │
└────────┬────────┘
         │
         │ Create document
         ▼
┌─────────────────┐
│ Firestore       │
│ - Create image  │
│   document      │
│ - Status:       │
│   'initial'     │
└─────────────────┘
\`\`\`

### 3.2 Analysis Processing Flow

\`\`\`
Firestore Document (status: 'initial')
    │
    ▼
┌─────────────────┐
│ Process Image   │
│ - Update status │
│   to            │
│   'processing'  │
└────────┬────────┘
         │
         │ Async processing
         ▼
┌─────────────────┐
│ Multi-Agent     │
│ Analysis        │
│ 1. UI Features  │
│ 2. Dark Patterns│
│ 3. Validation   │
│ 4. Law Fetching │
│ 5. Coordinates  │
└────────┬────────┘
         │
         │ Update document
         ▼
┌─────────────────┐
│ Firestore       │
│ - Update status │
│   to            │
│   'completed'   │
│ - Store results │
└────────┬────────┘
         │
         │ Real-time update
         ▼
┌─────────────────┐
│ Frontend        │
│ - Display       │
│   results       │
│ - Update UI     │
└─────────────────┘
\`\`\`

### 3.3 On-Device Processing Considerations

**Current Architecture:**
- All processing happens server-side
- Images uploaded to cloud storage
- LLM calls made from backend

**Future Considerations:**
- Client-side image preprocessing
- Edge computing for simple validations
- Progressive enhancement

### 3.4 GPU Benchmarking (If Applicable)

**Current State:**
- No GPU-specific processing
- LLM calls use cloud APIs
- Image processing uses CPU (Pillow)

**Potential GPU Usage:**
- Local vision model inference
- Batch image processing
- Custom model training

### 3.5 Compliance Repositories

#### Firestore Collections

**Sessions:**
\`\`\`
sessions-d4v-dark-pattern/
  {sessionId}/
    - sessionName
    - createdBy
    - domainName
    - adminId
    - createdAt
\`\`\`

**User Journeys:**
\`\`\`
sessions-d4v-dark-pattern/{sessionId}/userJourneys/
  {journeyId}/
    - journeyName
    - sessionId
    - createdAt
\`\`\`

**Images:**
\`\`\`
sessions-d4v-dark-pattern/{sessionId}/userJourneys/{journeyId}/images/
  {imageId}/
    - status
    - image_url
    - response
    - laws
    - fair_patterns
\`\`\`

#### Supabase Reasoning Storage

**Structure:**
\`\`\`sql
reasoning_storage (
  id UUID PRIMARY KEY,
  image_id TEXT,
  webpage_category TEXT,
  website_category TEXT,
  reasoning_response TEXT,
  reasoning_type TEXT,
  run_id TEXT,
  created_at TIMESTAMP
)
\`\`\`

**Purpose:**
- Store reasoning for pattern learning
- Enable context-aware detection
- Support continuous improvement

---

## 4. Deployment Architecture

### 4.1 Docker Containerization

#### Backend Container

**Base Image:** \`python:3.11-slim\`

**Components:**
- Python 3.11 runtime
- All Python dependencies
- Chrome and ChromeDriver (for Selenium)
- Google Cloud credentials
- Application code

**Configuration:**
\`\`\`dockerfile
FROM python:3.11-slim
WORKDIR /app
COPY requirements.txt ./
RUN pip install --no-cache-dir -r requirements.txt
# Install Chrome and ChromeDriver
COPY . .
ENV PORT=8080
CMD ["gunicorn", "app:app", "--bind", "0.0.0.0:8080", "--workers", "4", "--threads", "2"]
\`\`\`

**Resource Requirements:**
- CPU: 2+ cores recommended
- Memory: 2GB+ recommended
- Storage: 10GB+ for images and dependencies

### 4.2 Google Cloud Platform Deployment

#### Cloud Run

**Configuration:**
- **Region**: europe-west1 (or preferred)
- **CPU**: 2 vCPU
- **Memory**: 4GB
- **Min instances**: 0 (scale to zero)
- **Max instances**: 10
- **Concurrency**: 80 requests per instance
- **Timeout**: 300 seconds (5 minutes)

**Deployment:**
\`\`\`bash
gcloud run deploy chat-app-backend \\
  --source . \\
  --region europe-west1 \\
  --platform managed \\
  --allow-unauthenticated \\
  --memory 4Gi \\
  --cpu 2 \\
  --timeout 300
\`\`\`

#### Cloud Storage

**Buckets:**
- \`dd-media-files\`: Image storage
- \`dd-saved-result\`: Analysis results (if used)

**Configuration:**
- **Location**: Same region as Cloud Run
- **Storage Class**: Standard
- **Access Control**: Uniform bucket-level access
- **Lifecycle**: Configure retention policies

#### Firestore

**Configuration:**
- **Mode**: Native mode
- **Location**: Same region as other services
- **Indexes**: Composite indexes for queries
- **Security Rules**: Role-based access control

### 4.3 Multi-Cloud Considerations

**Current State:**
- Primary: Google Cloud Platform
- Firebase (Google)
- Supabase (optional, can be self-hosted)

**Multi-Cloud Strategy:**
- **Storage**: Could use AWS S3 or Azure Blob
- **Database**: Could use AWS DynamoDB or Azure Cosmos DB
- **Compute**: Could use AWS Lambda or Azure Functions
- **CDN**: CloudFlare or AWS CloudFront

**Abstraction Layer:**
- Use storage abstraction for multi-cloud support
- Database abstraction for different providers
- API gateway for unified access

### 4.4 On-Premise Deployment Options

#### Requirements
- Docker or Kubernetes cluster
- Network access to:
  - OpenAI API
  - Google APIs (if using)
  - Perplexity API
- Storage solution (local or network)
- Database (PostgreSQL or MongoDB)

#### Deployment Steps
1. Build Docker image
2. Configure environment variables
3. Set up reverse proxy (nginx)
4. Configure SSL certificates
5. Set up monitoring
6. Configure backups

#### Considerations
- API key management
- Network security
- Data residency requirements
- Compliance with regulations

---

## 5. Scalability & Resilience

### 5.1 Gunicorn Worker Configuration

**Current Setup:**
\`\`\`bash
gunicorn app:app \\
  --bind 0.0.0.0:8080 \\
  --workers 4 \\
  --threads 2 \\
  --timeout 0
\`\`\`

**Configuration Rationale:**
- **4 workers**: Handle 4 concurrent requests
- **2 threads per worker**: 8 total concurrent requests
- **Timeout 0**: No timeout for long-running LLM calls

**Scaling:**
- Increase workers based on CPU cores
- Formula: (2 × CPU cores) + 1
- Monitor memory usage per worker

### 5.2 Load Balancing Strategies

#### Cloud Run Load Balancing
- Automatic load balancing
- Request distribution across instances
- Health checks and auto-scaling

#### Application-Level Load Balancing
- Multiple Cloud Run services
- Traffic splitting for A/B testing
- Geographic distribution

### 5.3 Failover Mechanisms

#### Automatic Failover
- Cloud Run: Automatic instance restart
- Firestore: Automatic replication
- Cloud Storage: Multi-region buckets

#### Manual Failover
- Database backup and restore
- Service rollback procedures
- Disaster recovery plan

### 5.4 Continuous Improvement Loops

#### Feedback Loop
\`\`\`
User Feedback
    │
    ▼
┌─────────────────┐
│ Feedback        │
│ Processing      │
└────────┬────────┘
         │
         ▼
┌─────────────────┐
│ Regenerate      │
│ Analysis        │
└────────┬────────┘
         │
         ▼
┌─────────────────┐
│ Update          │
│ Validation Data │
└────────┬────────┘
         │
         ▼
┌─────────────────┐
│ Improved        │
│ Detection       │
└─────────────────┘
\`\`\`

#### Error Analysis Loop
\`\`\`
Error Logs
    │
    ▼
┌─────────────────┐
│ Error Analysis  │
│ - Identify      │
│   patterns      │
│ - Root cause    │
└────────┬────────┘
         │
         ▼
┌─────────────────┐
│ Update Code     │
│ - Fix bugs      │
│ - Improve      │
│   validation    │
└────────┬────────┘
         │
         ▼
┌─────────────────┐
│ Deploy Update   │
└─────────────────┘
\`\`\`

---

## 6. Security & Privacy Layers

### 6.1 Encryption

#### At Rest
- **Google Cloud Storage**: Automatic encryption (AES-256)
- **Firestore**: Automatic encryption
- **Service Account Keys**: Encrypted storage

#### In Transit
- **HTTPS/TLS**: All API endpoints
- **Firebase**: TLS 1.2+
- **API Keys**: Transmitted over secure channels only

### 6.2 Sandboxing for Image Processing

#### Current Implementation
- Docker container isolation
- Process-level sandboxing
- Resource limits (CPU, memory)

#### Enhanced Sandboxing (Future)
- Separate containers for image processing
- Read-only file systems
- Network isolation
- Capability dropping

### 6.3 Assurance Pipelines

#### Input Validation
- File type validation
- File size limits
- URL validation
- Payload sanitization

#### Output Validation
- Response schema validation
- Content filtering
- Sensitive data redaction

### 6.4 Data Privacy Measures

#### Data Minimization
- Only collect necessary data
- Delete old data automatically
- Anonymize logs

#### User Rights
- Data access requests
- Data deletion requests
- Data portability

#### Compliance
- GDPR compliance
- CCPA compliance
- HIPAA considerations (if applicable)

---

## 7. Integration Points

### 7.1 Law Firms (Legal Compliance Tools)

#### Integration Pattern
\`\`\`python
def integrate_with_legal_tool(image_url, case_id):
    # Analyze image
    results = analyze_image(image_url)
    
    # Extract violations
    violations = extract_violations(results)
    
    # Format for legal tool
    legal_format = format_for_legal_tool(violations, case_id)
    
    # Send to legal platform
    legal_tool_api.create_evidence(case_id, legal_format)
\`\`\`

#### Use Cases
- Case evidence collection
- Compliance audit reports
- Legal research integration
- Document generation

### 7.2 Hospitals (Healthcare Compliance)

#### HIPAA Considerations
- PHI (Protected Health Information) handling
- Audit logging requirements
- Access controls
- Data encryption

#### Integration Pattern
\`\`\`python
def check_emr_compliance(emr_screenshot):
    # Analyze for HIPAA violations
    results = analyze_image(emr_screenshot)
    
    # Filter HIPAA-relevant patterns
    hipaa_violations = filter_hipaa_violations(results)
    
    # Generate compliance report
    report = generate_compliance_report(hipaa_violations)
    
    return report
\`\`\`

### 7.3 Finance Risk Engines

#### Regulatory Frameworks
- SEC regulations
- FINRA rules
- MiFID (EU)
- Basel III

#### Integration Pattern
\`\`\`javascript
async function monitorFinancialDashboard(dashboardUrl) {
  // Capture and analyze
  const results = await analyzeDashboard(dashboardUrl);
  
  // Check for financial violations
  const violations = results.filter(item => 
    item.laws.some(law => 
      ['SEC', 'FINRA', 'MiFID'].includes(law.jurisdiction)
    )
  );
  
  // Send to risk engine
  await riskEngine.recordViolations(violations);
  
  return violations;
}
\`\`\`

---

## 8. Monitoring & Observability

### 8.1 Metrics Collection

#### Application Metrics
- Request rate
- Response times
- Error rates
- LLM call latency
- Token usage
- Cost per request

#### Infrastructure Metrics
- CPU utilization
- Memory usage
- Network I/O
- Storage usage
- Database query performance

#### Business Metrics
- Images processed per day
- Patterns detected
- User engagement
- Compliance scores

### 8.2 Dashboard Setup

#### Google Cloud Monitoring
- Cloud Run metrics
- Firestore metrics
- Cloud Storage metrics
- Custom metrics

#### Firebase Console
- Authentication metrics
- Firestore usage
- Error logs
- Performance monitoring

#### Custom Dashboards
- Grafana integration
- Data Studio reports
- Real-time monitoring

### 8.3 Compliance Scoring Rubrics

#### Scoring Components
1. **Pattern Severity** (0-100)
   - Critical: 80-100
   - High: 60-79
   - Medium: 40-59
   - Low: 20-39
   - None: 0-19

2. **Legal Coverage** (0-100)
   - Number of relevant laws
   - Jurisdiction coverage
   - Specific clause references

3. **Remediation Priority** (0-100)
   - Immediate: 80-100
   - High: 60-79
   - Medium: 40-59
   - Low: 20-39

#### Overall Score Calculation
\`\`\`
Overall Score = (Pattern Severity × 0.5) + 
               (Legal Coverage × 0.3) + 
               (Remediation Priority × 0.2)
\`\`\`

### 8.4 Alerting Strategies

#### Alert Types
- **Critical**: System down, data loss
- **High**: High error rate, performance degradation
- **Medium**: Unusual patterns, capacity warnings
- **Low**: Informational alerts

#### Alert Channels
- Email notifications
- Slack integration
- PagerDuty (for critical)
- SMS (for emergencies)

#### Alert Conditions
- Error rate > 5%
- Response time > 10 seconds
- LLM API failures
- Database connection issues
- Storage quota warnings

---

## Conclusion

This architecture documentation provides comprehensive coverage of the system design, components, data flow, deployment, and monitoring. For implementation details, see the [Code Documentation](./code-documentation.md).

**Related Documentation:**
- [Code Documentation](./code-documentation.md) - Technical implementation
- [API Documentation](./api-documentation.md) - API reference
- [Functional Flows](./functional-flows.md) - Process flows
- [User Manual](./user-manual.md) - User guides

`;

// Auto-generated from code-documentation.md
export const ChatAgentDocumentationCodeDocumentation = `# Code Documentation

## Table of Contents

1. [Libraries and Dependencies](#1-libraries-and-dependencies)
2. [Rendering / Deployment Environment and Integrations](#2-rendering--deployment-environment-and-integrations)
3. [Core Code Approach & Design Philosophy](#3-core-code-approach--design-philosophy)
4. [Examples (Standard + Edge Cases)](#4-examples-standard--edge-cases)
5. [Performance Benchmarking & Profiling](#5-performance-benchmarking--profiling)
6. [Database Management & Schema Notes](#6-database-management--schema-notes)
7. [Model Integrations](#7-model-integrations)
8. [Continuous Learning / Improvement Loops](#8-continuous-learning--improvement-loops)
9. [Security & Compliance](#9-security--compliance)
10. [Testing & QA](#10-testing--qa)
11. [Versioning & Change Management](#11-versioning--change-management)
12. [Operational Readiness](#12-operational-readiness)
13. [Developer Experience](#13-developer-experience)

---

## 1. Libraries and Dependencies

### 1.1 Backend Dependencies

The backend application uses Python 3.11 and relies on the following key libraries:

#### Core Framework
- **Flask**: Web framework for building REST APIs
- **Flask-CORS**: Cross-Origin Resource Sharing support for API endpoints
- **gunicorn**: Production WSGI HTTP server (4 workers, 2 threads per worker)

#### Google Cloud Services
- **google-cloud-storage**: Image and file storage in Google Cloud Storage buckets
- **google-cloud-firestore**: NoSQL document database for sessions, journeys, and images
- **firebase-admin**: Firebase SDK for authentication and Firestore operations
- **google-cloud-aiplatform** (>=1.38): Vertex AI integration for advanced ML models
- **vertexai**: Vertex AI Python SDK
- **google-cloud-tasks**: Asynchronous task processing

#### LLM and AI Libraries
- **openai**: OpenAI API client for GPT models
- **google-generativeai**: Google Gemini API client
- **langchain**: Framework for building LLM applications
- **langchain-openai**: LangChain integration for OpenAI
- **langchain-google-genai**: LangChain integration for Google Gemini
- **langchain-community**: Community integrations (vector stores, document loaders)
- **langchain-anthropic**: Anthropic Claude integration
- **langchain-huggingface**: Hugging Face model integration

#### Image and Web Processing
- **Pillow**: Image processing and manipulation
- **selenium**: Web browser automation for screenshot capture
- **webdriver-manager**: Automatic ChromeDriver management
- **beautifulsoup4**: HTML parsing and web scraping
- **tldextract**: Domain name extraction from URLs

#### Data Processing
- **chromadb**: Vector database for embeddings and similarity search
- **unstructured**: Document parsing and processing
- **jq**: JSON processing utilities

#### Other Utilities
- **python-dotenv** (>=0.19.0): Environment variable management
- **requests**: HTTP client library
- **gspread**: Google Sheets API integration
- **oauth2client**: OAuth 2.0 authentication
- **cachetools**: Caching utilities
- **boto3**: AWS SDK (for potential multi-cloud support)
- **supabase** (>=2.0.0): Supabase client for reasoning storage
- **agent-squad[openai]**: Agent framework for OpenAI
- **agent-squad[sql]**: SQL agent capabilities

### 1.2 Frontend Dependencies

The frontend is built with React 19 and TypeScript:

#### Core Framework
- **react** (^19.0.0): UI library
- **react-dom** (^19.0.0): React DOM rendering
- **typescript** (^4.9.5): Type-safe JavaScript

#### UI Components
- **bootstrap** (^5.3.3): CSS framework
- **react-bootstrap** (^2.10.9): Bootstrap components for React
- **bootstrap-icons** (^1.11.3): Icon library

#### Routing and State
- **react-router-dom** (^7.4.0): Client-side routing

#### HTTP and Data
- **axios** (^1.8.4): HTTP client for API calls
- **firebase** (^11.5.0): Firebase SDK for authentication and Firestore

#### Utilities
- **uuid** (^11.1.0): UUID generation

#### Testing
- **@testing-library/react** (^16.2.0): React testing utilities
- **@testing-library/jest-dom** (^6.6.3): DOM matchers for Jest
- **@testing-library/user-event** (^13.5.0): User interaction simulation
- **@testing-library/dom** (^10.4.0): DOM testing utilities

#### Build Tools
- **react-scripts** (5.0.1): Create React App build configuration
- **web-vitals** (^2.1.4): Web performance metrics

---

## 2. Rendering / Deployment Environment and Integrations

### 2.1 Backend Deployment

#### Docker Containerization
The backend is containerized using Docker with the following configuration:

\`\`\`dockerfile
FROM python:3.11-slim
WORKDIR /app
COPY requirements.txt ./
RUN pip install --no-cache-dir -r requirements.txt
RUN apt-get update && apt-get install -y google-chrome-stable
RUN CHROME_DRIVER_VERSION=$(curl -sS https://chromedriver.storage.googleapis.com/LATEST_RELEASE) && \\
    wget -O /tmp/chromedriver.zip https://chromedriver.storage.googleapis.com/$CHROME_DRIVER_VERSION/chromedriver_linux64.zip && \\
    unzip /tmp/chromedriver.zip -d /usr/local/bin/
ENV GOOGLE_APPLICATION_CREDENTIALS="/app/kinetic-mile-439413-k0-bc4aef71e45c.json"
ENV PORT=8080
CMD ["gunicorn", "app:app", "--bind", "0.0.0.0:8080", "--workers", "4", "--threads", "2", "--timeout", "0"]
\`\`\`

**Key Features:**
- Python 3.11-slim base image for minimal size
- Chrome and ChromeDriver installed for Selenium
- Google Cloud credentials mounted as volume
- Gunicorn with 4 workers and 2 threads per worker
- No timeout for long-running LLM operations

#### Environment Variables
Required environment variables:
- \`GOOGLE_APPLICATION_CREDENTIALS\`: Path to GCP service account JSON
- \`GOOGLE_APPLICATION_CREDENTIALS_2\`: Firebase service account path
- \`GCP_PROJECT\`: Google Cloud Project ID
- \`CLOUD_TASKS_QUEUE\`: Cloud Tasks queue name
- \`CLOUD_TASKS_LOCATION\`: Cloud Tasks region
- \`PROCESS_SCREEN_CAPTURE_URL\`: Background processing endpoint URL
- \`MODEL_NAME\`: Default LLM model (default: "gemini-2.0-flash")
- \`OPENAI_API_KEY\`: OpenAI API key
- \`OPENAI_API_KEY_USER_INFO\`: Task-specific OpenAI key
- \`OPENAI_API_KEY_PARENT_CATEGORY\`: Task-specific OpenAI key
- \`OPENAI_API_KEY_AGENTS\`: Task-specific OpenAI key
- \`OPENAI_API_KEY_SCANNER\`: Task-specific OpenAI key
- \`OPENAI_API_KEY_HIGHLIGHTS\`: Task-specific OpenAI key
- \`OPENAI_API_KEY_HARM_MEASURE\`: Task-specific OpenAI key
- \`OPENAI_API_KEY_VALIDATION\`: Task-specific OpenAI key
- \`OPENAI_API_KEY_FACT_CHECK\`: Task-specific OpenAI key
- \`GOOGLE_API_KEY\`: Google API key for Gemini
- \`PERPLEXITY_API_KEY\`: Perplexity API for law fetching
- \`AIRTABLE_API_KEY\`: Airtable API for validation data
- \`BASE_ID\`: Airtable base ID

### 2.2 Frontend Deployment

#### Build Process
The frontend uses Create React App with the following scripts:

\`\`\`json
{
  "start": "react-scripts start",      // Development server (port 3000)
  "build": "react-scripts build",      // Production build
  "test": "react-scripts test",        // Test runner
  "eject": "react-scripts eject"       // Eject from CRA (one-way)
}
\`\`\`

**Production Build:**
- Optimized and minified JavaScript bundles
- CSS extraction and minification
- Asset optimization with content hashing
- Source maps for debugging

#### Environment Configuration
- Development: \`http://localhost:8080\` (backend)
- Production: \`https://deceptive-design-server-d4v-client-413055756357.europe-west1.run.app\`

### 2.3 Integrations

#### Google Cloud Platform
- **Cloud Storage**: Image storage in \`dd-media-files\` bucket
- **Cloud Run**: Containerized backend deployment
- **Cloud Tasks**: Asynchronous background job processing
- **Firestore**: Document database for application data

#### Firebase
- **Authentication**: User login and session management
- **Firestore**: Real-time database for sessions, journeys, images
- **Storage**: File uploads (if used)

#### External APIs
- **OpenAI**: GPT-4o-mini, GPT-4o for various analysis tasks
- **Google Gemini**: Gemini 2.0 Flash for vision and text analysis
- **Anthropic**: Claude models (if configured)
- **Perplexity AI**: Legal information retrieval
- **Airtable**: Validation data storage

---

## 3. Core Code Approach & Design Philosophy

### 3.1 Multi-Agent AI Architecture

The system uses a hierarchical multi-agent architecture for dark pattern detection:

\`\`\`
Parent Category Scanners (6 types)
├── Coercing Scanner
├── Falsification Scanner
├── Misdirection Scanner
├── Obstructing Scanner
├── Sneaking Scanner
└── Omitting Scanner

Sub-Agents (30+ specialized agents)
├── Preselection
├── Hidden Legalese
├── False Social Proof
├── Hidden Costs
├── Bait and Switch
└── ... (many more)
\`\`\`

**Design Principles:**
1. **Modularity**: Each agent is independently callable and testable
2. **Context-Aware**: Agents receive context from parent scanners
3. **Validation Layers**: Multiple validation steps reduce false positives
4. **Task-Based Routing**: Different API keys for different task types to distribute load

### 3.2 Phased Processing Approach

Images are processed in phases with status tracking:

\`\`\`
initial → processing → completed
                ↓
             error
\`\`\`

**Benefits:**
- Resumable processing on failures
- Real-time status updates to frontend
- Clear error states for debugging

### 3.3 Async/Await Pattern

The codebase uses Python's \`asyncio\` for concurrent operations:

\`\`\`python
async def process_phased_image_figma(image_url, url, note, image_id):
    # Async processing without blocking
    result = await analyze_dark_patterns_figma(image_url, image_id)
    return result
\`\`\`

**Use Cases:**
- Figma image processing
- Multiple LLM calls in parallel
- Background task processing

### 3.4 Firebase-First Data Persistence

All application data is stored in Firestore with the following structure:

\`\`\`
sessions-d4v-dark-pattern/
  {sessionId}/
    userJourneys/
      {journeyId}/
        images/
          {imageId}/
            - status
            - image_url
            - response
            - createdAt
            - completedAt
\`\`\`

**Benefits:**
- Real-time updates via Firestore listeners
- Automatic offline support
- Built-in security rules
- Scalable document structure

### 3.5 Validation Layers

The system implements multiple validation layers:

1. **Initial Detection**: LLM agents detect potential dark patterns
2. **Deep Validation**: Validation against \`validation_data.json\` counterexamples
3. **Fact Checking**: Separate fact-checking agent
4. **Hallucination Detection**: Identify false positives
5. **Doubtful Items**: Flag uncertain detections

**Example:**
\`\`\`python
# Deep validation prompt
DEEP_VALIDATION_PROMPT = f"""
Compare detected patterns against counterexamples.
Return "no dark pattern" if false positive.
Only keep clearly manipulative patterns.
"""
\`\`\`

---

## 4. Examples (Standard + Edge Cases)

### 4.1 Standard Flow: Image Upload and Processing

**Standard Request:**
\`\`\`python
POST /upload_and_process_for_aiagents
Content-Type: multipart/form-data

{
  "image": <file>,
  "url": "https://example.com/checkout",
  "note": "User reported confusing checkout",
  "session_id": "session_123",
  "journey_id": "journey_456",
  "image_doc_id": "image_789"
}
\`\`\`

**Processing Steps:**
1. Upload image to Google Cloud Storage
2. Create Firestore document with status "initial"
3. Run dark pattern analysis
4. Deep validation layer
5. Fetch relevant laws
6. Generate coordinates for UI
7. Update Firestore with results

**Response:**
\`\`\`json
{
  "status": "success",
  "data": {
    "public_url": "https://storage.googleapis.com/...",
    "processed_metadata": {
      "status": "completed",
      "response": [
        {
          "type": "outcome",
          "text": "Hidden costs in checkout process",
          "coordinates": {"x": 100, "y": 200},
          "laws": [...],
          "fair_patterns": [...]
        }
      ]
    }
  }
}
\`\`\`

### 4.2 Edge Case: Base64 Image Handling

**Request:**
\`\`\`json
POST /upload_and_process_for_aiagents_figma
Content-Type: application/json

{
  "image": "data:image/png;base64,iVBORw0KGgo...",
  "filename": "design.png",
  "content_type": "image/png"
}
\`\`\`

**Processing:**
\`\`\`python
def get_image_from_request():
    # Check file upload first
    if 'image' in request.files:
        return request.files['image'], filename
    
    # Check base64 in JSON
    if request.is_json:
        image_data = data.get('image')
        if ',' in image_data:
            image_data = image_data.split(',')[1]  # Remove data URL prefix
        image_bytes = base64.b64decode(image_data)
        return BytesIO(image_bytes), filename
\`\`\`

### 4.3 Edge Case: Figma Analysis (No Database)

**Request:**
\`\`\`python
POST /upload_and_process_for_aiagents_figma
\`\`\`

**Key Differences:**
- No Firestore operations
- Direct analysis and response
- No session/journey tracking
- Suitable for one-off design analysis

**Implementation:**
\`\`\`python
async def process_phased_image_figma(image_url, url, note, image_id):
    # Process without database
    figma_result = await analyze_dark_patterns_figma(image_url, image_id)
    # Return results directly
    return {
        'figma_result': figma_result,
        'response': convert_outcomes_to_response(...),
        'status': 'completed'
    }
\`\`\`

### 4.4 Edge Case: Error Recovery in Phased Processing

**Scenario:** Processing fails at step 2 of 3

**Recovery:**
\`\`\`python
async def process_phased_image(session_id, journey_id, image_id, ...):
    image_doc_ref = db.collection(...).document(image_id)
    doc_snap = image_doc_ref.get()
    status = doc_snap.to_dict().get('status', 'initial')
    
    # Resume from last status
    if status == 'initial':
        # Run step 1
        image_doc_ref.update({'status': 'processing'})
    elif status == 'processing':
        # Continue from step 2
        pass
\`\`\`

### 4.5 Edge Case: Large File Uploads

**Configuration:**
\`\`\`python
app.config['MAX_CONTENT_LENGTH'] = 10 * 1024 * 1024  # 10MB limit
\`\`\`

**Validation:**
\`\`\`python
def allowed_file(filename):
    ALLOWED_EXTENSIONS = {
        'png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp',
        'pdf', 'doc', 'docx', 'txt', 'rtf'
    }
    return '.' in filename and \\
           filename.rsplit('.', 1)[1].lower() in ALLOWED_EXTENSIONS
\`\`\`

### 4.6 Edge Case: Network Timeouts

**Handling:**
\`\`\`python
@log_api_failures
def upload_and_process_for_aiagents():
    try:
        # Process with timeout handling
        processed_metadata = asyncio.run(process_phased_image(...))
    except asyncio.TimeoutError:
        # Update status to error, allow retry
        image_doc_ref.update({'status': 'error', 'error': 'timeout'})
        return jsonify({'status': 'error', 'message': 'Processing timeout'}), 500
\`\`\`

---

## 5. Performance Benchmarking & Profiling

### 5.1 LLM Call Optimization

#### Task-Based API Key Distribution
Different API keys are used for different task types to distribute load:

\`\`\`python
TASK_API_MAPPING = {
    "Page Name Extraction": "OPENAI_API_KEY_USER_INFO",
    "Coercing Scanner Analysis": "OPENAI_API_KEY_PARENT_CATEGORY",
    "Subagent 1 Analysis": "OPENAI_API_KEY_AGENTS",
    "Scanner Analysis": "OPENAI_API_KEY_SCANNER",
    # ... more mappings
}
\`\`\`

**Benefits:**
- Prevents rate limit exhaustion on single key
- Allows parallel processing of different task types
- Better cost tracking per task category

#### Token Usage Tracking
All LLM calls are tracked for cost analysis:

\`\`\`python
def call_llm(model_name, prompt, task_name, image_url=None, image_id=None):
    # Track tokens, cost, timing
    with get_openai_callback() as cb:
        response = llm.invoke(messages)
        # Log to Firebase
        log_llm_call(task_name, cb.total_tokens, cb.total_cost, duration)
\`\`\`

### 5.2 Async Processing

**Figma Images:**
\`\`\`python
async def process_figma_images_async(image_url, image_id):
    # Non-blocking async processing
    result = await analyze_dark_patterns_figma(image_url, image_id)
    return result
\`\`\`

**Benefits:**
- Non-blocking I/O operations
- Better resource utilization
- Improved response times

### 5.3 ThreadPoolExecutor Usage

For parallel processing of multiple images:

\`\`\`python
from concurrent.futures import ThreadPoolExecutor, as_completed

with ThreadPoolExecutor(max_workers=4) as executor:
    futures = {
        executor.submit(process_image, img) for img in images
    }
    for future in as_completed(futures):
        result = future.result()
\`\`\`

### 5.4 Firebase Batch Operations

**Batch Writes:**
\`\`\`python
batch = db.batch()
for doc_ref in doc_refs:
    batch.update(doc_ref, {'status': 'completed'})
batch.commit()
\`\`\`

**Benefits:**
- Reduced API calls
- Atomic operations
- Better performance for bulk updates

### 5.5 Image Upload Optimization

**Streaming Upload:**
\`\`\`python
def upload_image_to_cloud(bucket, source_file, session_name, destination_filename):
    blob = bucket.blob(destination_blob_name)
    blob.upload_from_file(source_file)  # Streaming, not loading into memory
    blob.make_public()
    return blob.public_url
\`\`\`

**Caching:**
- Image URLs cached in Firestore
- Avoid re-uploading same images
- CDN-ready public URLs

### 5.6 Performance Metrics

**Key Metrics Tracked:**
- LLM call duration
- Token usage per call
- API response times
- Image processing time
- Firebase operation latency

**Monitoring:**
- Firebase error logs
- Google Cloud Monitoring
- Custom metrics in Firestore

---

## 6. Database Management & Schema Notes

### 6.1 Firestore Collections Structure

#### Sessions Collection
\`\`\`
sessions-d4v-dark-pattern/
  {sessionId}/
    - sessionName: string
    - createdBy: string
    - createdAt: timestamp
    - domainName: string
    - adminId: string
    - userRole: string
    - type: string (default: 'dark_pattern')
\`\`\`

#### User Journeys Subcollection
\`\`\`
sessions-d4v-dark-pattern/{sessionId}/userJourneys/
  {journeyId}/
    - journeyName: string
    - sessionId: string
    - createdAt: timestamp
    - createdBy: string
\`\`\`

#### Images Subcollection
\`\`\`
sessions-d4v-dark-pattern/{sessionId}/userJourneys/{journeyId}/images/
  {imageId}/
    - status: string ('initial' | 'processing' | 'completed' | 'error')
    - url: string
    - image_url: string
    - note: string
    - response: array
    - figma_result: object
    - createdAt: timestamp
    - completedAt: timestamp
    - error: string (if status is 'error')
\`\`\`

### 6.2 Document Schema

#### Image Document Schema
\`\`\`typescript
interface ImageDocument {
  status: 'initial' | 'processing' | 'completed' | 'error';
  url: string;                    // Source webpage URL
  image_url: string;              // GCS public URL
  note?: string;                  // User notes
  response?: ImageResponse[];     // Analysis results
  figma_result?: object;          // Raw Figma analysis
  ui_features?: string;            // Extracted UI features
  dark_pattern_analysis?: string;  // Analysis text
  processing_metadata?: object;    // Processing info
  createdAt: Timestamp;
  completedAt?: Timestamp;
  error?: string;
}
\`\`\`

#### Image Response Schema
\`\`\`typescript
interface ImageResponse {
  type: 'outcome' | 'hallucination' | 'doubtful';
  text: string;
  coordinates: { x: number; y: number };
  parent_type?: string;
  dark_pattern?: string;
  dark_pattern_details?: object;
  laws?: Law[];
  fair_patterns?: FairPattern[];
}
\`\`\`

#### Law Schema
\`\`\`typescript
interface Law {
  law_name: string;
  validation_flag: string;
  availability: string;
  clause_name: string;
  type: string;
  jurisdiction?: string;
  link?: string;
  description?: string;
  validation_reasoning?: string;
}
\`\`\`

### 6.3 Supabase Integration

**Reasoning Storage:**
- Stores occurrence reasoning responses
- Handles duplicate detection via \`run_id\`
- Supports multiple reasoning types

**Schema:**
\`\`\`sql
reasoning_storage (
  id UUID PRIMARY KEY,
  image_id TEXT,
  webpage_category TEXT,
  website_category TEXT,
  reasoning_response TEXT,
  run_id TEXT,
  created_at TIMESTAMP
)
\`\`\`

### 6.4 Indexing Strategies

**Firestore Indexes:**
- Composite index on \`(adminId, createdAt)\` for session queries
- Composite index on \`(sessionId, createdAt)\` for journey queries
- Single-field index on \`status\` for image queries

**Query Optimization:**
- Use \`where()\` clauses before \`orderBy()\`
- Limit result sets with \`limit()\`
- Use pagination for large collections

---

## 7. Model Integrations

### 7.1 LLM Models

#### OpenAI Models
- **GPT-4o-mini**: Primary model for most analysis tasks
- **GPT-4o**: Used for complex reasoning tasks
- **Usage**: Text analysis, pattern detection, validation

#### Google Models
- **Gemini 2.0 Flash**: Primary vision model for image analysis
- **Usage**: Figma analysis, image understanding, UI feature extraction

#### Anthropic Models
- **Claude**: Alternative model for specific tasks
- **Usage**: Complex reasoning, long-context analysis

### 7.2 Task-Based Routing

**Mapping:**
\`\`\`python
TASK_API_MAPPING = {
    # User information extraction
    "Page Name Extraction": "OPENAI_API_KEY_USER_INFO",
    "User Notes Filtering": "OPENAI_API_KEY_USER_INFO",
    
    # Parent category tasks
    "Coercing Scanner Analysis": "OPENAI_API_KEY_PARENT_CATEGORY",
    "Falsification Scanner Analysis": "OPENAI_API_KEY_PARENT_CATEGORY",
    
    # Sub-agent tasks
    "Subagent 1 Analysis": "OPENAI_API_KEY_AGENTS",
    
    # Scanner tasks
    "Scanner Analysis": "OPENAI_API_KEY_SCANNER",
    
    # Outcome synthesis
    "Generate Key Highlights": "OPENAI_API_KEY_HIGHLIGHTS",
    "Validation Outcome": "OPENAI_API_KEY_VALIDATION",
    
    # Gemini tasks
    "Gemini Analysis": "GOOGLE_API_KEY",
    "Gemini Vision Analysis": "GOOGLE_API_KEY",
}
\`\`\`

### 7.3 External APIs

#### Google Cloud Services
- **Cloud Storage**: Image storage and retrieval
- **Firestore**: Real-time database
- **Cloud Tasks**: Background job processing
- **Vertex AI**: Advanced ML model hosting

#### Third-Party APIs
- **Perplexity AI**: Legal information retrieval
- **Airtable**: Validation data storage and retrieval

### 7.4 AutoML Integration

**Vertex AI:**
- Custom model training capabilities
- Model versioning and deployment
- A/B testing support

**Usage:**
\`\`\`python
from google.cloud import aiplatform
aiplatform.init(project=PROJECT_ID, location=LOCATION)
# Deploy custom models for specific pattern detection
\`\`\`

---

## 8. Continuous Learning / Improvement Loops

### 8.1 Feedback-Based Regeneration

**Endpoint:** \`POST /regerate_based_on_feedback\`

**Process:**
\`\`\`python
def regerate_based_on_feedback(image_path, feedback, outcome, image_id):
    prompt = f"""
    Original outcome: {outcome}
    User feedback: {feedback}
    Revise the outcome based on feedback.
    """
    revised = call_llm(prompt, model_name, task_name, image_url=image_path)
    return revised
\`\`\`

**Benefits:**
- User corrections improve accuracy
- System learns from false positives
- Continuous refinement of detections

### 8.2 Validation Data Updates

**File:** \`DarkPattern/validation_data.json\`

**Structure:**
\`\`\`json
{
  "counterexamples": [
    {
      "pattern": "pattern_name",
      "description": "Why this is not a dark pattern",
      "context": "When this pattern is acceptable"
    }
  ]
}
\`\`\`

**Update Process:**
1. Identify false positives
2. Add counterexamples to validation data
3. Deep validation agent uses updated data
4. Reduces future false positives

### 8.3 Reasoning Storage

**Purpose:** Store reasoning for pattern learning

**Implementation:**
\`\`\`python
async def store_occurrence_reasoning(image_id, webpage_category, website_category, reasoning_response):
    storage = ReasoningStorage()
    storage.store_reasoning(
        image_id=image_id,
        reasoning_type='occurrence',
        reasoning_response=reasoning_response,
        run_id=generate_run_id()
    )
\`\`\`

**Benefits:**
- Pattern recognition improvement
- Context-aware detection
- Historical reasoning analysis

### 8.4 Error Logging for Analysis

**Decorator:** \`@log_api_failures\`

**Process:**
\`\`\`python
@log_api_failures
def api_endpoint():
    try:
        # API logic
        pass
    except Exception as e:
        # Auto-logged to Firebase
        log_failure(api_name, payload, error, additional_info)
        raise
\`\`\`

**Analysis:**
- Identify common failure patterns
- Improve error handling
- Update validation rules based on errors

---

## 9. Security & Compliance

### 9.1 Threat Modeling

#### Input Validation
- **File Upload**: Type and size validation
- **URL Validation**: Domain extraction and validation
- **JSON Payload**: Schema validation
- **Sanitization**: Payload sanitization before logging

**Example:**
\`\`\`python
def sanitize_payload(payload):
    sensitive_fields = ['password', 'token', 'api_key', 'secret', 'auth']
    for key in payload:
        for sensitive in sensitive_fields:
            if sensitive in key.lower():
                payload[key] = "***REDACTED***"
    return payload
\`\`\`

#### File Upload Restrictions
\`\`\`python
ALLOWED_EXTENSIONS = {
    'png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp',
    'pdf', 'doc', 'docx', 'txt', 'rtf'
}
MAX_CONTENT_LENGTH = 10 * 1024 * 1024  # 10MB
\`\`\`

#### API Key Management
- Environment variables only
- Never logged or exposed
- Task-based key distribution
- Rotation support

### 9.2 Encryption

#### At Rest
- **Google Cloud Storage**: Automatic encryption
- **Firestore**: Encrypted by default
- **Service Account Keys**: Stored securely, not in code

#### In Transit
- **HTTPS**: All API endpoints use HTTPS
- **TLS**: Firebase connections use TLS
- **API Keys**: Transmitted over secure channels only

### 9.3 Role-Based Access Control

#### User Roles
\`\`\`typescript
type UserRole = 'admin' | 'manager' | 'member' | 'viewer';
\`\`\`

#### Access Control
\`\`\`python
# Organization-based access
adminId = session.get('adminId')
if user.adminId != adminId:
    return jsonify({'error': 'Unauthorized'}), 403
\`\`\`

#### Domain-Based Access
\`\`\`typescript
userDomains: string[]  // User can only access specified domains
\`\`\`

### 9.4 GDPR/HIPAA Hooks

#### Data Retention
- Configurable retention policies
- Automatic deletion of old data
- User-initiated data deletion

#### User Data Deletion
\`\`\`python
@app.route('/delete_user_data', methods=['POST'])
def delete_user_data():
    user_id = request.json.get('user_id')
    # Delete all user data
    delete_user_sessions(user_id)
    delete_user_images(user_id)
    return jsonify({'status': 'deleted'})
\`\`\`

#### Audit Trails
- All operations logged with timestamps
- User action tracking
- Compliance event logging

**Firestore Structure:**
\`\`\`
audit_logs/
  {logId}/
    - userId: string
    - action: string
    - resource: string
    - timestamp: timestamp
    - ipAddress: string
    - userAgent: string
\`\`\`

---

## 10. Testing & QA

### 10.1 Unit Tests Structure

**Frontend Tests:**
\`\`\`typescript
// App.test.tsx
import { render, screen } from '@testing-library/react';
import App from './App';

test('renders login when not authenticated', () => {
  render(<App />);
  expect(screen.getByText(/login/i)).toBeInTheDocument();
});
\`\`\`

**Backend Tests:**
\`\`\`python
# tests/test_agents.py
def test_coercing_scanner():
    result = generate_coercing_scanner(image_url, image_id)
    assert result is not None
    assert 'coercing' in result.lower()
\`\`\`

### 10.2 Integration Test Patterns

**API Integration Tests:**
\`\`\`python
def test_upload_and_process():
    response = client.post('/upload_and_process_for_aiagents', 
        data={'image': test_image, 'url': 'https://test.com'})
    assert response.status_code == 200
    assert 'public_url' in response.json()
\`\`\`

**Firebase Integration Tests:**
\`\`\`python
def test_firestore_operations():
    session_id = create_test_session()
    journey_id = create_test_journey(session_id)
    image_id = create_test_image(session_id, journey_id)
    assert get_image(session_id, journey_id, image_id) is not None
\`\`\`

### 10.3 Error Injection Points

**Test Scenarios:**
1. Invalid image format
2. Oversized file upload
3. Network timeout
4. LLM API failure
5. Firebase connection failure
6. Invalid session/journey IDs

**Example:**
\`\`\`python
def test_invalid_image_format():
    response = client.post('/upload_and_process_for_aiagents',
        data={'image': invalid_file, 'url': 'https://test.com'})
    assert response.status_code == 400
    assert 'invalid' in response.json()['message'].lower()
\`\`\`

### 10.4 Coverage Metrics

**Target Coverage:**
- Unit tests: >80%
- Integration tests: >70%
- API endpoints: 100%

**Tools:**
- **Frontend**: Jest coverage reports
- **Backend**: pytest-cov

**Commands:**
\`\`\`bash
# Frontend
npm test -- --coverage

# Backend
pytest --cov=app --cov-report=html
\`\`\`

---

## 11. Versioning & Change Management

### 11.1 Git Strategy

**Branching Model:**
- \`main\`: Production-ready code
- \`develop\`: Integration branch
- \`feature/*\`: Feature branches
- \`hotfix/*\`: Critical fixes

**Commit Convention:**
\`\`\`
type(scope): subject

body

footer
\`\`\`

**Types:**
- \`feat\`: New feature
- \`fix\`: Bug fix
- \`docs\`: Documentation
- \`refactor\`: Code refactoring
- \`test\`: Tests
- \`chore\`: Maintenance

### 11.2 Release Notes Template

\`\`\`markdown
# Release v1.2.0

## Features
- Added Figma design analysis
- Implemented feedback-based regeneration

## Improvements
- Optimized LLM call routing
- Enhanced validation layer

## Bug Fixes
- Fixed image upload timeout issue
- Resolved Firebase connection errors

## Breaking Changes
- Updated API response format for \`/get_image_metadata\`

## Migration Guide
[Instructions for upgrading]
\`\`\`

### 11.3 Backward Compatibility

**API Versioning:**
- URL-based: \`/api/v1/endpoint\`
- Header-based: \`API-Version: 1.0\`
- Deprecation warnings in responses

**Database Migrations:**
- Schema versioning in Firestore
- Migration scripts for data updates
- Backward-compatible field additions

---

## 12. Operational Readiness

### 12.1 Configuration

#### Environment Variables Checklist
- [ ] \`GOOGLE_APPLICATION_CREDENTIALS\`
- [ ] \`GOOGLE_APPLICATION_CREDENTIALS_2\`
- [ ] \`GCP_PROJECT\`
- [ ] \`CLOUD_TASKS_QUEUE\`
- [ ] \`CLOUD_TASKS_LOCATION\`
- [ ] \`PROCESS_SCREEN_CAPTURE_URL\`
- [ ] \`MODEL_NAME\`
- [ ] All \`OPENAI_API_KEY_*\` variants
- [ ] \`GOOGLE_API_KEY\`
- [ ] \`PERPLEXITY_API_KEY\`
- [ ] \`AIRTABLE_API_KEY\`
- [ ] \`BASE_ID\`

#### Configuration Files
- \`.env\`: Local development
- \`Dockerfile\`: Container configuration
- \`requirements.txt\`: Python dependencies
- \`package.json\`: Node dependencies

### 12.2 Containerization

#### Docker Build
\`\`\`bash
docker build -t chat-app-backend .
\`\`\`

#### Docker Run
\`\`\`bash
docker run -p 8080:8080 \\
  -e GOOGLE_APPLICATION_CREDENTIALS=/app/credentials.json \\
  -v /path/to/credentials.json:/app/credentials.json \\
  chat-app-backend
\`\`\`

#### Docker Compose (Optional)
\`\`\`yaml
version: '3.8'
services:
  backend:
    build: ./Chat-Application-Backend-main
    ports:
      - "8080:8080"
    environment:
      - GOOGLE_APPLICATION_CREDENTIALS=/app/credentials.json
    volumes:
      - ./credentials.json:/app/credentials.json
\`\`\`

### 12.3 Monitoring/Logging

#### Firebase Error Logging
\`\`\`python
log_failure(
    api_name="upload_and_process",
    payload=sanitized_payload,
    error=exception,
    additional_info={
        "url": request.url,
        "method": request.method,
        "remote_addr": request.remote_addr
    }
)
\`\`\`

#### Google Cloud Monitoring
- API latency metrics
- Error rate tracking
- Resource utilization
- Custom metrics

#### Log Levels
- **DEBUG**: Detailed diagnostic information
- **INFO**: General informational messages
- **WARNING**: Warning messages
- **ERROR**: Error conditions
- **CRITICAL**: Critical errors

### 12.4 Rollback Procedures

#### Version Tagging
\`\`\`bash
git tag -a v1.2.0 -m "Release version 1.2.0"
git push origin v1.2.0
\`\`\`

#### Rollback Steps
1. Identify problematic version
2. Checkout previous stable version
3. Rebuild Docker image
4. Deploy to Cloud Run
5. Verify functionality
6. Update environment variables if needed

#### Database Rollback
- Firestore: No automatic rollback
- Manual data restoration from backups
- Schema versioning for compatibility

---

## 13. Developer Experience

### 13.1 Contribution Guidelines

#### Setup
1. Fork the repository
2. Clone your fork
3. Create a feature branch
4. Install dependencies
5. Make changes
6. Write tests
7. Submit pull request

#### Code Style
- **Python**: PEP 8, Black formatter
- **TypeScript**: ESLint, Prettier
- **Commits**: Conventional commits

#### Pull Request Process
1. Update documentation
2. Add tests for new features
3. Ensure all tests pass
4. Request review from maintainers
5. Address feedback
6. Merge after approval

### 13.2 Extensibility Notes

#### Adding New Dark Pattern Types

**Step 1:** Add to parent category scanner
\`\`\`python
# utility/parentCategoryWithScanner.py
def generate_new_pattern_scanner(image_url, image_id):
    prompt = f"""
    Detect new pattern type in image.
    """
    return call_llm(prompt, model_name, task_name, image_url)
\`\`\`

**Step 2:** Create sub-agents
\`\`\`python
# utility/SubAgents.py
def generate_new_pattern_subagent(context, image_url, image_id):
    # Sub-agent logic
    pass
\`\`\`

**Step 3:** Update validation data
\`\`\`json
// DarkPattern/validation_data.json
{
  "new_pattern_type": {
    "description": "...",
    "counterexamples": [...]
  }
}
\`\`\`

#### Adding New LLM Models

**Step 1:** Add model to \`LlmCalls.py\`
\`\`\`python
from langchain_new_provider import NewModelLLM

def call_new_model(prompt, image_url=None):
    llm = NewModelLLM(api_key=os.getenv('NEW_MODEL_API_KEY'))
    return llm.invoke(prompt)
\`\`\`

**Step 2:** Add to task mapping
\`\`\`python
TASK_API_MAPPING = {
    "New Task": "NEW_MODEL_API_KEY",
    # ...
}
\`\`\`

### 13.3 Automated Doc Generation

#### API Documentation
- **Swagger/OpenAPI**: Auto-generate from Flask routes
- **Sphinx**: Python documentation generation

#### Setup Sphinx
\`\`\`bash
pip install sphinx sphinx-rtd-theme
sphinx-quickstart
\`\`\`

#### Generate Docs
\`\`\`bash
sphinx-build -b html docs/ docs/_build/html
\`\`\`

---

## Conclusion

This code documentation provides comprehensive coverage of the Chat Application's technical implementation. For functional flows, user guides, API references, and architecture details, refer to the other documentation sections.

**Next Steps:**
- Review [Functional Flows Documentation](./functional-flows.md)
- Check [User Manual](./user-manual.md) for setup instructions
- See [API Documentation](./api-documentation.md) for endpoint details
- Read [Architecture Documentation](./architecture.md) for system design

`;

// Auto-generated from functional-flows.md
export const ChatAgentDocumentationFunctionalFlows = `# Application Functional Flow Documentation

## Table of Contents

1. [End-to-End Workflow Diagrams](#1-end-to-end-workflow-diagrams)
2. [Use Case Scenarios (Legal Tech)](#2-use-case-scenarios-legal-tech)
3. [Process States & Transitions](#3-process-states--transitions)
4. [Error Handling & Recovery Paths](#4-error-handling--recovery-paths)
5. [Role-Based Access Flows](#5-role-based-access-flows)
6. [Compliance & Audit Trail Integration](#6-compliance--audit-trail-integration)
7. [Interactive Simulation/Game/Dashboard Flows](#7-interactive-simulationgamedashboard-flows)

---

## 1. End-to-End Workflow Diagrams

### 1.1 User Journey: Complete Analysis Flow

\`\`\`
┌─────────────┐
│   Login     │
│  (Firebase) │
└──────┬──────┘
       │
       ▼
┌─────────────┐
│ Create      │
│ Session     │
└──────┬──────┘
       │
       ▼
┌─────────────┐
│ Create User │
│ Journey     │
└──────┬──────┘
       │
       ▼
┌─────────────┐
│ Upload      │
│ Image       │
└──────┬──────┘
       │
       ▼
┌─────────────────┐
│ Upload to GCS    │
│ (Google Cloud    │
│  Storage)        │
└──────┬───────────┘
       │
       ▼
┌─────────────────┐
│ Create Firestore │
│ Document        │
│ (status:        │
│  'initial')     │
└──────┬───────────┘
       │
       ▼
┌─────────────────┐
│ Dark Pattern    │
│ Analysis        │
│ (Multi-Agent)   │
└──────┬───────────┘
       │
       ▼
┌─────────────────┐
│ Deep Validation │
│ (validation_    │
│  data.json)     │
└──────┬───────────┘
       │
       ▼
┌─────────────────┐
│ Law Fetching    │
│ (Perplexity AI) │
└──────┬───────────┘
       │
       ▼
┌─────────────────┐
│ Coordinate      │
│ Generation      │
│ (AI Vision)      │
└──────┬───────────┘
       │
       ▼
┌─────────────────┐
│ Update Firestore│
│ (status:        │
│  'completed')   │
└──────┬───────────┘
       │
       ▼
┌─────────────────┐
│ Display Results │
│ (Frontend)      │
└─────────────────┘
\`\`\`

### 1.2 Image Processing Pipeline

\`\`\`
Image Input
    │
    ├─► File Upload ──┐
    │                 │
    └─► Image URL ────┤
                      │
                      ▼
              ┌───────────────┐
              │ Validate File │
              │ (type, size)  │
              └───────┬───────┘
                      │
                      ▼
              ┌───────────────┐
              │ Upload to GCS │
              │ (Public URL)  │
              └───────┬───────┘
                      │
                      ▼
              ┌───────────────┐
              │ Store Metadata │
              │ (Firestore)    │
              └───────┬───────┘
                      │
                      ▼
              ┌───────────────┐
              │ Analysis Phase│
              │               │
              │ 1. UI Feature │
              │    Extraction │
              │               │
              │ 2. Dark       │
              │    Pattern    │
              │    Detection  │
              │               │
              │ 3. Validation │
              │               │
              │ 4. Law        │
              │    Mapping    │
              └───────┬───────┘
                      │
                      ▼
              ┌───────────────┐
              │ Generate      │
              │ Response      │
              │ (with coords) │
              └───────┬───────┘
                      │
                      ▼
              ┌───────────────┐
              │ Update Status │
              │ 'completed'   │
              └───────────────┘
\`\`\`

### 1.3 Figma Workflow (No Database)

\`\`\`
Figma Design
    │
    ├─► File Upload ──┐
    │                 │
    └─► Base64 ───────┤
                      │
                      ▼
              ┌───────────────┐
              │ Upload to GCS  │
              └───────┬───────┘
                      │
                      ▼
              ┌───────────────┐
              │ Figma Analysis│
              │ (Steps 1 & 2) │
              └───────┬───────┘
                      │
                      ▼
              ┌───────────────┐
              │ Deep          │
              │ Validation    │
              └───────┬───────┘
                      │
                      ▼
              ┌───────────────┐
              │ Return Results│
              │ (No DB Write) │
              └───────────────┘
\`\`\`

---

## 2. Use Case Scenarios (Legal Tech)

### 2.1 E-commerce Dark Pattern Detection

**Scenario:** A legal team needs to audit an e-commerce checkout process for dark patterns.

**Steps:**
1. **Login** to the application
2. **Create Session**: "E-commerce Audit - Q4 2025"
3. **Create User Journey**: "Checkout Process"
4. **Upload Screenshot** of checkout page
5. **Add Note**: "User reported confusion about shipping costs"
6. **Wait for Analysis** (typically 30-60 seconds)
7. **Review Results**:
   - Detected patterns: "Hidden costs in shipping", "Preselected premium shipping"
   - Relevant laws: EU Consumer Rights Directive, UCPD
   - Fair pattern recommendations
8. **Chat with Image**: Ask "What specific clauses apply to hidden costs?"
9. **Export Report** for legal documentation

**Expected Outcomes:**
- Identification of specific dark patterns
- Mapping to relevant regulations
- Actionable recommendations
- Legal documentation support

### 2.2 Cookie Consent Analysis

**Scenario:** Compliance officer needs to verify GDPR compliance of cookie consent banner.

**Steps:**
1. **Create Session**: "GDPR Compliance Audit"
2. **Create User Journey**: "Cookie Consent Banner"
3. **Upload Screenshot** of cookie banner
4. **Analysis Results**:
   - Detected: "Preselection of non-essential cookies"
   - Law: GDPR Article 7, ePrivacy Directive
   - Violation: Pre-ticked boxes for marketing cookies
5. **Generate Recommendation**: "Remove preselection, require explicit opt-in"
6. **Create Task** in project board for development team

**Compliance Impact:**
- Identifies GDPR violations
- Provides specific legal references
- Suggests compliant alternatives

### 2.3 Subscription Cancellation Flow Analysis

**Scenario:** Consumer protection agency investigating subscription cancellation practices.

**Steps:**
1. **Create Session**: "Subscription Cancellation Investigation"
2. **Multiple User Journeys**:
   - "Cancel Subscription - Desktop"
   - "Cancel Subscription - Mobile"
   - "Cancel Subscription - App"
3. **Upload Screenshots** of each cancellation flow
4. **Analysis**:
   - Detected: "Hard to cancel", "Obstruction", "Nagging"
   - Laws: EU Digital Content Directive, UK Consumer Rights Act
5. **Compare Results** across platforms
6. **Generate Compliance Report**

**Legal Relevance:**
- Identifies "dark patterns" in cancellation flows
- Maps to consumer protection laws
- Supports regulatory investigations

### 2.4 Privacy Policy Compliance Check

**Scenario:** Legal team reviewing privacy policy page for transparency issues.

**Steps:**
1. **Create Session**: "Privacy Policy Review"
2. **Upload Screenshot** of privacy policy page
3. **Analysis**:
   - Detected: "Hidden legalese", "Unclear terms"
   - Laws: GDPR Article 12 (Transparency), CCPA
4. **Chat Feature**: "What specific transparency requirements are violated?"
5. **Export Findings** for legal documentation

---

## 3. Process States & Transitions

### 3.1 Image Status States

\`\`\`
┌─────────┐
│ initial │ ◄─── New image uploaded
└────┬────┘
     │
     │ Processing starts
     ▼
┌─────────────┐
│ processing  │ ◄─── Analysis in progress
└────┬────┘
     │
     ├───► Success ────┐
     │                 │
     └───► Error ───────┤
                       │
                       ▼
              ┌─────────────┐
              │  completed  │
              │     OR     │
              │   error    │
              └─────────────┘
\`\`\`

**State Descriptions:**

- **\`initial\`**: Image uploaded, document created, ready for processing
- **\`processing\`**: Analysis in progress (UI features, dark patterns, validation)
- **\`completed\`**: Analysis finished, results available
- **\`error\`**: Processing failed, error message stored

**State Transitions:**

\`\`\`python
# Initial → Processing
image_doc_ref.update({'status': 'processing'})

# Processing → Completed
image_doc_ref.update({
    'status': 'completed',
    'response': results,
    'completedAt': firestore.SERVER_TIMESTAMP
})

# Any → Error
image_doc_ref.update({
    'status': 'error',
    'error': error_message
})
\`\`\`

### 3.2 Session Lifecycle

\`\`\`
┌──────────────┐
│   Created    │
│ (createdAt)  │
└──────┬───────┘
       │
       │ Active
       ▼
┌──────────────┐
│   Active     │ ◄─── User journeys added
│              │      Images uploaded
└──────┬───────┘
       │
       │ End session
       ▼
┌──────────────┐
│   Ended      │
│ (endedAt)    │
└──────────────┘
\`\`\`

### 3.3 User Journey States

\`\`\`
┌──────────────┐
│   Created    │
└──────┬───────┘
       │
       │ Images added
       ▼
┌──────────────┐
│   In Progress│
└──────┬───────┘
       │
       │ All images processed
       ▼
┌──────────────┐
│  Completed   │
└──────────────┘
\`\`\`

---

## 4. Error Handling & Recovery Paths

### 4.1 API Failure Logging Decorator

**Implementation:**
\`\`\`python
@log_api_failures
def api_endpoint():
    # Endpoint logic
    pass
\`\`\`

**What it does:**
1. Catches all exceptions
2. Sanitizes payload (removes sensitive data)
3. Logs to Firebase with context:
   - API name
   - Payload (sanitized)
   - Error message
   - URL, method, IP, user agent
4. Re-raises exception (maintains original error flow)

**Error Log Structure:**
\`\`\`json
{
  "api_name": "upload_and_process_for_aiagents",
  "timestamp": "2025-11-26T10:30:00Z",
  "error": "TimeoutError: LLM call timed out",
  "payload": {
    "url": "https://example.com",
    "session_id": "session_123"
  },
  "additional_info": {
    "url": "/upload_and_process_for_aiagents",
    "method": "POST",
    "remote_addr": "192.168.1.1",
    "user_agent": "Mozilla/5.0..."
  }
}
\`\`\`

### 4.2 Retry Mechanisms

**LLM Call Retries:**
\`\`\`python
def call_llm_with_retry(prompt, max_retries=3):
    for attempt in range(max_retries):
        try:
            return call_llm(prompt)
        except Exception as e:
            if attempt == max_retries - 1:
                raise
            time.sleep(2 ** attempt)  # Exponential backoff
\`\`\`

**Image Upload Retries:**
- Automatic retry on network failures
- Exponential backoff
- Max 3 attempts

### 4.3 Graceful Degradation

**Scenario:** LLM API unavailable

**Fallback:**
1. Log error to Firebase
2. Return partial results if available
3. Set status to 'error' with message
4. Allow user to retry

**User-Facing Error:**
\`\`\`json
{
  "status": "error",
  "message": "Analysis service temporarily unavailable. Please try again in a few moments.",
  "retry_available": true
}
\`\`\`

### 4.4 User-Facing Error Messages

**Error Types:**

1. **Validation Errors** (400):
   - "Invalid input. 'image_url' and 'url' are required."
   - "File type not allowed. Supported: png, jpg, jpeg, gif, bmp, webp"
   - "File size exceeds 10MB limit"

2. **Processing Errors** (500):
   - "Failed to process image. Please try again."
   - "Analysis service temporarily unavailable."

3. **Not Found Errors** (404):
   - "No image found with ID: {image_id}"
   - "Session not found"

4. **Timeout Errors** (504):
   - "Processing timeout. The image may be too complex. Please try again."

---

## 5. Role-Based Access Flows

### 5.1 Admin vs User Permissions

\`\`\`
┌──────────────┐
│   Login      │
└──────┬───────┘
       │
       ├─── Admin ────┐
       │              │
       └─── User ─────┤
                      │
                      ▼
              ┌───────────────┐
              │ Check Role    │
              └───────┬───────┘
                      │
        ┌─────────────┴─────────────┐
        │                             │
        ▼                             ▼
┌───────────────┐          ┌───────────────┐
│ Admin Access  │          │ User Access   │
│               │          │               │
│ - All sessions│          │ - Own sessions│
│ - All domains │          │ - Assigned    │
│ - User mgmt   │          │   domains     │
│ - Settings    │          │ - View only   │
└───────────────┘          └───────────────┘
\`\`\`

### 5.2 Domain-Based Access Control

**Flow:**
1. User logs in
2. System loads \`userDomains\` array
3. User can only:
   - Create sessions for assigned domains
   - View sessions for assigned domains
   - Upload images from assigned domains

**Implementation:**
\`\`\`typescript
// Frontend
const canAccessDomain = (domain: string, userDomains: string[]) => {
  return userDomains.includes(domain) || userDomains.length === 0;
};

// Backend
if user.adminId != session.adminId:
    return jsonify({'error': 'Unauthorized'}), 403
\`\`\`

### 5.3 Project Management Access

**Roles:**
- **Admin**: Full access to all projects
- **Manager**: Can create/edit projects in assigned organization
- **Member**: Can view and comment on assigned tasks
- **Viewer**: Read-only access

**Access Flow:**
\`\`\`
User → Check Role → Check Organization → Check Project Assignment → Grant/Deny Access
\`\`\`

---

## 6. Compliance & Audit Trail Integration

### 6.1 Firebase Timestamp Tracking

**Automatic Timestamps:**
- \`createdAt\`: Document creation time
- \`updatedAt\`: Last update time
- \`completedAt\`: Processing completion time

**Usage:**
\`\`\`python
image_doc_ref.update({
    'createdAt': firestore.SERVER_TIMESTAMP,
    'completedAt': firestore.SERVER_TIMESTAMP
})
\`\`\`

### 6.2 Error Logging with Context

**Logged Information:**
- API endpoint name
- Request payload (sanitized)
- Error message and stack trace
- User information (if available)
- Timestamp
- Request metadata (URL, method, IP, user agent)

**Storage:**
\`\`\`
firestore/
  error_logs/
    {logId}/
      - api_name: string
      - error: string
      - payload: object
      - timestamp: timestamp
      - user_id: string (optional)
      - additional_info: object
\`\`\`

### 6.3 User Action Tracking

**Tracked Actions:**
- Session creation
- Image upload
- Analysis requests
- Chat interactions
- Feedback submissions
- Project management actions

**Implementation:**
\`\`\`typescript
// Frontend
const trackUserAction = (action: string, resource: string) => {
  logAction({
    userId: userSession.userId,
    action,
    resource,
    timestamp: new Date(),
    ipAddress: getClientIP(),
    userAgent: navigator.userAgent
  });
};
\`\`\`

### 6.4 Compliance Event Logging

**Events Logged:**
- Data access (who accessed what)
- Data modifications
- User authentication
- API failures
- Security events

**Retention:**
- Configurable retention period
- GDPR-compliant deletion
- Export capability for audits

---

## 7. Interactive Simulation/Game/Dashboard Flows

### 7.1 Chat Interface with Image Context

**Flow:**
\`\`\`
User Question
    │
    ▼
┌───────────────┐
│ Load Image    │
│ Context from  │
│ Firestore     │
└───────┬───────┘
        │
        ▼
┌───────────────┐
│ Build Prompt  │
│ with Full     │
│ Context       │
└───────┬───────┘
        │
        ▼
┌───────────────┐
│ Call LLM      │
│ (GPT-4o-mini) │
└───────┬───────┘
        │
        ▼
┌───────────────┐
│ Display       │
│ Response      │
│ (80-100 words)│
└───────────────┘
\`\`\`

**Example Interaction:**
- **User**: "What specific laws apply to the hidden costs I see?"
- **System**: Loads image analysis data, extracts law information, generates contextual response
- **Response**: "The hidden costs in your checkout process violate EU Consumer Rights Directive Article 6, which requires clear pricing information. Additionally, UCPD Article 7 prohibits misleading omissions about material information..."

### 7.2 Real-time Analysis Updates

**Frontend Polling:**
\`\`\`typescript
const pollImageStatus = async (imageId: string) => {
  const interval = setInterval(async () => {
    const status = await getImageStatus(imageId);
    updateUI(status);
    
    if (status === 'completed' || status === 'error') {
      clearInterval(interval);
    }
  }, 2000); // Poll every 2 seconds
};
\`\`\`

**Status Updates:**
- \`initial\` → Show "Uploaded, waiting to process"
- \`processing\` → Show "Analyzing image..." with progress indicator
- \`completed\` → Display results
- \`error\` → Show error message with retry option

### 7.3 Compliance Issue Visualization

**Display Format:**
\`\`\`
┌─────────────────────────────────────┐
│ Issue #1: Hidden costs in checkout  │
│                                      │
│ [Expand Laws ▼]                     │
│                                      │
│ ┌─────────────────────────────────┐ │
│ │ Relevant Laws:                  │ │
│ │ • EU Consumer Rights Directive  │ │
│ │ • UCPD Article 7                │ │
│ │                                 │ │
│ │ Fair Patterns:                  │ │
│ │ • Transparent pricing display   │ │
│ │ • Clear cost breakdown          │ │
│ └─────────────────────────────────┘ │
└─────────────────────────────────────┘
\`\`\`

**Interactive Elements:**
- Expand/collapse laws
- Click to view full law text
- Link to external legal resources
- Copy law references

### 7.4 Law Expansion/Collapse Interactions

**UI Component:**
\`\`\`typescript
const ComplianceIssueCard = ({ point, index }) => {
  const [expanded, setExpanded] = useState(false);
  
  return (
    <div className="compliance-issue-card">
      <div className="issue-header">
        <span>{index + 1}. {point.text}</span>
        {point.laws?.length > 0 && (
          <button onClick={() => setExpanded(!expanded)}>
            <i className={\`bi bi-chevron-\${expanded ? 'up' : 'down'}\`}></i>
          </button>
        )}
      </div>
      {expanded && (
        <div className="laws-section">
          {point.laws.map(law => (
            <LawCard key={law.law_name} law={law} />
          ))}
        </div>
      )}
    </div>
  );
};
\`\`\`

**User Experience:**
1. User sees compliance issue
2. Clicks expand icon if laws available
3. Laws slide down with animation
4. User can read law details
5. Click again to collapse

### 7.5 Project Dashboard Flow

**Kanban Board Interaction:**
\`\`\`
Create Board
    │
    ▼
Add Columns (Statuses)
    │
    ▼
Create Tasks
    │
    ▼
Assign to Team Members
    │
    ▼
Move Tasks Across Columns
    │
    ▼
Add Comments & Attachments
    │
    ▼
Mark Complete
\`\`\`

**Task Creation from Analysis:**
1. User views analysis results
2. Clicks "Create Task" on compliance issue
3. Task automatically created in project board
4. Task includes:
   - Issue description
   - Relevant laws
   - Image reference
   - Recommended actions

---

## Conclusion

This functional flow documentation provides comprehensive coverage of how users interact with the system, how data flows through processes, and how errors are handled. For technical implementation details, refer to the [Code Documentation](./code-documentation.md) and [Architecture Documentation](./architecture.md).

**Related Documentation:**
- [User Manual](./user-manual.md) - Step-by-step user guides
- [API Documentation](./api-documentation.md) - API endpoint details
- [Architecture Documentation](./architecture.md) - System design

`;

// Auto-generated from user-manual.md
export const ChatAgentDocumentationUserManual = `# User Manual / Documentation

## Table of Contents

1. [Getting Started Guide](#1-getting-started-guide)
2. [Feature Walkthroughs](#2-feature-walkthroughs)
3. [Interactive Tutorials](#3-interactive-tutorials)
4. [Troubleshooting & FAQs](#4-troubleshooting--faqs)
5. [Glossary of Terms](#5-glossary-of-terms)
6. [Accessibility & Localization Notes](#6-accessibility--localization-notes)
7. [Versioning & Release Notes](#7-versioning--release-notes)

---

## 1. Getting Started Guide

### 1.1 Installation Prerequisites

#### Backend Requirements
- **Python**: 3.11 or higher
- **Docker**: For containerized deployment (optional)
- **Google Cloud Platform Account**: For storage and Firestore
- **Firebase Project**: For authentication and database
- **API Keys**:
  - OpenAI API key (or multiple keys for different tasks)
  - Google API key (for Gemini)
  - Perplexity API key (for law fetching)
  - Airtable API key (for validation data)

#### Frontend Requirements
- **Node.js**: 16.x or higher
- **npm**: 8.x or higher (comes with Node.js)
- **Firebase Project**: Same as backend

### 1.2 Environment Setup

#### Backend Environment Variables

Create a \`.env\` file in the \`Chat-Application-Backend-main\` directory:

\`\`\`bash
# Google Cloud
GOOGLE_APPLICATION_CREDENTIALS=/path/to/service-account.json
GOOGLE_APPLICATION_CREDENTIALS_2=/path/to/firebase-service-account.json
GCP_PROJECT=your-project-id
CLOUD_TASKS_QUEUE=your-queue-name
CLOUD_TASKS_LOCATION=europe-west1
PROCESS_SCREEN_CAPTURE_URL=https://your-backend-url.com/process

# LLM Models
MODEL_NAME=gemini-2.0-flash
OPENAI_API_KEY=sk-...
OPENAI_API_KEY_USER_INFO=sk-...
OPENAI_API_KEY_PARENT_CATEGORY=sk-...
OPENAI_API_KEY_AGENTS=sk-...
OPENAI_API_KEY_SCANNER=sk-...
OPENAI_API_KEY_HIGHLIGHTS=sk-...
OPENAI_API_KEY_HARM_MEASURE=sk-...
OPENAI_API_KEY_VALIDATION=sk-...
OPENAI_API_KEY_FACT_CHECK=sk-...
GOOGLE_API_KEY=your-google-api-key

# External APIs
PERPLEXITY_API_KEY=pplx-...
AIRTABLE_API_KEY=pat-...
BASE_ID=your-base-id
\`\`\`

#### Frontend Environment Configuration

Create a \`.env\` file in the \`Chat-Application-Frontend-main\` directory (optional for local development):

\`\`\`bash
REACT_APP_API_BASE_URL=http://localhost:8080
# Or for production:
# REACT_APP_API_BASE_URL=https://your-backend-url.com
\`\`\`

### 1.3 Firebase Project Setup

#### Step 1: Create Firebase Project
1. Go to [Firebase Console](https://console.firebase.google.com/)
2. Click "Add project"
3. Enter project name
4. Enable Google Analytics (optional)
5. Create project

#### Step 2: Enable Authentication
1. In Firebase Console, go to "Authentication"
2. Click "Get started"
3. Enable "Email/Password" sign-in method
4. Save

#### Step 3: Create Firestore Database
1. Go to "Firestore Database"
2. Click "Create database"
3. Start in "Production mode" (you can add security rules later)
4. Choose location (preferably same as Cloud Run region)
5. Enable

#### Step 4: Get Service Account Key
1. Go to Project Settings → Service Accounts
2. Click "Generate new private key"
3. Download JSON file
4. Save as \`firebase-service-account.json\` in backend directory

#### Step 5: Configure Security Rules
\`\`\`javascript
rules_version = '2';
service cloud.firestore {
  match /databases/{database}/documents {
    match /sessions-d4v-dark-pattern/{sessionId} {
      allow read, write: if request.auth != null;
      match /userJourneys/{journeyId} {
        allow read, write: if request.auth != null;
        match /images/{imageId} {
          allow read, write: if request.auth != null;
        }
      }
    }
  }
}
\`\`\`

### 1.4 Google Cloud Storage Bucket Configuration

#### Step 1: Create Storage Bucket
1. Go to [Google Cloud Console](https://console.cloud.google.com/)
2. Navigate to "Cloud Storage" → "Buckets"
3. Click "Create bucket"
4. Name: \`dd-media-files\` (or update in code)
5. Choose location type: Region
6. Select region (e.g., \`europe-west1\`)
7. Choose storage class: Standard
8. Access control: Uniform
9. Create

#### Step 2: Set Bucket Permissions
1. Click on bucket name
2. Go to "Permissions" tab
3. Add service account with "Storage Admin" role

#### Step 3: Make Bucket Public (for image URLs)
1. Go to "Permissions" tab
2. Add "allUsers" with "Storage Object Viewer" role
3. Or use signed URLs (more secure)

### 1.5 Running Locally vs Production

#### Local Development

**Backend:**
\`\`\`bash
cd Chat-Application-Backend-main
pip install -r requirements.txt
python app.py
# Server runs on http://localhost:8080
\`\`\`

**Frontend:**
\`\`\`bash
cd Chat-Application-Frontend-main
npm install
npm start
# App runs on http://localhost:3000
\`\`\`

#### Production Deployment

**Backend (Docker):**
\`\`\`bash
docker build -t chat-app-backend .
docker run -p 8080:8080 \\
  -e GOOGLE_APPLICATION_CREDENTIALS=/app/credentials.json \\
  -v /path/to/credentials.json:/app/credentials.json \\
  chat-app-backend
\`\`\`

**Backend (Cloud Run):**
\`\`\`bash
gcloud run deploy chat-app-backend \\
  --source . \\
  --region europe-west1 \\
  --allow-unauthenticated
\`\`\`

**Frontend:**
\`\`\`bash
npm run build
# Deploy build/ folder to hosting service (Firebase Hosting, Netlify, etc.)
\`\`\`

---

## 2. Feature Walkthroughs

### 2.1 Framework Generation: How Dark Pattern Detection Works

#### Overview
The system uses a multi-agent AI architecture to detect dark patterns in user interfaces.

#### Process Flow

1. **Image Upload**
   - User uploads screenshot or design file
   - Image stored in Google Cloud Storage
   - Metadata created in Firestore

2. **UI Feature Extraction**
   - AI analyzes image to extract UI elements
   - Identifies buttons, text, forms, modals, etc.
   - Creates structured representation

3. **Parent Category Scanning**
   - Six parent category scanners analyze the image:
     - **Coercing**: Forcing user actions
     - **Falsification**: False claims or information
     - **Misdirection**: Distracting or misleading
     - **Obstructing**: Making actions difficult
     - **Sneaking**: Hidden information or costs
     - **Omitting**: Missing important information

4. **Sub-Agent Analysis**
   - 30+ specialized sub-agents detect specific patterns
   - Examples: Hidden costs, preselection, false urgency, bait and switch

5. **Deep Validation**
   - Validates detections against counterexamples
   - Reduces false positives
   - Ensures only manipulative patterns are flagged

6. **Law Mapping**
   - Fetches relevant laws from legal databases
   - Maps patterns to specific regulations
   - Provides jurisdiction-specific information

7. **Coordinate Generation**
   - AI vision identifies exact locations of patterns
   - Generates bounding box coordinates
   - Enables visual highlighting in UI

#### Example Output
\`\`\`json
{
  "type": "outcome",
  "text": "Hidden shipping costs revealed only at checkout",
  "coordinates": {"x": 150, "y": 300},
  "parent_type": "Sneaking",
  "dark_pattern": "Hidden costs - commerce",
  "laws": [
    {
      "law_name": "EU Consumer Rights Directive",
      "clause_name": "Article 6 - Clear pricing",
      "jurisdiction": "EU"
    }
  ],
  "fair_patterns": [
    "Display all costs upfront",
    "Clear cost breakdown"
  ]
}
\`\`\`

### 2.2 Benchmarking: Performance Metrics Interpretation

#### Key Metrics

**Processing Time:**
- **Fast**: < 30 seconds (simple pages, few patterns)
- **Normal**: 30-60 seconds (standard complexity)
- **Slow**: > 60 seconds (complex pages, many patterns)

**Detection Accuracy:**
- **High Confidence**: Clear manipulative patterns
- **Medium Confidence**: Likely patterns, may need review
- **Low Confidence**: Uncertain, marked as "doubtful"

**Token Usage:**
- Tracked per analysis
- Visible in Firebase logs
- Used for cost optimization

#### Understanding Results

**Outcome Items:**
- High-confidence dark pattern detections
- Include law mappings and recommendations
- Actionable for compliance teams

**Hallucination Items:**
- False positives detected by validation
- Filtered out from main results
- Logged for system improvement

**Doubtful Items:**
- Uncertain detections
- Require human review
- May be patterns or legitimate design

### 2.3 Reporting: Understanding Compliance Scores and Recommendations

#### Compliance Score Components

1. **Pattern Severity**
   - Critical: Direct legal violations
   - High: Strong manipulation indicators
   - Medium: Potential issues
   - Low: Minor concerns

2. **Legal Coverage**
   - Number of relevant laws found
   - Jurisdiction coverage
   - Specific clause references

3. **Remediation Priority**
   - Immediate: Legal violations
   - High: User harm likely
   - Medium: Best practice violations
   - Low: Minor improvements

#### Recommendations

Each detected pattern includes:
- **Description**: What the pattern is
- **Impact**: How it affects users
- **Legal Basis**: Which laws apply
- **Fair Patterns**: Alternative compliant designs
- **Implementation**: How to fix

#### Export Options

- **PDF Report**: Formatted compliance report
- **JSON Export**: Machine-readable data
- **CSV Export**: For spreadsheet analysis
- **Integration**: Direct to project management tools

---

## 3. Interactive Tutorials

### 3.1 Step-by-Step: Image Upload and Analysis

#### Step 1: Login
1. Open the application
2. Enter your email and password
3. Click "Login"
4. You'll be redirected to the main dashboard

#### Step 2: Create a Session
1. Click "New Session" button
2. Enter session name (e.g., "E-commerce Audit Q4")
3. Select or enter domain name
4. Click "Create"
5. Session appears in sidebar

#### Step 3: Create a User Journey
1. Click on your session
2. Click "New User Journey"
3. Select from predefined journeys or create custom:
   - Landing page
   - Account creation
   - Product page
   - Purchasing journey
   - Subscription journey
   - Cookie banner
   - Personal data preferences
4. Click "Create"

#### Step 4: Upload an Image
1. Click "Upload Image" button
2. Choose file or paste screenshot
3. Enter webpage URL (where screenshot was taken)
4. Add optional note (e.g., "User reported confusion")
5. Click "Upload and Analyze"

#### Step 5: Wait for Analysis
- Status indicator shows progress
- Typically takes 30-60 seconds
- You can navigate away and return

#### Step 6: Review Results
1. View detected dark patterns
2. Expand laws to see legal references
3. Review fair pattern recommendations
4. Check coordinates for visual highlighting

#### Step 7: Interact with Results
- Click "Chat" to ask questions about the analysis
- Click "Create Task" to add to project board
- Click "Export" to download report

### 3.2 Interpreting Dark Pattern Results

#### Understanding the Results Panel

**Issue Cards:**
- Numbered list of detected patterns
- Each card shows:
  - Pattern description
  - Pattern type (coercing, sneaking, etc.)
  - Expandable law section
  - Fair pattern recommendations

**Visual Indicators:**
- Red border: Critical violation
- Orange border: High priority
- Yellow border: Medium priority
- Blue border: Low priority

**Law Expansion:**
- Click chevron icon to expand
- View relevant laws and regulations
- Click law name for more details
- Copy law references

#### Pattern Types Explained

**Coercing:**
- Forcing user to take actions
- Example: "You must accept cookies to continue"

**Falsification:**
- False claims or misleading information
- Example: "Only 3 items left!" when many available

**Misdirection:**
- Distracting from important information
- Example: Bright "Continue" button, dim "Cancel"

**Obstructing:**
- Making desired actions difficult
- Example: Hidden unsubscribe link

**Sneaking:**
- Hidden information or costs
- Example: Shipping costs revealed at checkout

**Omitting:**
- Missing important information
- Example: No clear privacy policy link

### 3.3 Using the Chat Feature

#### Accessing Chat
1. Select an analyzed image
2. Click "Chat with Image" button
3. Chat panel opens on the right

#### Asking Questions
- "What laws apply to the hidden costs?"
- "Explain the preselection pattern"
- "What are the compliance risks?"
- "How should we fix this issue?"

#### Understanding Responses
- Responses are 80-100 words
- Based on complete image analysis data
- Include specific legal references
- Provide actionable insights

#### Best Practices
- Ask specific questions for better answers
- Reference issue numbers for clarity
- Use legal terminology for precise responses

### 3.4 Project Management Features

#### Creating a Project Board
1. Navigate to "Projects" in sidebar
2. Click "New Board"
3. Enter board name and description
4. Select board template (optional)
5. Click "Create"

#### Adding Tasks from Analysis
1. View analysis results
2. Click "Create Task" on any issue
3. Task automatically created with:
   - Issue description
   - Relevant laws
   - Image reference
   - Recommended actions

#### Managing Tasks
- Drag tasks between columns (To Do, In Progress, Done)
- Assign tasks to team members
- Add comments and attachments
- Set due dates and priorities
- Add custom properties

#### Kanban Board Features
- Visual workflow management
- Custom status columns
- Filter and sort tasks
- Group by assignee, priority, or custom fields
- Export board data

---

## 4. Troubleshooting & FAQs

### 4.1 Common Errors and Solutions

#### Error: "Invalid input. 'image_url' and 'url' are required."
**Solution:** Make sure you provide both the image file/URL and the webpage URL where the screenshot was taken.

#### Error: "File type not allowed"
**Solution:** Supported formats: PNG, JPG, JPEG, GIF, BMP, WEBP, PDF, DOC, DOCX, TXT, RTF. Convert your file to one of these formats.

#### Error: "File size exceeds 10MB limit"
**Solution:** Compress your image or use a smaller resolution. Recommended: Max 1920x1080 pixels.

#### Error: "Failed to process image"
**Solution:**
1. Check your internet connection
2. Verify backend service is running
3. Try uploading again
4. If persistent, check Firebase logs

#### Error: "Analysis timeout"
**Solution:**
- Image may be too complex
- Try cropping to specific area of interest
- Reduce image resolution
- Contact support if issue persists

### 4.2 Image Upload Issues

#### Image Not Appearing
- Check file format (must be image file)
- Verify file size (< 10MB)
- Clear browser cache
- Try different browser

#### Upload Stuck
- Check network connection
- Refresh page
- Try smaller image
- Check browser console for errors

#### Base64 Image Issues
- Ensure proper format: \`data:image/png;base64,...\`
- Remove data URL prefix if needed
- Check encoding is correct

### 4.3 API Timeout Handling

#### Understanding Timeouts
- Standard analysis: 30-60 seconds
- Complex images: Up to 2 minutes
- Timeout occurs after 5 minutes

#### What to Do
1. Wait for processing to complete
2. Check status indicator
3. Refresh page if stuck
4. Try uploading again with smaller image

#### Prevention
- Use screenshots, not full-page captures
- Crop to relevant area
- Reduce image resolution if needed
- Avoid very complex designs

### 4.4 Firebase Connection Problems

#### Error: "Firebase connection failed"
**Solution:**
1. Check internet connection
2. Verify Firebase project is active
3. Check Firebase service status
4. Verify credentials are correct

#### Error: "Permission denied"
**Solution:**
1. Check user is logged in
2. Verify user has access to session
3. Contact admin for access
4. Check Firebase security rules

#### Session Not Loading
**Solution:**
1. Refresh page
2. Clear browser cache
3. Log out and log back in
4. Check browser console for errors

### 4.5 Frequently Asked Questions

#### Q: How accurate is the dark pattern detection?
**A:** The system uses multiple validation layers to reduce false positives. Accuracy is typically 85-90% for clear patterns. Uncertain detections are marked as "doubtful" for human review.

#### Q: Which jurisdictions are supported?
**A:** Currently supports EU, US, and UK. More jurisdictions can be added on request.

#### Q: Can I analyze Figma designs?
**A:** Yes! Use the Figma-specific endpoints. Designs are analyzed without database storage for quick feedback.

#### Q: How long are analysis results stored?
**A:** Results are stored indefinitely unless you delete them. You can configure retention policies in settings.

#### Q: Can I export results?
**A:** Yes, you can export as PDF, JSON, or CSV. Integration with project management tools is also available.

#### Q: Is my data secure?
**A:** Yes. All data is encrypted in transit and at rest. Images are stored in Google Cloud Storage with access controls.

#### Q: Can multiple users work on the same session?
**A:** Yes, if they have access to the same organization and domain. Real-time updates are synchronized.

#### Q: What if I disagree with a detection?
**A:** Use the feedback feature to provide corrections. The system learns from feedback to improve accuracy.

---

## 5. Glossary of Terms

### 5.1 Dark Patterns

**Coercing:**
Manipulative design that forces users to take actions they don't want to take, often by making alternatives difficult or impossible.

**Falsification:**
False claims, misleading information, or deceptive statements designed to influence user behavior.

**Misdirection:**
Design that distracts users from important information or guides attention away from negative aspects.

**Obstructing:**
Making desired actions (like cancellation or opting out) difficult, hidden, or confusing.

**Sneaking:**
Hiding important information, costs, or terms until after user commitment.

**Omitting:**
Failing to provide important information that users need to make informed decisions.

### 5.2 Compliance Terms

**GDPR (General Data Protection Regulation):**
EU regulation governing data protection and privacy for individuals within the European Union.

**CCPA (California Consumer Privacy Act):**
California state law providing privacy rights and consumer protection for California residents.

**UCPD (Unfair Commercial Practices Directive):**
EU directive prohibiting unfair business-to-consumer commercial practices.

**CRD (Consumer Rights Directive):**
EU directive on consumer rights in distance and off-premises contracts.

**DMA (Digital Markets Act):**
EU regulation promoting fair and open digital markets.

**DSA (Digital Services Act):**
EU regulation on intermediary services and online platforms.

### 5.3 AI/ML Concepts

**LLM (Large Language Model):**
AI model trained on vast amounts of text data, capable of understanding and generating human-like text.

**Vision Model:**
AI model trained to understand and analyze images, identifying objects, text, and patterns.

**Multi-Agent System:**
AI architecture using multiple specialized agents that work together to solve complex problems.

**Prompt Engineering:**
The practice of designing effective prompts to get desired outputs from AI models.

**Token:**
Unit of text processed by LLMs. Token usage affects API costs and rate limits.

**Embedding:**
Numerical representation of text or images used for similarity search and retrieval.

### 5.4 Technical Terms

**Firestore:**
Google's NoSQL document database, used for storing application data.

**GCS (Google Cloud Storage):**
Object storage service for storing files and images.

**API Endpoint:**
URL where API requests are sent to perform specific operations.

**Webhook:**
HTTP callback that sends data to a specified URL when an event occurs.

**CORS (Cross-Origin Resource Sharing):**
Mechanism allowing web pages to make requests to different domains.

**JWT (JSON Web Token):**
Compact token format used for authentication and authorization.

---

## 6. Accessibility & Localization Notes

### 6.1 Current Accessibility Features

#### Keyboard Navigation
- All interactive elements are keyboard accessible
- Tab order follows logical flow
- Enter/Space activate buttons
- Escape closes modals

#### Screen Reader Support
- Semantic HTML elements
- ARIA labels where needed
- Alt text for images
- Form labels associated with inputs

#### Visual Accessibility
- High contrast mode support
- Resizable text
- Focus indicators
- Color not sole indicator

#### Known Limitations
- Some complex interactions may require mouse
- Image analysis results may need screen reader improvements
- Chat interface could benefit from better ARIA support

### 6.2 Internationalization Considerations

#### Current Language Support
- English (primary)
- UI text in English
- Law content in original language (EU, US, UK)

#### Localization Roadmap
- Multi-language UI support planned
- Jurisdiction-specific law translations
- Date/time formatting by locale
- Currency formatting

#### Character Encoding
- UTF-8 support for all text
- Proper handling of special characters
- Right-to-left language support (planned)

### 6.3 Browser Compatibility

#### Supported Browsers
- Chrome 90+ (recommended)
- Firefox 88+
- Safari 14+
- Edge 90+

#### Known Issues
- Internet Explorer: Not supported
- Older browsers: May have limited functionality
- Mobile browsers: Full support on iOS Safari and Chrome Android

#### Recommendations
- Use latest browser version
- Enable JavaScript
- Allow cookies for session management
- Enable local storage

---

## 7. Versioning & Release Notes

### 7.1 Version History

#### Version 1.0.0 (Current)
**Release Date:** November 2025

**Features:**
- Multi-agent dark pattern detection
- Figma design analysis
- Chat interface with image context
- Project management with Kanban boards
- Law mapping and compliance scoring
- Multi-tenant organization support

**Improvements:**
- Deep validation layer reduces false positives
- Task-based API key distribution
- Async processing for better performance
- Enhanced error logging

**Known Issues:**
- Some complex images may timeout
- Mobile UI needs optimization
- Limited language support

### 7.2 Feature Additions

#### Recent Additions
- Feedback-based regeneration
- Figma integration (no database mode)
- Base64 image support
- Real-time status updates
- Export functionality (PDF, JSON, CSV)

#### Upcoming Features
- Multi-language UI support
- Additional jurisdiction coverage
- Advanced filtering and search
- Custom validation rules
- API webhooks

### 7.3 Breaking Changes

#### Version 1.0.0
- None (initial release)

#### Migration Notes
- No migrations required for initial setup
- Database schema is forward-compatible
- API endpoints remain stable

### 7.4 Update Instructions

#### Backend Updates
\`\`\`bash
git pull origin main
pip install -r requirements.txt
# Restart service
\`\`\`

#### Frontend Updates
\`\`\`bash
git pull origin main
npm install
npm run build
# Deploy build/
\`\`\`

#### Database Updates
- Automatic schema updates
- No manual migration needed
- Backward compatible changes only

---

## Conclusion

This user manual provides comprehensive guidance for using the Chat Application. For technical details, see the [Code Documentation](./code-documentation.md) and [API Documentation](./api-documentation.md).

**Need Help?**
- Check [Troubleshooting & FAQs](#4-troubleshooting--faqs)
- Review [Functional Flows](./functional-flows.md) for process details
- Contact support for additional assistance

`;

// Auto-generated from functional-summary.md
export const ChromeExtensionDocumentationFunctionalSummary = `# Functional Summary: Deceptive Design Detection System

## Overview

The Deceptive Design Detection System is a Chrome Extension that helps legal professionals, compliance officers, and researchers identify and document dark patterns and digital rights violations. It provides comprehensive audit trails, visual evidence capture, and AI-powered compliance analysis for EU and US Federal jurisdictions.

## Core Utility

**Primary Use Cases:** Legal compliance audits, regulatory compliance verification (GDPR/FTC/EU DSA), UX research, privacy policy audits, evidence collection for legal cases.

**Key Capabilities:** Real-time interaction tracking, visual evidence capture (screenshots), jurisdiction-specific AI analysis, comprehensive audit trails, asynchronous scalable processing.

## 10 Critical Design Decisions

### 1. User Session Initialization & Authentication (UX/Security)
**Decision:** Chrome Identity API + Local Storage + Email Header Authentication  
**Rationale:** Leverages built-in browser security (no password storage), persists session state across restarts, simple effective authentication without token complexity.  
**Benefit:** Secure, seamless authentication with clear audit trail linking actions to authenticated users.

### 2. Granular User Interaction Logging (Client Design)
**Decision:** Content Script + Human-Readable Descriptions + Background Worker Processing  
**Rationale:** Content script captures real DOM interactions, human-readable descriptions make logs understandable to non-technical users (lawyers/compliance officers), background worker centralizes processing.  
**Benefit:** Detailed, understandable audit trails without performance impact.  
**Example:** \`"clicked on 'Accept Cookies' button"\`, \`"scrolled to section: Privacy Policy"\`

### 3. Throttled Scroll Tracking (Performance Nuance)
**Decision:** 500ms throttle + 300px minimum distance threshold  
**Rationale:** Prevents browser performance degradation, balances responsiveness with performance, filters minor scroll adjustments.  
**Benefit:** Maintains browser performance, reduces Firestore writes by ~90%, optimizes storage costs.

### 4. Navigation and Action Attribution (Accuracy/State Management)
**Decision:** Tab History Tracking + Chrome webNavigation Events + Action Differentiation  
**Rationale:** Enables accurate navigation cause attribution, provides comprehensive context, improves audit trail accuracy.  
**Benefit:** Accurate distinction between "clicked on link" vs "form submission and navigation" vs generic page loads.

### 5. Data Persistence & Collection Routing (Schema Design)
**Decision:** Array Union + Dynamic Collection Selection (sessions/digital-sessions) + Hierarchical Structure  
**Rationale:** Atomic updates prevent data loss, segregates audit types for different processing pipelines, logical organization enables efficient querying.  
**Benefit:** Data integrity, clear separation of audit types, scalable organization.  
**Structure:** \`Session → Journey → Images → Navigation Data\`

### 6. Media Capture & Storage (Cloud Integration)
**Decision:** Direct Cloud Storage Upload + Session-Based Organization + Timestamp Folders  
**Rationale:** Scalable/reliable/cost-effective for large media files, enables easy evidence retrieval, prevents naming conflicts.  
**Benefit:** Unlimited screenshot storage, easy legal case evidence retrieval, cost-effective solution.

### 7. Decoupled Processing Orchestration (Scalability Decision)
**Decision:** Google Cloud Tasks + Non-Blocking Architecture + Asynchronous Processing  
**Rationale:** Decouples request handling from processing (enables horizontal scaling), prevents API timeouts, allows concurrent requests without queueing.  
**Benefit:** Fast API responses (<500ms), scalable to high concurrent load, resilient to processing failures.  
**Flow:** \`User Request → API (200ms) → Cloud Task → Immediate Response | Background Processing (5-15s) → Status Update\`

### 8. Full-Page Screenshot Utility (Robustness Nuance)
**Decision:** Headless Chrome + Anti-Detection Measures + Scroll-to-Bottom + Viewport Adjustment  
**Rationale:** Some websites block automated browsers (anti-detection ensures success), modern sites use lazy loading (scrolling loads all content), standard Selenium captures viewport only (adjustment captures full page).  
**Benefit:** Reliable capture across diverse websites, complete page capture including below-fold content, avoids blocking mechanisms.

### 9. Administrative Audit Logging (Compliance Requirement)
**Decision:** Fixed Admin Document + Session Completion Trigger + Domain Tracking  
**Rationale:** Centralized location for compliance activity (easy reporting), captures complete sessions not partial data, enables analysis of audited domains.  
**Benefit:** Centralized compliance reporting, easy domain identification, administrative oversight.

### 10. Error and Failure Handling (Operational Readiness)
**Decision:** Try/Catch Everywhere + Status Updates + Continue on Failure  
**Rationale:** Prevents cascading failures (graceful degradation), provides visibility without blocking workflow, user can still capture even if processing fails.  
**Benefit:** Resilient to partial failures, administrative visibility, non-blocking user experience.

## System Architecture Benefits

**Scalability:** Cloud Run auto-scaling, asynchronous processing handles high concurrent load, decoupled components enable independent scaling.  
**Reliability:** Graceful error handling, atomic Firestore operations, automatic retry for transient failures.  
**Security:** Chrome Identity API authentication, session ownership validation, minimal data collection with user control.  
**Performance:** <500ms API responses, throttled events prevent degradation, efficient organized storage structure.

## Technical Stack

**Frontend:** React 18.3.1, TypeScript 5.6.2, Chrome Extension APIs, Firebase SDK 11.2.0  
**Backend:** Flask (Python 3.10), Google Cloud Run, Cloud Tasks, Selenium  
**Infrastructure:** Firestore (NoSQL), Cloud Storage (object storage), Cloud Tasks (async queue)

## Use Case Examples

**Legal Case:** Attorney creates session → creates "Checkout Process" journey → navigates/captures → system logs all interactions → generates comprehensive audit trail for court evidence.

**Compliance Audit:** Officer creates digital violation session → enters Terms URLs → creates multiple journeys → captures with HTML → reviews AI compliance analysis → generates report.

**Research Study:** Researcher creates session → multiple journeys for different flows → captures/logs interactions → analyzes patterns across websites → exports for statistical analysis.

## Conclusion

The system achieves fast responsive UX, scalable high-load performance, resilient error handling, secure authentication, and comprehensive compliance audit trails. These 10 design decisions work together to create a robust, scalable, user-friendly system meeting the needs of legal professionals, compliance officers, and researchers documenting deceptive design patterns.
`;

// Auto-generated from api-versioning.md
export const ChromeExtensionDocumentationApiApiVersioning = `# API Versioning & Lifecycle Management

## Versioning Strategy

### Current Version
- **API Version**: 1.0
- **Base URL**: \`https://dd-extension-server-413055756357.europe-west1.run.app\`

### Versioning Approach
- **URL Versioning**: Future versions may use \`/v1/\`, \`/v2/\` in URL
- **Header Versioning**: \`X-API-Version\` header (future)
- **Semantic Versioning**: Major.Minor.Patch

## Endpoint Lifecycle

### Stable Endpoints
- \`/screen_capture\` - Stable
- \`/screenshot\` - Stable
- \`/digital_violation_capture\` - Stable

### Deprecation Policy

1. **Announcement**: 3 months before deprecation
2. **Support Period**: 6 months after deprecation
3. **Removal**: After support period ends

### Migration Guide

When endpoints are deprecated:
1. Review migration documentation
2. Update client code
3. Test with new endpoints
4. Deploy updated version

## Backward Compatibility

### Guaranteed Compatibility
- Response format changes (additive only)
- New optional fields
- New endpoints

### Breaking Changes
- Removed fields
- Changed field types
- Removed endpoints
- Changed authentication

## Change Management

### Version Updates
- **Major**: Breaking changes
- **Minor**: New features, backward compatible
- **Patch**: Bug fixes, backward compatible

### Release Notes
- Document all changes
- Provide migration guides
- Include examples
- List breaking changes

## Future Versions

### Planned Features
- OAuth2 authentication
- GraphQL API (optional)
- Webhook support
- Real-time updates
- Batch operations

`;

// Auto-generated from authentication.md
export const ChromeExtensionDocumentationApiAuthentication = `# Authentication & Security

## Chrome Identity API

### Usage
\`\`\`javascript
chrome.identity.getProfileUserInfo({ accountStatus: 'ANY' }, (info) => {
  if (info.email) {
    // Use email for authentication
  }
});
\`\`\`

### Authentication Flow
1. Extension requests user identity
2. Chrome returns user email
3. Email used in API requests
4. Backend validates email header

## API Authentication

### Header-Based Authentication

**Header**: \`X-User-Email\`

**Example**:
\`\`\`javascript
fetch('https://api-endpoint/screen_capture', {
  method: 'POST',
  headers: {
    'X-User-Email': 'user@example.com'
  },
  body: formData
});
\`\`\`

### Backend Validation

\`\`\`python
user_email = request.headers.get('X-User-Email', None)
if not user_email:
    return jsonify({'message': 'Missing X-User-Email header'}), 403
\`\`\`

## Future OAuth2 Implementation

### Token Management
- Bearer token authentication
- Token refresh mechanism
- Token validation
- Scope-based access control

### Privacy-Preserving Protocols
- Zero-knowledge authentication
- Encrypted token storage
- Secure token transmission

`;

// Auto-generated from data-schemas.md
export const ChromeExtensionDocumentationApiDataSchemas = `# Data Schemas

## Session Creation

### Request (Frontend to Firestore)
\`\`\`javascript
{
  sessionName: string,
  type: "dark_pattern" | "digital_violation",
  createdBy: string,  // User email
  createdAt: Timestamp,
  domainName: string,
  jurisdictions: string[],  // ["EU", "US Federal"]
  metadata: {
    browserInfo: string,
    timestamp: string,
    url: string
  },
  termsUrls?: string[]  // Only for digital_violation
}
\`\`\`

## Journey Creation

### Request (Frontend to Firestore)
\`\`\`javascript
{
  journeyName: string,
  createdBy: string,
  createdAt: Timestamp,
  navigationData: []
}
\`\`\`

## Image Metadata

### Structure (Firestore)
\`\`\`javascript
{
  Url: string,
  type: "Screen Capture" | "Full Page Screenshot",
  note: string,
  imageUrl: string,
  htmlUrl?: string,  // For digital violations
  createdAt: Timestamp,
  status: "initial" | "processing" | "completed" | "failed",
  processingStatus?: string,
  processingError?: string
}
\`\`\`

## Navigation Data

### Structure
\`\`\`javascript
{
  from: string,  // Source URL
  to: string,   // Destination URL
  action: string,  // Action description
  timestamp: string,  // ISO timestamp
  tabId: string,
  interactionType?: string  // "clicked", "scrolled", "submitted_form"
}
\`\`\`

## Screen Capture Request

### FormData
\`\`\`
sessionId: string
journeyName: string
url: string
note: string (optional)
image: File (Blob)
html: File (Blob, optional, for digital violations)
\`\`\`

## Cloud Task Payload

### Structure
\`\`\`json
{
  "session_id": "string",
  "journey_id": "string",
  "image_doc_id": "string",
  "public_url": "string",
  "session_name": "string",
  "url": "string",
  "note": "string",
  "html_url": "string (optional)"
}
\`\`\`

## Error Response

### Structure
\`\`\`json
{
  "status": "error" | "partial_success",
  "message": "Error description"
}
\`\`\`

`;

// Auto-generated from endpoints.md
export const ChromeExtensionDocumentationApiEndpoints = `# API Endpoints & Methods

## Base URL

\`\`\`
https://dd-extension-server-413055756357.europe-west1.run.app
\`\`\`

## Endpoints

### POST /screen_capture

**Description**: Capture visible tab and upload to Cloud Storage

**Headers**:
- \`X-User-Email\`: User email (required)

**Request Body** (multipart/form-data):
- \`sessionId\`: Session ID (required)
- \`journeyName\`: Journey name (required)
- \`url\`: Page URL (required)
- \`note\`: User note (optional)
- \`image\`: Image file (required)

**Response**:
\`\`\`json
{
  "status": "success",
  "message": "Image saved and processing initiated successfully."
}
\`\`\`

**Status Codes**:
- \`200\`: Success
- \`400\`: Missing required fields
- \`403\`: Missing X-User-Email header
- \`404\`: Session or journey not found
- \`500\`: Server error

### POST /screenshot

**Description**: Take full page screenshot using Selenium

**Headers**:
- \`X-User-Email\`: User email (required)

**Request Body** (JSON):
\`\`\`json
{
  "sessionId": "session-id",
  "journeyName": "journey-name",
  "url": "https://example.com",
  "note": "Optional note"
}
\`\`\`

**Response**:
\`\`\`json
{
  "status": "success",
  "message": "Screenshot saved and processing initiated successfully."
}
\`\`\`

**Status Codes**: Same as \`/screen_capture\`

### POST /digital_violation_capture

**Description**: Capture for digital violation analysis (includes HTML)

**Headers**:
- \`X-User-Email\`: User email (required)

**Request Body** (multipart/form-data):
- \`sessionId\`: Session ID (required)
- \`journeyName\`: Journey name (required)
- \`url\`: Page URL (required)
- \`note\`: User note (optional)
- \`image\`: Image file (required)
- \`html\`: HTML file (optional, for digital violations)

**Response**:
\`\`\`json
{
  "status": "success",
  "message": "Digital violation image saved and processing initiated successfully."
}
\`\`\`

**Status Codes**: Same as \`/screen_capture\`

### OPTIONS Endpoints

All endpoints support OPTIONS for CORS preflight:
- \`/screen_capture\` (OPTIONS)
- \`/screenshot\` (OPTIONS)
- \`/digital_violation_capture\` (OPTIONS)

**Response**: \`204 No Content\`

`;

// Auto-generated from error-codes-responses.md
export const ChromeExtensionDocumentationApiErrorCodesResponses = `# Error Codes & Responses

## HTTP Status Codes

### 200 OK
**Success Response**
\`\`\`json
{
  "status": "success",
  "message": "Operation completed successfully"
}
\`\`\`

### 400 Bad Request
**Missing Required Fields**
\`\`\`json
{
  "message": "Missing required fields"
}
\`\`\`

**Invalid Data Format**
\`\`\`json
{
  "status": "error",
  "message": "Invalid data format"
}
\`\`\`

### 403 Forbidden
**Missing Authentication**
\`\`\`json
{
  "message": "Missing X-User-Email header"
}
\`\`\`

**Unauthorized Access**
\`\`\`json
{
  "message": "Unauthorized"
}
\`\`\`

### 404 Not Found
**Session Not Found**
\`\`\`json
{
  "message": "Session not found"
}
\`\`\`

**Journey Not Found**
\`\`\`json
{
  "message": "Journey not found"
}
\`\`\`

### 500 Internal Server Error
**Server Error**
\`\`\`json
{
  "status": "error",
  "message": "Failed to capture image: [error details]"
}
\`\`\`

**Storage Error**
\`\`\`json
{
  "status": "error",
  "message": "Failed to upload image to cloud storage: [error]"
}
\`\`\`

## Partial Success Responses

### 200 Partial Success
**Image Saved, Processing Failed**
\`\`\`json
{
  "status": "partial_success",
  "message": "Image saved but failed to initiate processing."
}
\`\`\`

## Error Handling Patterns

### Frontend Error Handling
\`\`\`javascript
try {
  const response = await fetch(endpoint, options);
  const result = await response.json();
  
  if (response.ok) {
    // Success
  } else {
    // Handle error
    setStatus(\`Error: \${result.message}\`, 'danger');
  }
} catch (err) {
  // Network or other error
  setStatus('Failed to complete operation', 'danger');
}
\`\`\`

### Backend Error Handling
\`\`\`python
try:
    # Operation
    result = perform_operation()
    return jsonify({'status': 'success', ...}), 200
except ValidationError as e:
    return jsonify({'message': str(e)}), 400
except NotFoundError as e:
    return jsonify({'message': str(e)}), 404
except Exception as e:
    return jsonify({'status': 'error', 'message': str(e)}), 500
\`\`\`

## Common Error Scenarios

### Network Errors
- **Timeout**: Request took too long
- **Connection Refused**: Server unavailable
- **DNS Error**: Domain resolution failed

### Validation Errors
- **Missing Fields**: Required fields not provided
- **Invalid Format**: Data format incorrect
- **Business Rules**: Validation rules violated

### Permission Errors
- **Unauthorized**: Missing or invalid credentials
- **Forbidden**: Insufficient permissions
- **Session Ownership**: Not session owner

### System Errors
- **Database Error**: Firestore connection failed
- **Storage Error**: Cloud Storage upload failed
- **Processing Error**: Background processing failed

## Error Recovery

### Automatic Retry
- Network errors: Retry with exponential backoff
- Transient errors: Retry up to 3 times
- Permanent errors: Show error message

### User Actions
- **Retry**: User can retry failed operations
- **Contact Support**: For persistent errors
- **Check Status**: Verify system status

`;

// Auto-generated from integration-examples.md
export const ChromeExtensionDocumentationApiIntegrationExamples = `# Integration Examples

## Legal Case Management Tools

### Integration Pattern
\`\`\`javascript
// Export session data for case management
const exportSessionData = async (sessionId) => {
  const session = await getSession(sessionId);
  const journeys = await getJourneys(sessionId);
  const images = await getAllImages(sessionId);
  
  return {
    session,
    journeys,
    images,
    exportDate: new Date().toISOString()
  };
};
\`\`\`

### Use Cases
- Export evidence for legal cases
- Generate compliance reports
- Document violations
- Create case files

## EMR Systems (Healthcare)

### Integration Pattern
\`\`\`javascript
// Check privacy policy compliance
const checkPrivacyCompliance = async (privacyPolicyUrl) => {
  const session = await createDigitalViolationSession({
    sessionName: "Privacy Policy Compliance Check",
    termsUrls: [privacyPolicyUrl],
    jurisdictions: ["EU"]
  });
  
  // Capture privacy-related pages
  // Analyze for HIPAA/GDPR compliance
  return session;
};
\`\`\`

### Use Cases
- Privacy policy audits
- Consent mechanism verification
- Data handling compliance
- Patient rights verification

## Finance Risk Dashboards

### Integration Pattern
\`\`\`javascript
// Analyze terms for financial compliance
const analyzeFinancialTerms = async (termsUrl) => {
  const session = await createDigitalViolationSession({
    sessionName: "Financial Terms Analysis",
    termsUrls: [termsUrl],
    jurisdictions: ["US Federal"]
  });
  
  // Capture terms pages
  // Analyze for FTC compliance
  return session;
};
\`\`\`

### Use Cases
- Terms & conditions audits
- Consumer protection compliance
- Financial disclosure verification
- Risk assessment

## Webhook Integrations (Future)

### Webhook Pattern
\`\`\`javascript
// Webhook endpoint for external systems
app.post('/webhook/session-completed', async (req, res) => {
  const { sessionId, status, results } = req.body;
  
  // Process webhook
  // Notify external system
  // Update status
  
  res.json({ status: 'received' });
});
\`\`\`

### Use Cases
- Real-time notifications
- External system integration
- Automated reporting
- Workflow automation

## API Client Examples

### JavaScript/TypeScript
\`\`\`javascript
class ExtensionAPI {
  constructor(baseURL, userEmail) {
    this.baseURL = baseURL;
    this.userEmail = userEmail;
  }
  
  async captureScreen(sessionId, journeyName, url, imageBlob, note) {
    const formData = new FormData();
    formData.append('sessionId', sessionId);
    formData.append('journeyName', journeyName);
    formData.append('url', url);
    formData.append('image', imageBlob);
    formData.append('note', note);
    
    const response = await fetch(\`\${this.baseURL}/screen_capture\`, {
      method: 'POST',
      headers: {
        'X-User-Email': this.userEmail
      },
      body: formData
    });
    
    return response.json();
  }
}
\`\`\`

### Python
\`\`\`python
import requests

class ExtensionAPI:
    def __init__(self, base_url, user_email):
        self.base_url = base_url
        self.user_email = user_email
    
    def capture_screen(self, session_id, journey_name, url, image_path, note=""):
        with open(image_path, 'rb') as image_file:
            files = {'image': image_file}
            data = {
                'sessionId': session_id,
                'journeyName': journey_name,
                'url': url,
                'note': note
            }
            headers = {'X-User-Email': self.user_email}
            
            response = requests.post(
                f'{self.base_url}/screen_capture',
                files=files,
                data=data,
                headers=headers
            )
            
            return response.json()
\`\`\`

`;

// Auto-generated from rate-limits-quotas.md
export const ChromeExtensionDocumentationApiRateLimitsQuotas = `# Rate Limits & Quotas

## Cloud Tasks Queue

### Limits
- **Queue Depth**: Configurable (default: 1000 tasks)
- **Task Creation Rate**: 100 tasks/second
- **Task Execution**: Asynchronous, no rate limit

### Quotas
- Monitor queue depth
- Alert on backlog
- Auto-scaling available

## Firestore

### Write Quotas
- **Free Tier**: 20,000 writes/day
- **Paid Tier**: Higher limits
- **Document Size**: 1MB max

### Read Quotas
- **Free Tier**: 50,000 reads/day
- **Paid Tier**: Higher limits

### Best Practices
- Batch writes when possible
- Use transactions for atomic operations
- Implement pagination for large queries

## Perplexity API

### Rate Limits
- **Free Tier**: Limited requests
- **Paid Tier**: Higher limits
- Check API documentation for current limits

### Best Practices
- Cache analysis results
- Reuse analysis for same domain
- Implement retry with backoff

## Cloud Storage

### Limits
- **File Size**: 5TB max per object
- **Bucket Size**: Unlimited
- **Upload Rate**: Depends on network

### Quotas
- Monitor storage usage
- Implement lifecycle policies
- Archive old files

## Extension API Limits

### Chrome Storage
- **Local Storage**: 10MB max
- **Sync Storage**: 100KB max (if using sync)

### Tab Capture
- **Rate**: No specific limit
- **Size**: Limited by browser memory

## Recommendations

1. **Monitor Usage**: Track API usage and quotas
2. **Implement Caching**: Reduce API calls
3. **Batch Operations**: Combine multiple operations
4. **Error Handling**: Handle rate limit errors gracefully
5. **Retry Logic**: Implement exponential backoff

`;

// Auto-generated from component-design.md
export const ChromeExtensionDocumentationArchitectureComponentDesign = `# Component-Level Design

## Extension Popup (React)

### Component Structure
\`\`\`
App.jsx
  ├── Session Management
  │   ├── Session Creation
  │   ├── Session Selection
  │   └── Session Stopping
  ├── Journey Management
  │   ├── Journey Creation
  │   ├── Journey Selection
  │   └── Journey Stopping
  ├── Capture Interface
  │   ├── Note Input
  │   ├── Screen Capture Button
  │   └── Full Page Button
  └── Domain Analysis
      ├── Analysis Trigger
      └── Recommendations Display
\`\`\`

### State Management
- React hooks (useState, useEffect)
- Chrome storage for persistence
- Firestore for server state

## Background Service Worker

### Responsibilities
- Navigation tracking
- User interaction logging
- Tab management
- Message handling

### Event Listeners
- \`chrome.tabs.onUpdated\`
- \`chrome.webNavigation.onCommitted\`
- \`chrome.runtime.onMessage\`

## Content Script

### Responsibilities
- Track user interactions
- Monitor page changes
- Send interaction data to background

### Event Listeners
- \`click\` events
- \`scroll\` events
- \`submit\` events

## Backend Flask Application

### Structure
\`\`\`
app.py
  ├── Flask App Initialization
  ├── CORS Configuration
  ├── Firestore Setup
  ├── Cloud Storage Setup
  └── API Endpoints
      ├── /screen_capture
      ├── /screenshot
      └── /digital_violation_capture
\`\`\`

### Utility Functions
\`\`\`
utility/ChromeExtension.py
  ├── upload_image_to_cloud()
  └── take_screenshot()
\`\`\`

## Cloud Tasks Processor

### Responsibilities
- Process images asynchronously
- Update Firestore status
- Handle errors
- Retry failed tasks

## Firestore Database Layer

### Collections
- \`sessions\` - Dark pattern sessions
- \`digital-sessions\` - Digital violation sessions
- \`report-user-ids\` - Admin tracking

### Subcollections
- \`userJourneys\` - Under sessions
- \`images\` - Under userJourneys

`;

// Auto-generated from data-flow-storage.md
export const ChromeExtensionDocumentationArchitectureDataFlowStorage = `# Data Flow & Storage

## Session Data Flow

\`\`\`mermaid
sequenceDiagram
    participant U as User
    participant E as Extension
    participant F as Firestore
    participant P as Perplexity AI
    
    U->>E: Create Session
    E->>F: Save Session Document
    F-->>E: Session ID
    E->>P: Analyze Domain
    P-->>E: Recommendations
    E->>F: Save Analysis
\`\`\`

## Image Upload Flow

\`\`\`mermaid
sequenceDiagram
    participant E as Extension
    participant B as Backend
    participant S as Cloud Storage
    participant F as Firestore
    participant T as Cloud Tasks
    
    E->>B: Upload Image
    B->>S: Store Image
    S-->>B: Public URL
    B->>F: Save Metadata
    F-->>B: Document ID
    B->>T: Create Task
    T-->>B: Task Created
    B-->>E: Success Response
\`\`\`

## Navigation Tracking Flow

\`\`\`mermaid
sequenceDiagram
    participant P as Page
    participant C as Content Script
    participant B as Background
    participant F as Firestore
    
    P->>C: User Interaction
    C->>B: Send Message
    B->>F: Update Navigation Data
    F-->>B: Updated
\`\`\`

## Storage Architecture

### Firestore Structure
\`\`\`
sessions/
  {sessionId}/
    - sessionName
    - type
    - createdBy
    - ...
    userJourneys/
      {journeyId}/
        - journeyName
        - navigationData: [...]
        images/
          {imageId}/
            - Url
            - imageUrl
            - status
            - ...
\`\`\`

### Cloud Storage Structure
\`\`\`
dd-media-files/
  {sessionName}_{timestamp}/
    {sessionId}_{journeyName}_{timestamp}.jpeg
    {sessionId}_{journeyName}_{timestamp}.html (optional)
\`\`\`

## Data Persistence

### Chrome Storage
- Session state
- Journey state
- User preferences
- Temporary data

### Firestore
- Permanent session data
- Journey data
- Image metadata
- Navigation data

### Cloud Storage
- Screenshot images
- HTML files
- Public URLs

`;

// Auto-generated from deployment-architecture.md
export const ChromeExtensionDocumentationArchitectureDeploymentArchitecture = `# Deployment Architecture

## Google Cloud Run Backend

### Configuration
- **Runtime**: Python 3.10
- **Container**: Docker
- **Scaling**: Automatic
- **Region**: europe-west1
- **Port**: 8080

### Deployment Process
1. Build Docker image
2. Push to Container Registry
3. Deploy to Cloud Run
4. Configure environment variables
5. Set up service account

## Chrome Extension Distribution

### Development
- Load unpacked from \`dist/\` directory
- Developer mode enabled
- Hot reload during development

### Production
- Build with \`npm run build\`
- Package as ZIP file
- Submit to Chrome Web Store (if applicable)
- Or distribute ZIP file

## Cloud Storage Buckets

### Configuration
- **Bucket**: \`dd-media-files\`
- **Region**: europe-west1
- **Access**: Public URLs for images
- **Lifecycle**: Configure retention policies

## Firestore Database

### Configuration
- **Mode**: Native mode
- **Region**: Multi-region (default)
- **Indexes**: Auto-created or manual
- **Security Rules**: Configured in console

## Environment Setup

### Backend Environment Variables
\`\`\`bash
GOOGLE_APPLICATION_CREDENTIALS_2=path/to/credentials.json
GCP_PROJECT=project-id
CLOUD_TASKS_QUEUE=queue-name
CLOUD_TASKS_LOCATION=europe-west1
PROCESS_SCREEN_CAPTURE_URL=https://...
PROCESS_SCREEN_CAPTURE_DIGITAL_VIOLATION_URL=https://...
\`\`\`

### Frontend Environment Variables
\`\`\`bash
VITE_PERPLEXITY_API_KEY=api-key (optional)
\`\`\`

## Multi-Cloud Considerations

### Current: Single Cloud (GCP)
- All services on Google Cloud
- Simplified management
- Integrated services

### Future: Multi-Cloud
- CDN for static assets
- Backup storage
- Disaster recovery

## Hybrid Deployment

### Current: Cloud-Only
- Backend: Cloud Run
- Database: Firestore
- Storage: Cloud Storage

### On-Premise Option
- Self-hosted backend
- Local database
- Local storage
- Requires infrastructure setup

`;

// Auto-generated from integration-points.md
export const ChromeExtensionDocumentationArchitectureIntegrationPoints = `# Integration Points

## Perplexity AI API

### Integration Type
- Direct API calls from extension
- REST API
- Bearer token authentication

### Data Flow
1. Extension sends domain analysis request
2. Perplexity AI analyzes domain
3. Returns recommendations
4. Extension stores in Firestore

## Google Cloud Services

### Cloud Storage
- Image upload
- Public URL generation
- File management

### Cloud Tasks
- Async task creation
- Task execution
- Status tracking

### Firestore
- Data storage
- Real-time updates
- Query capabilities

## Chrome Extension APIs

### Identity API
- User authentication
- Email retrieval

### Storage API
- Local data persistence
- State management

### Tabs API
- Tab management
- Screenshot capture

### Scripting API
- Content script injection
- DOM manipulation

### Web Navigation API
- Navigation tracking
- Page transitions

## Future Integration Possibilities

### Legal Case Management
- Export session data
- Generate reports
- Case file integration

### EMR Systems
- Healthcare compliance checks
- Privacy policy verification

### Finance Systems
- Terms analysis
- Risk assessment
- Compliance reporting

### Webhook Support
- Real-time notifications
- External system integration
- Event-driven workflows

### SLM Integration
- On-device models
- Privacy-preserving analysis
- Local processing

`;

// Auto-generated from monitoring-observability.md
export const ChromeExtensionDocumentationArchitectureMonitoringObservability = `# Monitoring & Observability

## Metrics

### Application Metrics
- Request count
- Response times
- Error rates
- Success rates

### System Metrics
- CPU utilization
- Memory usage
- Network traffic
- Queue depth

### Business Metrics
- Sessions created
- Images captured
- Journeys completed
- Domain analyses performed

## Dashboards

### Google Cloud Console
- Cloud Run metrics
- Firestore metrics
- Cloud Storage metrics
- Cloud Tasks metrics

### Custom Dashboards
- Application-specific metrics
- User activity
- Performance trends
- Error analysis

## Compliance Scoring Rubrics

### Dark Pattern Detection
- Pattern identification rate
- Legal framework compliance
- User impact assessment

### Digital Violation Analysis
- Violation detection rate
- Compliance score
- Risk assessment

## Logging

### Structured Logging
- JSON format
- Consistent structure
- Searchable fields

### Log Levels
- DEBUG: Detailed debugging
- INFO: General information
- WARNING: Potential issues
- ERROR: Errors requiring attention

### Log Aggregation
- Google Cloud Logging
- Centralized logging
- Log search and analysis

## Alerting

### Error Alerts
- High error rates
- Critical failures
- System errors

### Performance Alerts
- Slow response times
- High latency
- Resource exhaustion

### Business Alerts
- Unusual activity
- Usage spikes
- Anomalies

## Tracing

### Request Tracing
- End-to-end request tracking
- Performance bottlenecks
- Error source identification

### Distributed Tracing
- Cross-service tracing
- Async operation tracing
- Task execution tracing

`;

// Auto-generated from scalability-resilience.md
export const ChromeExtensionDocumentationArchitectureScalabilityResilience = `# Scalability & Resilience

## Load Balancing

### Cloud Run Auto-Scaling
- Automatic instance scaling
- Handles traffic spikes
- Cost-effective scaling
- No manual configuration needed

### Scaling Metrics
- Request rate
- CPU utilization
- Memory usage
- Concurrent requests

## Failover Mechanisms

### Cloud Tasks
- Automatic retry on failure
- Exponential backoff
- Dead letter queue for failed tasks
- Manual retry capability

### Firestore
- Automatic failover
- Multi-region replication
- High availability
- Data consistency

## Continuous Improvement

### Feedback Loops
1. User feedback collection
2. Error monitoring
3. Performance metrics
4. Usage analytics

### Improvement Process
1. Identify issues
2. Prioritize improvements
3. Implement changes
4. Deploy updates
5. Monitor results

## Resilience Patterns

### Circuit Breaker
- Prevent cascade failures
- Automatic recovery
- Fallback mechanisms

### Retry Logic
- Exponential backoff
- Maximum retry attempts
- Error classification

### Graceful Degradation
- Partial functionality
- User-friendly errors
- Alternative workflows

## Performance Optimization

### Caching
- Firestore query caching
- Chrome storage caching
- CDN for static assets (future)

### Database Optimization
- Index optimization
- Query optimization
- Pagination
- Batch operations

### Code Optimization
- Lazy loading
- Code splitting
- Bundle optimization
- Tree shaking

`;

// Auto-generated from security-privacy.md
export const ChromeExtensionDocumentationArchitectureSecurityPrivacy = `# Security & Privacy Layers

## Encryption

### Data in Transit
- HTTPS/TLS 1.2+
- Certificate validation
- Secure API communication

### Data at Rest
- Firestore encryption (automatic)
- Cloud Storage encryption (automatic)
- Chrome storage (not encrypted, no sensitive data)

## Sandboxing

### Extension Isolation
- Content scripts in page context
- Background script in extension context
- Popup in separate context

### API Isolation
- Backend in container
- Isolated from other services
- Network isolation

## Assurance Pipelines

### Security Scanning
- Dependency vulnerability scanning
- Code security analysis
- Container image scanning

### Compliance Checks
- GDPR compliance
- HIPAA considerations
- Security best practices

## Privacy Protection

### Data Minimization
- Only collect necessary data
- No personal information beyond email
- Screenshots of public pages only

### User Control
- Session ownership
- Data access rights
- Deletion capabilities (future)

### Transparency
- Clear privacy policy
- Data usage disclosure
- User consent

## Access Control

### Authentication
- Chrome Identity API
- Email-based authentication
- Future: OAuth2

### Authorization
- Session ownership validation
- Admin access control
- Role-based access (future)

## Security Monitoring

### Logging
- Security event logging
- Access logging
- Error logging

### Alerts
- Unusual access patterns
- Failed authentication attempts
- Security violations

`;

// Auto-generated from system-overview.md
export const ChromeExtensionDocumentationArchitectureSystemOverview = `# System Overview

## Architecture Diagram

\`\`\`mermaid
graph TB
    A[Chrome Extension] --> B[Backend API]
    B --> C[Cloud Tasks]
    B --> D[Firestore]
    B --> E[Cloud Storage]
    C --> F[Background Processor]
    A --> G[Perplexity AI]
    A --> D
    F --> D
    F --> E
\`\`\`

## Components

### 1. Chrome Extension (Frontend)
- **Technology**: React + TypeScript
- **Purpose**: User interface and interaction
- **Components**: Popup, Background Script, Content Script

### 2. Backend API
- **Technology**: Flask (Python)
- **Purpose**: Request handling and orchestration
- **Deployment**: Google Cloud Run

### 3. Cloud Tasks
- **Purpose**: Asynchronous task queue
- **Usage**: Image processing tasks

### 4. Firestore
- **Purpose**: Primary database
- **Data**: Sessions, journeys, images, navigation

### 5. Cloud Storage
- **Purpose**: File storage
- **Data**: Screenshot images, HTML files

### 6. Background Processor
- **Purpose**: Process images asynchronously
- **Triggered**: Via Cloud Tasks

### 7. Perplexity AI
- **Purpose**: Domain analysis
- **Integration**: Direct from extension

## Technology Stack

### Frontend
- React 18.3.1
- TypeScript 5.6.2
- Vite 6.0.5
- Bootstrap 5.3.3
- Firebase SDK 11.2.0

### Backend
- Python 3.10
- Flask
- Gunicorn
- Google Cloud SDK

### Infrastructure
- Google Cloud Run
- Google Cloud Firestore
- Google Cloud Storage
- Google Cloud Tasks

## Data Flow

1. **User Action** → Extension captures screenshot
2. **Extension** → Uploads to Backend API
3. **Backend** → Saves to Cloud Storage
4. **Backend** → Saves metadata to Firestore
5. **Backend** → Creates Cloud Task
6. **Cloud Task** → Triggers Background Processor
7. **Processor** → Updates Firestore with results

`;

// Auto-generated from code-examples.md
export const ChromeExtensionDocumentationCodeCodeExamples = `# Code Examples

## Overview

This document provides code examples for standard use cases and edge cases in the Chrome Extension system.

## Standard Use Cases

### 1. Creating a Dark Pattern Detection Session

**Frontend (React):**

\`\`\`javascript
const startSession = async () => {
  const sessionData = {
    sessionName: "E-commerce Dark Pattern Analysis",
    type: "dark_pattern",
    createdBy: userEmail,
    createdAt: serverTimestamp(),
    domainName: domain,
    jurisdictions: ["EU", "US Federal"],
    metadata: {
      browserInfo: navigator.userAgent,
      timestamp: new Date().toISOString(),
      url: activeTab?.url || 'unknown'
    }
  };

  const collectionRef = collection(db, 'sessions');
  const docRef = await addDoc(collectionRef, sessionData);
  setActiveSessionId(docRef.id);
};
\`\`\`

**Backend (Flask):**

\`\`\`python
@app.route('/screen_capture', methods=['POST'])
def screen_capture():
    user_email = request.headers.get('X-User-Email', None)
    if not user_email:
        return jsonify({'message': 'Missing X-User-Email header'}), 403
    
    session_id = request.form.get('sessionId')
    journey_name = request.form.get('journeyName')
    url = request.form.get('url')
    image = request.files['image']
    
    # Upload to Cloud Storage
    public_url = upload_image_to_cloud(bucket_screenshots, image, session_name, filename)
    
    # Save metadata to Firestore
    image_metadata = {
        "Url": url,
        "type": "Screen Capture",
        "imageUrl": public_url,
        "createdAt": firestore.SERVER_TIMESTAMP,
        "status": "initial"
    }
    
    # Create Cloud Task for processing
    create_task(queue=CLOUD_TASKS_QUEUE, ...)
\`\`\`

### 2. Creating a User Journey

\`\`\`javascript
const createJourney = async () => {
  const journeyRef = await addDoc(
    collection(db, 'sessions', activeSessionId, 'userJourneys'),
    {
      journeyName: "Checkout Process",
      createdBy: userEmail || 'Unknown',
      createdAt: serverTimestamp(),
      navigationData: []
    }
  );
  
  setActiveJourneyId(journeyRef.id);
  setActiveJourneyName("Checkout Process");
};
\`\`\`

### 3. Capturing a Screen Shot

**Visible Tab Capture:**

\`\`\`javascript
const handleScreenCapture = async () => {
  const tab = await getActiveTab();
  const dataUrl = await captureVisibleTab();
  const blob = dataURItoBlob(dataUrl);

  const formData = new FormData();
  formData.append('sessionId', activeSessionId);
  formData.append('journeyName', activeJourneyName);
  formData.append('url', tab.url || '');
  formData.append('note', imageNote);
  formData.append('image', blob, 'screencap.jpg');

  const response = await fetch(endpoint, {
    method: 'POST',
    headers: { 'X-User-Email': userEmail },
    body: formData,
  });
};
\`\`\`

**Full Page Screenshot (Backend):**

\`\`\`python
def take_screenshot(url, output_dir):
    options = webdriver.ChromeOptions()
    options.add_argument('--headless=new')
    options.add_argument('--no-sandbox')
    
    driver = webdriver.Chrome(service=ChromeService(...), options=options)
    driver.get(url)
    
    # Wait for page load
    WebDriverWait(driver, 20).until(
        lambda d: d.execute_script('return document.readyState') == 'complete'
    )
    
    # Scroll to load dynamic content
    scroll_to_bottom()
    
    # Take screenshot
    screenshot_path = os.path.join(output_dir, f"screenshot_{timestamp}.png")
    driver.save_screenshot(screenshot_path)
    driver.quit()
    
    return screenshot_path
\`\`\`

### 4. Domain Analysis with Perplexity AI

\`\`\`javascript
const getDomainSuggestions = async (domain, sessionId) => {
  const response = await fetch('https://api.perplexity.ai/chat/completions', {
    method: 'POST',
    headers: {
      'Authorization': \`Bearer \${apiKey}\`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      model: 'sonar',
      messages: [
        {
          role: 'system',
          content: 'You are a UX compliance expert specializing in dark patterns...'
        },
        {
          role: 'user',
          content: \`Analyze the website domain "\${domain}" and provide...\`
        }
      ],
      max_tokens: 1200,
      temperature: 0.2,
      search_mode: 'web'
    }),
  });
  
  const result = await response.json();
  return result.choices[0].message.content;
};
\`\`\`

## Edge Cases

### 1. Digital Violation Analysis with Terms & Conditions

\`\`\`javascript
// Session creation with Terms URLs
const sessionData = {
  sessionName: "Privacy Policy Compliance Check",
  type: "digital_violation",
  termsUrls: [
    "https://example.com/terms",
    "https://example.com/privacy"
  ],
  jurisdictions: ["EU"],
  // ... other fields
};

// Capture with HTML content
const handleScreenCapture = async () => {
  // ... capture image ...
  
  if (sessionType === 'digital_violation') {
    const htmlContent = await getTabHTML();
    const htmlBlob = new Blob([htmlContent], { type: 'text/html' });
    formData.append('html', htmlBlob, 'page.html');
  }
};
\`\`\`

### 2. Multi-Jurisdiction Compliance

\`\`\`javascript
const selectedJurisdictions = ['EU', 'US Federal'];

const jurisdictionContext = selectedJurisdictions.includes('EU') && 
  selectedJurisdictions.includes('US Federal') ? 
  'Focus on both GDPR/EU Digital Services Act compliance and US Federal Trade Commission (FTC) regulations.' :
  selectedJurisdictions.includes('EU') ? 
  'Focus on GDPR, EU Digital Services Act, and EU consumer protection laws.' : 
  'Focus on US Federal Trade Commission (FTC) regulations...';
\`\`\`

### 3. Error Handling and Partial Success

**Backend:**

\`\`\`python
try:
    # Upload image
    public_url = upload_image_to_cloud(...)
    
    # Save metadata
    image_ref = db.collection(...).add(image_metadata)
    
    # Create Cloud Task
    task_response = create_task(...)
    return jsonify({'status': 'success', ...}), 200
    
except Exception as e:
    # If task creation fails, still mark image as saved
    db.collection(...).update({
        'processingStatus': 'failed',
        'processingError': str(e)
    })
    return jsonify({'status': 'partial_success', ...}), 200
\`\`\`

**Frontend:**

\`\`\`javascript
try {
  const response = await fetch(endpoint, {...});
  const result = await response.json();
  
  if (response.ok) {
    setStatus(\`Screen capture saved: \${result.message}\`, 'success');
  } else {
    setStatus(\`Error: \${result.message}\`, 'danger');
  }
} catch (err) {
  console.error('Screen capture error:', err);
  setStatus('Failed to capture screen', 'danger');
}
\`\`\`

### 4. Navigation Tracking

**Content Script:**

\`\`\`javascript
// Track clicks
document.addEventListener('click', (event) => {
  const elementInfo = getElementInfo(event.target);
  chrome.runtime.sendMessage({
    type: 'USER_INTERACTION',
    action: 'clicked',
    element: elementInfo,
    url: window.location.href,
    timestamp: new Date().toISOString()
  });
});

// Track scrolls (throttled)
document.addEventListener('scroll', () => {
  clearTimeout(scrollTimeout);
  scrollTimeout = setTimeout(() => {
    const scrollSection = determineScrollSection();
    chrome.runtime.sendMessage({
      type: 'USER_INTERACTION',
      action: 'scrolled',
      section: scrollSection,
      url: window.location.href,
      timestamp: new Date().toISOString()
    });
  }, 500);
});
\`\`\`

**Background Script:**

\`\`\`javascript
chrome.tabs.onUpdated.addListener(async (tabId, changeInfo, tab) => {
  if (changeInfo.url) {
    const { activeSessionId, activeJourneyId } = await chrome.storage.local.get([
      'activeSessionId', 'activeJourneyId'
    ]);
    
    const journeyDocRef = doc(db, 'sessions', activeSessionId, 
      'userJourneys', activeJourneyId);
    
    await updateDoc(journeyDocRef, {
      navigationData: arrayUnion({
        from: fromUrl,
        to: toUrl,
        action: determineAction(...),
        timestamp: new Date().toISOString(),
        tabId: tabId.toString()
      })
    });
  }
});
\`\`\`

### 5. State Persistence

\`\`\`javascript
// Save state to Chrome storage
useEffect(() => {
  chrome.storage.local.set({
    activeSessionId,
    activeSessionName,
    activeJourneyId,
    activeJourneyName,
    selectedJurisdictions,
    sessionType
  });
}, [activeSessionId, activeSessionName, ...]);

// Load state from Chrome storage
useEffect(() => {
  chrome.storage.local.get([
    'activeSessionId', 'activeSessionName', ...
  ], (data) => {
    if (data.activeSessionId) {
      setActiveSessionId(data.activeSessionId);
      // ... restore other state
    }
  });
}, []);
\`\`\`

### 6. Full Page Screenshot with Stitching

\`\`\`javascript
const handleFullScreenshot = async () => {
  // Get page dimensions
  const dimensions = await chrome.scripting.executeScript({
    target: { tabId: tab.id },
    function: () => ({
      scrollHeight: document.documentElement.scrollHeight,
      clientHeight: document.documentElement.clientHeight,
      // ...
    })
  });
  
  const screenshots = [];
  const numScreenshots = Math.ceil(totalHeight / viewportHeight);
  
  // Capture multiple screenshots
  for (let i = 0; i < numScreenshots; i++) {
    await chrome.scripting.executeScript({
      target: { tabId: tab.id },
      function: (y) => window.scrollTo(0, y),
      args: [i * viewportHeight]
    });
    
    await new Promise(resolve => setTimeout(resolve, 800));
    const dataUrl = await captureVisibleTab();
    screenshots.push({ dataUrl, index: i });
  }
  
  // Stitch screenshots
  const fullPageBlob = await stitchScreenshots(screenshots, dimensions);
  
  // Upload
  formData.append('image', fullPageBlob, 'fullpage_screenshot.jpg');
};
\`\`\`

## Best Practices

### 1. Error Handling

Always wrap API calls in try-catch blocks and provide user feedback:

\`\`\`javascript
try {
  const result = await someAsyncOperation();
  setStatus('Operation successful', 'success');
} catch (error) {
  console.error('Operation failed:', error);
  setStatus('Operation failed. Please try again.', 'danger');
}
\`\`\`

### 2. State Management

Use React hooks for component state and Chrome storage for persistence:

\`\`\`javascript
const [state, setState] = useState(initialValue);

useEffect(() => {
  // Persist to Chrome storage
  chrome.storage.local.set({ key: state });
}, [state]);
\`\`\`

### 3. Async Operations

Use async/await for cleaner asynchronous code:

\`\`\`javascript
const fetchData = async () => {
  try {
    const response = await fetch(url);
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Fetch error:', error);
    throw error;
  }
};
\`\`\`

`;

// Auto-generated from database-schema.md
export const ChromeExtensionDocumentationCodeDatabaseSchema = `# Database Management & Schema Notes

## Overview

The application uses Google Cloud Firestore as the primary database for storing sessions, journeys, images, and navigation data. This document describes the database schema, collections, document structures, and query patterns.

## Firestore Collections

### 1. \`sessions\` Collection

Stores dark pattern detection sessions.

**Document Structure:**

\`\`\`javascript
{
  sessionName: string,              // User-provided session name
  type: "dark_pattern",             // Session type
  createdBy: string,                // User email
  createdAt: Timestamp,             // Server timestamp
  domainName: string,                // Domain being analyzed
  jurisdictions: string[],          // ["EU", "US Federal"]
  metadata: {
    browserInfo: string,
    timestamp: string,
    url: string
  },
  domain_analysis: {                // Optional: Perplexity AI analysis
    recommendations: string,
    analysis_data: object,
    completed_at: Timestamp
  },
  endedAt: Timestamp                 // Optional: Session end time
}
\`\`\`

**Subcollections:**

- \`userJourneys\` - User journeys within the session

### 2. \`digital-sessions\` Collection

Stores digital rights violation analysis sessions.

**Document Structure:**

\`\`\`javascript
{
  sessionName: string,
  type: "digital_violation",
  createdBy: string,
  createdAt: Timestamp,
  domainName: string,
  jurisdictions: string[],
  termsUrls: string[],               // Terms & Conditions URLs
  metadata: {
    browserInfo: string,
    timestamp: string,
    url: string
  },
  domain_analysis: {
    recommendations: string,
    analysis_data: object,
    completed_at: Timestamp
  },
  endedAt: Timestamp
}
\`\`\`

**Subcollections:**

- \`userJourneys\` - User journeys within the session

### 3. \`userJourneys\` Subcollection

Nested under either \`sessions\` or \`digital-sessions\`.

**Document Structure:**

\`\`\`javascript
{
  journeyName: string,              // Journey name (e.g., "Checkout Process")
  createdBy: string,                 // User email
  createdAt: Timestamp,
  navigationData: [                  // Array of navigation events
    {
      from: string,                 // Source URL
      to: string,                    // Destination URL
      action: string,                // Action description
      timestamp: string,             // ISO timestamp
      tabId: string,                 // Chrome tab ID
      interactionType: string        // Optional: click, scroll, etc.
    }
  ],
  endedAt: Timestamp                 // Optional: Journey end time
}
\`\`\`

**Subcollections:**

- \`images\` - Screenshots and captures for this journey

### 4. \`images\` Subcollection

Nested under \`userJourneys\`.

**Document Structure:**

\`\`\`javascript
{
  Url: string,                       // Page URL where capture was taken
  type: string,                      // "Screen Capture" or "Full Page Screenshot"
  note: string,                      // User-provided note
  imageUrl: string,                  // Cloud Storage public URL
  htmlUrl: string,                   // Optional: HTML file URL (digital violations)
  createdAt: Timestamp,
  status: string,                    // "initial", "processing", "completed", "failed"
  processingStatus: string,         // Optional: Processing status
  processingError: string           // Optional: Error message if processing failed
}
\`\`\`

### 5. \`report-user-ids\` Collection

Admin account tracking collection.

**Document Structure:**

\`\`\`javascript
{
  domains: string[],                 // Array of unique domains analyzed
  sessions: string[]                 // Array of session IDs
}
\`\`\`

**Example Document ID:** \`GAPfk6Y3EcCZLu6iQqyY\`

## Query Patterns

### 1. Get Session by ID

\`\`\`python
session_doc = db.collection('sessions').document(session_id).get()
if session_doc.exists:
    session_data = session_doc.to_dict()
\`\`\`

### 2. Find Journey by Name

\`\`\`python
journey_docs = db.collection('sessions').document(session_id)\\
    .collection('userJourneys')\\
    .where('journeyName', '==', journey_name)\\
    .get()

if journey_docs:
    journey_id = journey_docs[0].id
\`\`\`

### 3. Add Navigation Data

\`\`\`python
journey_ref = db.collection('sessions').document(session_id)\\
    .collection('userJourneys').document(journey_id)

updateDoc(journey_ref, {
    'navigationData': arrayUnion({
        'from': from_url,
        'to': to_url,
        'action': action,
        'timestamp': timestamp,
        'tabId': tab_id
    })
})
\`\`\`

### 4. Create Image Document

\`\`\`python
image_ref = db.collection('sessions').document(session_id)\\
    .collection('userJourneys').document(journey_id)\\
    .collection('images')\\
    .add({
        'Url': url,
        'type': 'Screen Capture',
        'imageUrl': public_url,
        'createdAt': firestore.SERVER_TIMESTAMP,
        'status': 'initial'
    })

image_doc_id = image_ref[1].id
\`\`\`

### 5. Update Image Processing Status

\`\`\`python
image_ref = db.collection('sessions').document(session_id)\\
    .collection('userJourneys').document(journey_id)\\
    .collection('images').document(image_doc_id)

image_ref.update({
    'processingStatus': 'failed',
    'processingError': error_message
})
\`\`\`

## Indexing Strategy

### Composite Indexes

Firestore automatically creates single-field indexes. For complex queries, composite indexes may be needed:

**Example Composite Index:**
- Collection: \`sessions\`
- Fields: \`createdBy\` (Ascending), \`createdAt\` (Descending)

This would enable queries like:
\`\`\`python
db.collection('sessions')\\
    .where('createdBy', '==', user_email)\\
    .order_by('createdAt', direction=firestore.Query.DESCENDING)\\
    .limit(10)
\`\`\`

### Index Creation

Composite indexes can be created:
1. Automatically via Firestore console when query is first run
2. Manually via \`firestore.indexes.json\` configuration file
3. Via Firebase CLI: \`firebase deploy --only firestore:indexes\`

## Data Relationships

\`\`\`
sessions (or digital-sessions)
  └── [session_id]
      ├── Document fields (sessionName, type, etc.)
      └── userJourneys (subcollection)
          └── [journey_id]
              ├── Document fields (journeyName, navigationData, etc.)
              └── images (subcollection)
                  └── [image_id]
                      └── Document fields (Url, imageUrl, status, etc.)
\`\`\`

## Data Validation

### Client-Side Validation

Frontend validates:
- Session name is not empty
- At least one jurisdiction selected
- Terms URLs provided for digital violation sessions
- Required fields present before API calls

### Server-Side Validation

Backend validates:
- User email header present
- Session exists before creating journey
- Journey exists before adding images
- Required form fields present

## Data Retention

### Current Policy

- Sessions and associated data are retained indefinitely
- No automatic deletion policy implemented

### Future Considerations

- Implement data retention policies based on user preferences
- GDPR right to deletion support
- Archive old sessions to cold storage

## Backup and Recovery

### Firestore Backups

- Firestore provides automatic backups
- Point-in-time recovery available
- Export/import functionality via \`gcloud firestore export\`

### Manual Backup

\`\`\`bash
gcloud firestore export gs://[BUCKET_NAME]/[EXPORT_PREFIX]
\`\`\`

## Security Rules

### Current Implementation

Security rules should be configured in Firestore console. Example rules:

\`\`\`javascript
rules_version = '2';
service cloud.firestore {
  match /databases/{database}/documents {
    // Sessions: Users can only access their own sessions
    match /sessions/{sessionId} {
      allow read, write: if request.auth != null && 
        resource.data.createdBy == request.auth.token.email;
      
      // User journeys: Same ownership
      match /userJourneys/{journeyId} {
        allow read, write: if request.auth != null;
        
        // Images: Same ownership
        match /images/{imageId} {
          allow read, write: if request.auth != null;
        }
      }
    }
    
    // Digital sessions: Same rules
    match /digital-sessions/{sessionId} {
      // ... similar rules
    }
  }
}
\`\`\`

## Performance Considerations

### Query Optimization

1. **Limit Results**: Always use \`.limit()\` for large collections
2. **Index Usage**: Ensure queries use indexes
3. **Pagination**: Implement pagination for large result sets
4. **Field Selection**: Only fetch required fields

### Write Optimization

1. **Batch Writes**: Use batch writes for multiple operations
2. **Transaction Usage**: Use transactions for atomic operations
3. **Array Updates**: Use \`arrayUnion\` for navigation data

### Read Optimization

1. **Caching**: Client SDK caches reads automatically
2. **Offline Support**: Firestore provides offline persistence
3. **Real-time Listeners**: Use listeners for real-time updates

## Migration Strategy

### Schema Changes

When adding new fields:
1. Make fields optional in code
2. Use default values for missing fields
3. Migrate existing documents if needed

### Collection Changes

When restructuring:
1. Create new collection structure
2. Migrate data in batches
3. Update application code
4. Remove old structure after migration complete

## Monitoring

### Firestore Metrics

Monitor:
- Read/write operations count
- Document count per collection
- Query performance
- Error rates

### Alerts

Set up alerts for:
- High error rates
- Unusual read/write patterns
- Storage quota approaching limits

`;

// Auto-generated from deployment-environment.md
export const ChromeExtensionDocumentationCodeDeploymentEnvironment = `# Deployment Environment and Integrations

## Overview

This document describes the deployment environment, containerization, build processes, and integration points for both the backend API and frontend Chrome Extension.

## Backend Deployment

### Containerization

The backend is containerized using Docker for consistent deployment across environments.

**Dockerfile Configuration:**
- Base Image: \`python:3.10-slim\`
- Working Directory: \`/app\`
- Dependencies: Installs system packages (wget, gnupg, unzip, curl) and Python packages
- Chrome Installation: Installs Google Chrome Stable for Selenium
- ChromeDriver: Automatically downloads and installs compatible ChromeDriver
- Credentials: Copies Google Cloud service account JSON credentials
- Port: Exposes port 8080

### Production Server

**Gunicorn Configuration:**
- WSGI Server: Gunicorn with 4 worker processes
- Threads: 2 threads per worker
- Timeout: No timeout (set to 0 for long-running tasks)
- Binding: Listens on all interfaces (0.0.0.0) on port 8080

\`\`\`bash
gunicorn app:app --bind 0.0.0.0:8080 --workers 4 --threads 2 --timeout 0
\`\`\`

### Google Cloud Run

The backend is deployed on Google Cloud Run, which provides:
- Automatic scaling based on traffic
- HTTPS termination
- Request timeout management
- Environment variable injection
- Service account authentication

**Deployment URL:**
- Production: \`https://dd-extension-server-413055756357.europe-west1.run.app\`

### Environment Variables

Required environment variables:

\`\`\`bash
# Google Cloud Credentials
GOOGLE_APPLICATION_CREDENTIALS_2=/path/to/service-account.json
GCP_PROJECT=your-project-id

# Cloud Tasks Configuration
CLOUD_TASKS_QUEUE=your-queue-name
CLOUD_TASKS_LOCATION=europe-west1

# Processing Endpoints
PROCESS_SCREEN_CAPTURE_URL=https://your-endpoint/process
PROCESS_SCREEN_CAPTURE_DIGITAL_VIOLATION_URL=https://your-endpoint/process-digital
\`\`\`

## Frontend Deployment

### Build System

The frontend uses Vite as the build tool, providing:
- Fast development server with HMR (Hot Module Replacement)
- Optimized production builds
- TypeScript compilation
- Code splitting and tree shaking

**Build Commands:**
\`\`\`bash
# Development
npm run dev

# Production Build
npm run build

# Preview Production Build
npm run preview

# Linting
npm run lint
\`\`\`

### Chrome Extension Manifest

The extension uses Manifest V3, which includes:

**Permissions:**
- \`identity\` - User authentication
- \`identity.email\` - Access to user email
- \`storage\` - Local data persistence
- \`tabs\` - Tab management
- \`activeTab\` - Active tab access
- \`webNavigation\` - Navigation tracking
- \`scripting\` - Content script injection

**Host Permissions:**
- Backend API endpoint for API calls

**Background Service Worker:**
- Type: Module (ES6 modules)
- File: \`background.js\`

**Content Scripts:**
- Matches: All URLs (\`<all_urls>\`)
- Run At: \`document_idle\` (after DOM is ready)

### Extension Distribution

1. **Development:**
   - Load unpacked extension from \`dist/\` directory
   - Chrome Developer Mode enabled

2. **Production:**
   - Build extension: \`npm run build\`
   - Package as \`.zip\` file
   - Submit to Chrome Web Store (if applicable)

## Integration Points

### Google Cloud Services

1. **Cloud Storage**
   - Bucket: \`dd-media-files\`
   - Purpose: Store screenshot images
   - Access: Public URLs for uploaded images

2. **Cloud Firestore**
   - Collections: \`sessions\`, \`digital-sessions\`, \`report-user-ids\`
   - Purpose: Store session data, journeys, navigation data
   - Access: Server-side and client-side SDKs

3. **Cloud Tasks**
   - Queue: Configurable queue name
   - Purpose: Asynchronous image processing
   - Location: \`europe-west1\`

### External APIs

1. **Perplexity AI**
   - Endpoint: \`https://api.perplexity.ai/chat/completions\`
   - Authentication: Bearer token (API key)
   - Model: \`sonar\`
   - Purpose: Domain analysis and recommendations

2. **Chrome Extension APIs**
   - Native browser APIs for extension functionality
   - No external dependencies required

## Configuration Management

### Backend Configuration

Configuration is managed through:
1. Environment variables (\`.env\` file in development)
2. Google Cloud Run environment variables (production)
3. Service account JSON files for authentication

### Frontend Configuration

Configuration is managed through:
1. Environment variables (Vite \`import.meta.env\`)
2. Chrome storage API for user preferences
3. Manifest.json for extension permissions

## Deployment Workflow

### Backend Deployment

1. Build Docker image:
   \`\`\`bash
   docker build -t extension-backend .
   \`\`\`

2. Push to Google Container Registry:
   \`\`\`bash
   docker push gcr.io/PROJECT_ID/extension-backend
   \`\`\`

3. Deploy to Cloud Run:
   \`\`\`bash
   gcloud run deploy extension-backend \\
     --image gcr.io/PROJECT_ID/extension-backend \\
     --platform managed \\
     --region europe-west1
   \`\`\`

### Frontend Deployment

1. Install dependencies:
   \`\`\`bash
   npm install
   \`\`\`

2. Build extension:
   \`\`\`bash
   npm run build
   \`\`\`

3. Package extension:
   - Zip the \`dist/\` directory
   - Or load unpacked in Chrome for development

## Monitoring and Logging

### Backend Logging

- Google Cloud Logging integration
- Flask request logging
- Error tracking and stack traces
- Cloud Tasks execution logs

### Frontend Logging

- Chrome DevTools console logging
- Error tracking in background script
- User interaction logging (optional)

## Performance Considerations

1. **Backend:**
   - Gunicorn worker processes for concurrent requests
   - Cloud Tasks for async processing
   - Cloud Storage for scalable file storage

2. **Frontend:**
   - Vite build optimization
   - Code splitting for smaller bundle size
   - Lazy loading where applicable

## Security Considerations

1. **CORS Configuration:**
   - Restricted to Chrome extension origins
   - Localhost allowed for development

2. **Authentication:**
   - Chrome Identity API for user authentication
   - X-User-Email header for API authentication

3. **Credentials:**
   - Service account JSON files stored securely
   - Environment variables for sensitive data
   - No credentials in source code

## Rollback Procedures

### Backend Rollback

1. Revert to previous Docker image version
2. Update Cloud Run service to previous revision
3. Verify functionality

### Frontend Rollback

1. Revert to previous build
2. Rebuild and redeploy extension
3. Test in development environment first

## Environment-Specific Configurations

### Development

- Local Flask development server
- Unpacked Chrome extension
- Local Firestore emulator (optional)
- Debug logging enabled

### Production

- Google Cloud Run deployment
- Packaged Chrome extension
- Production Firestore database
- Optimized logging levels

`;

// Auto-generated from design-philosophy.md
export const ChromeExtensionDocumentationCodeDesignPhilosophy = `# Core Code Approach & Design Philosophy

## Overview

This document outlines the core design principles, architectural patterns, and coding philosophy that guide the development of the Chrome Extension system.

## Design Principles

### 1. Separation of Concerns

The system is divided into distinct layers with clear responsibilities:

- **Frontend Extension**: User interface and interaction tracking
- **Backend API**: Request handling and orchestration
- **Background Processing**: Asynchronous task execution
- **Data Layer**: Firestore database and Cloud Storage

### 2. Asynchronous Processing

Heavy operations are offloaded to background tasks:

- Image processing via Cloud Tasks
- Domain analysis via Perplexity AI
- Screenshot capture via Selenium (when needed)

This ensures the extension remains responsive and the API can handle concurrent requests efficiently.

### 3. Session-Based Workflow

The system uses a hierarchical data model:

\`\`\`
Session
  └── User Journey
      └── Images/Captures
          └── Navigation Data
\`\`\`

This structure allows:
- Multiple journeys per session
- Multiple captures per journey
- Detailed navigation tracking per capture

### 4. Type Safety and Error Handling

- **Backend**: Python type hints and comprehensive error handling
- **Frontend**: TypeScript for compile-time type checking
- **Error Recovery**: Graceful degradation with user-friendly error messages

### 5. Privacy and Security First

- User authentication via Chrome Identity API
- No sensitive data stored in extension storage
- Secure API communication
- GDPR-compliant data handling

## Architectural Patterns

### Session Management Pattern

Sessions represent a complete analysis workflow:

\`\`\`python
# Session Creation Flow
1. User creates session with name, type, jurisdictions
2. Session stored in Firestore (sessions or digital-sessions)
3. Domain analysis triggered asynchronously
4. Session ID stored in Chrome storage for persistence
\`\`\`

### Journey Tracking Pattern

Journeys represent specific user flows within a session:

\`\`\`javascript
// Journey Creation Flow
1. User selects or creates journey name
2. Journey document created in Firestore subcollection
3. Journey ID stored in Chrome storage
4. Navigation and captures linked to journey
\`\`\`

### Capture Pattern

Two capture methods with different use cases:

1. **Screen Capture** (Visible Tab)
   - Fast, client-side capture
   - Immediate upload
   - Suitable for quick annotations

2. **Full Page Screenshot** (Selenium)
   - Server-side processing
   - Complete page capture
   - Handles dynamic content

### Asynchronous Task Pattern

Image processing follows this pattern:

\`\`\`python
1. Image uploaded to Cloud Storage
2. Metadata saved to Firestore with "initial" status
3. Cloud Task created for processing
4. Background processor updates status to "processing" → "completed"/"failed"
\`\`\`

## Code Organization

### Backend Structure

\`\`\`
app.py
  ├── Flask app initialization
  ├── CORS configuration
  ├── Firestore initialization
  ├── Cloud Storage setup
  ├── Helper functions (create_task)
  └── API endpoints
      ├── /screen_capture
      ├── /screenshot
      └── /digital_violation_capture

utility/
  └── ChromeExtension.py
      ├── upload_image_to_cloud()
      └── take_screenshot()
\`\`\`

### Frontend Structure

\`\`\`
src/
  ├── App.jsx          # Main popup component
  ├── background.js    # Service worker
  ├── content.js       # Content script
  ├── main.jsx         # React entry point
  └── firebase.js      # Firebase configuration
\`\`\`

## State Management

### Frontend State

React hooks manage component state:

- **Session State**: Active session ID, name, type
- **Journey State**: Active journey ID, name
- **UI State**: Loading states, error messages, form inputs
- **Persistence**: Chrome storage API for state persistence

### Backend State

Stateless API design:
- No server-side session storage
- All state in Firestore database
- Request authentication via headers

## Error Handling Philosophy

### Graceful Degradation

1. **Partial Success**: Image saved even if processing fails
2. **User Feedback**: Clear error messages with actionable guidance
3. **Retry Logic**: Automatic retries for transient failures
4. **Fallback Options**: Alternative methods when primary fails

### Error Categories

1. **User Errors**: Invalid input, missing permissions
   - HTTP 400/403 responses
   - User-friendly error messages

2. **System Errors**: Database failures, API errors
   - HTTP 500 responses
   - Logged for debugging
   - User sees generic error message

3. **Transient Errors**: Network timeouts, rate limits
   - Retry with exponential backoff
   - Queue for later processing

## Code Quality Standards

### Backend (Python)

- PEP 8 style guide
- Type hints for function signatures
- Docstrings for public functions
- Error handling with specific exception types

### Frontend (TypeScript/JavaScript)

- ESLint configuration
- React best practices
- Functional components with hooks
- Proper error boundaries

## Testing Philosophy

### Unit Testing

- Test individual functions in isolation
- Mock external dependencies
- Test edge cases and error conditions

### Integration Testing

- Test API endpoints with test database
- Test extension with mock backend
- Test end-to-end workflows

### Manual Testing

- Test in real Chrome environment
- Test with various websites
- Test error scenarios

## Performance Philosophy

### Optimization Priorities

1. **User Experience**: Fast extension response times
2. **API Responsiveness**: Quick request handling
3. **Background Processing**: Efficient async operations
4. **Resource Usage**: Minimal memory and CPU usage

### Caching Strategy

- Chrome storage for session/journey state
- Firestore query caching (where applicable)
- Cloud Storage public URLs for images

## Security Philosophy

### Defense in Depth

1. **Authentication**: Chrome Identity API + email header
2. **Authorization**: Session ownership validation
3. **Input Validation**: Sanitize all user inputs
4. **Output Encoding**: Prevent XSS attacks
5. **CORS**: Restrict origins to extension only

### Data Privacy

- Minimal data collection
- User consent for data usage
- GDPR compliance considerations
- Secure data transmission (HTTPS)

## Extensibility

### Plugin Architecture

The system is designed for easy extension:

1. **New Session Types**: Add to sessionTypes array
2. **New Journey Types**: Extend journeyOptions
3. **New Capture Methods**: Add new endpoints
4. **New Analysis Types**: Extend processing pipeline

### Integration Points

- Perplexity AI for domain analysis
- Future: Custom ML models
- Future: Additional compliance frameworks
- Future: Third-party integrations

## Documentation Philosophy

- Code comments for complex logic
- Inline documentation for API endpoints
- User-facing documentation for features
- Architecture documentation for system design

## Continuous Improvement

### Feedback Loops

1. **User Feedback**: Error reports and feature requests
2. **Analytics**: Usage patterns and performance metrics
3. **Code Reviews**: Peer review for quality
4. **Testing**: Automated and manual testing

### Iterative Development

- Small, incremental changes
- Regular releases
- Backward compatibility considerations
- Version management

`;

// Auto-generated from developer-experience.md
export const ChromeExtensionDocumentationCodeDeveloperExperience = `# Developer Experience

## Contribution Guidelines

### Code Style
- **Backend**: PEP 8, type hints
- **Frontend**: ESLint, Prettier
- **Commits**: Conventional commits

### Pull Request Process
1. Create feature branch
2. Make changes
3. Write tests
4. Update documentation
5. Submit PR
6. Code review
7. Merge to develop

## Local Development Setup

### Backend
\`\`\`bash
# Install dependencies
pip install -r requirements.txt

# Set environment variables
export GOOGLE_APPLICATION_CREDENTIALS_2=path/to/credentials.json

# Run development server
python app.py
\`\`\`

### Frontend
\`\`\`bash
# Install dependencies
npm install

# Run development server
npm run dev

# Build extension
npm run build
\`\`\`

## Code Structure

### Backend
- \`app.py\`: Main Flask application
- \`utility/\`: Utility functions
- \`requirements.txt\`: Dependencies

### Frontend
- \`src/App.jsx\`: Main component
- \`src/background.js\`: Service worker
- \`src/content.js\`: Content script
- \`public/\`: Static assets

## Extensibility

### Adding New Session Types
1. Add to \`sessionTypes\` array
2. Update Firestore collection logic
3. Add UI components
4. Update documentation

### Adding New Journey Types
1. Add to \`journeyOptions\` array
2. Update journey creation logic
3. Add UI components

## Automated Documentation

### Doc Generation
- Sphinx for Python (future)
- JSDoc for JavaScript (future)
- API documentation from code

`;

// Auto-generated from libraries-dependencies.md
export const ChromeExtensionDocumentationCodeLibrariesDependencies = `# Libraries and Dependencies

## Overview

This document provides a comprehensive overview of all libraries and dependencies used in the Chrome Extension system, including both backend and frontend components.

## Backend Dependencies

The backend is built using Python and Flask, with dependencies managed through \`requirements.txt\`.

### Core Framework

- **Flask** - Lightweight WSGI web application framework
  - Version: Latest stable
  - Purpose: Main web framework for REST API endpoints
  - Usage: Handles HTTP requests, routing, and response formatting

- **Flask-CORS** - Flask extension for handling Cross-Origin Resource Sharing
  - Purpose: Enables the Chrome Extension to make cross-origin requests to the backend
  - Configuration: Configured to allow requests from Chrome extensions and localhost

### Google Cloud Platform Services

- **google-cloud-storage** - Google Cloud Storage client library
  - Purpose: Upload and manage screenshot images in Cloud Storage buckets
  - Usage: Stores captured screenshots in \`dd-media-files\` bucket

- **google-cloud-firestore** - Firestore database client
  - Purpose: NoSQL document database for storing sessions, journeys, and metadata
  - Usage: Primary database for application data

- **google-cloud-tasks** - Cloud Tasks client library
  - Purpose: Asynchronous task queue management
  - Usage: Queues image processing tasks for background execution

- **google-cloud-aiplatform** (>=1.38) - Vertex AI platform client
  - Purpose: Integration with Google Cloud AI/ML services
  - Usage: Future integration for AI-powered analysis

- **firebase-admin** - Firebase Admin SDK
  - Purpose: Server-side Firebase operations
  - Usage: Firestore database operations and authentication

### Web Automation

- **selenium** - Web browser automation framework
  - Purpose: Automated full-page screenshot capture
  - Usage: Headless Chrome browser for taking screenshots of URLs

- **webdriver-manager** - Automatic WebDriver management
  - Purpose: Automatically downloads and manages ChromeDriver
  - Usage: Simplifies Selenium setup without manual driver installation

### Data Processing

- **beautifulsoup4** - HTML parsing library
  - Purpose: Parse and extract data from HTML content
  - Usage: Processing HTML files captured during digital violation analysis

- **tldextract** - Domain name extraction
  - Purpose: Extract domain information from URLs
  - Usage: Domain analysis and categorization

- **chromadb** - Vector database
  - Purpose: Embedding storage and similarity search
  - Usage: Future integration for semantic search capabilities

### Utilities

- **python-dotenv** - Environment variable management
  - Purpose: Load environment variables from \`.env\` files
  - Usage: Secure configuration management

- **requests** - HTTP library
  - Purpose: Make HTTP requests to external APIs
  - Usage: API integrations

- **gunicorn** - Python WSGI HTTP Server
  - Purpose: Production WSGI server
  - Usage: Runs Flask application in production with multiple workers

- **gspread** - Google Sheets API client
  - Purpose: Interact with Google Sheets
  - Usage: Data export and reporting features

- **oauth2client** - OAuth 2.0 client library
  - Purpose: OAuth authentication
  - Usage: Google API authentication

- **cachetools** - Caching utilities
  - Purpose: In-memory caching
  - Usage: Performance optimization through caching

## Frontend Dependencies

The frontend is built using React with TypeScript, bundled with Vite.

### Core Framework

- **react** (^18.3.1) - JavaScript library for building user interfaces
  - Purpose: Component-based UI framework
  - Usage: Extension popup interface and state management

- **react-dom** (^18.3.1) - React DOM renderer
  - Purpose: Render React components to the DOM
  - Usage: Extension popup rendering

### UI Framework

- **bootstrap** (^5.3.3) - CSS framework
  - Purpose: Responsive UI components and styling
  - Usage: Extension popup styling and layout

### Firebase Integration

- **firebase** (^11.2.0) - Firebase JavaScript SDK
  - Purpose: Client-side Firebase operations
  - Usage: Firestore database operations from the extension

### Build Tools

- **vite** (^6.0.5) - Next-generation frontend build tool
  - Purpose: Fast development server and optimized production builds
  - Usage: Development and build process

- **typescript** (~5.6.2) - Typed JavaScript
  - Purpose: Type safety and enhanced developer experience
  - Usage: Type checking and compilation

### Development Dependencies

- **@vitejs/plugin-react** (^4.3.4) - Vite plugin for React
  - Purpose: React support in Vite
  - Usage: HMR and JSX transformation

- **@types/react** (^18.3.18) - TypeScript definitions for React
- **@types/react-dom** (^18.3.5) - TypeScript definitions for React DOM
- **eslint** (^9.17.0) - JavaScript linter
- **@eslint/js** (^9.17.0) - ESLint JavaScript configuration
- **eslint-plugin-react-hooks** (^5.0.0) - ESLint plugin for React Hooks
- **eslint-plugin-react-refresh** (^0.4.16) - ESLint plugin for React Fast Refresh
- **typescript-eslint** (^8.18.2) - TypeScript ESLint integration
- **globals** (^15.14.0) - Global variable definitions

## External Services

### Perplexity AI

- **API Integration** - Perplexity AI Chat API
  - Purpose: Domain analysis and legal compliance recommendations
  - Usage: Provides AI-powered analysis of websites for dark patterns and digital rights violations
  - Model: \`sonar\` model with web search enabled

### Chrome Extension APIs

The extension uses native Chrome Extension APIs:

- **chrome.identity** - User authentication
- **chrome.storage** - Local data persistence
- **chrome.tabs** - Tab management and screenshot capture
- **chrome.scripting** - Content script injection
- **chrome.webNavigation** - Navigation tracking

## Dependency Management

### Backend

Dependencies are managed through \`requirements.txt\`. To install:

\`\`\`bash
pip install -r requirements.txt
\`\`\`

### Frontend

Dependencies are managed through \`package.json\`. To install:

\`\`\`bash
npm install
\`\`\`

## Version Pinning Strategy

- **Backend**: Most dependencies use latest stable versions (no version pinning)
- **Frontend**: Uses semantic versioning with caret (^) for minor updates
- **Critical Dependencies**: Firebase and React use specific version ranges

## Security Considerations

1. **Regular Updates**: Dependencies should be regularly updated to patch security vulnerabilities
2. **Dependency Scanning**: Use tools like \`npm audit\` and \`pip-audit\` to identify vulnerabilities
3. **Minimal Dependencies**: Only include necessary dependencies to reduce attack surface
4. **Environment Variables**: Sensitive credentials stored in environment variables, not in code

## Future Dependencies

Potential future additions:

- **SLM Integration**: On-device small language models for privacy-preserving analysis
- **Vector Database**: Enhanced semantic search capabilities
- **Analytics**: Usage analytics and performance monitoring
- **Testing Frameworks**: Jest, pytest for comprehensive testing

`;

// Auto-generated from model-integrations.md
export const ChromeExtensionDocumentationCodeModelIntegrations = `# Model Integrations

## Perplexity AI Integration

### Domain Analysis
- **Model**: \`sonar\`
- **Purpose**: Domain analysis and legal compliance recommendations
- **API**: \`https://api.perplexity.ai/chat/completions\`
- **Authentication**: Bearer token

### Usage
\`\`\`javascript
const response = await fetch('https://api.perplexity.ai/chat/completions', {
  method: 'POST',
  headers: {
    'Authorization': \`Bearer \${apiKey}\`,
    'Content-Type': 'application/json',
  },
  body: JSON.stringify({
    model: 'sonar',
    messages: [...],
    max_tokens: 1200,
    temperature: 0.2,
    search_mode: 'web'
  })
});
\`\`\`

## External Processing Endpoints

### Screen Capture Processing
- **URL**: \`PROCESS_SCREEN_CAPTURE_URL\`
- **Method**: POST
- **Payload**: Session, journey, image metadata

### Digital Violation Processing
- **URL**: \`PROCESS_SCREEN_CAPTURE_DIGITAL_VIOLATION_URL\`
- **Method**: POST
- **Payload**: Includes HTML content

## Future SLM Integration

### On-Device Models
- Privacy-preserving analysis
- No data sent to external APIs
- Local processing capabilities

### Integration Points
- Model loading in extension
- Inference pipeline
- Result storage

`;

// Auto-generated from operational-readiness.md
export const ChromeExtensionDocumentationCodeOperationalReadiness = `# Operational Readiness

## Configuration

### Environment Variables
- Backend: \`.env\` file or Cloud Run environment
- Frontend: Vite environment variables
- Secrets: Google Secret Manager (recommended)

## Containerization

### Docker
- Dockerfile for backend
- Multi-stage builds for optimization
- Health checks configured

## Monitoring & Logging

### Google Cloud Logging
- Structured logging
- Log levels (DEBUG, INFO, WARNING, ERROR)
- Log aggregation and search

### Metrics
- Request counts
- Error rates
- Response times
- Queue depths

## Rollback Procedures

### Backend Rollback
1. Identify previous revision
2. Update Cloud Run service
3. Verify functionality
4. Monitor for issues

### Frontend Rollback
1. Revert to previous build
2. Rebuild extension
3. Test in development
4. Deploy to production

`;

// Auto-generated from performance-benchmarking.md
export const ChromeExtensionDocumentationCodePerformanceBenchmarking = `# Performance Benchmarking & Profiling

## Screenshot Processing Benchmarks

### Visible Tab Capture
- **Average Time**: 200-500ms
- **Factors**: Image size, network speed
- **Optimization**: JPEG compression (80% quality)

### Full Page Screenshot (Selenium)
- **Average Time**: 5-15 seconds
- **Factors**: Page complexity, dynamic content
- **Optimization**: Headless mode, optimized Chrome options

## Firestore Query Optimization

### Query Performance
- **Single Document Read**: <50ms
- **Collection Query**: <100ms (with index)
- **Subcollection Query**: <150ms

### Optimization Strategies
1. Use indexes for filtered queries
2. Limit result sets
3. Paginate large collections
4. Cache frequently accessed data

## Cloud Tasks Queue Management

### Task Creation
- **Average Time**: <100ms
- **Queue Processing**: Asynchronous, non-blocking

### Queue Monitoring
- Monitor queue depth
- Track processing times
- Alert on queue backlog

## Frontend Rendering Performance

### React Component Performance
- **Initial Render**: <100ms
- **State Updates**: <50ms
- **Re-renders**: Minimized with React.memo

### Optimization Techniques
- Code splitting
- Lazy loading
- Memoization
- Virtual scrolling (if needed)

`;

// Auto-generated from security-compliance.md
export const ChromeExtensionDocumentationCodeSecurityCompliance = `# Security & Compliance

## Threat Modeling

### Extension Permissions

**Required Permissions:**
- \`identity\` - User authentication
- \`identity.email\` - Access to user email
- \`storage\` - Local data persistence
- \`tabs\` - Tab management
- \`activeTab\` - Active tab access
- \`webNavigation\` - Navigation tracking
- \`scripting\` - Content script injection

**Threats:**
- Unauthorized access to user data
- Malicious content script injection
- Tab data exposure
- Storage data leakage

**Mitigations:**
- Minimal permission set
- Content script only on user-initiated actions
- Encrypted storage for sensitive data
- Regular security audits

## Authentication

### Chrome Identity API

\`\`\`javascript
chrome.identity.getProfileUserInfo({ accountStatus: 'ANY' }, (info) => {
  if (info.email) {
    setUserEmail(info.email);
  }
});
\`\`\`

**Security Features:**
- OAuth 2.0 authentication
- No password storage
- Token-based authentication

### API Authentication

**Header-Based Authentication:**
\`\`\`python
user_email = request.headers.get('X-User-Email', None)
if not user_email:
    return jsonify({'message': 'Missing X-User-Email header'}), 403
\`\`\`

**Future OAuth2 Implementation:**
- Bearer token authentication
- Token validation
- Refresh token support

## Encryption

### Data in Transit

- **HTTPS**: All API communications use HTTPS
- **TLS 1.2+**: Minimum TLS version enforced
- **Certificate Validation**: Proper certificate chain validation

### Data at Rest

- **Firestore**: Encrypted by default
- **Cloud Storage**: Encrypted by default
- **Chrome Storage**: Local storage (not encrypted, no sensitive data)

## Role-Based Access Control

### Session Ownership

\`\`\`python
# Verify session ownership
session_doc = db.collection('sessions').document(session_id).get()
if session_doc.exists:
    session_data = session_doc.to_dict()
    if session_data.get('createdBy') != user_email:
        return jsonify({'message': 'Unauthorized'}), 403
\`\`\`

### Admin Access

Admin account tracking:
\`\`\`python
admin_doc_ref = doc(db, 'report-user-ids', 'GAPfk6Y3EcCZLu6iQqyY')
# Admin-only operations
\`\`\`

## CORS Configuration

\`\`\`python
CORS(app, resources={
    r"/*": {
        "origins": ["chrome-extension://*", "http://localhost:*"],
        "methods": ["GET", "POST", "OPTIONS"],
        "allow_headers": ["Content-Type", "X-User-Email", "Authorization"],
        "expose_headers": ["Content-Type"],
        "supports_credentials": True
    }
})
\`\`\`

**Security:**
- Restricted to Chrome extension origins
- Localhost allowed for development only
- Specific headers allowed

## GDPR Compliance

### Data Collection

**Collected Data:**
- User email (for session ownership)
- Session metadata
- Screenshot images
- Navigation data
- Domain analysis results

### User Rights

1. **Right to Access**: Users can view their session data
2. **Right to Deletion**: Implement session deletion
3. **Right to Portability**: Export session data
4. **Right to Rectification**: Update session data

### Data Minimization

- Only collect necessary data
- No personal information beyond email
- Screenshots of public websites only

### Consent

- Implicit consent through extension usage
- Clear privacy policy
- Transparent data usage

## HIPAA Considerations

### Healthcare Data

If processing healthcare-related websites:
- No PHI (Protected Health Information) collection
- Screenshots of public pages only
- No patient data storage
- Compliance with healthcare regulations

## Input Validation

### Frontend Validation

\`\`\`javascript
if (!sessionName) {
  setStatus("Please provide a session name.", "danger");
  return;
}

if (selectedJurisdictions.length === 0) {
  setStatus("Please select at least one jurisdiction.", "danger");
  return;
}
\`\`\`

### Backend Validation

\`\`\`python
if not session_id or not journey_name or not url or not image:
    return jsonify({'message': 'Missing required fields'}), 400

# Validate session exists
session_doc = db.collection('sessions').document(session_id).get()
if not session_doc.exists:
    return jsonify({'message': 'Session not found'}), 404
\`\`\`

## Output Encoding

### XSS Prevention

- React automatically escapes content
- No \`dangerouslySetInnerHTML\` usage
- Sanitize user inputs before display

### SQL Injection

- Firestore uses parameterized queries
- No raw query construction
- Input sanitization

## Security Headers

### Backend Headers

\`\`\`python
response.headers.add('Access-Control-Allow-Origin', '*')
response.headers.add('Access-Control-Allow-Headers', 'Content-Type,X-User-Email,Authorization')
response.headers.add('Access-Control-Allow-Methods', 'POST,OPTIONS')
\`\`\`

### Additional Headers (Future)

- \`X-Content-Type-Options: nosniff\`
- \`X-Frame-Options: DENY\`
- \`Content-Security-Policy\`
- \`Strict-Transport-Security\`

## Secure Storage

### Environment Variables

\`\`\`python
# .env file (not committed)
GOOGLE_APPLICATION_CREDENTIALS_2=/path/to/credentials.json
GCP_PROJECT=project-id
CLOUD_TASKS_QUEUE=queue-name
\`\`\`

### Service Account Keys

- Stored securely in Google Cloud
- Not committed to version control
- Rotated regularly

## Audit Trail

### Session Tracking

\`\`\`javascript
metadata: {
  browserInfo: navigator.userAgent,
  timestamp: new Date().toISOString(),
  url: activeTab?.url || 'unknown'
}
\`\`\`

### Navigation Logging

All navigation events logged with:
- Timestamp
- Source and destination URLs
- User action
- Tab ID

## Vulnerability Management

### Dependency Scanning

\`\`\`bash
# Frontend
npm audit

# Backend
pip-audit
\`\`\`

### Regular Updates

- Update dependencies regularly
- Monitor security advisories
- Patch vulnerabilities promptly

## Incident Response

### Security Incident Procedure

1. **Identify**: Detect security incident
2. **Contain**: Isolate affected systems
3. **Eradicate**: Remove threat
4. **Recover**: Restore normal operations
5. **Document**: Record incident details
6. **Notify**: Inform affected users if required

## Compliance Hooks

### GDPR Hooks

- Data export functionality
- Data deletion endpoints
- Consent management
- Privacy policy updates

### HIPAA Hooks

- PHI detection
- Healthcare-specific compliance checks
- Audit logging enhancements

## Best Practices

1. **Principle of Least Privilege**: Minimal permissions
2. **Defense in Depth**: Multiple security layers
3. **Secure by Default**: Secure configurations
4. **Regular Audits**: Security reviews
5. **User Education**: Security awareness

`;

// Auto-generated from testing-qa.md
export const ChromeExtensionDocumentationCodeTestingQa = `# Testing & QA

## Unit Testing

### Backend Unit Tests

**Example Test Structure:**

\`\`\`python
import unittest
from app import app, create_task

class TestScreenCapture(unittest.TestCase):
    def setUp(self):
        self.app = app.test_client()
        self.app.testing = True
    
    def test_missing_email_header(self):
        response = self.app.post('/screen_capture')
        self.assertEqual(response.status_code, 403)
    
    def test_missing_required_fields(self):
        headers = {'X-User-Email': 'test@example.com'}
        response = self.app.post('/screen_capture', headers=headers)
        self.assertEqual(response.status_code, 400)
\`\`\`

### Frontend Unit Tests

**Example Test Structure:**

\`\`\`javascript
import { render, screen, fireEvent } from '@testing-library/react';
import App from './App';

describe('App Component', () => {
  test('renders session creation form', () => {
    render(<App />);
    expect(screen.getByText('Enter Session Name')).toBeInTheDocument();
  });
  
  test('validates session name', () => {
    render(<App />);
    const button = screen.getByText('Start Session');
    fireEvent.click(button);
    expect(screen.getByText(/Please provide a session name/)).toBeInTheDocument();
  });
});
\`\`\`

## Integration Testing

### API Integration Tests

\`\`\`python
def test_screen_capture_flow():
    # Create session
    session = create_test_session()
    
    # Create journey
    journey = create_test_journey(session.id)
    
    # Capture screen
    response = capture_screen(session.id, journey.name)
    assert response.status_code == 200
    
    # Verify image in Firestore
    images = get_journey_images(session.id, journey.id)
    assert len(images) == 1
\`\`\`

### Extension Integration Tests

\`\`\`javascript
// Test extension popup flow
describe('Extension Flow', () => {
  test('creates session and journey', async () => {
    // Mock Chrome APIs
    chrome.identity.getProfileUserInfo = jest.fn((callback) => {
      callback({ email: 'test@example.com' });
    });
    
    // Test session creation
    // Test journey creation
    // Test screen capture
  });
});
\`\`\`

## End-to-End Testing

### E2E Test Scenarios

1. **Complete Dark Pattern Analysis:**
   - Create session
   - Create journey
   - Capture screenshots
   - Verify data in Firestore

2. **Digital Violation Analysis:**
   - Create digital violation session
   - Add Terms URLs
   - Capture with HTML
   - Verify processing

3. **Multi-Jurisdiction Analysis:**
   - Select multiple jurisdictions
   - Verify domain analysis includes all jurisdictions

## Regression Testing

### Test Cases

**Session Management:**
- Create session
- Stop session
- Resume session
- Delete session

**Journey Management:**
- Create journey
- Switch journeys
- Stop journey
- Multiple journeys per session

**Capture Functionality:**
- Visible tab capture
- Full page screenshot
- Error handling
- Retry logic

## Failure Mode Injection

### Error Scenarios

**Network Failures:**
\`\`\`python
# Simulate network timeout
with patch('requests.post', side_effect=TimeoutError):
    response = capture_screen(...)
    assert response.status_code == 500
\`\`\`

**Database Failures:**
\`\`\`python
# Simulate Firestore error
with patch('firestore.Client') as mock_db:
    mock_db.side_effect = Exception("Database error")
    response = create_session(...)
    assert response.status_code == 500
\`\`\`

**Storage Failures:**
\`\`\`python
# Simulate Cloud Storage error
with patch('storage.Client') as mock_storage:
    mock_storage.upload.side_effect = Exception("Storage error")
    response = upload_image(...)
    assert response.status_code == 500
\`\`\`

## Coverage Metrics

### Target Coverage

- **Unit Tests**: 80% code coverage
- **Integration Tests**: 70% coverage of critical paths
- **E2E Tests**: All user workflows covered

### Coverage Tools

**Backend:**
\`\`\`bash
pytest --cov=app --cov-report=html
\`\`\`

**Frontend:**
\`\`\`bash
npm test -- --coverage
\`\`\`

## Manual Testing

### Chrome Extension Testing

1. **Load Extension:**
   - Open Chrome Extensions page
   - Enable Developer Mode
   - Load unpacked extension from \`dist/\` directory

2. **Test Scenarios:**
   - Create session
   - Create journey
   - Capture screenshots
   - View domain analysis
   - Stop session

3. **Browser Compatibility:**
   - Chrome (primary)
   - Edge (Chromium-based)
   - Test on different OS (Windows, macOS, Linux)

### API Testing

**Using curl:**
\`\`\`bash
curl -X POST https://api-endpoint/screen_capture \\
  -H "X-User-Email: test@example.com" \\
  -F "sessionId=xxx" \\
  -F "journeyName=test" \\
  -F "url=https://example.com" \\
  -F "image=@screenshot.jpg"
\`\`\`

**Using Postman:**
- Import API collection
- Test all endpoints
- Verify responses

## Performance Testing

### Load Testing

\`\`\`python
# Simulate concurrent requests
import concurrent.futures

def test_concurrent_captures():
    with concurrent.futures.ThreadPoolExecutor(max_workers=10) as executor:
        futures = [executor.submit(capture_screen) for _ in range(10)]
        results = [f.result() for f in futures]
        assert all(r.status_code == 200 for r in results)
\`\`\`

### Response Time Testing

\`\`\`python
import time

def test_response_time():
    start = time.time()
    response = capture_screen(...)
    elapsed = time.time() - start
    assert elapsed < 2.0  # Response within 2 seconds
\`\`\`

## Extension Testing with Chrome DevTools

### Debugging

1. **Popup Debugging:**
   - Right-click extension icon → Inspect popup
   - Use Chrome DevTools for debugging

2. **Background Script Debugging:**
   - Go to \`chrome://extensions\`
   - Click "service worker" link
   - Use DevTools for debugging

3. **Content Script Debugging:**
   - Open DevTools on the page
   - Content script runs in page context
   - Use console for debugging

## Test Data Management

### Test Fixtures

\`\`\`python
# test_fixtures.py
TEST_SESSION = {
    'sessionName': 'Test Session',
    'type': 'dark_pattern',
    'createdBy': 'test@example.com',
    'jurisdictions': ['EU']
}

TEST_JOURNEY = {
    'journeyName': 'Test Journey',
    'createdBy': 'test@example.com'
}
\`\`\`

### Test Database

- Use Firestore emulator for testing
- Isolated test data
- Cleanup after tests

## Continuous Integration

### CI/CD Pipeline

\`\`\`yaml
# .github/workflows/test.yml
name: Tests
on: [push, pull_request]
jobs:
  test:
    runs-on: ubuntu-latest
    steps:
      - uses: actions/checkout@v2
      - name: Setup Python
        uses: actions/setup-python@v2
      - name: Install dependencies
        run: pip install -r requirements.txt
      - name: Run tests
        run: pytest
\`\`\`

## QA Checklist

### Pre-Release Checklist

- [ ] All unit tests passing
- [ ] Integration tests passing
- [ ] E2E tests passing
- [ ] Manual testing completed
- [ ] Performance benchmarks met
- [ ] Security scan completed
- [ ] Documentation updated
- [ ] Release notes prepared

`;

// Auto-generated from versioning-change-management.md
export const ChromeExtensionDocumentationCodeVersioningChangeManagement = `# Versioning & Change Management

## Git Strategy

### Branching Model
- **main**: Production-ready code
- **develop**: Development branch
- **feature/**: Feature branches
- **hotfix/**: Hotfix branches

### Commit Messages
- Follow conventional commits
- Clear, descriptive messages
- Reference issue numbers

## Release Notes Template

\`\`\`markdown
## Version X.X.X - Date

### Added
- New features

### Changed
- Modified features

### Fixed
- Bug fixes

### Deprecated
- Features to be removed

### Security
- Security updates
\`\`\`

## Backward Compatibility

### Firestore Schema Changes
- Add new fields as optional
- Provide default values
- Migrate existing documents
- Version API endpoints

### Extension Updates
- Maintain Chrome storage format
- Handle missing fields gracefully
- Provide migration scripts if needed

`;

// Auto-generated from compliance-audit-trail.md
export const ChromeExtensionDocumentationFlowsComplianceAuditTrail = `# Compliance & Audit Trail Integration

## Session Metadata Tracking

### Tracked Data
- Session name
- Creator email
- Creation timestamp
- Domain analyzed
- Jurisdictions selected
- Browser information
- Initial URL

### Storage
\`\`\`javascript
{
  sessionName: string,
  createdBy: string,
  createdAt: Timestamp,
  domainName: string,
  jurisdictions: string[],
  metadata: {
    browserInfo: string,
    timestamp: string,
    url: string
  }
}
\`\`\`

## Navigation Data Logging

### Logged Events
- Page navigations
- User interactions (clicks, scrolls)
- Form submissions
- Tab changes

### Data Structure
\`\`\`javascript
{
  from: string,
  to: string,
  action: string,
  timestamp: string,
  tabId: string,
  interactionType: string
}
\`\`\`

## Image Capture Audit Trail

### Captured Information
- URL where capture taken
- Capture type (visible/full page)
- User note
- Timestamp
- Processing status
- Error information (if any)

## Jurisdiction-Based Analysis Tracking

### Tracked Information
- Selected jurisdictions
- Legal framework analysis
- Compliance recommendations
- Violation documentation

### Storage
- Stored in session document
- Linked to domain analysis
- Available for compliance reports

`;

// Auto-generated from end-to-end-workflows.md
export const ChromeExtensionDocumentationFlowsEndToEndWorkflows = `# End-to-End Workflows

## Dark Pattern Detection Workflow

\`\`\`mermaid
graph TD
    A[User Opens Extension] --> B[Create Session]
    B --> C[Select Jurisdictions]
    C --> D[Enter Session Name]
    D --> E[Start Session]
    E --> F[Domain Analysis Triggered]
    F --> G[Create Journey]
    G --> H[Navigate Website]
    H --> I[Capture Screenshot]
    I --> J[Upload to Cloud Storage]
    J --> K[Save Metadata to Firestore]
    K --> L[Create Cloud Task]
    L --> M[Background Processing]
    M --> N[Update Status]
    N --> O[View Results]
\`\`\`

## Digital Violation Analysis Workflow

\`\`\`mermaid
graph TD
    A[User Opens Extension] --> B[Select Digital Violation Type]
    B --> C[Enter Terms & Conditions URLs]
    C --> D[Select Jurisdictions]
    D --> E[Create Session]
    E --> F[Domain Analysis]
    F --> G[Create Journey]
    G --> H[Capture with HTML]
    H --> I[Upload Image & HTML]
    I --> J[Process Digital Violation]
    J --> K[Generate Compliance Report]
\`\`\`

## Session Creation Flow

1. User clicks extension icon
2. Extension popup opens
3. User selects session type (dark pattern or digital violation)
4. User enters session name
5. User selects jurisdictions
6. For digital violation: User enters Terms URLs
7. User clicks "Start Session"
8. Extension creates session in Firestore
9. Domain analysis triggered in background
10. Session ID stored in Chrome storage

## Journey Creation Flow

1. User has active session
2. User selects journey from dropdown or enters custom name
3. User clicks "Start Journey"
4. Extension creates journey document in Firestore subcollection
5. Journey ID stored in Chrome storage
6. Navigation tracking begins

## Capture Flow

### Visible Tab Capture

1. User navigates to page
2. User enters note (optional)
3. User clicks "Screen Capture"
4. Extension captures visible tab
5. Image converted to Blob
6. FormData created with metadata
7. POST request to \`/screen_capture\`
8. Backend uploads to Cloud Storage
9. Metadata saved to Firestore
10. Cloud Task created for processing

### Full Page Screenshot

1. User clicks "Full Page"
2. Extension gets page dimensions
3. Extension scrolls and captures multiple screenshots
4. Screenshots stitched into single image
5. Image uploaded to backend
6. Backend processes via Selenium (if needed)
7. Result saved to Firestore

## Processing Flow

1. Cloud Task triggered
2. Background processor receives payload
3. Image downloaded from Cloud Storage
4. Analysis performed
5. Results saved to Firestore
6. Status updated to "completed" or "failed"

`;

// Auto-generated from error-handling-recovery.md
export const ChromeExtensionDocumentationFlowsErrorHandlingRecovery = `# Error Handling & Recovery Paths

## Firestore Connection Failures

### Error Detection
- Connection timeout
- Permission denied
- Network error

### Recovery
1. Retry with exponential backoff
2. Show user-friendly error message
3. Log error for debugging
4. Allow user to retry operation

## Cloud Storage Upload Errors

### Error Detection
- Upload timeout
- Permission denied
- File size exceeded

### Recovery
1. Retry upload
2. Compress image if too large
3. Show error message to user
4. Save metadata with error status

## Cloud Tasks Queue Failures

### Error Detection
- Task creation failed
- Queue full
- Invalid payload

### Recovery
1. Mark image as "failed to process"
2. Store error message
3. Allow manual retry
4. Notify user of partial success

## Extension Permission Errors

### Error Detection
- Missing permissions
- Permission denied
- Invalid permission scope

### Recovery
1. Request missing permissions
2. Show permission request dialog
3. Guide user to enable permissions
4. Provide troubleshooting steps

## Network Errors

### Error Detection
- Request timeout
- Connection refused
- DNS resolution failed

### Recovery
1. Retry with backoff
2. Check network connectivity
3. Show offline indicator
4. Queue requests for retry

## Validation Errors

### Error Detection
- Missing required fields
- Invalid data format
- Business rule violations

### Recovery
1. Return 400 Bad Request
2. Provide specific error message
3. Highlight invalid fields
4. Allow user to correct and retry

`;

// Auto-generated from interactive-flows.md
export const ChromeExtensionDocumentationFlowsInteractiveFlows = `# Interactive Flows

## Extension Popup UI Flow

### Initial State
- No active session
- Session creation form visible
- Jurisdiction selection
- Session type selection

### Active Session State
- Session name displayed
- Journey creation/selection
- Capture buttons enabled
- Status messages

### Active Journey State
- Journey name displayed
- Note input field
- Capture buttons active
- Navigation tracking active

## Domain Analysis Recommendations Display

### Flow
1. Session created
2. Domain analysis triggered
3. Perplexity AI analysis completes
4. Recommendations stored in Firestore
5. "View Guide" button appears
6. User clicks button
7. Popup window opens with analysis
8. Formatted recommendations displayed

### Popup Content
- Domain information
- Jurisdictions analyzed
- Dark pattern analysis
- Legal framework information
- Priority recommendations
- Citations and sources

## Journey Guide Popup Window

### Features
- Formatted AI analysis
- Legal guidelines
- Journey recommendations
- Priority areas
- Print functionality
- Close button

### Implementation
\`\`\`javascript
const popup = window.open('', 'recommendations', 
  'width=900,height=700,scrollbars=yes,resizable=yes'
);
popup.document.write(formattedHTML);
\`\`\`

`;

// Auto-generated from process-states.md
export const ChromeExtensionDocumentationFlowsProcessStates = `# Process States & Transitions

## Session Lifecycle

### States
1. **Created**: Session document created in Firestore
2. **Active**: Session has active journey
3. **Stopped**: Session ended, \`endedAt\` timestamp set

### Transitions
\`\`\`
Created → Active (when journey created)
Active → Stopped (user stops session)
\`\`\`

## Journey Lifecycle

### States
1. **Created**: Journey document created
2. **Active**: Journey has active captures
3. **Stopped**: Journey ended, \`endedAt\` timestamp set

### Transitions
\`\`\`
Created → Active (immediately)
Active → Stopped (user stops journey)
\`\`\`

## Image Processing States

### States
1. **initial**: Image uploaded, metadata saved
2. **processing**: Cloud Task processing
3. **completed**: Processing successful
4. **failed**: Processing failed

### Transitions
\`\`\`
initial → processing (Cloud Task starts)
processing → completed (success)
processing → failed (error)
\`\`\`

## Navigation Tracking States

### States
- **page_load**: Initial page load
- **navigation**: URL change
- **interaction**: User interaction (click, scroll)

### Data Structure
\`\`\`javascript
{
  from: string,
  to: string,
  action: string,
  timestamp: string,
  tabId: string,
  interactionType: string
}
\`\`\`

`;

// Auto-generated from role-based-access.md
export const ChromeExtensionDocumentationFlowsRoleBasedAccess = `# Role-Based Access Flows

## User Authentication Flow

\`\`\`mermaid
graph TD
    A[Extension Opens] --> B[Chrome Identity API]
    B --> C{Email Available?}
    C -->|Yes| D[Set User Email]
    C -->|No| E[Request Permission]
    E --> F{Permission Granted?}
    F -->|Yes| D
    F -->|No| G[Show Error]
    D --> H[Authenticate API Requests]
\`\`\`

## Session Ownership Model

### Ownership Rules
- Sessions are owned by creator (email)
- Users can only access their own sessions
- Admin account has read access to all sessions

### Access Control
\`\`\`python
# Verify ownership
session_doc = db.collection('sessions').document(session_id).get()
if session_doc.to_dict().get('createdBy') != user_email:
    return jsonify({'message': 'Unauthorized'}), 403
\`\`\`

## Admin Account Tracking

### Admin Functions
- Track all domains analyzed
- Track all sessions created
- Generate aggregate reports

### Implementation
\`\`\`python
admin_doc_ref = doc(db, 'report-user-ids', 'GAPfk6Y3EcCZLu6iQqyY')
updateDoc(admin_doc_ref, {
    'domains': arrayUnion(domain),
    'sessions': arrayUnion(session_id)
})
\`\`\`

`;

// Auto-generated from use-case-scenarios.md
export const ChromeExtensionDocumentationFlowsUseCaseScenarios = `# Use Case Scenarios

## Legal Tech: E-commerce Dark Pattern Audit

### Scenario
A legal team needs to audit an e-commerce website for dark patterns to support a consumer protection case.

### Steps
1. Create dark pattern detection session
2. Select EU and US Federal jurisdictions
3. Create "Checkout Process" journey
4. Navigate through checkout flow
5. Capture screenshots at each step
6. Document suspicious patterns
7. Generate compliance report

### Expected Outcomes
- Screenshots of dark patterns
- Navigation flow documentation
- Legal framework analysis
- Compliance recommendations

## Healthcare: Privacy Policy Compliance Check

### Scenario
A healthcare organization needs to verify their privacy policy compliance with HIPAA and GDPR.

### Steps
1. Create digital violation session
2. Enter privacy policy URL
3. Select EU jurisdiction
4. Create "Privacy Settings" journey
5. Capture privacy-related pages
6. Analyze Terms & Conditions
7. Review compliance report

### Expected Outcomes
- Privacy policy analysis
- Compliance gaps identified
- Recommendations for improvement
- Audit trail for compliance

## Finance: Terms & Conditions Violation Detection

### Scenario
A financial services company needs to audit their terms and conditions for potential violations.

### Steps
1. Create digital violation session
2. Enter Terms & Conditions URLs
3. Select US Federal jurisdiction
4. Create multiple journeys (Account Setup, Subscription, Account Deletion)
5. Capture relevant pages
6. Analyze for violations
7. Document findings

### Expected Outcomes
- Terms analysis report
- Violation documentation
- Compliance recommendations
- Legal risk assessment

`;

// Auto-generated from accessibility-localization.md
export const ChromeExtensionDocumentationUserAccessibilityLocalization = `# Accessibility & Localization

## Keyboard Navigation

### Supported Shortcuts
- **Tab**: Navigate between form fields
- **Enter**: Submit forms, activate buttons
- **Escape**: Close popup (if supported)

### Focus Management
- Focus indicators visible
- Logical tab order
- Skip links where applicable

## Screen Reader Compatibility

### ARIA Labels
- Form fields have labels
- Buttons have descriptive text
- Status messages announced

### Semantic HTML
- Proper heading hierarchy
- Form labels associated
- Button roles defined

## Localization

### Current Language
- English (en-US) only

### Future Localization
- Multi-language support planned
- Translation files structure
- Locale-specific formatting

### Localization Notes
- Dates: ISO 8601 format
- Numbers: Standard formatting
- Currency: Not applicable

## Color Contrast

### Standards
- WCAG AA compliance
- Minimum 4.5:1 contrast ratio
- Color not sole indicator

## Responsive Design

### Screen Sizes
- Extension popup: 450px width
- Recommendations popup: 900x700px
- Responsive to content

## Browser Compatibility

### Supported Browsers
- Chrome (primary)
- Edge (Chromium)
- Brave
- Other Chromium-based browsers

### Unsupported
- Firefox (different extension API)
- Safari (different extension API)

`;

// Auto-generated from feature-walkthroughs.md
export const ChromeExtensionDocumentationUserFeatureWalkthroughs = `# Feature Walkthroughs

## Creating a Session

### Dark Pattern Detection Session

1. Click extension icon
2. Select "Dark Pattern Detection" from Analysis Type dropdown
3. Enter session name (e.g., "E-commerce Audit")
4. Select jurisdictions (EU, US Federal, or both)
5. Click "Start Session"
6. Session created, domain analysis begins automatically

### Digital Violation Session

1. Click extension icon
2. Select "Digital Rights Violation" from Analysis Type
3. Enter Terms & Conditions URLs (one per line)
4. Enter session name
5. Select jurisdictions
6. Click "Start Session"

## Selecting Jurisdictions

### Available Jurisdictions
- **EU**: European Union (GDPR, EU DSA)
- **US Federal**: United States Federal (FTC regulations)

### Multi-Jurisdiction Analysis
- Select multiple jurisdictions for comprehensive analysis
- Analysis includes all selected legal frameworks
- Recommendations cover all jurisdictions

## Creating User Journeys

### Predefined Journeys
- Product Research
- Purchase Flow
- Account Setup
- Customer Support
- Checkout Process
- Privacy Settings
- Subscription Management
- Account Deletion
- Custom (enter your own)

### Steps
1. With active session, select journey from dropdown
2. Or select "Custom" and enter custom name
3. Click "Start Journey"
4. Journey active, ready for captures

## Capturing Screenshots

### Visible Tab Capture
1. Navigate to page you want to capture
2. Enter note describing what you're looking for (optional)
3. Click "Screen Capture" button
4. Image captured and uploaded
5. Status message confirms success

### Full Page Screenshot
1. Navigate to page
2. Enter note (optional)
3. Click "Full Page" button
4. Extension scrolls and captures entire page
5. Stitched image uploaded
6. Status message confirms success

## Viewing Domain Analysis Recommendations

### Accessing Recommendations
1. After session creation, wait for domain analysis
2. "View Guide" button appears when ready
3. Click "View Guide" button
4. Popup window opens with analysis

### Analysis Content
- Domain information
- Jurisdictions analyzed
- Dark pattern analysis
- Legal framework information
- Priority recommendations
- Citations and sources

### Using Recommendations
- Review recommended journeys
- Prioritize high-risk areas
- Use legal framework information
- Reference citations for compliance

`;

// Auto-generated from getting-started.md
export const ChromeExtensionDocumentationUserGettingStarted = `# Getting Started Guide

## Installation

### Step 1: Download Extension

1. Download the extension package
2. Extract the ZIP file
3. Note the location of the extracted folder

### Step 2: Load Extension in Chrome

1. Open Chrome browser
2. Navigate to \`chrome://extensions/\`
3. Enable "Developer mode" (toggle in top right)
4. Click "Load unpacked"
5. Select the extracted extension folder
6. Extension icon should appear in toolbar

### Step 3: Configure API Key

1. Click extension icon
2. Go to Settings (if available)
3. Enter Perplexity API key (optional, for domain analysis)
4. Save settings

## First-Time Setup

### Permissions

The extension will request permissions:
- **Identity**: To identify you
- **Storage**: To save session data
- **Tabs**: To capture screenshots
- **Web Navigation**: To track navigation

Click "Allow" to grant permissions.

### Initial Configuration

1. Open extension popup
2. Verify you're logged in (email displayed)
3. Ready to create your first session!

## Environment Setup

### Perplexity API Key (Optional)

For domain analysis features:
1. Sign up at [Perplexity AI](https://www.perplexity.ai/)
2. Get your API key
3. Configure in extension settings
4. Or set as environment variable: \`VITE_PERPLEXITY_API_KEY\`

### Backend Configuration

Backend is pre-configured. No user setup required.

## Quick Start

1. **Create Session**: Click extension, enter session name, select type
2. **Select Jurisdictions**: Choose EU, US Federal, or both
3. **Start Session**: Click "Start Session"
4. **Create Journey**: Select or create journey name
5. **Capture Screenshots**: Navigate website, click "Screen Capture"
6. **View Results**: Check Firestore or extension popup

## Next Steps

- Read [Feature Walkthroughs](./feature-walkthroughs.md)
- Check [Troubleshooting](./troubleshooting-faqs.md) if issues
- Review [Glossary](./glossary.md) for terminology

`;

// Auto-generated from glossary.md
export const ChromeExtensionDocumentationUserGlossary = `# Glossary

## Dark Patterns

**Definition**: User interface design choices that manipulate users into taking actions they might not otherwise take.

**Examples**:
- **Roach Motel**: Easy to get in, hard to get out
- **Bait and Switch**: Advertise one thing, deliver another
- **Hidden Costs**: Costs revealed only at checkout
- **Confirmshaming**: Shaming users into opting in
- **Forced Continuity**: Charging after free trial without clear notice

## Digital Rights Violations

**Definition**: Violations of user digital rights including privacy, data protection, and user autonomy.

**Examples**:
- Insufficient consent mechanisms
- Unclear privacy policies
- Difficult account deletion
- Data sharing without consent
- Hidden data collection

## GDPR (General Data Protection Regulation)

**Definition**: EU regulation governing data protection and privacy.

**Key Principles**:
- Right to access personal data
- Right to deletion
- Right to data portability
- Consent requirements
- Data minimization

## FTC (Federal Trade Commission)

**Definition**: US agency enforcing consumer protection laws.

**Relevant Regulations**:
- Section 5 of FTC Act (unfair/deceptive practices)
- COPPA (Children's Online Privacy)
- CAN-SPAM Act

## EU DSA (Digital Services Act)

**Definition**: EU regulation governing digital services and platforms.

**Key Provisions**:
- Transparency requirements
- Content moderation rules
- User rights protection
- Platform accountability

## Session

**Definition**: A complete analysis workflow for a specific domain.

**Components**:
- Session name
- Type (dark pattern or digital violation)
- Jurisdictions
- Domain analyzed
- Associated journeys

## Journey

**Definition**: A specific user flow within a session.

**Examples**:
- Checkout process
- Account setup
- Privacy settings
- Subscription management

## Capture

**Definition**: A screenshot or image taken during a journey.

**Types**:
- **Screen Capture**: Visible tab capture
- **Full Page Screenshot**: Complete page capture

## Jurisdiction

**Definition**: Legal framework or region for compliance analysis.

**Available**:
- **EU**: European Union regulations
- **US Federal**: United States federal regulations

## Domain Analysis

**Definition**: AI-powered analysis of a website domain for compliance issues.

**Includes**:
- Dark pattern identification
- Legal framework analysis
- Compliance recommendations
- Priority areas

## Navigation Data

**Definition**: Logged user interactions and page navigations.

**Includes**:
- Page URLs
- User actions (clicks, scrolls)
- Timestamps
- Interaction types

`;

// Auto-generated from interactive-tutorials.md
export const ChromeExtensionDocumentationUserInteractiveTutorials = `# Interactive Tutorials

## Step-by-Step Tutorial: Dark Pattern Detection

### Step 1: Install Extension
1. Download extension package
2. Extract ZIP file
3. Open Chrome Extensions page
4. Enable Developer Mode
5. Load unpacked extension

### Step 2: Create Your First Session
1. Click extension icon
2. Select "Dark Pattern Detection"
3. Enter session name: "My First Audit"
4. Select jurisdictions: EU
5. Click "Start Session"

### Step 3: Create a Journey
1. Select "Checkout Process" from dropdown
2. Click "Start Journey"
3. Journey is now active

### Step 4: Capture Screenshots
1. Navigate to an e-commerce website
2. Go to checkout page
3. Enter note: "Testing checkout flow"
4. Click "Screen Capture"
5. Continue through checkout
6. Capture additional screenshots as needed

### Step 5: View Results
1. Check extension popup for status
2. View captured images in Firestore
3. Review navigation data
4. Analyze for dark patterns

## Common Workflows

### E-commerce Audit Workflow
1. Create dark pattern session
2. Create "Product Research" journey
3. Create "Checkout Process" journey
4. Create "Subscription" journey
5. Capture screenshots for each
6. Review domain analysis recommendations
7. Document findings

### Privacy Policy Compliance Workflow
1. Create digital violation session
2. Enter privacy policy URL
3. Create "Privacy Settings" journey
4. Capture privacy-related pages
5. Review compliance analysis
6. Generate compliance report

## Video Walkthroughs

*Note: Video walkthroughs to be added in future updates*

### Planned Videos
- Installation and Setup
- Creating Your First Session
- Capturing Screenshots
- Using Domain Analysis
- Generating Reports

`;

// Auto-generated from troubleshooting-faqs.md
export const ChromeExtensionDocumentationUserTroubleshootingFaqs = `# Troubleshooting & FAQs

## Extension Not Loading

### Symptoms
- Extension icon not visible
- Popup doesn't open
- Error in Chrome Extensions page

### Solutions
1. Check Developer Mode is enabled
2. Reload extension from \`chrome://extensions/\`
3. Check for error messages in Extensions page
4. Verify extension folder structure is correct
5. Try removing and re-adding extension

## Screenshot Capture Failures

### Symptoms
- Capture button doesn't work
- Error message appears
- No image saved

### Solutions
1. Check you have active session and journey
2. Verify tab permissions granted
3. Check network connection
4. Try refreshing the page
5. Check browser console for errors

## Firestore Connection Issues

### Symptoms
- Session not saving
- Data not appearing
- Connection errors

### Solutions
1. Check internet connection
2. Verify Firebase configuration
3. Check browser console for errors
4. Try refreshing extension
5. Clear browser cache

## API Key Configuration Problems

### Symptoms
- Domain analysis not working
- API error messages
- No recommendations

### Solutions
1. Verify API key is correct
2. Check API key has sufficient credits
3. Verify environment variable set correctly
4. Check Perplexity API status
5. Review API key permissions

## Permission Errors

### Symptoms
- Permission denied errors
- Features not working
- Access errors

### Solutions
1. Check extension permissions in \`chrome://extensions/\`
2. Grant missing permissions
3. Reload extension
4. Check site permissions if applicable

## Session Not Saving

### Symptoms
- Session disappears after closing popup
- Data lost
- State not persisting

### Solutions
1. Check Chrome storage permissions
2. Verify Chrome storage not full
3. Check browser console for errors
4. Try creating session again
5. Clear extension storage and retry

## Frequently Asked Questions

### Q: Can I use the extension without Perplexity API key?
A: Yes, but domain analysis features won't work. You can still capture screenshots and create sessions.

### Q: How do I delete a session?
A: Currently, sessions are not deleted through the extension. Contact support for deletion requests.

### Q: Can I export my session data?
A: Data is stored in Firestore. You can access it through Firebase console or request export.

### Q: Does the extension work offline?
A: No, the extension requires internet connection for API calls and Firestore access.

### Q: Can I use multiple sessions simultaneously?
A: No, only one active session at a time. Stop current session before starting new one.

### Q: How long are sessions stored?
A: Sessions are stored indefinitely in Firestore until manually deleted.

### Q: Can I share sessions with others?
A: Sessions are private to the creator. Sharing functionality not currently available.

### Q: What browsers are supported?
A: Chrome and Chromium-based browsers (Edge, Brave, etc.)

### Q: Is my data secure?
A: Yes, data is encrypted in transit and at rest. Only you can access your sessions.

### Q: How do I report a bug?
A: Contact support or create an issue in the project repository.

`;

// Auto-generated from user-versioning.md
export const ChromeExtensionDocumentationUserUserVersioning = `# Versioning & Release Notes

## Current Version

**Version**: 17.0

## Version History

### Version 17.0
- Initial release
- Dark pattern detection
- Digital violation analysis
- Domain analysis with Perplexity AI
- Multi-jurisdiction support

## Feature Updates

### Planned Features
- Session export functionality
- Session sharing
- Enhanced analytics
- Additional jurisdictions
- Custom compliance frameworks

## Breaking Changes

### None Currently

Future breaking changes will be documented here with migration guides.

## Update Process

### Automatic Updates
- Chrome Web Store: Automatic updates
- Manual installation: Manual update required

### Update Notifications
- Version number displayed in extension
- Release notes in popup (if available)

## Compatibility

### Chrome Version
- Minimum: Chrome 88+
- Recommended: Latest Chrome version

### API Compatibility
- Backend API: Version 1.0
- Firestore: Current schema version

`;

// Auto-generated from code-examples.md
export const CookieConsentBackendDocumentation01CodeDocumentationCodeExamples = `# Code Examples

## Overview

This document provides comprehensive code examples for common use cases, edge cases, error handling, and integration scenarios in the Cookie Consent Analyzer backend.

## Standard Use Cases

### 1. Basic Analysis

\`\`\`python
import asyncio
from app import analyze_cookie_banner_modular

async def main():
    result = await analyze_cookie_banner_modular(
        url="https://www.example.com",
        user_id="user123"
    )
    
    print(f"Banner detected: {result.banner_detection.banner_detected}")
    print(f"Total stages: {len(result.stages)}")
    print(f"Compliance score: {result.legal_compliance_analysis.get('compliance_score')}")

asyncio.run(main())
\`\`\`

### 2. API Integration

\`\`\`python
import requests
import time

def analyze_website(url: str, user_id: str):
    # Start analysis
    response = requests.post(
        "http://localhost:8080/api/v1/analyze",
        json={"url": url, "user_id": user_id}
    )
    task_id = response.json()["task_id"]
    
    # Poll for completion
    while True:
        status = requests.get(f"http://localhost:8080/api/v1/status/{task_id}").json()
        
        if status["status"] == "completed":
            results = requests.get(f"http://localhost:8080/api/v1/results/{task_id}").json()
            return results
        elif status["status"] == "failed":
            raise Exception(f"Analysis failed: {status['error']}")
        
        time.sleep(5)

# Usage
results = analyze_website("https://www.example.com", "user123")
print(f"Compliance: {results['summary']['compliance_status']}")
\`\`\`

### 3. Banner Detection

\`\`\`python
from utility.llm_analysis import detect_banner_with_llm
from pathlib import Path

async def detect_banner(screenshot_path: Path):
    result = await detect_banner_with_llm(screenshot_path)
    
    if result.banner_detected:
        print(f"Banner position: {result.banner_position}")
        print(f"Accept button: {result.accept_button}")
        print(f"Reject button: {result.reject_button}")
        print(f"Manage preferences: {result.manage_preferences_button}")
    else:
        print("No banner detected")

# Usage
await detect_banner(Path("screenshot.png"))
\`\`\`

### 4. Cookie Analysis

\`\`\`python
from utility.llm_analysis import analyze_cookies_data_with_llm

async def analyze_cookies(cookies: list, stage_name: str):
    result = await analyze_cookies_data_with_llm(cookies, stage_name)
    
    summary = result.get('summary', {})
    print(f"High risk: {summary.get('high_risk_count', 0)}")
    print(f"Medium risk: {summary.get('medium_risk_count', 0)}")
    print(f"Low risk: {summary.get('low_risk_count', 0)}")
    
    return result

# Usage
cookies = [{"name": "session_id", "domain": ".example.com", ...}]
result = await analyze_cookies(cookies, "After Accept")
\`\`\`

## Edge Cases

### 1. No Banner Detected

\`\`\`python
async def handle_no_banner(url: str):
    result = await analyze_cookie_banner_modular(url)
    
    if not result.banner_detection.banner_detected:
        print("⚠️ No cookie banner detected")
        print("Analysis will be limited to initial cookie capture")
        
        # Still analyze initial cookies
        if result.stages:
            initial_cookies = result.stages[0].cookies
            if initial_cookies:
                print(f"Found {len(initial_cookies)} cookies before interaction")
                # This may indicate non-compliance if non-essential cookies are present
\`\`\`

### 2. Multiple Banners

\`\`\`python
async def handle_multiple_banners(url: str):
    # The LLM detection should identify the primary cookie banner
    # If multiple banners are detected, the first/main one is used
    
    result = await analyze_cookie_banner_modular(url)
    
    # Check banner text for multiple banner indicators
    banner_text = result.banner_detection.banner_text_snippet
    
    if "cookie" in banner_text.lower() and "newsletter" in banner_text.lower():
        print("⚠️ Multiple popups detected - may need manual review")
\`\`\`

### 3. Complex Preferences Dialog

\`\`\`python
async def handle_complex_preferences(url: str, button_text: str):
    from utility.sessions import execute_action_and_capture
    from utility.llm_analysis import analyze_preferences_dialog_with_llm
    from browser_use import ChatOpenAI
    from pathlib import Path
    
    llm = ChatOpenAI(model="gpt-4o-mini")
    output_dir = Path("cookie_analysis") / "example" / "timestamp"
    output_dir.mkdir(parents=True, exist_ok=True)
    
    # Open preferences dialog
    task = f"""
    Navigate to {url}
    Click the "{button_text}" button
    Wait for preferences dialog to fully load
    """
    
    cookies, screenshot = await execute_action_and_capture(
        task=task,
        llm=llm,
        output_dir=output_dir,
        stage_number=1,
        action_name="open_preferences",
        max_steps=15
    )
    
    # Analyze preferences dialog
    if screenshot:
        analysis = await analyze_preferences_dialog_with_llm(
            screenshot,
            banner_has_accept=True,
            banner_has_reject=True
        )
        
        print(f"Categories found: {len(analysis.get('categories', []))}")
        print(f"Granular control: {analysis.get('has_granular_options')}")
\`\`\`

### 4. Slow-Loading Websites

\`\`\`python
async def handle_slow_loading(url: str):
    # Increase wait times in prompts
    from utility.prompts import create_initial_load_task
    
    # Custom task with longer wait
    task = f"""
    Navigate to {url}
    Wait 15 seconds for page and banner to fully load
    Some sites load very slowly
    """
    
    # Use longer max_steps
    cookies, screenshot = await execute_action_and_capture(
        task=task,
        llm=llm,
        output_dir=output_dir,
        stage_number=0,
        action_name="initial",
        max_steps=5  # More steps for slow sites
    )
\`\`\`

### 5. Non-English Banners

\`\`\`python
async def handle_multilingual_banner(url: str):
    # The LLM should handle multiple languages automatically
    # But you can add language hints in prompts
    
    result = await analyze_cookie_banner_modular(url)
    
    banner_text = result.banner_detection.banner_text_snippet
    
    # Detect language
    languages = {
        "accepter": "French",
        "aceptar": "Spanish",
        "akzeptieren": "German",
        "accetta": "Italian"
    }
    
    detected_lang = None
    for keyword, lang in languages.items():
        if keyword.lower() in banner_text.lower():
            detected_lang = lang
            break
    
    if detected_lang:
        print(f"Detected language: {detected_lang}")
\`\`\`

## Error Handling Examples

### 1. Graceful Degradation

\`\`\`python
async def analyze_with_fallback(url: str):
    try:
        # Try with cloud services
        gcs_manager = get_gcs_manager()
        firebase_manager = get_firebase_manager()
        
        result = await analyze_cookie_banner_modular(
            url=url,
            user_id="user123"
        )
        
    except Exception as e:
        print(f"Cloud services failed: {e}")
        print("Falling back to local-only analysis")
        
        # Continue without cloud services
        result = await analyze_cookie_banner_modular(url=url)
        
        # Save locally
        import json
        with open("local_result.json", "w") as f:
            json.dump(result.__dict__, f, default=str)
    
    return result
\`\`\`

### 2. Retry Logic

\`\`\`python
import asyncio
from tenacity import retry, stop_after_attempt, wait_exponential

@retry(
    stop=stop_after_attempt(3),
    wait=wait_exponential(multiplier=1, min=4, max=10)
)
async def analyze_with_retry(url: str):
    try:
        result = await analyze_cookie_banner_modular(url)
        return result
    except Exception as e:
        print(f"Analysis failed: {e}, retrying...")
        raise

# Usage
result = await analyze_with_retry("https://www.example.com")
\`\`\`

### 3. Timeout Handling

\`\`\`python
import asyncio

async def analyze_with_timeout(url: str, timeout: int = 300):
    try:
        result = await asyncio.wait_for(
            analyze_cookie_banner_modular(url),
            timeout=timeout
        )
        return result
    except asyncio.TimeoutError:
        print(f"Analysis timed out after {timeout} seconds")
        return None
\`\`\`

### 4. LLM API Error Handling

\`\`\`python
from utility.llm_analysis import detect_banner_with_llm
from utility.data_models import BannerDetectionResult

async def safe_banner_detection(screenshot_path: Path):
    try:
        result = await detect_banner_with_llm(screenshot_path)
        return result
    except Exception as e:
        print(f"LLM detection failed: {e}")
        # Return safe default
        return BannerDetectionResult(
            banner_detected=False,
            accept_button=False,
            reject_button=False,
            close_button=False,
            manage_preferences_button=False,
            banner_position="unknown",
            confidence="low"
        )
\`\`\`

## Integration Examples

### 1. Batch Processing

\`\`\`python
import asyncio
from typing import List

async def batch_analyze(urls: List[str], user_id: str):
    tasks = [
        analyze_cookie_banner_modular(url, user_id=user_id)
        for url in urls
    ]
    
    results = await asyncio.gather(*tasks, return_exceptions=True)
    
    successful = []
    failed = []
    
    for url, result in zip(urls, results):
        if isinstance(result, Exception):
            failed.append((url, str(result)))
        else:
            successful.append((url, result))
    
    return successful, failed

# Usage
urls = [
    "https://www.example1.com",
    "https://www.example2.com",
    "https://www.example3.com"
]

successful, failed = await batch_analyze(urls, "user123")
print(f"Successful: {len(successful)}, Failed: {len(failed)}")
\`\`\`

### 2. Webhook Integration

\`\`\`python
import requests
from fastapi import BackgroundTasks

async def process_analysis_with_webhook(
    url: str,
    user_id: str,
    webhook_url: str,
    background_tasks: BackgroundTasks
):
    def send_webhook(result_data: dict):
        try:
            requests.post(webhook_url, json=result_data, timeout=10)
        except Exception as e:
            print(f"Webhook failed: {e}")
    
    result = await analyze_cookie_banner_modular(url, user_id=user_id)
    
    # Send webhook in background
    background_tasks.add_task(
        send_webhook,
        {
            "url": url,
            "status": "completed",
            "compliance_score": result.legal_compliance_analysis.get('compliance_score'),
            "firebase_doc_id": result.firebase_doc_id
        }
    )
    
    return result
\`\`\`

### 3. Database Integration

\`\`\`python
from utility.firebase_storage import get_firebase_manager

async def analyze_and_store(url: str, user_id: str):
    # Perform analysis
    result = await analyze_cookie_banner_modular(url, user_id=user_id)
    
    # Store in database
    firebase_manager = get_firebase_manager(user_id=user_id)
    
    # Get existing analyses for this domain
    domain = firebase_manager.extract_domain_from_url(url)
    existing = firebase_manager.get_analysis_by_domain(domain, limit=5, user_id=user_id)
    
    print(f"Found {len(existing)} previous analyses for {domain}")
    
    return result
\`\`\`

### 4. Report Generation

\`\`\`python
import json
from datetime import datetime

def generate_report(result):
    report = {
        "metadata": {
            "url": result.url,
            "timestamp": result.timestamp,
            "analysis_version": "2.0"
        },
        "banner_detection": result.banner_detection.dict(),
        "stages": [
            {
                "stage_name": stage.stage_name,
                "cookies_count": stage.cookies_count,
                "description": stage.description
            }
            for stage in result.stages
        ],
        "compliance": {
            "status": result.legal_compliance_analysis.get('overall_compliance'),
            "score": result.legal_compliance_analysis.get('compliance_score'),
            "critical_issues": result.legal_compliance_analysis.get('critical_violations', [])
        },
        "summary": result.overall_summary
    }
    
    return report

# Usage
result = await analyze_cookie_banner_modular("https://www.example.com")
report = generate_report(result)

with open("report.json", "w") as f:
    json.dump(report, f, indent=2, default=str)
\`\`\`

## Testing Examples

### 1. Unit Test

\`\`\`python
import pytest
from utility.data_models import BannerDetectionResult

def test_banner_detection_result():
    result = BannerDetectionResult(
        banner_detected=True,
        accept_button=True,
        reject_button=True,
        close_button=False,
        manage_preferences_button=True,
        banner_position="bottom",
        confidence="high"
    )
    
    assert result.banner_detected is True
    assert result.accept_button is True
    assert result.reject_button is True
\`\`\`

### 2. Integration Test

\`\`\`python
import pytest
from app import analyze_cookie_banner_modular

@pytest.mark.asyncio
async def test_analysis_flow():
    result = await analyze_cookie_banner_modular(
        url="https://www.example.com",
        user_id="test_user"
    )
    
    assert result is not None
    assert result.url == "https://www.example.com"
    assert len(result.stages) > 0
\`\`\`

### 3. API Test

\`\`\`python
import pytest
from fastapi.testclient import TestClient
from api import app

client = TestClient(app)

def test_analyze_endpoint():
    response = client.post(
        "/api/v1/analyze",
        json={"url": "https://www.example.com", "user_id": "test_user"}
    )
    
    assert response.status_code == 202
    data = response.json()
    assert "task_id" in data
    assert data["status"] == "pending"
\`\`\`

## Performance Optimization Examples

### 1. Caching LLM Responses

\`\`\`python
from functools import lru_cache
import hashlib

def cache_key(url: str, screenshot_hash: str) -> str:
    return hashlib.md5(f"{url}:{screenshot_hash}".encode()).hexdigest()

# Cache banner detection results
@lru_cache(maxsize=100)
async def cached_banner_detection(url: str, screenshot_path: Path):
    return await detect_banner_with_llm(screenshot_path)
\`\`\`

### 2. Parallel Processing

\`\`\`python
import asyncio

async def parallel_analyses(urls: List[str]):
    # Process multiple URLs in parallel
    tasks = [
        analyze_cookie_banner_modular(url)
        for url in urls
    ]
    
    results = await asyncio.gather(*tasks)
    return results
\`\`\`

## References

- [FastAPI Examples](https://fastapi.tiangolo.com/tutorial/)
- [Python Async Patterns](https://docs.python.org/3/library/asyncio.html)
- [Pydantic Models](https://docs.pydantic.dev/)
`;

// Auto-generated from continuous-improvement.md
export const CookieConsentBackendDocumentation01CodeDocumentationContinuousImprovement = `# Continuous Learning / Improvement Loops

## Overview

Feedback loops and model refinement.

## Content

This section needs to be completed with detailed information.

## References

- [Main Documentation](../index.html)
- [Related Documentation](#)

---

*This documentation file is a template and needs to be completed with detailed content.*
`;

// Auto-generated from core-design-philosophy.md
export const CookieConsentBackendDocumentation01CodeDocumentationCoreDesignPhilosophy = `# Core Code Approach & Design Philosophy

## Overview

The Cookie Consent Analyzer backend follows a modular, async-first architecture designed for scalability, maintainability, and extensibility. This document outlines the core design principles, architectural patterns, and code organization strategies.

## Design Principles

### 1. Modular Architecture

The codebase is organized into distinct, reusable modules:

\`\`\`
Cookie-Consent-Backend-main/
├── app.py                 # Main analysis orchestrator
├── api.py                 # FastAPI REST API layer
└── utility/               # Reusable utility modules
    ├── data_models.py     # Pydantic models
    ├── prompts.py         # LLM prompt templates
    ├── sessions.py        # Browser session management
    ├── llm_analysis.py    # LLM analysis functions
    ├── firebase_storage.py # Firebase operations
    ├── gcs_storage.py     # GCS operations
    ├── cleanup.py         # Resource cleanup
    └── cookie_storage_capture.py # Cookie capture utilities
\`\`\`

**Benefits**:
- Clear separation of concerns
- Easy to test individual components
- Reusable across different contexts
- Simplified maintenance

### 2. Async/Await First

All I/O operations use async/await patterns:

\`\`\`python
async def analyze_cookie_banner_modular(url: str) -> AnalysisResult:
    # Async operations throughout
    initial_cookies, screenshot = await execute_action_and_capture(...)
    banner_detection = await detect_banner_with_llm(screenshot)
    # ...
\`\`\`

**Benefits**:
- Non-blocking I/O operations
- Better resource utilization
- Scalable concurrent operations
- Improved performance

### 3. Type Safety with Pydantic

All data structures use Pydantic models for validation:

\`\`\`python
from pydantic import BaseModel, Field

class BannerDetectionResult(BaseModel):
    banner_detected: bool = Field(description="Whether banner was detected")
    accept_button: bool = Field(description="Accept button present")
    # ...
\`\`\`

**Benefits**:
- Automatic validation
- Type hints for IDE support
- Self-documenting code
- Prevents runtime errors

### 4. Structured LLM Outputs

LLM responses are validated using Pydantic models:

\`\`\`python
from langchain_openai import ChatOpenAI

llm = ChatOpenAI(model="gpt-4o-mini")
structured_llm = llm.with_structured_output(BannerDetectionResult)
result = await structured_llm.ainvoke([message])
\`\`\`

**Benefits**:
- Guaranteed valid JSON responses
- Type-safe LLM interactions
- No manual JSON parsing
- Automatic error handling

## Architectural Patterns

### 1. Orchestrator Pattern

\`app.py\` acts as the main orchestrator, coordinating all analysis steps:

\`\`\`python
async def analyze_cookie_banner_modular(url: str) -> AnalysisResult:
    # Stage 0: Initial load
    initial_cookies, screenshot = await execute_action_and_capture(...)
    
    # Stage 1: Banner detection
    banner_detection = await detect_banner_with_llm(screenshot)
    
    # Stage 2-N: Dynamic actions based on detected buttons
    if banner_detection.accept_button:
        await execute_accept_action(...)
    # ...
    
    # Stage 6: Comprehensive LLM analysis
    legal_analysis = await perform_comprehensive_legal_compliance_analysis(...)
    
    return AnalysisResult(...)
\`\`\`

**Flow**:
1. Initial page load and cookie capture
2. Banner detection via LLM vision
3. Dynamic button actions (accept, reject, close, manage preferences)
4. Comprehensive LLM analysis (6 types)
5. Cloud storage upload
6. Result compilation

### 2. Strategy Pattern for Button Actions

Different strategies for different button combinations:

\`\`\`python
async def execute_manage_preferences_action(...):
    if banner_has_accept and not banner_has_reject:
        # Strategy 1: Reject via preferences
        task = create_manage_prefs_reject_task(...)
    elif banner_has_accept and banner_has_reject:
        # Strategy 2: Analyze preferences
        task = create_manage_prefs_analyze_task(...)
    elif not banner_has_accept and banner_has_reject:
        # Strategy 3: Accept via preferences
        task = create_manage_prefs_accept_all_task(...)
    else:
        # Strategy 4: Test both scenarios
        task = create_manage_prefs_full_test_task(...)
\`\`\`

### 3. Factory Pattern for Task Creation

Task templates are created via factory functions:

\`\`\`python
def create_accept_task(url: str, button_text: str) -> str:
    return f"""
    TASK: Accept cookie consent banner
    INSTRUCTIONS:
    1. Navigate to {url}
    2. Wait 10 seconds for banner to load
    3. Click Accept button: "{button_text}"
    ...
    """
\`\`\`

**Benefits**:
- Consistent task format
- Easy to modify task templates
- Reusable across different scenarios

### 4. Manager Pattern for Cloud Services

Cloud operations are abstracted through manager classes:

\`\`\`python
class FirebaseManager:
    def create_analysis_document(...) -> str:
        # Firebase operations
    
    def update_stage(...):
        # Update stage data

class GCSStorageManager:
    def upload_screenshot(...) -> str:
        # GCS upload operations
\`\`\`

**Benefits**:
- Single responsibility
- Easy to mock for testing
- Consistent error handling
- Centralized configuration

## Code Organization

### Utility Modules Pattern

All reusable functionality is in the \`utility/\` folder:

- **data_models.py**: Pydantic models for type safety
- **prompts.py**: Centralized LLM prompt templates
- **sessions.py**: Browser automation abstraction
- **llm_analysis.py**: LLM analysis functions
- **firebase_storage.py**: Database operations
- **gcs_storage.py**: File storage operations
- **cleanup.py**: Resource management
- **cookie_storage_capture.py**: Cookie capture utilities

### Separation of Concerns

1. **API Layer** (\`api.py\`): HTTP endpoints, request/response handling
2. **Business Logic** (\`app.py\`): Analysis orchestration
3. **Utilities** (\`utility/\`): Reusable functions and classes
4. **Data Models** (\`utility/data_models.py\`): Type definitions

## Data Flow

### Analysis Flow

\`\`\`
User Request
    ↓
API Endpoint (api.py)
    ↓
Background Task
    ↓
Analysis Orchestrator (app.py)
    ↓
┌─────────────────────────────────┐
│ Stage 0: Initial Load           │
│ - Browser automation            │
│ - Cookie capture                │
│ - Screenshot capture            │
└─────────────────────────────────┘
    ↓
┌─────────────────────────────────┐
│ Stage 1: Banner Detection       │
│ - LLM vision analysis           │
│ - Button detection              │
└─────────────────────────────────┘
    ↓
┌─────────────────────────────────┐
│ Stage 2-N: Button Actions       │
│ - Accept/Reject/Close/Manage    │
│ - Cookie capture per action     │
└─────────────────────────────────┘
    ↓
┌─────────────────────────────────┐
│ Stage 6: LLM Analysis           │
│ - Banner UI/UX                  │
│ - Cookie analysis (3 stages)    │
│ - Legal compliance              │
│ - Executive summary             │
└─────────────────────────────────┘
    ↓
Cloud Storage (Firebase + GCS)
    ↓
Result Return
\`\`\`

### Data Models Flow

\`\`\`
Pydantic Models (data_models.py)
    ↓
Structured LLM Output
    ↓
Validation & Type Safety
    ↓
Firebase Storage (firebase_storage.py)
    ↓
JSON Serialization
\`\`\`

## Error Handling Strategy

### Graceful Degradation

\`\`\`python
try:
    gcs_manager = get_gcs_manager()
    firebase_manager = get_firebase_manager(user_id=user_id)
    cloud_services_enabled = True
except Exception as e:
    print(f"⚠️ Cloud services initialization failed: {str(e)}")
    print("   Continuing with local storage only...\\n")
    cloud_services_enabled = False
    gcs_manager = None
    firebase_manager = None
\`\`\`

**Principle**: System continues to function even if optional services fail.

### Error Propagation

\`\`\`python
async def detect_banner_with_llm(screenshot_path: Path) -> BannerDetectionResult:
    try:
        # LLM analysis
        detection = await structured_llm.ainvoke([message])
        return detection
    except Exception as e:
        print(f"   ❌ Banner detection failed: {e}")
        # Return default safe value
        return BannerDetectionResult(
            banner_detected=False,
            # ... safe defaults
        )
\`\`\`

**Principle**: Errors are caught, logged, and safe defaults are returned.

## Browser Automation Strategy

### Storage State Pattern

Cookies are automatically captured via browser-use's \`storage_state\`:

\`\`\`python
browser = Browser(
    use_cloud=False,
    headless=True,
    storage_state=str(storage_file)  # Auto-saves cookies
)
\`\`\`

**Benefits**:
- Automatic cookie persistence
- No manual CDP (Chrome DevTools Protocol) calls
- Consistent cookie capture
- Easy to reload cookies in new sessions

### Fresh Browser Per Action

Each action creates a fresh browser instance:

\`\`\`python
async def execute_accept_action(...):
    # New browser instance
    cookies, screenshot = await execute_action_and_capture(...)
    # Browser automatically cleaned up
\`\`\`

**Benefits**:
- Isolation between actions
- No state leakage
- Reliable cookie capture
- Parallel execution possible

## LLM Integration Approach

### Structured Output Pattern

All LLM calls use structured outputs:

\`\`\`python
from utility.data_models import BannerDetectionResult

llm = ChatOpenAI(model="gpt-4o-mini")
structured_llm = llm.with_structured_output(BannerDetectionResult)

result = await structured_llm.ainvoke([message])
# result is guaranteed to be a valid BannerDetectionResult
\`\`\`

**Benefits**:
- Type safety
- No JSON parsing errors
- Automatic validation
- Self-documenting

### Prompt Template Pattern

Prompts are centralized in \`utility/prompts.py\`:

\`\`\`python
BANNER_DETECTION_PROMPT = """
Analyze this webpage screenshot carefully.
TASK: Detect and describe ONLY the COOKIE CONSENT BANNER
...
"""

def create_accept_task(url: str, button_text: str) -> str:
    return f"""
    TASK: Accept cookie consent banner
    INSTRUCTIONS:
    1. Navigate to {url}
    ...
    """
\`\`\`

**Benefits**:
- Easy to modify prompts
- Consistent formatting
- Version control for prompts
- A/B testing capability

## Cloud Storage Strategy

### Dual Storage Pattern

Results are stored in both Firebase and GCS:

- **Firebase**: Structured data, real-time updates, user-specific subcollections
- **GCS**: Files (screenshots, JSON reports), public URLs

\`\`\`python
# Upload screenshot to GCS
screenshot_url = gcs_manager.upload_screenshot(...)

# Update Firebase with URL
firebase_manager.update_stage(
    doc_id=firebase_doc_id,
    stage_data={"screenshot_url": screenshot_url, ...}
)
\`\`\`

**Benefits**:
- Best tool for each job
- Firebase for queries and real-time
- GCS for large files
- Public URLs for screenshots

### User-Specific Subcollections

Each user has their own analysis subcollection:

\`\`\`
report-user-ids/
  {user_id}/
    cookies-analysis-database/
      {analysis_doc_id}/
        - stages[]
        - analyses{}
        - statistics{}
\`\`\`

**Benefits**:
- Data isolation
- Easy user-specific queries
- Scalable architecture
- Privacy compliance

## Performance Optimization

### Async Operations

All I/O operations are async:

\`\`\`python
async def analyze_cookie_banner_modular(...):
    # Parallel operations where possible
    tasks = [
        upload_stage_to_cloud(...),
        perform_llm_analysis(...)
    ]
    results = await asyncio.gather(*tasks)
\`\`\`

### Resource Cleanup

Automatic cleanup before analysis:

\`\`\`python
# Cleanup old analysis folders
cleanup_before_analysis()

# Prevents disk space issues
# Removes folders older than 6 hours
# Limits total folder count
\`\`\`

### Incremental Updates

Firebase updates are incremental:

\`\`\`python
# Update only changed fields
firebase_manager.update_analysis(
    doc_id=firebase_doc_id,
    analysis_type="banner_ui_analysis",
    analysis_data=banner_ui_analysis
)
\`\`\`

## Testing Strategy

### Unit Testing

Each utility module can be tested independently:

\`\`\`python
# test_sessions.py
async def test_execute_action_and_capture():
    cookies, screenshot = await execute_action_and_capture(...)
    assert len(cookies) > 0
    assert screenshot.exists()
\`\`\`

### Integration Testing

API endpoints are tested end-to-end:

\`\`\`python
# test_api.py
def test_analyze_endpoint():
    response = client.post("/api/v1/analyze", json={"url": "..."})
    assert response.status_code == 202
    task_id = response.json()["task_id"]
    # Poll for completion
    # Verify results
\`\`\`

## Extensibility

### Adding New Analysis Types

1. Create Pydantic model in \`utility/data_models.py\`
2. Add analysis function in \`utility/llm_analysis.py\`
3. Integrate into orchestrator in \`app.py\`
4. Update Firebase schema if needed

### Adding New Button Actions

1. Create task template in \`utility/prompts.py\`
2. Add execution function in \`app.py\`
3. Integrate into orchestrator flow
4. Update data models if needed

### Adding New Cloud Services

1. Create manager class (e.g., \`utility/aws_storage.py\`)
2. Implement singleton pattern
3. Integrate into upload functions
4. Update error handling

## Best Practices

### Code Style
- Use type hints everywhere
- Follow PEP 8 style guide
- Use descriptive variable names
- Add docstrings to all functions

### Error Handling
- Always catch and log errors
- Return safe defaults
- Don't fail silently
- Provide meaningful error messages

### Performance
- Use async/await for I/O
- Implement connection pooling
- Cache where appropriate
- Clean up resources promptly

### Security
- Never commit credentials
- Use environment variables
- Validate all inputs
- Sanitize user data

## Future Enhancements

### Planned Improvements
1. **Caching Layer**: Redis for LLM response caching
2. **Queue System**: Celery/RQ for task queuing
3. **Monitoring**: Prometheus metrics integration
4. **Logging**: Structured logging with ELK stack
5. **Testing**: Increase test coverage to 80%+

### Architecture Evolution
- Move to microservices if scale requires
- Implement event-driven architecture
- Add GraphQL API layer
- Support multiple LLM providers

## References

- [FastAPI Best Practices](https://fastapi.tiangolo.com/tutorial/)
- [Python Async Patterns](https://docs.python.org/3/library/asyncio.html)
- [Pydantic Documentation](https://docs.pydantic.dev/)
- [LangChain Structured Outputs](https://python.langchain.com/docs/modules/model_io/output_parsers/structured)

`;

// Auto-generated from database-schema.md
export const CookieConsentBackendDocumentation01CodeDocumentationDatabaseSchema = `# Database Management & Schema Notes

## Overview

The Cookie Consent Analyzer uses Firebase Firestore for structured data storage and Google Cloud Storage (GCS) for file storage. This document describes the database schemas, organization, and management strategies.

## Firebase Firestore Structure

### Collection Hierarchy

\`\`\`
report-user-ids/ (collection)
  └── {user_id}/ (document)
      ├── email: string
      ├── password: string (hashed in production)
      ├── first_name?: string
      ├── last_name?: string
      ├── created_at: Timestamp
      ├── updated_at: Timestamp
      ├── total_analyses: number
      └── cookies-analysis-database/ (subcollection)
          └── {analysis_doc_id}/ (document)
              ├── url: string
              ├── domain: string
              ├── timestamp: string
              ├── status: string
              ├── banner_detection: object
              ├── stages: array
              ├── analyses: object
              ├── statistics: object
              └── complete_analysis: object
\`\`\`

## User Document Schema

**Collection**: \`report-user-ids\`  
**Document ID**: \`{user_id}\` (auto-generated or custom)

\`\`\`typescript
{
  email: string;                 // User email (unique identifier)
  password: string;              // Hashed password (use bcrypt in production)
  first_name?: string;           // Optional first name
  last_name?: string;            // Optional last name
  created_at: Timestamp;         // Account creation time
  updated_at: Timestamp;         // Last update time
  total_analyses: number;        // Count of analyses performed
}
\`\`\`

**Example**:
\`\`\`json
{
  "email": "user@example.com",
  "password": "$2b$10$...",  // bcrypt hash
  "first_name": "John",
  "last_name": "Doe",
  "created_at": "2025-01-01T00:00:00Z",
  "updated_at": "2025-11-26T22:00:00Z",
  "total_analyses": 42
}
\`\`\`

## Analysis Document Schema

**Collection**: \`report-user-ids/{user_id}/cookies-analysis-database\`  
**Document ID**: Auto-generated by Firestore

### Core Fields

\`\`\`typescript
{
  // Metadata
  url: string;                   // Full website URL
  domain: string;                // Extracted domain (e.g., "example.com")
  timestamp: string;             // Analysis timestamp (YYYY-MM-DD-HHMMSS)
  analysis_version: "2.0";       // Analysis version
  status: "in_progress" | "completed" | "failed";
  created_at: Timestamp;
  updated_at: Timestamp;
  completed_at?: Timestamp;
  
  // Banner Detection
  banner_detection: {
    banner_detected: boolean;
    accept_button: boolean;
    accept_button_text: string;
    reject_button: boolean;
    reject_button_text: string;
    close_button: boolean;
    close_button_text: string;
    manage_preferences_button: boolean;
    manage_preferences_button_text: string;
    banner_position: "top" | "bottom" | "center" | "side" | "corner" | "unknown";
    banner_text_snippet: string;  // First 150 characters
    confidence: "high" | "medium" | "low";
  };
  
  // Analysis Stages
  stages: [
    {
      stage_number: number;
      stage_name: string;         // "initial_load", "after_accept", etc.
      description: string;
      cookies_count: number;
      screenshot_url: string;     // GCS public URL
      timestamp: string;
      cookies: [                  // Full cookie array
        {
          name: string;
          value: string;
          domain: string;
          path: string;
          expires: number;        // -1 for session cookies
          httpOnly: boolean;
          secure: boolean;
          sameSite: string;
        }
      ];
    }
  ];
  
  // LLM Analyses
  analyses: {
    banner_ui_analysis: object | null;
    cookie_analysis_before_consent: object | null;
    cookie_analysis_after_accept: object | null;
    cookie_analysis_after_reject: object | null;
    legal_compliance_analysis: object | null;
    overall_summary: object | null;
  };
  
  // Statistics
  statistics: {
    total_stages: number;
    total_cookies_initial: number;
    total_cookies_after_accept: number;
    total_cookies_after_reject: number;
    analyses_completed: number;
    banner_detected: boolean;
    has_accept_button: boolean;
    has_reject_button: boolean;
    has_manage_preferences: boolean;
  };
  
  // Complete Analysis (full JSON)
  complete_analysis: object;    // Complete analysis result
  
  // Final Summary
  final_summary: {
    total_stages: number;
    banner_detected: boolean;
    compliance_status: string;
    overall_score: string | number;
  };
}
\`\`\`

## Google Cloud Storage Organization

### Bucket Structure

\`\`\`
gs://cookie-analyzer-bucket/
  cookie-analysis/
    {url_slug}/                  # e.g., "example-com"
      {timestamp}/               # e.g., "2025-11-26-220000"
        screenshots/
          00_after_initial.png
          01_after_accept.png
          02_after_reject.png
          ...
        reports/
          complete_analysis.json
          llm_analyses/
            01_banner_ui_analysis.json
            02_cookies_before_consent.json
            ...
\`\`\`

### File Naming Conventions

- **Screenshots**: \`{stage_number:02d}_after_{action_name}.png\`
- **Reports**: \`complete_analysis.json\`
- **LLM Analyses**: \`{number:02d}_{analysis_name}.json\`

### Public URLs

All files are made publicly accessible with URLs like:
\`\`\`
https://storage.googleapis.com/bucket-name/cookie-analysis/example-com/2025-11-26-220000/screenshots/00_after_initial.png
\`\`\`

## Excel Database Schema

### Cookies_Database.xlsx

Used for batch processing and company data management.

**Structure**:
- **Sheet**: Company list
- **Header Row**: Row 3 (data starts at row 4)
- **Columns**:
  - Company Name
  - Website URL
  - Additional metadata columns

**Usage**: Loaded by \`testing/check_status.py\` for batch analysis tracking.

## Data Models (Pydantic)

### BannerDetectionResult

Defined in \`utility/data_models.py\`:

\`\`\`python
class BannerDetectionResult(BaseModel):
    banner_detected: bool
    accept_button: bool
    accept_button_text: str = ""
    reject_button: bool
    reject_button_text: str = ""
    close_button: bool
    close_button_text: str = ""
    manage_preferences_button: bool
    manage_preferences_button_text: str = ""
    banner_position: Literal["top", "bottom", "center", "side", "corner", "unknown"]
    banner_text_snippet: str = ""
    confidence: Literal["high", "medium", "low"]
\`\`\`

### CookieStage (Dataclass)

Defined in \`app.py\`:

\`\`\`python
@dataclass
class CookieStage:
    stage_name: str
    stage_number: int
    description: str
    cookies_count: int
    cookies: List[Dict[str, Any]]
    screenshot_path: Optional[str]
    timestamp: str
\`\`\`

## Database Operations

### Creating Analysis Document

\`\`\`python
firebase_manager = get_firebase_manager(user_id="user123")
doc_id = firebase_manager.create_analysis_document(
    url="https://www.example.com",
    timestamp="2025-11-26-220000",
    banner_detection=banner_detection.dict()
)
\`\`\`

### Updating Stage

\`\`\`python
firebase_manager.update_stage(
    doc_id=doc_id,
    stage_data={
        "stage_name": "after_accept",
        "stage_number": 1,
        "description": "After clicking Accept button",
        "cookies_count": 45,
        "screenshot_url": "https://storage.googleapis.com/.../screenshot.png",
        "timestamp": "2025-11-26-220015",
        "cookies": [...]  # Full cookie array
    },
    stage_number=1
)
\`\`\`

### Updating Analysis

\`\`\`python
firebase_manager.update_analysis(
    doc_id=doc_id,
    analysis_type="legal_compliance_analysis",
    analysis_data={
        "overall_compliance": "non_compliant",
        "compliance_score": 4.5,
        "critical_violations": [...]
    }
)
\`\`\`

### Querying Analyses

\`\`\`python
# Get analyses for a domain
analyses = firebase_manager.get_analysis_by_domain(
    domain="example.com",
    limit=10,
    user_id="user123"
)

# Get specific analysis
analysis = firebase_manager.get_analysis_by_id(
    doc_id="abc123",
    user_id="user123"
)
\`\`\`

## Data Relationships

### User → Analyses

One-to-many relationship:
- One user can have many analyses
- Each analysis belongs to one user
- Stored in user-specific subcollection

### Analysis → Stages

One-to-many relationship:
- One analysis has multiple stages
- Stages stored as array in analysis document
- Ordered by stage_number

### Analysis → Screenshots

One-to-many relationship:
- One analysis has multiple screenshots
- Screenshots stored in GCS
- URLs stored in Firebase stages

## Indexing Strategy

### Firestore Indexes

Required composite indexes:

1. **Domain queries**:
   - Collection: \`cookies-analysis-database\`
   - Fields: \`domain\` (Ascending), \`created_at\` (Descending)

2. **Status queries** (if needed):
   - Collection: \`cookies-analysis-database\`
   - Fields: \`status\` (Ascending), \`created_at\` (Descending)

### GCS Organization

- Organized by URL slug and timestamp
- Easy to find all analyses for a domain
- Automatic cleanup of old folders

## Data Retention

### Local Storage

- **Retention**: 6 hours (configurable)
- **Cleanup**: Automatic before each analysis
- **Location**: \`cookie_analysis/\` directory

### Cloud Storage

- **GCS**: Permanent (or set lifecycle policy)
- **Firebase**: Permanent (or set retention rules)

### Lifecycle Policies

Recommended GCS lifecycle policy:

\`\`\`json
{
  "lifecycle": {
    "rule": [
      {
        "action": {"type": "Delete"},
        "condition": {
          "age": 90,  // Delete after 90 days
          "matchesPrefix": ["cookie-analysis/"]
        }
      }
    ]
  }
}
\`\`\`

## Backup Strategy

### Firebase

- Automatic backups via Firestore export
- Export to GCS bucket
- Schedule: Daily exports recommended

### GCS

- Versioning enabled (optional)
- Cross-region replication (optional)
- Regular exports to cold storage

## Security Rules

### Firestore Security Rules

\`\`\`javascript
rules_version = '2';
service cloud.firestore {
  match /databases/{database}/documents {
    // User documents
    match /report-user-ids/{userId} {
      allow read, write: if request.auth != null && request.auth.uid == userId;
      
      // Analysis subcollection
      match /cookies-analysis-database/{analysisId} {
        allow read, write: if request.auth != null && request.auth.uid == userId;
      }
    }
  }
}
\`\`\`

### GCS Access Control

- Public read access for screenshots
- Service account write access
- IAM roles for bucket management

## Performance Considerations

### Firestore

- **Reads**: Optimize with indexes
- **Writes**: Batch writes where possible
- **Queries**: Use composite indexes
- **Limits**: 1MB per document

### GCS

- **Uploads**: Use resumable uploads for large files
- **Downloads**: Use signed URLs for private files
- **Caching**: Set cache headers appropriately

## Migration Notes

### Version 1.0 → 2.0

- Added \`analysis_version\` field
- Changed stage structure
- Added \`complete_analysis\` field
- Enhanced statistics

### Future Versions

- Consider splitting large documents
- Add versioning to schema
- Implement data archiving

## References

- [Firebase Firestore Documentation](https://firebase.google.com/docs/firestore)
- [Google Cloud Storage Documentation](https://cloud.google.com/storage/docs)
- [Data Models](../01-code-documentation/code-examples.html)

`;

// Auto-generated from deployment-environment.md
export const CookieConsentBackendDocumentation01CodeDocumentationDeploymentEnvironment = `# Rendering / Deployment Environment

## Overview

The Cookie Consent Analyzer backend is designed to run in multiple deployment environments, from local development to cloud production. This document covers environment setup, deployment configurations, and integration requirements.

## Python Environment

### Version Requirements

- **Minimum**: Python 3.8
- **Recommended**: Python 3.10 or higher
- **Tested**: Python 3.10, 3.11, 3.12

### Virtual Environment Setup

\`\`\`bash
# Create virtual environment
python -m venv venv

# Activate (Windows)
venv\\Scripts\\activate

# Activate (Linux/Mac)
source venv/bin/activate

# Install dependencies
pip install -r requirements.txt
\`\`\`

### Environment Variables

Create a \`.env\` file in the project root:

\`\`\`env
# Required: OpenAI API Key
OPENAI_API_KEY=sk-your-openai-api-key-here

# Required: Google Cloud Credentials
GOOGLE_APPLICATION_CREDENTIALS=path/to/service-account-key.json
GCS_BUCKET_NAME=your-gcs-bucket-name

# Required: Firebase Credentials
FIREBASE_CREDENTIALS_PATH=path/to/firebase-service-account.json

# Optional: Cleanup Configuration
CLEANUP_MAX_AGE_HOURS=6
CLEANUP_MAX_FOLDERS=50
CLEANUP_MAX_SIZE_MB=2000

# Optional: API Configuration
API_HOST=0.0.0.0
API_PORT=8080
\`\`\`

## Local Development

### Prerequisites

1. Python 3.8+
2. Node.js 14+ (for frontend development)
3. Google Cloud SDK (for GCS operations)
4. Firebase CLI (optional, for Firebase operations)

### Running the API Server

\`\`\`bash
# Development mode with auto-reload
python api.py

# Or with uvicorn directly
uvicorn api:app --host 0.0.0.0 --port 8080 --reload
\`\`\`

### Running Analysis Directly

\`\`\`bash
# Command-line analysis
python app.py https://www.example.com
\`\`\`

## Docker Deployment

### Dockerfile

\`\`\`dockerfile
FROM python:3.10-slim

WORKDIR /app

# Install system dependencies
RUN apt-get update && apt-get install -y \\
    gcc \\
    g++ \\
    && rm -rf /var/lib/apt/lists/*

# Copy requirements
COPY requirements.txt .

# Install Python dependencies
RUN pip install --no-cache-dir -r requirements.txt

# Copy application code
COPY . .

# Expose port
EXPOSE 8080

# Run API server
CMD ["uvicorn", "api:app", "--host", "0.0.0.0", "--port", "8080"]
\`\`\`

### Docker Compose

\`\`\`yaml
version: '3.8'

services:
  api:
    build: .
    ports:
      - "8080:8080"
    environment:
      - OPENAI_API_KEY=\${OPENAI_API_KEY}
      - GOOGLE_APPLICATION_CREDENTIALS=/app/credentials/gcs-key.json
      - GCS_BUCKET_NAME=\${GCS_BUCKET_NAME}
      - FIREBASE_CREDENTIALS_PATH=/app/credentials/firebase-key.json
    volumes:
      - ./credentials:/app/credentials:ro
      - ./cookie_analysis:/app/cookie_analysis
    restart: unless-stopped
\`\`\`

### Building and Running

\`\`\`bash
# Build image
docker build -t cookie-analyzer-backend .

# Run container
docker run -p 8080:8080 \\
  -e OPENAI_API_KEY=your-key \\
  -v $(pwd)/credentials:/app/credentials:ro \\
  cookie-analyzer-backend
\`\`\`

## Google Cloud Run Deployment

### Cloud Run Configuration

\`\`\`yaml
# cloud-run.yaml
apiVersion: serving.knative.dev/v1
kind: Service
metadata:
  name: cookie-analyzer-api
spec:
  template:
    metadata:
      annotations:
        autoscaling.knative.dev/maxScale: "10"
        run.googleapis.com/memory: "2Gi"
        run.googleapis.com/cpu: "2"
        run.googleapis.com/timeout: "900s"
    spec:
      containers:
      - image: gcr.io/PROJECT_ID/cookie-analyzer-backend
        env:
        - name: OPENAI_API_KEY
          valueFrom:
            secretKeyRef:
              name: openai-key
              key: api-key
        - name: GOOGLE_APPLICATION_CREDENTIALS
          value: /app/credentials/gcs-key.json
        resources:
          limits:
            cpu: "2"
            memory: "2Gi"
\`\`\`

### Deployment Steps

\`\`\`bash
# Build and push to GCR
gcloud builds submit --tag gcr.io/PROJECT_ID/cookie-analyzer-backend

# Deploy to Cloud Run
gcloud run deploy cookie-analyzer-api \\
  --image gcr.io/PROJECT_ID/cookie-analyzer-backend \\
  --platform managed \\
  --region us-central1 \\
  --memory 2Gi \\
  --cpu 2 \\
  --timeout 900 \\
  --max-instances 10 \\
  --set-env-vars GCS_BUCKET_NAME=your-bucket-name
\`\`\`

## Google Cloud Storage Setup

### Bucket Configuration

\`\`\`bash
# Create bucket
gsutil mb -p PROJECT_ID -c STANDARD -l us-central1 gs://cookie-analyzer-bucket

# Set CORS for public access
gsutil cors set cors.json gs://cookie-analyzer-bucket

# Set public access (for screenshots)
gsutil iam ch allUsers:objectViewer gs://cookie-analyzer-bucket
\`\`\`

### CORS Configuration (cors.json)

\`\`\`json
[
  {
    "origin": ["*"],
    "method": ["GET", "HEAD"],
    "responseHeader": ["Content-Type"],
    "maxAgeSeconds": 3600
  }
]
\`\`\`

## Firebase Setup

### Firestore Database Structure

\`\`\`
report-user-ids/ (collection)
  {user_id}/ (document)
    - email: string
    - password: string (hashed in production)
    - created_at: timestamp
    - total_analyses: number
    cookies-analysis-database/ (subcollection)
      {analysis_doc_id}/ (document)
        - url: string
        - domain: string
        - timestamp: string
        - status: string
        - banner_detection: object
        - stages: array
        - analyses: object
        - statistics: object
\`\`\`

### Security Rules

\`\`\`javascript
rules_version = '2';
service cloud.firestore {
  match /databases/{database}/documents {
    match /report-user-ids/{userId} {
      allow read, write: if request.auth != null && request.auth.uid == userId;
      
      match /cookies-analysis-database/{analysisId} {
        allow read, write: if request.auth != null && request.auth.uid == userId;
      }
    }
  }
}
\`\`\`

## Environment-Specific Configurations

### Development

\`\`\`env
API_HOST=localhost
API_PORT=8080
LOG_LEVEL=DEBUG
CLEANUP_MAX_AGE_HOURS=1
CLEANUP_MAX_FOLDERS=10
\`\`\`

### Staging

\`\`\`env
API_HOST=0.0.0.0
API_PORT=8080
LOG_LEVEL=INFO
CLEANUP_MAX_AGE_HOURS=6
CLEANUP_MAX_FOLDERS=50
\`\`\`

### Production

\`\`\`env
API_HOST=0.0.0.0
API_PORT=8080
LOG_LEVEL=WARNING
CLEANUP_MAX_AGE_HOURS=6
CLEANUP_MAX_FOLDERS=50
CLEANUP_MAX_SIZE_MB=2000
\`\`\`

## Node.js/React Build Process (Frontend)

### Frontend Build

\`\`\`bash
cd Cookie-Consent-Frontend-main
npm install
npm run build
\`\`\`

### Serving Frontend

\`\`\`bash
# Development
npm start

# Production build
npm run build
# Serve from build/ directory
\`\`\`

### Integration with Backend

The frontend communicates with the backend API:

\`\`\`typescript
// src/config/api.ts
export const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';
\`\`\`

## CI/CD Pipeline

### GitHub Actions Example

\`\`\`yaml
name: Deploy to Cloud Run

on:
  push:
    branches: [main]

jobs:
  deploy:
    runs-on: ubuntu-latest
    steps:
      - uses: actions/checkout@v2
      
      - name: Setup Python
        uses: actions/setup-python@v2
        with:
          python-version: '3.10'
      
      - name: Install dependencies
        run: |
          pip install -r requirements.txt
      
      - name: Run tests
        run: |
          pytest testing/
      
      - name: Build Docker image
        run: |
          docker build -t gcr.io/\${{ secrets.GCP_PROJECT }}/cookie-analyzer-backend .
      
      - name: Push to GCR
        run: |
          echo "\${{ secrets.GCP_SA_KEY }}" | docker login -u _json_key --password-stdin https://gcr.io
          docker push gcr.io/\${{ secrets.GCP_PROJECT }}/cookie-analyzer-backend
      
      - name: Deploy to Cloud Run
        run: |
          gcloud run deploy cookie-analyzer-api \\
            --image gcr.io/\${{ secrets.GCP_PROJECT }}/cookie-analyzer-backend \\
            --region us-central1
\`\`\`

## Monitoring and Logging

### Cloud Logging

Logs are automatically sent to Google Cloud Logging when running on Cloud Run.

### Local Logging

\`\`\`python
import logging

logging.basicConfig(
    level=logging.INFO,
    format='%(asctime)s - %(name)s - %(levelname)s - %(message)s'
)
\`\`\`

### Health Check Endpoint

\`\`\`bash
curl http://localhost:8080/api/v1/health
\`\`\`

## Performance Considerations

### Resource Requirements

- **CPU**: 2 cores minimum for concurrent analyses
- **Memory**: 2GB minimum (browser automation is memory-intensive)
- **Disk**: 10GB for temporary analysis files
- **Network**: High bandwidth for LLM API calls

### Scaling

- **Horizontal**: Cloud Run auto-scales based on traffic
- **Vertical**: Increase memory/CPU for faster processing
- **Queue**: Consider Celery/RQ for task queuing at scale

## Troubleshooting

### Common Issues

1. **OpenAI API Rate Limits**
   - Implement exponential backoff
   - Use request queuing
   - Consider API key rotation

2. **Firebase Connection Timeouts**
   - Check network connectivity
   - Verify credentials
   - Increase timeout values

3. **GCS Upload Failures**
   - Verify bucket permissions
   - Check service account roles
   - Ensure bucket exists

4. **Memory Issues**
   - Reduce concurrent analyses
   - Increase container memory
   - Implement cleanup more frequently

## Security Best Practices

1. **Never commit credentials**
   - Use environment variables
   - Use secret management (GCP Secret Manager)
   - Rotate keys regularly

2. **Network Security**
   - Use HTTPS in production
   - Implement CORS properly
   - Use API keys or OAuth2

3. **Data Privacy**
   - Encrypt sensitive data
   - Use user-specific subcollections
   - Implement data retention policies

## References

- [Google Cloud Run Documentation](https://cloud.google.com/run/docs)
- [Firebase Admin SDK Setup](https://firebase.google.com/docs/admin/setup)
- [Google Cloud Storage Setup](https://cloud.google.com/storage/docs)
- [FastAPI Deployment](https://fastapi.tiangolo.com/deployment/)
`;

// Auto-generated from developer-experience.md
export const CookieConsentBackendDocumentation01CodeDocumentationDeveloperExperience = `# Developer Experience

## Overview

Contribution guidelines, extensibility notes, automated doc generation.

## Content

This section needs to be completed with detailed information.

## References

- [Main Documentation](../index.html)
- [Related Documentation](#)

---

*This documentation file is a template and needs to be completed with detailed content.*
`;

// Auto-generated from libraries-dependencies.md
export const CookieConsentBackendDocumentation01CodeDocumentationLibrariesDependencies = `# Libraries and Dependencies

## Overview

The Cookie Consent Analyzer backend relies on 88 dependencies organized into several categories. This document provides a comprehensive overview of each dependency, its purpose, version requirements, and usage within the application.

## Dependency Categories

### Browser Automation & AI Agent Dependencies

#### browser-use (0.9.5)
- **Purpose**: Browser automation framework with AI agent capabilities
- **Usage**: Core browser automation engine that uses LLM to interact with web pages
- **Key Features**:
  - Automatic cookie capture via \`storage_state\` parameter
  - Vision-based page understanding
  - Task-based automation
- **Location**: Used in \`utility/sessions.py\` and \`app.py\`

#### playwright
- **Purpose**: Browser automation library (used as fallback)
- **Usage**: Underlying browser control for browser-use
- **Key Features**: Cross-browser automation, headless browsing

#### selenium
- **Purpose**: Alternative browser automation library
- **Usage**: Legacy support and fallback scenarios

#### webdriver-manager
- **Purpose**: Automatic management of browser drivers
- **Usage**: Handles ChromeDriver, GeckoDriver, etc. automatically

#### undetected-chromedriver
- **Purpose**: ChromeDriver that bypasses bot detection
- **Usage**: Prevents websites from detecting automated browsing

### LLM/AI Libraries

#### openai
- **Purpose**: OpenAI API client for GPT-4 Vision and chat completions
- **Usage**: Required by browser-use (version >=1.99.2)
- **Key Features**: 
  - GPT-4 Vision for banner detection
  - Chat completions for analysis

#### langchain
- **Purpose**: Framework for structured LLM outputs and message handling
- **Usage**: 
  - Structured output generation in \`utility/llm_analysis.py\`
  - Type-safe LLM responses using Pydantic models
- **Key Features**:
  - \`with_structured_output()\` for validated responses
  - Message chain management

#### langchain-openai
- **Purpose**: LangChain integration with OpenAI
- **Usage**: ChatOpenAI wrapper for structured outputs

#### langchain-core
- **Purpose**: Core LangChain functionality
- **Usage**: Base classes and utilities

#### langchain-community (>=0.0.20)
- **Purpose**: Community integrations for LangChain
- **Usage**: Additional LLM providers and tools

### Data Validation & Models

#### pydantic (>=2.0.0)
- **Purpose**: Data validation using Python type annotations
- **Usage**: 
  - Request/response models in \`api.py\`
  - Data models in \`utility/data_models.py\`
  - Structured LLM output validation
- **Key Features**: Type safety, automatic validation, JSON serialization

### Environment & Configuration

#### python-dotenv
- **Purpose**: Environment variable management
- **Usage**: Loads \`.env\` file for secure credential storage
- **Required Variables**:
  - \`OPENAI_API_KEY\`
  - \`GOOGLE_APPLICATION_CREDENTIALS\`
  - \`GCS_BUCKET_NAME\`
  - \`FIREBASE_CREDENTIALS_PATH\`

### Image Processing

#### Pillow
- **Purpose**: Image processing library
- **Usage**: 
  - GIF frame extraction
  - Screenshot manipulation
  - Image format conversion

#### numpy
- **Purpose**: Numerical computing
- **Usage**: Image analysis and numerical operations

#### opencv-python
- **Purpose**: Computer vision library
- **Usage**: Advanced image processing and analysis

#### pytesseract
- **Purpose**: OCR (Optical Character Recognition)
- **Usage**: Text extraction from images

### Web Frameworks

#### fastapi (>=0.109.0)
- **Purpose**: Modern, fast web framework for building APIs
- **Usage**: REST API in \`api.py\`
- **Key Features**:
  - Async request handling
  - Automatic OpenAPI documentation
  - Type validation with Pydantic
- **Endpoints**: \`/api/v1/analyze\`, \`/api/v1/status/{task_id}\`, etc.

#### uvicorn[standard] (>=0.27.0)
- **Purpose**: ASGI server for FastAPI
- **Usage**: Production server for API
- **Features**: 
  - Async support
  - WebSocket support
  - Production-ready

#### flask (==3.0.2)
- **Purpose**: Alternative web framework
- **Usage**: Legacy support or alternative API implementation

#### flask-cors (==4.0.0)
- **Purpose**: CORS support for Flask
- **Usage**: Cross-origin resource sharing

#### Werkzeug (==3.0.1)
- **Purpose**: WSGI utility library
- **Usage**: Required by Flask

### Cloud Services

#### google-cloud-storage
- **Purpose**: Google Cloud Storage client
- **Usage**: Screenshot and report storage in \`utility/gcs_storage.py\`
- **Features**:
  - File uploads
  - Public URL generation
  - Bucket management

#### firebase-admin
- **Purpose**: Firebase Admin SDK
- **Usage**: Firestore database operations in \`utility/firebase_storage.py\`
- **Features**:
  - User-specific subcollections
  - Real-time data storage
  - Document management

### Data Processing

#### pandas
- **Purpose**: Data analysis and manipulation
- **Usage**: 
  - Excel file processing (\`Cookies_Database.xlsx\`)
  - Data aggregation and analysis
  - Status checking in \`testing/check_status.py\`

#### openpyxl
- **Purpose**: Excel file reading/writing
- **Usage**: Processing \`Cookies_Database.xlsx\` file

### Web Scraping & Utilities

#### beautifulsoup4
- **Purpose**: HTML parsing
- **Usage**: Web content extraction

#### requests (>=2.31.0)
- **Purpose**: HTTP library
- **Usage**: API calls, webhook notifications

#### urllib3
- **Purpose**: HTTP client library
- **Usage**: Low-level HTTP operations

#### duckduckgo-search (>=4.1.1)
- **Purpose**: Search functionality
- **Usage**: Web search capabilities

### Document Conversion

#### pypandoc
- **Purpose**: Document format conversion
- **Usage**: Report generation in various formats

### System Utilities

#### psutil
- **Purpose**: System and process utilities
- **Usage**: 
  - Resource monitoring
  - Performance profiling
  - System information

### Streamlit (Optional)

#### streamlit
- **Purpose**: Web interface framework
- **Usage**: Interactive UI for analysis (optional component)

## Version Requirements

### Python Version
- **Minimum**: Python 3.8+
- **Recommended**: Python 3.10+

### Critical Version Constraints
- \`browser-use==0.9.5\` (exact version required)
- \`openai>=1.99.2\` (required by browser-use)
- \`pydantic>=2.0.0\` (required for LangChain structured outputs)
- \`langchain-community>=0.0.20\` (minimum version)

## Installation

### Standard Installation
\`\`\`bash
pip install -r requirements.txt
\`\`\`

### Development Installation
\`\`\`bash
pip install -r requirements.txt
pip install pytest pytest-asyncio black flake8 mypy
\`\`\`

## Environment Setup

Create a \`.env\` file with the following variables:

\`\`\`env
# OpenAI API Key (required)
OPENAI_API_KEY=your_openai_api_key_here

# Google Cloud Credentials (required for cloud storage)
GOOGLE_APPLICATION_CREDENTIALS=path/to/service-account-key.json
GCS_BUCKET_NAME=your-gcs-bucket-name

# Firebase Credentials (required for database)
FIREBASE_CREDENTIALS_PATH=path/to/firebase-service-account.json

# Optional: Cleanup Configuration
CLEANUP_MAX_AGE_HOURS=6
CLEANUP_MAX_FOLDERS=50
CLEANUP_MAX_SIZE_MB=2000
\`\`\`

## Dependency Usage Patterns

### Browser Automation Pattern
\`\`\`python
from browser_use import Agent, Browser, ChatOpenAI

llm = ChatOpenAI(model="gpt-4o-mini")
browser = Browser(use_cloud=False, headless=True, storage_state="state.json")
agent = Agent(task="...", llm=llm, browser=browser)
history = await agent.run(max_steps=10)
\`\`\`

### LLM Structured Output Pattern
\`\`\`python
from langchain_openai import ChatOpenAI
from langchain_core.messages import HumanMessage

llm = ChatOpenAI(model="gpt-4o-mini")
structured_llm = llm.with_structured_output(BannerDetectionResult)
result = await structured_llm.ainvoke([message])
\`\`\`

### FastAPI Endpoint Pattern
\`\`\`python
from fastapi import FastAPI, BackgroundTasks
from pydantic import BaseModel

app = FastAPI()

@app.post("/api/v1/analyze")
async def analyze(request: AnalysisRequest, background_tasks: BackgroundTasks):
    # Process in background
    background_tasks.add_task(process_analysis, request.url)
    return {"task_id": "..."}
\`\`\`

## Known Issues & Workarounds

### browser-use Storage State
- **Issue**: Cookies may not be captured immediately
- **Workaround**: Add \`await asyncio.sleep(5)\` after agent execution

### OpenAI Rate Limits
- **Issue**: API rate limits may cause failures
- **Workaround**: Implement exponential backoff and retry logic

### Firebase Connection
- **Issue**: Connection timeouts in some environments
- **Workaround**: Use connection pooling and retry mechanisms

## Security Considerations

### API Keys
- Never commit \`.env\` files to version control
- Use environment variables in production
- Rotate keys regularly

### Credentials
- Store service account keys securely
- Use least-privilege IAM roles
- Enable audit logging

## Performance Considerations

### Heavy Dependencies
- \`browser-use\`: High memory usage (browser instances)
- \`opencv-python\`: Large binary size
- \`firebase-admin\`: Network latency for database operations

### Optimization Tips
1. Use headless browsers to reduce memory
2. Implement connection pooling for Firebase
3. Cache LLM responses where possible
4. Use async/await for concurrent operations

## Maintenance

### Regular Updates
- Check for security updates monthly
- Update browser drivers regularly
- Monitor dependency vulnerabilities

### Testing After Updates
- Run full test suite
- Verify browser automation still works
- Check LLM integration
- Validate API endpoints

## Standard Library Dependencies

The following are part of Python standard library (no installation needed):
- \`asyncio\` - Async/await support
- \`base64\` - Image encoding
- \`json\` - JSON parsing
- \`time\` - Timestamp generation
- \`re\` - Regular expressions
- \`hashlib\` - Hashing functions
- \`os\` - Operating system interface
- \`pathlib\` - Path manipulation
- \`typing\` - Type hints
- \`dataclasses\` - Data classes
- \`enum\` - Enumerations
- \`uuid\` - UUID generation
- \`datetime\` - Date/time handling

## Dependency Tree

\`\`\`
Cookie Consent Analyzer
├── Browser Automation
│   ├── browser-use (0.9.5)
│   │   ├── openai (>=1.99.2)
│   │   └── playwright
│   ├── selenium
│   └── webdriver-manager
├── LLM/AI
│   ├── langchain
│   │   ├── langchain-openai
│   │   ├── langchain-core
│   │   └── langchain-community
│   └── openai
├── Web Framework
│   ├── fastapi
│   │   └── uvicorn
│   └── flask
├── Cloud Services
│   ├── google-cloud-storage
│   └── firebase-admin
├── Data Processing
│   ├── pandas
│   └── openpyxl
└── Utilities
    ├── python-dotenv
    ├── Pillow
    └── requests
\`\`\`

## References

- [browser-use Documentation](https://github.com/browser-use/browser-use)
- [LangChain Documentation](https://python.langchain.com/)
- [FastAPI Documentation](https://fastapi.tiangolo.com/)
- [Firebase Admin SDK](https://firebase.google.com/docs/admin/setup)
- [Google Cloud Storage](https://cloud.google.com/storage/docs)

`;

// Auto-generated from model-integrations.md
export const CookieConsentBackendDocumentation01CodeDocumentationModelIntegrations = `# Model Integrations

## Overview

LLM integrations, external APIs, and AutoML.

## Content

This section needs to be completed with detailed information.

## References

- [Main Documentation](../index.html)
- [Related Documentation](#)

---

*This documentation file is a template and needs to be completed with detailed content.*
`;

// Auto-generated from operational-readiness.md
export const CookieConsentBackendDocumentation01CodeDocumentationOperationalReadiness = `# Operational Readiness

## Overview

Configuration, containerization, monitoring/logging, rollback procedures.

## Content

This section needs to be completed with detailed information.

## References

- [Main Documentation](../index.html)
- [Related Documentation](#)

---

*This documentation file is a template and needs to be completed with detailed content.*
`;

// Auto-generated from performance-benchmarking.md
export const CookieConsentBackendDocumentation01CodeDocumentationPerformanceBenchmarking = `# Performance Benchmarking & Profiling

## Overview

Performance analysis, profiling, and optimization strategies.

## Content

This section needs to be completed with detailed information.

## References

- [Main Documentation](../index.html)
- [Related Documentation](#)

---

*This documentation file is a template and needs to be completed with detailed content.*
`;

// Auto-generated from security-compliance.md
export const CookieConsentBackendDocumentation01CodeDocumentationSecurityCompliance = `# Security & Compliance

## Overview

The Cookie Consent Analyzer implements comprehensive security measures and compliance features to protect user data and ensure GDPR/ePrivacy compliance. This document covers threat modeling, encryption, role-based access, and compliance hooks.

## Threat Modeling

### Identified Threats

#### 1. Unauthorized Access

**Threat**: Unauthorized users accessing analysis data

**Mitigation**:
- User-specific Firebase subcollections
- Firebase security rules
- API authentication (to be implemented)
- Input validation

#### 2. Data Leakage

**Threat**: Sensitive data exposed in logs or errors

**Mitigation**:
- Sanitize error messages
- No credentials in logs
- Secure credential storage (.env)
- Encrypted data in transit (HTTPS)

#### 3. API Abuse

**Threat**: Rate limiting bypass, DDoS attacks

**Mitigation**:
- Rate limiting (to be implemented)
- Request validation
- Input sanitization
- Monitoring and alerting

#### 4. Injection Attacks

**Threat**: SQL injection, command injection

**Mitigation**:
- Parameterized queries (Firestore)
- Input validation (Pydantic)
- No direct command execution
- Sanitized user inputs

## Encryption

### Data in Transit

**HTTPS/TLS**:
- All API communications use HTTPS
- TLS 1.2+ required
- Certificate validation
- HSTS headers (recommended)

**Implementation**:
\`\`\`python
# FastAPI with HTTPS
uvicorn.run(
    "api:app",
    host="0.0.0.0",
    port=443,
    ssl_keyfile="key.pem",
    ssl_certfile="cert.pem"
)
\`\`\`

### Data at Rest

**Firebase Firestore**:
- Encrypted by default
- Google-managed encryption keys
- Optional customer-managed keys

**Google Cloud Storage**:
- Encrypted by default
- Server-side encryption
- Optional customer-supplied keys

**Local Storage**:
- Analysis files stored locally (temporary)
- Cleaned up after 6 hours
- No sensitive data in filenames

## Role-Based Access Control

### Current Implementation

**User-Based Access**:
- Each user has their own subcollection
- Users can only access their own analyses
- No cross-user data access

**Firebase Security Rules**:
\`\`\`javascript
rules_version = '2';
service cloud.firestore {
  match /databases/{database}/documents {
    match /report-user-ids/{userId} {
      // Only authenticated user can access their own data
      allow read, write: if request.auth != null && request.auth.uid == userId;
      
      match /cookies-analysis-database/{analysisId} {
        allow read, write: if request.auth != null && request.auth.uid == userId;
      }
    }
  }
}
\`\`\`

### Future Enhancements

**Admin Role**:
- Admin users can access all analyses
- Separate admin collection
- Admin API endpoints

**Role Hierarchy**:
- Viewer: Read-only access
- Analyst: Create and view analyses
- Admin: Full access

## GDPR Compliance Hooks

### Data Minimization

**Principle**: Only collect necessary data

**Implementation**:
- User ID only (no PII in analysis)
- Optional user metadata
- No tracking of user behavior
- Analysis data is user-specific

### Right to Access

**Principle**: Users can access their data

**Implementation**:
\`\`\`python
# Get all analyses for user
analyses = firebase_manager.get_analysis_by_domain(
    domain="example.com",
    user_id=user_id
)
\`\`\`

### Right to Erasure

**Principle**: Users can delete their data

**Implementation**:
\`\`\`python
def delete_user_analyses(user_id: str):
    """Delete all analyses for a user."""
    collection = firebase_manager._get_analysis_collection(user_id)
    
    # Delete all documents in subcollection
    docs = collection.stream()
    for doc in docs:
        doc.reference.delete()
    
    # Delete from GCS
    gcs_manager.delete_user_files(user_id)
\`\`\`

### Data Portability

**Principle**: Users can export their data

**Implementation**:
\`\`\`python
def export_user_data(user_id: str) -> dict:
    """Export all user data in portable format."""
    analyses = firebase_manager.get_all_user_analyses(user_id)
    
    return {
        "user_id": user_id,
        "export_date": datetime.now().isoformat(),
        "analyses": analyses,
        "format_version": "1.0"
    }
\`\`\`

### Consent Management

**Principle**: Explicit consent for data processing

**Implementation**:
- User must provide user_id (consent)
- Optional metadata for additional consent
- Clear purpose of data collection
- Easy withdrawal of consent

## HIPAA Considerations

### Healthcare Data

**If processing healthcare websites**:
- No PHI (Protected Health Information) stored
- Analysis data is website metadata only
- No patient data involved
- Compliance with HIPAA if handling PHI

### Recommendations

1. **Separate instances** for healthcare clients
2. **Enhanced encryption** for sensitive data
3. **Audit logging** for all access
4. **BAAs** (Business Associate Agreements) if required

## Security Best Practices

### Credential Management

**Environment Variables**:
\`\`\`env
# Never commit to version control
OPENAI_API_KEY=sk-...
GOOGLE_APPLICATION_CREDENTIALS=path/to/key.json
FIREBASE_CREDENTIALS_PATH=path/to/firebase-key.json
\`\`\`

**Secret Management** (Production):
- Use Google Secret Manager
- Rotate keys regularly
- Use least-privilege IAM roles
- Enable audit logging

### Input Validation

**Pydantic Models**:
\`\`\`python
class AnalysisRequest(BaseModel):
    url: str = Field(..., regex=r'^https?://')
    user_id: str = Field(..., min_length=1, max_length=100)
    
    @validator('url')
    def validate_url(cls, v):
        # Additional validation
        if not v.startswith(('http://', 'https://')):
            raise ValueError('Invalid URL format')
        return v
\`\`\`

### Output Sanitization

**Error Messages**:
\`\`\`python
# Don't expose internal details
try:
    result = await operation()
except Exception as e:
    # Log full error internally
    logger.error(f"Operation failed: {e}", exc_info=True)
    
    # Return sanitized error to user
    return {"error": "Operation failed. Please try again."}
\`\`\`

### Logging Security

**Safe Logging**:
\`\`\`python
# Don't log sensitive data
logger.info(f"Analysis started for user: {user_id}")  # OK
logger.info(f"API key: {api_key}")  # BAD - Never do this

# Sanitize URLs in logs
logger.info(f"Analyzing: {sanitize_url(url)}")
\`\`\`

## Compliance Scoring

### GDPR Compliance Score

**Factors**:
1. Banner presence and visibility
2. Accept/Reject button availability
3. Granular preferences
4. Cookie behavior (before/after consent)
5. Dark patterns detection
6. Transparency of information

**Scoring**:
- 0-3: Non-compliant (critical violations)
- 4-6: Partially compliant (needs improvement)
- 7-8: Mostly compliant (minor issues)
- 9-10: Fully compliant

### Compliance Violations

**Critical Violations**:
- Non-essential cookies set before consent
- No reject option
- Pre-checked consent boxes
- Misleading information

**High Priority**:
- Hard to find reject button
- No granular preferences
- Dark patterns

**Medium Priority**:
- Unclear information
- Poor UX
- Accessibility issues

## Audit Trail

### Analysis History

**Firebase Audit**:
- All analyses stored with timestamps
- User ID associated with each analysis
- Status tracking (in_progress, completed, failed)
- Version tracking (analysis_version field)

### Access Logging

**Recommended** (to be implemented):
- Log all API access
- Track user actions
- Monitor suspicious activity
- Alert on anomalies

## Security Checklist

### Development

- [ ] No credentials in code
- [ ] Environment variables used
- [ ] Input validation implemented
- [ ] Error messages sanitized
- [ ] Logging doesn't expose secrets

### Production

- [ ] HTTPS enabled
- [ ] Firewall configured
- [ ] Rate limiting enabled
- [ ] Monitoring active
- [ ] Backup strategy in place
- [ ] Incident response plan
- [ ] Security updates applied

## Incident Response

### Security Incident Procedure

1. **Identify**: Detect security issue
2. **Contain**: Isolate affected systems
3. **Assess**: Determine scope and impact
4. **Remediate**: Fix the issue
5. **Notify**: Inform affected users (if required)
6. **Document**: Record incident and resolution
7. **Review**: Post-incident review

## Compliance Reporting

### GDPR Compliance Report

\`\`\`python
def generate_compliance_report(analysis_result: AnalysisResult) -> dict:
    """Generate GDPR compliance report."""
    legal_analysis = analysis_result.legal_compliance_analysis
    
    return {
        "website": analysis_result.url,
        "analysis_date": analysis_result.timestamp,
        "gdpr_compliance": {
            "status": legal_analysis.get("overall_compliance"),
            "score": legal_analysis.get("compliance_score"),
            "critical_violations": legal_analysis.get("critical_violations", []),
            "recommendations": legal_analysis.get("recommendations", [])
        },
        "cookie_analysis": {
            "before_consent": analysis_result.cookie_analyses.get("before_consent"),
            "after_reject": analysis_result.cookie_analyses.get("after_reject")
        },
        "banner_analysis": {
            "detected": analysis_result.banner_detection.banner_detected,
            "has_reject": analysis_result.banner_detection.reject_button,
            "has_granular_prefs": analysis_result.banner_detection.manage_preferences_button
        }
    }
\`\`\`

## References

- [GDPR Official Text](https://gdpr.eu/)
- [Firebase Security Rules](https://firebase.google.com/docs/firestore/security/get-started)
- [Google Cloud Security](https://cloud.google.com/security)
- [OWASP Top 10](https://owasp.org/www-project-top-ten/)
`;

// Auto-generated from testing-qa.md
export const CookieConsentBackendDocumentation01CodeDocumentationTestingQa = `# Testing & QA

## Overview

Unit/integration tests, regression, failure mode injection, coverage metrics.

## Content

This section needs to be completed with detailed information.

## References

- [Main Documentation](../index.html)
- [Related Documentation](#)

---

*This documentation file is a template and needs to be completed with detailed content.*
`;

// Auto-generated from versioning.md
export const CookieConsentBackendDocumentation01CodeDocumentationVersioning = `# Versioning & Change Management

## Overview

Git strategy, release notes, backward compatibility.

## Content

This section needs to be completed with detailed information.

## References

- [Main Documentation](../index.html)
- [Related Documentation](#)

---

*This documentation file is a template and needs to be completed with detailed content.*
`;

// Auto-generated from compliance-audit.md
export const CookieConsentBackendDocumentation02FunctionalFlowsComplianceAudit = `# Compliance & Audit Trail Integration

## Overview

Audit logging and compliance tracking.

## Content

This section needs to be completed with detailed information.

## References

- [Main Documentation](../index.html)
- [Related Documentation](#)

---

*This documentation file is a template and needs to be completed with detailed content.*
`;

// Auto-generated from end-to-end-workflow.md
export const CookieConsentBackendDocumentation02FunctionalFlowsEndToEndWorkflow = `# End-to-End Workflow

## Overview

This document describes the complete end-to-end workflow of the Cookie Consent Analyzer, from user request to final report generation.

## Workflow Diagram

\`\`\`mermaid
graph TD
    A[User Submits URL] --> B[API Receives Request]
    B --> C[Create Task ID]
    C --> D[Start Background Task]
    D --> E[Stage 0: Initial Page Load]
    E --> F[Capture Initial Cookies]
    F --> G[Take Screenshot]
    G --> H[Stage 1: Banner Detection]
    H --> I{Banner Detected?}
    I -->|Yes| J[Detect Buttons]
    I -->|No| Z[Limited Analysis]
    J --> K{Button Actions}
    K -->|Accept| L[Stage 2: Accept Action]
    K -->|Reject| M[Stage 3: Reject Action]
    K -->|Close| N[Stage 4: Close Action]
    K -->|Manage| O[Stage 5: Manage Preferences]
    L --> P[Capture Cookies After Accept]
    M --> Q[Capture Cookies After Reject]
    N --> R[Capture Cookies After Close]
    O --> S[Analyze Preferences Dialog]
    P --> T[Stage 6: LLM Analysis]
    Q --> T
    R --> T
    S --> T
    Z --> T
    T --> U[Upload to Cloud Storage]
    U --> V[Update Firebase]
    V --> W[Generate Report]
    W --> X[Return Results]
\`\`\`

## Detailed Workflow Steps

### Step 1: Request Initiation

**User Action**: Submits URL via API or frontend

**API Endpoint**: \`POST /api/v1/analyze\`

**Request Body**:
\`\`\`json
{
  "url": "https://www.example.com",
  "user_id": "user123",
  "callback_url": "https://webhook.com/callback",
  "metadata": {}
}
\`\`\`

**System Action**:
1. Validate URL format
2. Generate unique task ID (UUID)
3. Create task entry in TaskStore
4. Return task ID immediately (202 Accepted)
5. Start background processing

**Response**:
\`\`\`json
{
  "task_id": "550e8400-e29b-41d4-a716-446655440000",
  "status": "pending",
  "message": "Analysis task created successfully",
  "created_at": "2025-11-26T22:00:00Z",
  "estimated_duration": 120
}
\`\`\`

### Step 2: Background Task Initialization

**System Action**:
1. Update task status to "in_progress"
2. Initialize output directory structure
3. Initialize LLM (ChatOpenAI with gpt-4o-mini)
4. Initialize cloud services (GCS, Firebase)
5. Perform cleanup of old analysis folders

**Status Update**:
- Progress: 5%
- Stage: "Initializing analysis environment"

### Step 3: Stage 0 - Initial Page Load

**System Action**:
1. Create browser instance with storage_state
2. Navigate to URL
3. Wait 10 seconds for page and banner to load
4. Capture initial cookies from storage_state
5. Take screenshot of page
6. Create initial CookieStage object

**Data Captured**:
- Initial cookies (before any interaction)
- Screenshot of page with banner
- Timestamp

**Status Update**:
- Progress: 10-15%
- Stage: "Loading initial page"

**Cloud Upload**:
- Screenshot uploaded to GCS
- Stage data saved to Firebase

### Step 4: Stage 1 - Banner Detection

**System Action**:
1. Encode screenshot to base64
2. Send to GPT-4o-mini Vision with structured output
3. Receive BannerDetectionResult with:
   - Banner detected (boolean)
   - Button availability (accept, reject, close, manage)
   - Banner position
   - Button text snippets
   - Confidence level

**LLM Prompt**: Analyzes screenshot to detect cookie banner and buttons

**Status Update**:
- Progress: 20%
- Stage: "Detecting cookie banner with AI"

**Firebase Update**:
- Create analysis document with banner detection results
- Store Firebase document ID for future updates

### Step 5: Dynamic Button Actions (Stages 2-N)

**System Decision**: Based on detected buttons, execute appropriate actions

#### 5a. Accept Button Action

**Condition**: \`banner_detection.accept_button == True\`

**System Action**:
1. Create fresh browser instance
2. Navigate to URL
3. Wait for banner to load
4. Click Accept button (using detected button text)
5. Wait 5 seconds for cookies to be set
6. Capture cookies after accept
7. Take screenshot
8. Create CookieStage for "after_accept"

**Status Update**:
- Progress: 30-35%
- Stage: "Executing Accept button action"

#### 5b. Reject Button Action

**Condition**: \`banner_detection.reject_button == True\`

**System Action**:
1. Create fresh browser instance
2. Navigate to URL
3. Wait for banner to load
4. Click Reject button
5. Wait 5 seconds
6. Capture cookies after reject
7. Take screenshot
8. Create CookieStage for "after_reject"

**Status Update**:
- Progress: 40-45%
- Stage: "Executing Reject button action"

#### 5c. Close Button Action

**Condition**: \`banner_detection.close_button == True AND accept_button == False\`

**System Action**:
1. Create fresh browser instance
2. Navigate to URL
3. Click Close button
4. Capture cookies after close
5. Take screenshot
6. Create CookieStage for "after_close"

**Status Update**:
- Progress: 48%
- Stage: "Executing Close button action"

#### 5d. Manage Preferences Action

**Condition**: \`banner_detection.manage_preferences_button == True\`

**Four Different Strategies Based on Button Availability**:

**Strategy 1**: Accept present, Reject NOT present
- Open preferences and reject all cookies

**Strategy 2**: Both Accept AND Reject present
- Open preferences and analyze available options (don't click)

**Strategy 3**: Accept NOT present, Reject present
- Open preferences and accept all cookies

**Strategy 4**: Neither Accept NOR Reject present
- Test both reject and accept scenarios via preferences

**System Action**:
1. Create browser instance
2. Navigate to URL
3. Click Manage Preferences button
4. Wait for dialog to load
5. Execute strategy-specific actions
6. Capture cookies
7. Take screenshot
8. Create CookieStage(s)

**Status Update**:
- Progress: 50-55%
- Stage: "Executing Manage Preferences action"

### Step 6: Comprehensive LLM Analysis (6 Types)

**System Action**: Perform 6 different LLM analyses

#### Analysis 1: Banner UI/UX Analysis

**Input**: Initial screenshot, banner detection results

**Output**: 
- Overall score (excellent/good/fair/poor)
- Visibility, clarity, choice freedom scores (1-10)
- Color scheme analysis
- Dark patterns detected
- Accessibility issues
- Improvement suggestions

**Status Update**:
- Progress: 60%
- Stage: "Banner UI/UX Analysis"

#### Analysis 2: Cookie Data Analysis - Before Consent

**Input**: Initial cookies (Stage 0)

**Output**:
- Cookie-by-cookie analysis
- Risk levels (high/medium/low)
- Tracking assessment
- Personal data assessment
- GDPR compliance issues
- Recommendations

**Status Update**:
- Progress: 68%
- Stage: "Cookie Data Analysis - Before Consent"

#### Analysis 3: Cookie Data Analysis - After Accept

**Input**: Cookies after accept action

**Output**: Same structure as Analysis 2, for after-accept state

**Status Update**:
- Progress: 74%
- Stage: "Cookie Data Analysis - After Accept"

#### Analysis 4: Cookie Data Analysis - After Reject

**Input**: Cookies after reject action

**Output**: Same structure as Analysis 2, for after-reject state

**Status Update**:
- Progress: 80%
- Stage: "Cookie Data Analysis - After Reject"

#### Analysis 5: Comprehensive Legal Compliance Analysis

**Input**: All stages, banner detection, UI analysis

**Output**:
- Overall compliance status
- Compliance score (0-10)
- Critical violations list
- GDPR compliance assessment
- ePrivacy compliance assessment
- Risk assessment
- Remediation recommendations

**Status Update**:
- Progress: 86%
- Stage: "Legal Compliance Analysis (GDPR/ePrivacy)"

#### Analysis 6: Overall Executive Summary

**Input**: All previous analyses

**Output**:
- Executive summary text
- Compliance status overview
- Risk level
- Critical issues summary
- Estimated remediation effort
- Key recommendations

**Status Update**:
- Progress: 92%
- Stage: "Overall Executive Summary"

### Step 7: Cloud Storage & Database Updates

**System Action**:
1. Upload all screenshots to GCS
2. Upload complete JSON report to GCS
3. Update Firebase with all analysis results
4. Update statistics in Firebase
5. Mark analysis as complete

**Status Update**:
- Progress: 95-97%
- Stage: "Uploading final results to cloud storage"

### Step 8: Result Compilation

**System Action**:
1. Create AnalysisResult object with all data
2. Save comprehensive JSON report locally
3. Save individual analysis files
4. Generate human-readable summary
5. Update task status to "completed"

**Status Update**:
- Progress: 100%
- Stage: "Analysis completed successfully"

### Step 9: Result Retrieval

**User Action**: Polls status endpoint or retrieves results

**API Endpoint**: \`GET /api/v1/results/{task_id}\`

**Response**:
\`\`\`json
{
  "task_id": "550e8400-e29b-41d4-a716-446655440000",
  "url": "https://www.example.com",
  "status": "completed",
  "summary": {
    "banner_detected": true,
    "total_stages": 5,
    "compliance_status": "non_compliant",
    "compliance_score": 4.5,
    "critical_issues_count": 3
  },
  "firebase_doc_id": "abc123",
  "gcs_report_url": "https://storage.googleapis.com/.../report.json",
  "created_at": "2025-11-26T22:00:00Z",
  "completed_at": "2025-11-26T22:02:15Z"
}
\`\`\`

## Workflow Variations

### Variation 1: No Banner Detected

If no banner is detected:
- Skip button actions (Stages 2-N)
- Still perform cookie analysis on initial state
- Flag as potential non-compliance
- Limited LLM analysis

### Variation 2: Webhook Notification

If callback_url provided:
- Send POST request to webhook when complete
- Include task_id, status, and summary
- Retry on failure

### Variation 3: Error Handling

If error occurs:
- Update task status to "failed"
- Store error message
- Clean up resources
- Return error in results

## Timing Estimates

| Stage | Estimated Time | Notes |
|-------|---------------|-------|
| Initialization | 5-10s | Cloud service setup |
| Page Load | 10-15s | Wait for banner |
| Banner Detection | 5-10s | LLM vision call |
| Button Actions | 15-30s each | Browser automation |
| LLM Analyses | 10-20s each | 6 analyses total |
| Cloud Upload | 5-10s | Screenshots + reports |
| **Total** | **90-180s** | 1.5-3 minutes |

## Data Flow

\`\`\`
User Request
    ↓
API Layer (api.py)
    ↓
Task Store (in-memory)
    ↓
Background Task
    ↓
Analysis Orchestrator (app.py)
    ↓
┌─────────────────────────────┐
│ Browser Automation          │
│ - Initial load              │
│ - Button actions            │
│ - Cookie capture            │
└─────────────────────────────┘
    ↓
┌─────────────────────────────┐
│ LLM Analysis                │
│ - Banner detection          │
│ - Cookie analysis           │
│ - Compliance analysis       │
└─────────────────────────────┘
    ↓
┌─────────────────────────────┐
│ Cloud Storage               │
│ - GCS (screenshots, reports)│
│ - Firebase (structured data)│
└─────────────────────────────┘
    ↓
Result Return
\`\`\`

## Status Progression

\`\`\`
pending → in_progress → completed
                          ↓
                       failed (if error)
\`\`\`

Progress percentages:
- 0-5%: Initialization
- 5-15%: Initial page load
- 15-20%: Banner detection
- 20-55%: Button actions (varies by buttons)
- 55-95%: LLM analyses
- 95-100%: Cloud upload and completion

## References

- [API Documentation](../04-api-documentation/endpoints.html)
- [Architecture Overview](../05-architecture/system-overview.html)
- [Error Handling](./error-handling.html)
`;

// Auto-generated from error-handling.md
export const CookieConsentBackendDocumentation02FunctionalFlowsErrorHandling = `# Error Handling & Recovery Paths

## Overview

The Cookie Consent Analyzer implements comprehensive error handling strategies to ensure system resilience and provide meaningful feedback to users. This document describes error handling patterns, recovery mechanisms, and user-facing error messages.

## Error Categories

### 1. Network Errors

**Types**:
- Connection timeouts
- DNS resolution failures
- SSL/TLS errors
- Network unreachable

**Handling**:
\`\`\`python
try:
    response = await fetch_url(url)
except asyncio.TimeoutError:
    print("⚠️ Connection timeout")
    return {"error": "Connection timeout", "retry": True}
except requests.exceptions.ConnectionError:
    print("⚠️ Network error")
    return {"error": "Network error", "retry": True}
\`\`\`

**Recovery**:
- Automatic retry with exponential backoff
- Fallback to local-only analysis
- User notification with retry option

### 2. LLM API Errors

**Types**:
- Rate limit exceeded
- Invalid API key
- Service unavailable
- Response parsing errors

**Handling**:
\`\`\`python
try:
    result = await structured_llm.ainvoke([message])
except openai.RateLimitError:
    print("⚠️ Rate limit exceeded, waiting...")
    await asyncio.sleep(60)  # Wait 1 minute
    # Retry
except openai.AuthenticationError:
    print("❌ Invalid API key")
    return BannerDetectionResult(banner_detected=False, ...)  # Safe default
\`\`\`

**Recovery**:
- Exponential backoff for rate limits
- Safe default values
- Graceful degradation
- Error logging

### 3. Browser Automation Errors

**Types**:
- Page load failures
- Element not found
- Timeout errors
- Browser crash

**Handling**:
\`\`\`python
try:
    cookies, screenshot = await execute_action_and_capture(...)
except Exception as e:
    print(f"⚠️ Browser automation error: {e}")
    # Return empty results, continue with other stages
    return [], None
\`\`\`

**Recovery**:
- Skip failed action, continue with others
- Use fallback browser (Selenium if Playwright fails)
- Retry with increased timeout
- Log error for debugging

### 4. Cloud Service Errors

**Types**:
- Firebase connection timeout
- GCS upload failure
- Authentication errors
- Permission denied

**Handling**:
\`\`\`python
try:
    gcs_manager = get_gcs_manager()
    firebase_manager = get_firebase_manager()
except Exception as e:
    print(f"⚠️ Cloud services failed: {e}")
    print("   Continuing with local storage only...")
    cloud_services_enabled = False
    # Continue analysis without cloud services
\`\`\`

**Recovery**:
- Continue with local storage
- Retry cloud operations in background
- Queue failed uploads for later
- User notification

### 5. Data Validation Errors

**Types**:
- Invalid URL format
- Missing required fields
- Type mismatches
- Schema validation failures

**Handling**:
\`\`\`python
from pydantic import ValidationError

try:
    request = AnalysisRequest(**data)
except ValidationError as e:
    return {
        "detail": e.errors(),
        "status_code": 422
    }
\`\`\`

**Recovery**:
- Return validation errors to user
- Suggest corrections
- Log validation failures

## Error Handling Patterns

### Pattern 1: Graceful Degradation

System continues to function even if optional services fail:

\`\`\`python
# Try cloud services, but don't fail if they're unavailable
try:
    screenshot_url = gcs_manager.upload_screenshot(...)
except Exception:
    screenshot_url = None  # Continue without cloud upload
    print("⚠️ Screenshot upload failed, continuing locally")
\`\`\`

### Pattern 2: Safe Defaults

Return safe default values when operations fail:

\`\`\`python
async def detect_banner_with_llm(screenshot_path: Path) -> BannerDetectionResult:
    try:
        result = await structured_llm.ainvoke([message])
        return result
    except Exception as e:
        # Return safe default
        return BannerDetectionResult(
            banner_detected=False,
            accept_button=False,
            reject_button=False,
            # ... all False/empty defaults
        )
\`\`\`

### Pattern 3: Retry with Backoff

Automatic retry for transient failures:

\`\`\`python
from tenacity import retry, stop_after_attempt, wait_exponential

@retry(
    stop=stop_after_attempt(3),
    wait=wait_exponential(multiplier=1, min=4, max=10)
)
async def upload_with_retry(file_path: Path):
    return gcs_manager.upload_file(file_path, ...)
\`\`\`

### Pattern 4: Error Propagation

Propagate errors with context:

\`\`\`python
try:
    result = await analyze_cookie_banner_modular(url)
except Exception as e:
    error_message = f"Analysis failed for {url}: {str(e)}"
    logger.error(error_message, exc_info=True)
    raise AnalysisError(error_message) from e
\`\`\`

## User-Facing Error Messages

### API Error Responses

**400 Bad Request**:
\`\`\`json
{
  "detail": "Invalid URL format. Please provide a valid HTTP/HTTPS URL."
}
\`\`\`

**404 Not Found**:
\`\`\`json
{
  "detail": "Task 550e8400-e29b-41d4-a716-446655440000 not found"
}
\`\`\`

**422 Unprocessable Entity**:
\`\`\`json
{
  "detail": [
    {
      "loc": ["body", "url"],
      "msg": "field required",
      "type": "value_error.missing"
    }
  ]
}
\`\`\`

**500 Internal Server Error**:
\`\`\`json
{
  "detail": "Internal server error. Please try again later."
}
\`\`\`

### Analysis Error Messages

**Banner Detection Failed**:
\`\`\`
⚠️ Banner detection failed. Analysis will continue with limited functionality.
\`\`\`

**LLM Analysis Failed**:
\`\`\`
⚠️ LLM analysis encountered an error. Some analyses may be incomplete.
\`\`\`

**Cloud Upload Failed**:
\`\`\`
⚠️ Cloud storage upload failed. Results saved locally only.
\`\`\`

## Recovery Mechanisms

### 1. Automatic Retry

For transient errors:

\`\`\`python
MAX_RETRIES = 3
RETRY_DELAY = 5  # seconds

for attempt in range(MAX_RETRIES):
    try:
        result = await operation()
        break
    except TransientError as e:
        if attempt < MAX_RETRIES - 1:
            await asyncio.sleep(RETRY_DELAY * (attempt + 1))
            continue
        raise
\`\`\`

### 2. Fallback Strategies

Use alternative methods when primary fails:

\`\`\`python
# Try browser-use first, fallback to Selenium
try:
    cookies = await browser_use_capture(...)
except Exception:
    cookies = await selenium_capture(...)
\`\`\`

### 3. Partial Results

Return partial results when some operations fail:

\`\`\`python
result = AnalysisResult(
    url=url,
    banner_detection=banner_detection,  # Success
    stages=stages,  # Some stages may be missing
    legal_compliance_analysis=None,  # Failed
    # ... other fields
)
\`\`\`

### 4. Error Queuing

Queue failed operations for later retry:

\`\`\`python
failed_uploads = []

try:
    upload_to_gcs(...)
except Exception as e:
    failed_uploads.append((file_path, e))
    # Retry later in background
\`\`\`

## Error Logging

### Structured Logging

\`\`\`python
import logging

logger = logging.getLogger(__name__)

try:
    result = await operation()
except Exception as e:
    logger.error(
        "Operation failed",
        extra={
            "operation": "banner_detection",
            "url": url,
            "error": str(e),
            "error_type": type(e).__name__
        },
        exc_info=True
    )
\`\`\`

### Error Tracking

Track error rates and types:

\`\`\`python
error_metrics = {
    "llm_errors": 0,
    "browser_errors": 0,
    "cloud_errors": 0
}

try:
    await operation()
except LLMError:
    error_metrics["llm_errors"] += 1
except BrowserError:
    error_metrics["browser_errors"] += 1
\`\`\`

## Testing Error Scenarios

### Unit Tests

\`\`\`python
@pytest.mark.asyncio
async def test_llm_error_handling():
    # Mock LLM to raise error
    with patch('utility.llm_analysis.structured_llm') as mock_llm:
        mock_llm.ainvoke.side_effect = Exception("API Error")
        
        result = await detect_banner_with_llm(screenshot_path)
        
        # Should return safe default
        assert result.banner_detected is False
\`\`\`

### Integration Tests

\`\`\`python
@pytest.mark.asyncio
async def test_cloud_service_failure():
    # Disable cloud services
    with patch('utility.gcs_storage.get_gcs_manager', return_value=None):
        result = await analyze_cookie_banner_modular(url)
        
        # Should complete without cloud services
        assert result is not None
        assert result.firebase_doc_id is None
\`\`\`

## Best Practices

1. **Always catch specific exceptions**
2. **Provide meaningful error messages**
3. **Log errors with context**
4. **Return safe defaults when possible**
5. **Implement retry logic for transient errors**
6. **Notify users of errors appropriately**
7. **Monitor error rates and types**

## References

- [Error Codes Documentation](../04-api-documentation/error-codes.html)
- [API Endpoints](../04-api-documentation/endpoints.html)
- [Testing & QA](../01-code-documentation/testing-qa.html)
`;

// Auto-generated from interactive-flows.md
export const CookieConsentBackendDocumentation02FunctionalFlowsInteractiveFlows = `# Interactive Flows

## Overview

Dashboard and UI interaction flows.

## Content

This section needs to be completed with detailed information.

## References

- [Main Documentation](../index.html)
- [Related Documentation](#)

---

*This documentation file is a template and needs to be completed with detailed content.*
`;

// Auto-generated from process-states.md
export const CookieConsentBackendDocumentation02FunctionalFlowsProcessStates = `# Process States & Transitions

## Overview

State machine and process transitions.

## Content

This section needs to be completed with detailed information.

## References

- [Main Documentation](../index.html)
- [Related Documentation](#)

---

*This documentation file is a template and needs to be completed with detailed content.*
`;

// Auto-generated from role-based-access.md
export const CookieConsentBackendDocumentation02FunctionalFlowsRoleBasedAccess = `# Role-Based Access Flows

## Overview

User access and permission flows.

## Content

This section needs to be completed with detailed information.

## References

- [Main Documentation](../index.html)
- [Related Documentation](#)

---

*This documentation file is a template and needs to be completed with detailed content.*
`;

// Auto-generated from use-case-scenarios.md
export const CookieConsentBackendDocumentation02FunctionalFlowsUseCaseScenarios = `# Use Case Scenarios

## Overview

Legal tech use cases and scenarios.

## Content

This section needs to be completed with detailed information.

## References

- [Main Documentation](../index.html)
- [Related Documentation](#)

---

*This documentation file is a template and needs to be completed with detailed content.*
`;

// Auto-generated from accessibility.md
export const CookieConsentBackendDocumentation03UserManualAccessibility = `# Accessibility & Localization Notes

## Overview

WCAG compliance and localization.

## Content

This section needs to be completed with detailed information.

## References

- [Main Documentation](../index.html)
- [Related Documentation](#)

---

*This documentation file is a template and needs to be completed with detailed content.*
`;

// Auto-generated from feature-walkthroughs.md
export const CookieConsentBackendDocumentation03UserManualFeatureWalkthroughs = `# Feature Walkthroughs

## Overview

Detailed feature explanations and walkthroughs.

## Content

This section needs to be completed with detailed information.

## References

- [Main Documentation](../index.html)
- [Related Documentation](#)

---

*This documentation file is a template and needs to be completed with detailed content.*
`;

// Auto-generated from getting-started.md
export const CookieConsentBackendDocumentation03UserManualGettingStarted = `# Getting Started Guide

## Overview

This guide will help you get started with the Cookie Consent Analyzer. You'll learn how to install, configure, and run your first analysis.

## Prerequisites

### Required

- **Python 3.8+** (3.10+ recommended)
- **pip** (Python package manager)
- **OpenAI API Key** (for LLM analysis)
- **Google Cloud Account** (for GCS and Firebase)

### Optional

- **Docker** (for containerized deployment)
- **Node.js 14+** (for frontend development)

## Installation

### Step 1: Clone Repository

\`\`\`bash
git clone <repository-url>
cd Cookie-Consent-Backend-main
\`\`\`

### Step 2: Create Virtual Environment

\`\`\`bash
# Create virtual environment
python -m venv venv

# Activate (Windows)
venv\\Scripts\\activate

# Activate (Linux/Mac)
source venv/bin/activate
\`\`\`

### Step 3: Install Dependencies

\`\`\`bash
pip install -r requirements.txt
\`\`\`

This will install all 88 dependencies including:
- browser-use (browser automation)
- fastapi (web framework)
- langchain (LLM framework)
- firebase-admin (database)
- google-cloud-storage (file storage)

### Step 4: Configure Environment

Create a \`.env\` file in the project root:

\`\`\`env
# Required: OpenAI API Key
OPENAI_API_KEY=sk-your-openai-api-key-here

# Required: Google Cloud Credentials
GOOGLE_APPLICATION_CREDENTIALS=path/to/service-account-key.json
GCS_BUCKET_NAME=your-gcs-bucket-name

# Required: Firebase Credentials
FIREBASE_CREDENTIALS_PATH=path/to/firebase-service-account.json
\`\`\`

### Step 5: Set Up Google Cloud Services

#### Google Cloud Storage

1. Create a GCS bucket:
\`\`\`bash
gsutil mb -p PROJECT_ID -c STANDARD -l us-central1 gs://cookie-analyzer-bucket
\`\`\`

2. Set public access for screenshots:
\`\`\`bash
gsutil iam ch allUsers:objectViewer gs://cookie-analyzer-bucket
\`\`\`

#### Firebase Firestore

1. Create a Firebase project
2. Enable Firestore Database
3. Download service account key
4. Set security rules (see Architecture documentation)

## Quick Start

### Option 1: Command Line Analysis

\`\`\`bash
python app.py https://www.example.com
\`\`\`

This will:
1. Load the website
2. Detect the cookie banner
3. Perform all button actions
4. Run comprehensive LLM analysis
5. Save results locally

### Option 2: API Server

\`\`\`bash
# Start API server
python api.py

# In another terminal, test the API
curl -X POST "http://localhost:8080/api/v1/analyze" \\
  -H "Content-Type: application/json" \\
  -d '{"url": "https://www.example.com", "user_id": "test_user"}'
\`\`\`

### Option 3: Python Script

\`\`\`python
import asyncio
from app import analyze_cookie_banner_modular

async def main():
    result = await analyze_cookie_banner_modular(
        url="https://www.example.com",
        user_id="user123"
    )
    
    print(f"Banner detected: {result.banner_detection.banner_detected}")
    print(f"Compliance score: {result.legal_compliance_analysis.get('compliance_score')}")

asyncio.run(main())
\`\`\`

## First-Time User Onboarding

### 1. Verify Installation

\`\`\`bash
# Check Python version
python --version  # Should be 3.8+

# Check dependencies
pip list | grep browser-use
pip list | grep fastapi
\`\`\`

### 2. Test API Health

\`\`\`bash
curl http://localhost:8080/api/v1/health
\`\`\`

Expected response:
\`\`\`json
{
  "status": "healthy",
  "version": "1.0.0",
  "services": {
    "openai": "configured",
    "firebase": "configured",
    "gcs": "configured"
  }
}
\`\`\`

### 3. Run Your First Analysis

\`\`\`bash
# Simple analysis
python app.py https://www.example.com
\`\`\`

### 4. Check Results

Results are saved in:
\`\`\`
cookie_analysis/
  example-com/
    2025-11-26-220000/
      complete_analysis_report.json
      summary.txt
      screenshots/
      llm_analyses/
\`\`\`

## Configuration

### API Server Configuration

Edit \`api.py\` or use environment variables:

\`\`\`python
# Default configuration
host = "0.0.0.0"
port = 8080
\`\`\`

Or set via command line:
\`\`\`bash
python api.py --host 0.0.0.0 --port 8080
\`\`\`

### Cleanup Configuration

Control automatic cleanup of old analysis folders:

\`\`\`env
CLEANUP_MAX_AGE_HOURS=6      # Delete folders older than 6 hours
CLEANUP_MAX_FOLDERS=50       # Keep maximum 50 folders
CLEANUP_MAX_SIZE_MB=2000     # Maximum 2GB total storage
\`\`\`

### LLM Configuration

Default model is \`gpt-4o-mini\`. To change:

\`\`\`python
# In app.py or utility/llm_analysis.py
llm = ChatOpenAI(model="gpt-4")  # Use GPT-4 instead
\`\`\`

## Common Setup Issues

### Issue 1: OpenAI API Key Not Found

**Error**: \`OPENAI_API_KEY not found in .env file\`

**Solution**:
1. Create \`.env\` file in project root
2. Add \`OPENAI_API_KEY=your-key-here\`
3. Restart the application

### Issue 2: Google Cloud Credentials Error

**Error**: \`GOOGLE_APPLICATION_CREDENTIALS must be set\`

**Solution**:
1. Download service account key from GCP Console
2. Set path in \`.env\`: \`GOOGLE_APPLICATION_CREDENTIALS=path/to/key.json\`
3. Ensure key has required permissions

### Issue 3: Firebase Connection Failed

**Error**: \`Firebase connection timeout\`

**Solution**:
1. Verify Firebase project exists
2. Check service account has Firestore permissions
3. Verify network connectivity
4. Check firewall settings

### Issue 4: Browser Automation Fails

**Error**: \`Browser session error\`

**Solution**:
1. Ensure Playwright browsers are installed: \`playwright install\`
2. Check system has required dependencies
3. Try running with headless=False for debugging

## Next Steps

1. **Read API Documentation**: Learn about all available endpoints
2. **Explore Examples**: See code examples for common use cases
3. **Review Architecture**: Understand system design
4. **Check Troubleshooting**: Common issues and solutions

## Frontend Setup (Optional)

If you want to use the React frontend:

\`\`\`bash
cd Cookie-Consent-Frontend-main
npm install
npm start
\`\`\`

The frontend will run on \`http://localhost:3000\` and connect to the backend API.

## Production Deployment

For production deployment, see:
- [Deployment Environment](../01-code-documentation/deployment-environment.html)
- [Architecture Documentation](../05-architecture/deployment-architecture.html)

## Getting Help

- **Documentation**: Browse all documentation sections
- **API Docs**: Interactive Swagger UI at \`/api/v1/docs\`
- **Issues**: Check GitHub issues or create new one
- **Examples**: See code examples documentation

## Verification Checklist

- [ ] Python 3.8+ installed
- [ ] Virtual environment created and activated
- [ ] Dependencies installed (\`pip install -r requirements.txt\`)
- [ ] \`.env\` file created with all required variables
- [ ] OpenAI API key configured
- [ ] Google Cloud credentials set up
- [ ] Firebase project configured
- [ ] GCS bucket created
- [ ] Health check endpoint returns "healthy"
- [ ] First analysis completed successfully

## Quick Reference

\`\`\`bash
# Start API server
python api.py

# Run analysis directly
python app.py https://www.example.com

# Check health
curl http://localhost:8080/api/v1/health

# View API docs
open http://localhost:8080/api/v1/docs
\`\`\`

## References

- [API Documentation](../04-api-documentation/endpoints.html)
- [Code Examples](../01-code-documentation/code-examples.html)
- [Architecture Overview](../05-architecture/system-overview.html)
`;

// Auto-generated from glossary.md
export const CookieConsentBackendDocumentation03UserManualGlossary = `# Glossary of Terms

## Overview

This glossary provides layperson-friendly explanations of technical terms, compliance concepts, and AI/ML terminology used in the Cookie Consent Analyzer.

## Cookie & Consent Terms

### Cookie

**Definition**: A small text file stored on your device by websites to remember information about you.

**Simple Explanation**: Like a name tag that websites use to remember who you are when you visit again.

**Types**:
- **Session Cookie**: Temporary, deleted when you close browser
- **Persistent Cookie**: Stays on your device for a set time
- **First-Party Cookie**: Set by the website you're visiting
- **Third-Party Cookie**: Set by other websites (like advertisers)

### Cookie Banner

**Definition**: A pop-up or notice that appears on websites asking for your consent to use cookies.

**Simple Explanation**: The message that pops up when you visit a website asking if you agree to cookies.

**Purpose**: To inform you about cookie usage and get your permission.

### Cookie Consent

**Definition**: Your agreement to allow a website to use cookies.

**Simple Explanation**: Saying "yes" or "no" to cookies when a website asks.

**Types**:
- **Explicit Consent**: You actively click "Accept" or "Reject"
- **Implied Consent**: Assumed from your actions (less common now)
- **Granular Consent**: Choosing which types of cookies to allow

### Accept Button

**Definition**: A button that allows all cookies when clicked.

**Simple Explanation**: The "Yes, I agree" button on cookie banners.

### Reject Button

**Definition**: A button that refuses non-essential cookies when clicked.

**Simple Explanation**: The "No, thanks" button that should stop tracking cookies.

**Importance**: Required by GDPR for valid consent.

### Manage Preferences

**Definition**: An option to customize which types of cookies you allow.

**Simple Explanation**: A settings button where you can choose specific cookie types.

**Example**: Allowing necessary cookies but rejecting advertising cookies.

## Compliance Terms

### GDPR (General Data Protection Regulation)

**Definition**: European Union law protecting personal data and privacy.

**Simple Explanation**: Rules that protect your personal information in Europe.

**Key Requirements**:
- Clear consent before using cookies
- Easy way to say "no"
- Transparent about what data is collected
- Right to delete your data

### ePrivacy Directive

**Definition**: EU law specifically about electronic communications and cookies.

**Simple Explanation**: Rules about how websites can use cookies and track you.

**Relationship to GDPR**: Works together with GDPR for complete protection.

### CCPA (California Consumer Privacy Act)

**Definition**: California law giving residents control over personal information.

**Simple Explanation**: Similar to GDPR but for California residents.

### Compliance Score

**Definition**: A number (0-10) showing how well a website follows privacy laws.

**Simple Explanation**: A grade showing if a website is following the rules.

**Interpretation**:
- 9-10: Excellent compliance
- 7-8: Good compliance
- 5-6: Needs improvement
- 0-4: Poor compliance

### Non-Compliance

**Definition**: When a website doesn't follow privacy laws.

**Simple Explanation**: Breaking the rules about privacy and cookies.

**Examples**:
- Setting tracking cookies before you agree
- Making it hard to say "no"
- Not explaining what cookies do

## Technical Terms

### Browser Automation

**Definition**: Using software to control a web browser automatically.

**Simple Explanation**: A computer program that can visit websites and click buttons like a human would.

**Use in Analyzer**: The system automatically visits websites and interacts with cookie banners.

### Screenshot

**Definition**: A picture of what's on a computer screen.

**Simple Explanation**: Like taking a photo of your computer screen.

**Use in Analyzer**: Captures images of cookie banners for analysis.

### API (Application Programming Interface)

**Definition**: A way for different software programs to talk to each other.

**Simple Explanation**: Like a menu at a restaurant - it tells you what you can order.

**Use in Analyzer**: How the frontend communicates with the backend.

### Database

**Definition**: An organized collection of information stored on a computer.

**Simple Explanation**: Like a digital filing cabinet that stores information.

**Use in Analyzer**: Stores analysis results and user data.

### Cloud Storage

**Definition**: Storing files on the internet instead of your computer.

**Simple Explanation**: Like saving files to Google Drive or Dropbox.

**Use in Analyzer**: Stores screenshots and reports online.

## AI/ML Terms

### LLM (Large Language Model)

**Definition**: An AI system that understands and generates human-like text.

**Simple Explanation**: A computer program that can read and write like a human.

**Examples**: ChatGPT, GPT-4

**Use in Analyzer**: Analyzes cookie banners and generates compliance reports.

### Vision Model

**Definition**: An AI that can "see" and understand images.

**Simple Explanation**: A computer program that can look at pictures and describe what it sees.

**Use in Analyzer**: Looks at screenshots to find cookie banners and buttons.

### AI Analysis

**Definition**: Using artificial intelligence to examine and understand data.

**Simple Explanation**: Having a smart computer program look at information and tell you what it means.

**Use in Analyzer**: AI examines cookie banners and tells you if they follow the rules.

### Structured Output

**Definition**: Getting information from AI in a specific, organized format.

**Simple Explanation**: Getting answers in a consistent format that's easy to use.

**Use in Analyzer**: Ensures AI responses are always in the same format.

## Analysis Terms

### Banner Detection

**Definition**: Finding and identifying cookie banners on websites.

**Simple Explanation**: The system looking for cookie banners on a webpage.

**Process**: AI looks at a screenshot and identifies if there's a cookie banner.

### Cookie Capture

**Definition**: Recording what cookies a website sets.

**Simple Explanation**: Writing down what cookies a website puts on your device.

**Stages**: Before clicking anything, after accepting, after rejecting.

### Compliance Analysis

**Definition**: Checking if a website follows privacy laws.

**Simple Explanation**: Grading a website on how well it follows the rules.

**Factors**: Banner visibility, button availability, cookie behavior, etc.

### Risk Level

**Definition**: How much a website's cookie practices could harm privacy.

**Simple Explanation**: How dangerous a website's cookie practices are.

**Levels**: Low, Medium, High, Critical

### Dark Patterns

**Definition**: Design tricks that make you do something you didn't intend.

**Simple Explanation**: Sneaky design that tricks you into clicking "Accept".

**Examples**:
- Hiding the reject button
- Making accept button much bigger
- Pre-checked boxes
- Confusing language

## UI/UX Terms

### User Experience (UX)

**Definition**: How easy and pleasant a website is to use.

**Simple Explanation**: How good it feels to use a website.

**In Cookie Banners**: Is it easy to understand and make choices?

### Accessibility

**Definition**: Making websites usable by everyone, including people with disabilities.

**Simple Explanation**: Making sure everyone can use a website, no matter their abilities.

**Examples**: Screen reader support, keyboard navigation, clear text.

### Granular Control

**Definition**: Being able to choose specific options instead of all-or-nothing.

**Simple Explanation**: Picking exactly what you want instead of everything or nothing.

**Example**: Allowing necessary cookies but rejecting advertising cookies.

## Data Terms

### Personal Data

**Definition**: Information that can identify you.

**Simple Explanation**: Information about you, like your name or email.

**Examples**: Name, email, IP address, location.

### Tracking

**Definition**: Following what you do online to build a profile of you.

**Simple Explanation**: Websites watching what you do to learn about you.

**Purpose**: Usually for advertising or analytics.

### Third-Party

**Definition**: A company other than the website you're visiting.

**Simple Explanation**: Someone else, not the website itself.

**Example**: An advertising company that the website works with.

## System Terms

### Backend

**Definition**: The part of a system that runs on servers and does the work.

**Simple Explanation**: The "behind the scenes" part that does the actual work.

**In This System**: Analyzes websites and stores results.

### Frontend

**Definition**: The part of a system that users see and interact with.

**Simple Explanation**: The website or app that you use.

**In This System**: The web interface where you submit URLs and see results.

### API Endpoint

**Definition**: A specific URL where you can send requests to get information.

**Simple Explanation**: An address where you can ask the system to do something.

**Example**: \`/api/v1/analyze\` - where you send a URL to analyze.

### Task ID

**Definition**: A unique identifier for a specific analysis job.

**Simple Explanation**: A tracking number for your analysis request.

**Use**: Used to check status and get results.

## References

- [GDPR Official Website](https://gdpr.eu/)
- [ePrivacy Directive](https://eur-lex.europa.eu/legal-content/EN/TXT/?uri=CELEX:32002L0058)
- [CCPA Information](https://oag.ca.gov/privacy/ccpa)
`;

// Auto-generated from interactive-tutorials.md
export const CookieConsentBackendDocumentation03UserManualInteractiveTutorials = `# Interactive Tutorials

## Overview

Step-by-step tutorials and guides.

## Content

This section needs to be completed with detailed information.

## References

- [Main Documentation](../index.html)
- [Related Documentation](#)

---

*This documentation file is a template and needs to be completed with detailed content.*
`;

// Auto-generated from release-notes.md
export const CookieConsentBackendDocumentation03UserManualReleaseNotes = `# Versioning & Release Notes

## Overview

Version history and release notes.

## Content

This section needs to be completed with detailed information.

## References

- [Main Documentation](../index.html)
- [Related Documentation](#)

---

*This documentation file is a template and needs to be completed with detailed content.*
`;

// Auto-generated from troubleshooting.md
export const CookieConsentBackendDocumentation03UserManualTroubleshooting = `# Troubleshooting & FAQs

## Common Issues and Solutions

### Installation Issues

#### Issue: Python Version Error

**Error**: \`Python 3.8+ required\`

**Solution**:
1. Check Python version: \`python --version\`
2. Install Python 3.8 or higher
3. Use virtual environment with correct Python version

#### Issue: Dependency Installation Fails

**Error**: \`pip install -r requirements.txt\` fails

**Solutions**:
1. **Update pip**: \`python -m pip install --upgrade pip\`
2. **Install system dependencies** (Linux):
   \`\`\`bash
   sudo apt-get install python3-dev python3-pip
   \`\`\`
3. **Use virtual environment**: Ensure you're in a virtual environment
4. **Install one by one**: Install problematic packages individually

#### Issue: Playwright Browsers Not Installed

**Error**: \`Browser not found\`

**Solution**:
\`\`\`bash
playwright install
playwright install chromium
\`\`\`

### Configuration Issues

#### Issue: OpenAI API Key Not Found

**Error**: \`OPENAI_API_KEY not found in .env file\`

**Solutions**:
1. Create \`.env\` file in project root
2. Add: \`OPENAI_API_KEY=sk-your-key-here\`
3. Restart the application
4. Verify key is valid: Check OpenAI dashboard

#### Issue: Google Cloud Credentials Error

**Error**: \`GOOGLE_APPLICATION_CREDENTIALS must be set\`

**Solutions**:
1. Download service account key from GCP Console
2. Set path in \`.env\`: \`GOOGLE_APPLICATION_CREDENTIALS=path/to/key.json\`
3. Verify file exists and is readable
4. Check service account has required permissions:
   - Storage Object Admin (for GCS)
   - Cloud Datastore User (for Firestore)

#### Issue: Firebase Connection Failed

**Error**: \`Firebase connection timeout\`

**Solutions**:
1. Verify Firebase project exists and is active
2. Check service account has Firestore permissions
3. Verify network connectivity
4. Check firewall settings
5. Try with increased timeout:
   \`\`\`python
   firestore.Settings(timestamps_in_snapshots=True)
   \`\`\`

### Runtime Issues

#### Issue: Analysis Takes Too Long

**Symptoms**: Analysis runs for more than 5 minutes

**Solutions**:
1. **Check network speed**: Slow internet affects LLM API calls
2. **Reduce timeout**: Some sites load slowly, increase wait times
3. **Check OpenAI API status**: API may be experiencing issues
4. **Monitor resources**: Ensure sufficient CPU/memory
5. **Check logs**: Look for specific bottlenecks

#### Issue: Banner Not Detected

**Symptoms**: Analysis reports "No banner detected"

**Possible Causes**:
1. Website doesn't have a cookie banner
2. Banner loads after initial screenshot
3. Banner is hidden or styled unusually
4. LLM vision model missed the banner

**Solutions**:
1. **Increase wait time**: Banner may load slowly
2. **Check manually**: Visit URL to verify banner exists
3. **Review screenshot**: Check if banner is visible
4. **Retry analysis**: LLM may detect on second attempt

#### Issue: Cookies Not Captured

**Symptoms**: Cookie count is 0 or very low

**Possible Causes**:
1. Cookies set after longer delay
2. JavaScript required for cookie setting
3. Browser automation detected
4. Site blocks automated browsers

**Solutions**:
1. **Increase wait time**: Add delays after actions
2. **Check storage_state file**: Verify cookies are saved
3. **Use undetected-chromedriver**: Bypass bot detection
4. **Review browser logs**: Check for errors

#### Issue: LLM Analysis Fails

**Error**: \`LLM analysis failed\` or \`JSON parsing error\`

**Solutions**:
1. **Check API key**: Verify OpenAI API key is valid
2. **Check rate limits**: Wait if rate limited
3. **Check API status**: OpenAI may be experiencing issues
4. **Review prompts**: Prompts may need adjustment
5. **Use structured output**: Already implemented, but verify it's working

### API Issues

#### Issue: API Server Won't Start

**Error**: \`Address already in use\` or port conflicts

**Solutions**:
1. **Change port**: \`python api.py --port 8081\`
2. **Kill existing process**: Find and kill process using port 8080
3. **Check firewall**: Ensure port is not blocked

#### Issue: CORS Errors (Frontend)

**Error**: \`CORS policy blocked\`

**Solutions**:
1. **Backend**: Verify CORS middleware is configured
2. **Frontend**: Check API_BASE_URL is correct
3. **Development**: Use proxy in package.json:
   \`\`\`json
   "proxy": "http://localhost:8080"
   \`\`\`

#### Issue: Task Not Found

**Error**: \`Task {task_id} not found\`

**Solutions**:
1. **Task expired**: In-memory store clears on restart
2. **Wrong task_id**: Verify task_id is correct
3. **Server restarted**: Tasks lost if server restarted
4. **Use Redis**: For production, use Redis for persistence

### Cloud Service Issues

#### Issue: GCS Upload Fails

**Error**: \`Failed to upload to GCS\`

**Solutions**:
1. **Check bucket exists**: Verify bucket name is correct
2. **Check permissions**: Service account needs Storage Object Admin
3. **Check network**: Verify internet connectivity
4. **Check file size**: Large files may timeout
5. **Retry**: Transient errors may resolve on retry

#### Issue: Firebase Write Fails

**Error**: \`Firebase write permission denied\`

**Solutions**:
1. **Check security rules**: Verify rules allow writes
2. **Check service account**: Verify has Firestore permissions
3. **Check collection name**: Verify collection path is correct
4. **Check document size**: Firestore has 1MB document limit

### Performance Issues

#### Issue: High Memory Usage

**Symptoms**: System runs out of memory

**Solutions**:
1. **Reduce concurrent analyses**: Process one at a time
2. **Increase system memory**: Add more RAM
3. **Cleanup old files**: Run cleanup more frequently
4. **Use headless browsers**: Reduces memory usage
5. **Close browsers promptly**: Don't keep browsers open

#### Issue: Slow Analysis

**Symptoms**: Analysis takes longer than expected

**Solutions**:
1. **Check LLM API latency**: May be experiencing delays
2. **Optimize wait times**: Reduce unnecessary waits
3. **Use faster LLM model**: gpt-4o-mini is already fast
4. **Parallel processing**: Process multiple stages in parallel (if possible)
5. **Cache results**: Cache LLM responses for similar banners

## Frequently Asked Questions

### Q: How long does an analysis take?

**A**: Typically 90-180 seconds (1.5-3 minutes), depending on:
- Website load time
- Number of button actions
- LLM API response time
- Network speed

### Q: Can I analyze multiple websites at once?

**A**: Yes, but:
- Each analysis uses significant resources
- API supports concurrent requests
- For production, use a task queue (Celery/RQ)

### Q: What if a website blocks automated browsers?

**A**: The system uses:
- \`undetected-chromedriver\` to bypass detection
- Headless browsers to appear more natural
- Realistic user agents
- If still blocked, manual analysis may be needed

### Q: How accurate is the banner detection?

**A**: 
- **High confidence**: ~95% accuracy
- **Medium confidence**: ~85% accuracy
- **Low confidence**: ~70% accuracy
- Uses GPT-4 Vision for detection

### Q: What happens if OpenAI API is down?

**A**: 
- Analysis will fail at LLM stages
- Initial cookie capture may still work
- System returns safe defaults
- User is notified of the error

### Q: Can I use my own LLM provider?

**A**: Currently uses OpenAI, but architecture supports:
- Adding other LangChain providers
- Custom LLM integration
- See \`utility/llm_analysis.py\` for integration points

### Q: How much does it cost to run?

**A**: Costs depend on:
- **OpenAI API**: ~$0.01-0.05 per analysis (GPT-4o-mini)
- **GCS Storage**: ~$0.01 per GB/month
- **Firebase**: Free tier covers most use cases
- **Compute**: Depends on hosting (Cloud Run, etc.)

### Q: Is my data secure?

**A**: Yes:
- User-specific subcollections in Firebase
- Encrypted data in transit (HTTPS)
- Service account authentication
- No data shared between users

### Q: Can I run this on-premise?

**A**: Yes, with modifications:
- Remove cloud service dependencies
- Use local file storage
- Configure local database
- See deployment documentation

### Q: How do I update the system?

**A**:
1. Pull latest code
2. Update dependencies: \`pip install -r requirements.txt --upgrade\`
3. Run tests: \`pytest testing/\`
4. Restart services
5. Check release notes for breaking changes

## Getting Help

### Check Documentation

1. Review relevant documentation section
2. Check code examples
3. Review API documentation
4. See architecture documentation

### Debug Mode

Enable debug logging:

\`\`\`python
import logging
logging.basicConfig(level=logging.DEBUG)
\`\`\`

### Check Logs

- **API logs**: Check console output
- **Analysis logs**: Check \`cookie_analysis/\` directory
- **Cloud logs**: Check Google Cloud Logging

### Report Issues

When reporting issues, include:
1. Error message
2. Steps to reproduce
3. Environment details (Python version, OS)
4. Relevant log output
5. Configuration (sanitized)

## References

- [Getting Started Guide](./getting-started.html)
- [API Documentation](../04-api-documentation/endpoints.html)
- [Error Handling](../02-functional-flows/error-handling.html)
- [Deployment Environment](../01-code-documentation/deployment-environment.html)
`;

// Auto-generated from api-versioning.md
export const CookieConsentBackendDocumentation04ApiDocumentationApiVersioning = `# Version Control & Lifecycle Management

## Overview

API versioning strategy and lifecycle.

## Content

This section needs to be completed with detailed information.

## References

- [Main Documentation](../index.html)
- [Related Documentation](#)

---

*This documentation file is a template and needs to be completed with detailed content.*
`;

// Auto-generated from authentication.md
export const CookieConsentBackendDocumentation04ApiDocumentationAuthentication = `# Authentication & Security

## Overview

OAuth2, token management, privacy-preserving protocols.

## Content

This section needs to be completed with detailed information.

## References

- [Main Documentation](../index.html)
- [Related Documentation](#)

---

*This documentation file is a template and needs to be completed with detailed content.*
`;

// Auto-generated from data-schemas.md
export const CookieConsentBackendDocumentation04ApiDocumentationDataSchemas = `# Data Schemas

## Overview

This document describes all data schemas used in the Cookie Consent Analyzer API, including request models, response models, and internal data structures.

## Request Schemas

### AnalysisRequest

Request model for initiating cookie banner analysis.

\`\`\`typescript
{
  url: string;                    // Required: Website URL to analyze
  user_id: string;                // Required: User ID for Firebase storage
  callback_url?: string;          // Optional: Webhook URL
  metadata?: {                    // Optional: Custom metadata
    [key: string]: any;
  };
}
\`\`\`

**Validation Rules**:
- \`url\`: Must be valid HTTP/HTTPS URL, auto-prefixes \`https://\` if missing
- \`user_id\`: Cannot be empty, trimmed whitespace

**Example**:
\`\`\`json
{
  "url": "https://www.example.com",
  "user_id": "user123",
  "callback_url": "https://webhook.com/callback",
  "metadata": {
    "client_id": "client123",
    "campaign": "gdpr_audit_2025"
  }
}
\`\`\`

## Response Schemas

### AnalysisResponse

Response when analysis is initiated.

\`\`\`typescript
{
  task_id: string;               // Unique task identifier
  status: "pending";             // Initial status
  message: string;               // Status message
  created_at: string;            // ISO timestamp
  estimated_duration: number;    // Seconds (default: 120)
}
\`\`\`

### StatusResponse

Response for status check.

\`\`\`typescript
{
  task_id: string;
  status: "pending" | "in_progress" | "completed" | "failed" | "cancelled";
  progress: number;              // 0-100
  current_stage?: string;        // Current stage description
  created_at: string;
  updated_at: string;
  completed_at?: string;         // null if not completed
  error?: string;                // null if no error
}
\`\`\`

### ResultsResponse

Complete analysis results.

\`\`\`typescript
{
  task_id: string;
  url: string;
  status: "completed" | "failed";
  summary?: AnalysisResultSummary;
  firebase_doc_id?: string;      // Firebase document ID
  gcs_report_url?: string;       // GCS URL for full report
  created_at: string;
  completed_at?: string;
  error?: string;
}
\`\`\`

### AnalysisResultSummary

Summary of analysis results.

\`\`\`typescript
{
  banner_detected: boolean;
  total_stages: number;
  compliance_status?: string;    // "compliant" | "non_compliant" | "partial"
  compliance_score?: number;      // 0-10
  critical_issues_count: number;
  total_cookies_initial: number;
  total_cookies_after_accept: number;
  total_cookies_after_reject: number;
}
\`\`\`

## Internal Data Models

### BannerDetectionResult

Structured result from banner detection.

\`\`\`python
class BannerDetectionResult(BaseModel):
    banner_detected: bool
    accept_button: bool
    accept_button_text: str
    reject_button: bool
    reject_button_text: str
    close_button: bool
    close_button_text: str
    manage_preferences_button: bool
    manage_preferences_button_text: str
    banner_position: Literal["top", "bottom", "center", "side", "corner", "unknown"]
    banner_text_snippet: str      # First 150 characters
    confidence: Literal["high", "medium", "low"]
\`\`\`

### CookieStage

Cookie capture at a specific stage.

\`\`\`python
@dataclass
class CookieStage:
    stage_name: str               # e.g., "initial_load", "after_accept"
    stage_number: int
    description: str
    cookies_count: int
    cookies: List[Dict[str, Any]]  # Full cookie data
    screenshot_path: Optional[str] # Local path or GCS URL
    timestamp: str
\`\`\`

**Cookie Dictionary Structure**:
\`\`\`python
{
    "name": str,                  # Cookie name
    "value": str,                 # Cookie value
    "domain": str,                # Domain
    "path": str,                  # Path
    "expires": float,             # Expiration timestamp (-1 for session)
    "httpOnly": bool,             # HttpOnly flag
    "secure": bool,               # Secure flag
    "sameSite": str               # SameSite attribute
}
\`\`\`

### AnalysisResult

Complete analysis result.

\`\`\`python
@dataclass
class AnalysisResult:
    url: str
    timestamp: str
    banner_detection: BannerDetectionResult
    stages: List[CookieStage]
    cookie_analyses: Dict[str, Dict[str, Any]]  # Stage-wise analyses
    banner_ui_analysis: Optional[Dict[str, Any]]
    legal_compliance_analysis: Optional[Dict[str, Any]]
    overall_summary: Optional[Dict[str, Any]]
    firebase_doc_id: Optional[str]
    gcs_report_url: Optional[str]
\`\`\`

## Firebase Document Schema

### User Document

\`\`\`
report-user-ids/{user_id}
{
  email: string
  password: string              # Hashed in production
  first_name?: string
  last_name?: string
  created_at: Timestamp
  updated_at: Timestamp
  total_analyses: number
}
\`\`\`

### Analysis Document

\`\`\`
report-user-ids/{user_id}/cookies-analysis-database/{analysis_doc_id}
{
  url: string
  domain: string
  created_at: Timestamp
  updated_at: Timestamp
  timestamp: string
  analysis_version: "2.0"
  status: "in_progress" | "completed" | "failed"
  
  banner_detection: {
    banner_detected: boolean
    accept_button: boolean
    reject_button: boolean
    // ... full BannerDetectionResult
  }
  
  stages: [
    {
      stage_number: number
      stage_name: string
      description: string
      cookies_count: number
      screenshot_url: string     # GCS public URL
      timestamp: string
      cookies: [...]             # Full cookie array
    }
  ]
  
  analyses: {
    banner_ui_analysis: {...}
    cookie_analysis_before_consent: {...}
    cookie_analysis_after_accept: {...}
    cookie_analysis_after_reject: {...}
    legal_compliance_analysis: {...}
    overall_summary: {...}
  }
  
  statistics: {
    total_stages: number
    total_cookies_initial: number
    total_cookies_after_accept: number
    total_cookies_after_reject: number
  }
  
  complete_analysis: {...}       # Full analysis JSON
  final_summary: {...}
  completed_at: Timestamp
}
\`\`\`

## LLM Analysis Schemas

### Banner UI Analysis

\`\`\`python
{
  "overall_score": "excellent" | "good" | "fair" | "poor",
  "visibility_score": 1-10,
  "clarity_score": 1-10,
  "choice_freedom_score": 1-10,
  "ux_score": 1-10,
  "color_scheme": {
    "primary_color": string,
    "text_color": string,
    "button_colors": string
  },
  "element_sizing": {
    "banner_size": string,
    "button_size": string,
    "text_size": string
  },
  "dark_patterns_detected": [string],
  "accessibility_issues": [string],
  "positive_aspects": [string],
  "improvement_suggestions": [string]
}
\`\`\`

### Cookie Analysis Result

\`\`\`python
{
  "stage": string,                # "Before Consent", "After Accept", etc.
  "cookies_count": number,
  "cookie_analysis_table": [
    {
      "name": string,
      "description": string,
      "tracks_user": "Yes" | "No" | "Unclear",
      "personal_data": "Yes" | "No" | "Unclear",
      "purpose": "Strictly Necessary" | "Preference/Functional" | 
                 "Statistics/Analytics" | "Marketing/Targeting" | "Unknown",
      "risk_level": "Low" | "Medium" | "High"
    }
  ],
  "stage_appropriateness": string,
  "privacy_concerns": [string],
  "gdpr_compliance_issues": [string],
  "summary": {
    "high_risk_count": number,
    "medium_risk_count": number,
    "low_risk_count": number,
    "tracking_cookies": number,
    "personal_data_cookies": number
  },
  "recommendations": [string]
}
\`\`\`

### Legal Compliance Analysis

\`\`\`python
{
  "overall_compliance": "compliant" | "non_compliant" | "partial",
  "compliance_score": 0-10,
  "gdpr_compliance": {
    "status": string,
    "issues": [string],
    "score": number
  },
  "eprivacy_compliance": {
    "status": string,
    "issues": [string],
    "score": number
  },
  "critical_violations": [
    {
      "type": string,
      "description": string,
      "severity": "critical" | "high" | "medium",
      "remediation": string
    }
  ],
  "risk_assessment": "low" | "medium" | "high" | "critical",
  "recommendations": [string]
}
\`\`\`

### Overall Summary

\`\`\`python
{
  "executive_summary": string,    # Text summary
  "compliance_status": {
    "gdpr_compliance": string,
    "eprivacy_compliance": string,
    "overall_status": string,
    "risk_level": string
  },
  "key_findings": [string],
  "critical_issues": [string],
  "estimated_remediation_effort": "low" | "medium" | "high",
  "recommendations": [string]
}
\`\`\`

## Error Response Schema

### API Error Response

\`\`\`typescript
{
  detail: string | Array<{
    loc: Array<string | number>;
    msg: string;
    type: string;
  }>;
}
\`\`\`

**Example (Validation Error)**:
\`\`\`json
{
  "detail": [
    {
      "loc": ["body", "url"],
      "msg": "field required",
      "type": "value_error.missing"
    }
  ]
}
\`\`\`

**Example (Simple Error)**:
\`\`\`json
{
  "detail": "Task 550e8400-e29b-41d4-a716-446655440000 not found"
}
\`\`\`

## Complete Analysis Report JSON

Structure of the complete analysis report saved to GCS:

\`\`\`json
{
  "url": "https://www.example.com",
  "timestamp": "2025-11-26-220000",
  "banner_detection": {...},      // BannerDetectionResult
  "stages": [...],                // Array of CookieStage
  "analyses": {
    "1_banner_ui_analysis": {...},
    "2_cookie_analysis_before_consent": {...},
    "3_cookie_analysis_after_accept": {...},
    "4_cookie_analysis_after_reject": {...},
    "5_legal_compliance_analysis": {...},
    "6_overall_summary": {...}
  }
}
\`\`\`

## Type Definitions

### AnalysisStatus Enum

\`\`\`python
class AnalysisStatus(str, Enum):
    PENDING = "pending"
    IN_PROGRESS = "in_progress"
    COMPLETED = "completed"
    FAILED = "failed"
    CANCELLED = "cancelled"
\`\`\`

### Banner Position Enum

\`\`\`python
Literal["top", "bottom", "center", "side", "corner", "unknown"]
\`\`\`

### Confidence Level Enum

\`\`\`python
Literal["high", "medium", "low"]
\`\`\`

## JSON Examples

### Complete Request Example

\`\`\`json
{
  "url": "https://www.example.com",
  "user_id": "user123",
  "callback_url": "https://webhook.example.com/analysis-complete",
  "metadata": {
    "source": "web_ui",
    "user_email": "user@example.com",
    "campaign": "gdpr_audit_q4_2025"
  }
}
\`\`\`

### Complete Response Example

\`\`\`json
{
  "task_id": "550e8400-e29b-41d4-a716-446655440000",
  "url": "https://www.example.com",
  "status": "completed",
  "summary": {
    "banner_detected": true,
    "total_stages": 5,
    "compliance_status": "non_compliant",
    "compliance_score": 4.5,
    "critical_issues_count": 3,
    "total_cookies_initial": 12,
    "total_cookies_after_accept": 45,
    "total_cookies_after_reject": 15
  },
  "firebase_doc_id": "abc123def456",
  "gcs_report_url": "https://storage.googleapis.com/bucket/.../report.json",
  "created_at": "2025-11-26T22:00:00Z",
  "completed_at": "2025-11-26T22:02:15Z",
  "error": null
}
\`\`\`

## Schema Validation

All schemas are validated using Pydantic models:

- **Request validation**: Automatic validation on API endpoints
- **Response validation**: Ensures consistent response format
- **Type safety**: TypeScript types for frontend integration

## References

- [Pydantic Documentation](https://docs.pydantic.dev/)
- [API Endpoints](./endpoints.html)
- [Integration Examples](./integration-examples.html)

`;

// Auto-generated from endpoints.md
export const CookieConsentBackendDocumentation04ApiDocumentationEndpoints = `# API Endpoints & Methods

## Overview

The Cookie Consent Analyzer API provides RESTful endpoints for analyzing cookie banners on websites. All endpoints are versioned under \`/api/v1/\` and return JSON responses.

## Base URL

\`\`\`
http://localhost:8080/api/v1
\`\`\`

For production, replace with your deployed API URL.

## Authentication

Currently, the API uses \`user_id\` in the request body for user identification. For production, implement OAuth2 or JWT token-based authentication.

## Endpoints

### 1. Health Check

**Endpoint**: \`GET /api/v1/health\`

**Description**: Check API health status and service configuration.

**Request**:
\`\`\`http
GET /api/v1/health HTTP/1.1
Host: localhost:8080
\`\`\`

**Response** (200 OK):
\`\`\`json
{
  "status": "healthy",
  "version": "1.0.0",
  "timestamp": "2025-11-26T22:00:00Z",
  "services": {
    "openai": "configured",
    "firebase": "configured",
    "gcs": "configured"
  }
}
\`\`\`

**Response Fields**:
- \`status\` (string): Health status ("healthy" or "unhealthy")
- \`version\` (string): API version
- \`timestamp\` (string): Current timestamp in ISO format
- \`services\` (object): Status of external services

**Use Cases**:
- Load balancer health checks
- Monitoring and alerting
- Service status verification

---

### 2. Start Analysis

**Endpoint**: \`POST /api/v1/analyze\`

**Description**: Initiate a new cookie banner analysis. Returns immediately with a task ID. Analysis runs asynchronously in the background.

**Request**:
\`\`\`http
POST /api/v1/analyze HTTP/1.1
Host: localhost:8080
Content-Type: application/json

{
  "url": "https://www.example.com",
  "user_id": "user123",
  "callback_url": "https://your-webhook.com/callback",
  "metadata": {
    "client_id": "client123",
    "campaign": "gdpr_audit_2025"
  }
}
\`\`\`

**Request Body**:
- \`url\` (string, required): Website URL to analyze (HTTP/HTTPS)
- \`user_id\` (string, required): User ID for Firebase subcollection storage
- \`callback_url\` (string, optional): Webhook URL for completion notification
- \`metadata\` (object, optional): Custom metadata to attach to analysis

**Response** (202 Accepted):
\`\`\`json
{
  "task_id": "550e8400-e29b-41d4-a716-446655440000",
  "status": "pending",
  "message": "Analysis task created successfully and processing in background",
  "created_at": "2025-11-26T22:00:00Z",
  "estimated_duration": 120
}
\`\`\`

**Response Fields**:
- \`task_id\` (string): Unique task identifier for tracking
- \`status\` (string): Initial status ("pending")
- \`message\` (string): Human-readable status message
- \`created_at\` (string): Task creation timestamp
- \`estimated_duration\` (integer): Estimated completion time in seconds

**Error Responses**:

400 Bad Request:
\`\`\`json
{
  "detail": "url field is required"
}
\`\`\`

422 Unprocessable Entity:
\`\`\`json
{
  "detail": [
    {
      "loc": ["body", "url"],
      "msg": "invalid url format",
      "type": "value_error"
    }
  ]
}
\`\`\`

**Example (cURL)**:
\`\`\`bash
curl -X POST "http://localhost:8080/api/v1/analyze" \\
  -H "Content-Type: application/json" \\
  -d '{
    "url": "https://www.example.com",
    "user_id": "user123"
  }'
\`\`\`

**Example (Python)**:
\`\`\`python
import requests

response = requests.post(
    "http://localhost:8080/api/v1/analyze",
    json={
        "url": "https://www.example.com",
        "user_id": "user123"
    }
)
task_id = response.json()["task_id"]
\`\`\`

---

### 3. Check Analysis Status

**Endpoint**: \`GET /api/v1/status/{task_id}\`

**Description**: Get current status and progress of an analysis task.

**Request**:
\`\`\`http
GET /api/v1/status/550e8400-e29b-41d4-a716-446655440000 HTTP/1.1
Host: localhost:8080
\`\`\`

**Path Parameters**:
- \`task_id\` (string, required): Task identifier from \`/analyze\` endpoint

**Response** (200 OK):
\`\`\`json
{
  "task_id": "550e8400-e29b-41d4-a716-446655440000",
  "status": "in_progress",
  "progress": 45,
  "current_stage": "Analyzing cookie banner UI/UX",
  "created_at": "2025-11-26T22:00:00Z",
  "updated_at": "2025-11-26T22:01:30Z",
  "completed_at": null,
  "error": null
}
\`\`\`

**Response Fields**:
- \`task_id\` (string): Task identifier
- \`status\` (string): Current status ("pending", "in_progress", "completed", "failed", "cancelled")
- \`progress\` (integer): Progress percentage (0-100)
- \`current_stage\` (string): Description of current analysis stage
- \`created_at\` (string): Task creation timestamp
- \`updated_at\` (string): Last update timestamp
- \`completed_at\` (string, nullable): Completion timestamp (null if not completed)
- \`error\` (string, nullable): Error message (null if no error)

**Status Values**:
- \`pending\`: Task created but not started
- \`in_progress\`: Analysis is running
- \`completed\`: Analysis finished successfully
- \`failed\`: Analysis encountered an error
- \`cancelled\`: Analysis was cancelled

**Error Responses**:

404 Not Found:
\`\`\`json
{
  "detail": "Task 550e8400-e29b-41d4-a716-446655440000 not found"
}
\`\`\`

**Example (cURL)**:
\`\`\`bash
curl "http://localhost:8080/api/v1/status/550e8400-e29b-41d4-a716-446655440000"
\`\`\`

**Polling Pattern**:
\`\`\`python
import time
import requests

task_id = "550e8400-e29b-41d4-a716-446655440000"
poll_interval = 5  # seconds

while True:
    response = requests.get(f"http://localhost:8080/api/v1/status/{task_id}")
    status_data = response.json()
    
    print(f"Status: {status_data['status']}, Progress: {status_data['progress']}%")
    
    if status_data['status'] in ['completed', 'failed', 'cancelled']:
        break
    
    time.sleep(poll_interval)
\`\`\`

---

### 4. Get Analysis Results

**Endpoint**: \`GET /api/v1/results/{task_id}\`

**Description**: Retrieve complete analysis results after analysis is completed.

**Request**:
\`\`\`http
GET /api/v1/results/550e8400-e29b-41d4-a716-446655440000 HTTP/1.1
Host: localhost:8080
\`\`\`

**Path Parameters**:
- \`task_id\` (string, required): Task identifier

**Response** (200 OK):
\`\`\`json
{
  "task_id": "550e8400-e29b-41d4-a716-446655440000",
  "url": "https://www.example.com",
  "status": "completed",
  "summary": {
    "banner_detected": true,
    "total_stages": 5,
    "compliance_status": "non_compliant",
    "compliance_score": 4.5,
    "critical_issues_count": 3,
    "total_cookies_initial": 12,
    "total_cookies_after_accept": 45,
    "total_cookies_after_reject": 15
  },
  "firebase_doc_id": "abc123def456",
  "gcs_report_url": "https://storage.googleapis.com/bucket/.../complete_analysis.json",
  "created_at": "2025-11-26T22:00:00Z",
  "completed_at": "2025-11-26T22:02:15Z",
  "error": null
}
\`\`\`

**Response Fields**:
- \`task_id\` (string): Task identifier
- \`url\` (string): Analyzed website URL
- \`status\` (string): Final status
- \`summary\` (object): Analysis summary
  - \`banner_detected\` (boolean): Whether cookie banner was detected
  - \`total_stages\` (integer): Number of analysis stages completed
  - \`compliance_status\` (string): GDPR compliance status
  - \`compliance_score\` (float): Compliance score (0-10)
  - \`critical_issues_count\` (integer): Number of critical compliance issues
  - \`total_cookies_initial\` (integer): Cookies before interaction
  - \`total_cookies_after_accept\` (integer): Cookies after accepting
  - \`total_cookies_after_reject\` (integer): Cookies after rejecting
- \`firebase_doc_id\` (string, nullable): Firebase document ID for detailed results
- \`gcs_report_url\` (string, nullable): GCS URL for complete JSON report
- \`created_at\` (string): Task creation timestamp
- \`completed_at\` (string, nullable): Completion timestamp
- \`error\` (string, nullable): Error message if failed

**Error Responses**:

404 Not Found:
\`\`\`json
{
  "detail": "Task 550e8400-e29b-41d4-a716-446655440000 not found"
}
\`\`\`

400 Bad Request (if not completed):
\`\`\`json
{
  "detail": "Analysis is still in progress (45%). Please wait."
}
\`\`\`

**Example (cURL)**:
\`\`\`bash
curl "http://localhost:8080/api/v1/results/550e8400-e29b-41d4-a716-446655440000"
\`\`\`

**Example (Python)**:
\`\`\`python
import requests

response = requests.get(
    "http://localhost:8080/api/v1/results/550e8400-e29b-41d4-a716-446655440000"
)
results = response.json()

print(f"Compliance Score: {results['summary']['compliance_score']}/10")
print(f"Firebase Doc: {results['firebase_doc_id']}")
print(f"Full Report: {results['gcs_report_url']}")
\`\`\`

---

### 5. List All Analyses

**Endpoint**: \`GET /api/v1/analyses\`

**Description**: List all analysis tasks with optional filtering by status.

**Request**:
\`\`\`http
GET /api/v1/analyses?status=completed&limit=10 HTTP/1.1
Host: localhost:8080
\`\`\`

**Query Parameters**:
- \`status\` (string, optional): Filter by status ("pending", "in_progress", "completed", "failed", "cancelled")
- \`limit\` (integer, optional): Maximum number of results (default: 100, max: 1000)

**Response** (200 OK):
\`\`\`json
{
  "total": 25,
  "analyses": [
    {
      "task_id": "550e8400-e29b-41d4-a716-446655440000",
      "url": "https://www.example.com",
      "status": "completed",
      "progress": 100,
      "created_at": "2025-11-26T22:00:00Z",
      "completed_at": "2025-11-26T22:02:15Z"
    },
    {
      "task_id": "660e8400-e29b-41d4-a716-446655440001",
      "url": "https://www.another-site.com",
      "status": "in_progress",
      "progress": 65,
      "created_at": "2025-11-26T22:05:00Z",
      "completed_at": null
    }
  ]
}
\`\`\`

**Response Fields**:
- \`total\` (integer): Total number of analyses matching filter
- \`analyses\` (array): List of analysis summaries

**Example (cURL)**:
\`\`\`bash
# Get all completed analyses
curl "http://localhost:8080/api/v1/analyses?status=completed"

# Get latest 10 analyses
curl "http://localhost:8080/api/v1/analyses?limit=10"
\`\`\`

---

### 6. Cancel Analysis

**Endpoint**: \`DELETE /api/v1/analysis/{task_id}\`

**Description**: Cancel a pending or in-progress analysis.

**Request**:
\`\`\`http
DELETE /api/v1/analysis/550e8400-e29b-41d4-a716-446655440000 HTTP/1.1
Host: localhost:8080
\`\`\`

**Path Parameters**:
- \`task_id\` (string, required): Task identifier to cancel

**Response** (200 OK):
\`\`\`json
{
  "message": "Task 550e8400-e29b-41d4-a716-446655440000 cancelled successfully",
  "task_id": "550e8400-e29b-41d4-a716-446655440000",
  "status": "cancelled"
}
\`\`\`

**Error Responses**:

404 Not Found:
\`\`\`json
{
  "detail": "Task 550e8400-e29b-41d4-a716-446655440000 not found"
}
\`\`\`

400 Bad Request (if already completed):
\`\`\`json
{
  "detail": "Cannot cancel completed task"
}
\`\`\`

**Example (cURL)**:
\`\`\`bash
curl -X DELETE "http://localhost:8080/api/v1/analysis/550e8400-e29b-41d4-a716-446655440000"
\`\`\`

---

## Complete Workflow Example

\`\`\`python
import requests
import time

API_BASE = "http://localhost:8080/api/v1"

# Step 1: Start analysis
response = requests.post(
    f"{API_BASE}/analyze",
    json={
        "url": "https://www.example.com",
        "user_id": "user123"
    }
)
task_id = response.json()["task_id"]
print(f"Analysis started: {task_id}")

# Step 2: Poll for status
while True:
    response = requests.get(f"{API_BASE}/status/{task_id}")
    status = response.json()
    
    print(f"Progress: {status['progress']}% - {status['current_stage']}")
    
    if status['status'] == 'completed':
        break
    elif status['status'] == 'failed':
        print(f"Analysis failed: {status['error']}")
        exit(1)
    
    time.sleep(5)

# Step 3: Get results
response = requests.get(f"{API_BASE}/results/{task_id}")
results = response.json()

print(f"\\nAnalysis Complete!")
print(f"Compliance Score: {results['summary']['compliance_score']}/10")
print(f"Firebase Doc ID: {results['firebase_doc_id']}")
print(f"Full Report: {results['gcs_report_url']}")
\`\`\`

## Rate Limits

Currently, there are no rate limits implemented. For production:

- **Recommended**: 10 requests per minute per user
- **Burst**: Allow up to 20 requests in a short window
- **Implementation**: Use Redis or similar for rate limiting

## Error Codes

| HTTP Code | Meaning | Description |
|-----------|---------|-------------|
| 200 | OK | Request successful |
| 202 | Accepted | Request accepted, processing in background |
| 400 | Bad Request | Invalid request parameters |
| 404 | Not Found | Resource not found |
| 422 | Unprocessable Entity | Validation error |
| 500 | Internal Server Error | Server error |

## Best Practices

1. **Polling**: Use exponential backoff when polling status
2. **Timeouts**: Set appropriate timeouts for long-running analyses
3. **Error Handling**: Always check response status codes
4. **Retries**: Implement retry logic for transient failures
5. **Webhooks**: Use callback_url for production to avoid polling

## OpenAPI Documentation

Interactive API documentation is available at:
- Swagger UI: \`http://localhost:8080/api/v1/docs\`
- ReDoc: \`http://localhost:8080/api/v1/redoc\`
- OpenAPI JSON: \`http://localhost:8080/api/v1/openapi.json\`

`;

// Auto-generated from error-codes.md
export const CookieConsentBackendDocumentation04ApiDocumentationErrorCodes = `# Error Codes & Responses

## Overview

HTTP error codes and response formats.

## Content

This section needs to be completed with detailed information.

## References

- [Main Documentation](../index.html)
- [Related Documentation](#)

---

*This documentation file is a template and needs to be completed with detailed content.*
`;

// Auto-generated from integration-examples.md
export const CookieConsentBackendDocumentation04ApiDocumentationIntegrationExamples = `# Integration Examples

## Overview

This document provides comprehensive integration examples for the Cookie Consent Analyzer API, including code samples in multiple languages and integration patterns for various use cases.

## Python Integration

### Basic Analysis

\`\`\`python
import requests
import time

API_BASE = "http://localhost:8080/api/v1"

def analyze_website(url: str, user_id: str):
    # Start analysis
    response = requests.post(
        f"{API_BASE}/analyze",
        json={"url": url, "user_id": user_id}
    )
    response.raise_for_status()
    task_id = response.json()["task_id"]
    
    # Poll for completion
    while True:
        status_response = requests.get(f"{API_BASE}/status/{task_id}")
        status = status_response.json()
        
        print(f"Progress: {status['progress']}% - {status['current_stage']}")
        
        if status["status"] == "completed":
            results = requests.get(f"{API_BASE}/results/{task_id}").json()
            return results
        elif status["status"] == "failed":
            raise Exception(f"Analysis failed: {status['error']}")
        
        time.sleep(5)

# Usage
results = analyze_website("https://www.example.com", "user123")
print(f"Compliance Score: {results['summary']['compliance_score']}/10")
\`\`\`

### Async Python with aiohttp

\`\`\`python
import aiohttp
import asyncio

async def analyze_website_async(url: str, user_id: str):
    async with aiohttp.ClientSession() as session:
        # Start analysis
        async with session.post(
            f"{API_BASE}/analyze",
            json={"url": url, "user_id": user_id}
        ) as response:
            data = await response.json()
            task_id = data["task_id"]
        
        # Poll for completion
        while True:
            async with session.get(f"{API_BASE}/status/{task_id}") as response:
                status = await response.json()
            
            if status["status"] == "completed":
                async with session.get(f"{API_BASE}/results/{task_id}") as response:
                    results = await response.json()
                return results
            elif status["status"] == "failed":
                raise Exception(f"Analysis failed: {status['error']}")
            
            await asyncio.sleep(5)

# Usage
results = asyncio.run(analyze_website_async("https://www.example.com", "user123"))
\`\`\`

### Batch Processing

\`\`\`python
import asyncio
from typing import List

async def batch_analyze(urls: List[str], user_id: str):
    tasks = [
        analyze_website_async(url, user_id)
        for url in urls
    ]
    
    results = await asyncio.gather(*tasks, return_exceptions=True)
    
    successful = []
    failed = []
    
    for url, result in zip(urls, results):
        if isinstance(result, Exception):
            failed.append((url, str(result)))
        else:
            successful.append((url, result))
    
    return successful, failed

# Usage
urls = ["https://site1.com", "https://site2.com", "https://site3.com"]
successful, failed = asyncio.run(batch_analyze(urls, "user123"))
\`\`\`

## JavaScript/TypeScript Integration

### Basic Analysis

\`\`\`typescript
const API_BASE = 'http://localhost:8080/api/v1';

async function analyzeWebsite(url: string, userId: string) {
  // Start analysis
  const startResponse = await fetch(\`\${API_BASE}/analyze\`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ url, user_id: userId })
  });
  
  const { task_id } = await startResponse.json();
  
  // Poll for completion
  while (true) {
    const statusResponse = await fetch(\`\${API_BASE}/status/\${task_id}\`);
    const status = await statusResponse.json();
    
    console.log(\`Progress: \${status.progress}% - \${status.current_stage}\`);
    
    if (status.status === 'completed') {
      const resultsResponse = await fetch(\`\${API_BASE}/results/\${task_id}\`);
      return await resultsResponse.json();
    } else if (status.status === 'failed') {
      throw new Error(\`Analysis failed: \${status.error}\`);
    }
    
    await new Promise(resolve => setTimeout(resolve, 5000));
  }
}

// Usage
const results = await analyzeWebsite('https://www.example.com', 'user123');
console.log(\`Compliance Score: \${results.summary.compliance_score}/10\`);
\`\`\`

### React Hook

\`\`\`typescript
import { useState, useEffect } from 'react';

function useAnalysis(url: string, userId: string) {
  const [status, setStatus] = useState('idle');
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    if (!url || !userId) return;
    
    let taskId: string;
    let polling: NodeJS.Timeout;
    
    async function startAnalysis() {
      try {
        setStatus('starting');
        
        // Start analysis
        const response = await fetch(\`\${API_BASE}/analyze\`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ url, user_id: userId })
        });
        
        const data = await response.json();
        taskId = data.task_id;
        setStatus('in_progress');
        
        // Poll for status
        polling = setInterval(async () => {
          const statusResponse = await fetch(\`\${API_BASE}/status/\${taskId}\`);
          const statusData = await statusResponse.json();
          
          setProgress(statusData.progress);
          
          if (statusData.status === 'completed') {
            const resultsResponse = await fetch(\`\${API_BASE}/results/\${taskId}\`);
            const resultsData = await resultsResponse.json();
            setResults(resultsData);
            setStatus('completed');
            clearInterval(polling);
          } else if (statusData.status === 'failed') {
            setError(statusData.error);
            setStatus('failed');
            clearInterval(polling);
          }
        }, 5000);
        
      } catch (err) {
        setError(err.message);
        setStatus('failed');
      }
    }
    
    startAnalysis();
    
    return () => {
      if (polling) clearInterval(polling);
    };
  }, [url, userId]);
  
  return { status, progress, results, error };
}

// Usage in component
function AnalysisComponent() {
  const { status, progress, results, error } = useAnalysis(
    'https://www.example.com',
    'user123'
  );
  
  return (
    <div>
      {status === 'in_progress' && <div>Progress: {progress}%</div>}
      {status === 'completed' && <div>Score: {results.summary.compliance_score}</div>}
      {error && <div>Error: {error}</div>}
    </div>
  );
}
\`\`\`

## cURL Examples

### Start Analysis

\`\`\`bash
curl -X POST "http://localhost:8080/api/v1/analyze" \\
  -H "Content-Type: application/json" \\
  -d '{
    "url": "https://www.example.com",
    "user_id": "user123"
  }'
\`\`\`

### Check Status

\`\`\`bash
curl "http://localhost:8080/api/v1/status/550e8400-e29b-41d4-a716-446655440000"
\`\`\`

### Get Results

\`\`\`bash
curl "http://localhost:8080/api/v1/results/550e8400-e29b-41d4-a716-446655440000"
\`\`\`

### Health Check

\`\`\`bash
curl "http://localhost:8080/api/v1/health"
\`\`\`

## Legal Tech Integration

### Law Firm Compliance Tool

\`\`\`python
class ComplianceChecker:
    def __init__(self, api_base: str):
        self.api_base = api_base
        self.client = requests.Session()
    
    def check_client_website(self, client_id: str, website_url: str):
        """Check client website for GDPR compliance."""
        # Start analysis
        response = self.client.post(
            f"{self.api_base}/analyze",
            json={
                "url": website_url,
                "user_id": f"lawfirm_{client_id}",
                "metadata": {
                    "client_id": client_id,
                    "check_type": "gdpr_compliance"
                }
            }
        )
        task_id = response.json()["task_id"]
        
        # Wait for completion
        results = self.wait_for_completion(task_id)
        
        # Generate compliance report
        report = self.generate_compliance_report(results, client_id)
        
        return report
    
    def wait_for_completion(self, task_id: str, timeout: int = 300):
        """Wait for analysis to complete."""
        start_time = time.time()
        
        while time.time() - start_time < timeout:
            status = self.client.get(f"{self.api_base}/status/{task_id}").json()
            
            if status["status"] == "completed":
                return self.client.get(f"{self.api_base}/results/{task_id}").json()
            elif status["status"] == "failed":
                raise Exception(f"Analysis failed: {status['error']}")
            
            time.sleep(5)
        
        raise TimeoutError("Analysis timed out")
    
    def generate_compliance_report(self, results: dict, client_id: str):
        """Generate compliance report for client."""
        summary = results["summary"]
        
        report = {
            "client_id": client_id,
            "website": results["url"],
            "compliance_status": summary["compliance_status"],
            "compliance_score": summary["compliance_score"],
            "critical_issues": summary["critical_issues_count"],
            "recommendations": self.get_recommendations(results),
            "report_url": results["gcs_report_url"]
        }
        
        return report
    
    def get_recommendations(self, results: dict) -> list:
        """Extract recommendations from analysis."""
        # Access full report from Firebase or GCS
        # Parse recommendations from legal_compliance_analysis
        return [
            "Add reject button to cookie banner",
            "Provide granular cookie preferences",
            "Stop setting non-essential cookies before consent"
        ]

# Usage
checker = ComplianceChecker("http://localhost:8080/api/v1")
report = checker.check_client_website("client123", "https://client-website.com")
\`\`\`

## EMR System Integration

### Healthcare Compliance Checker

\`\`\`python
class EMRComplianceChecker:
    def __init__(self, api_base: str):
        self.api_base = api_base
    
    def check_patient_portal(self, portal_url: str, facility_id: str):
        """Check patient portal for HIPAA/GDPR compliance."""
        response = requests.post(
            f"{self.api_base}/analyze",
            json={
                "url": portal_url,
                "user_id": f"emr_{facility_id}",
                "metadata": {
                    "facility_id": facility_id,
                    "compliance_type": "hipaa_gdpr",
                    "portal_type": "patient_portal"
                }
            }
        )
        
        task_id = response.json()["task_id"]
        results = self.wait_for_completion(task_id)
        
        # Check for HIPAA-specific issues
        hipaa_issues = self.check_hipaa_compliance(results)
        
        return {
            "facility_id": facility_id,
            "portal_url": portal_url,
            "gdpr_compliance": results["summary"]["compliance_status"],
            "hipaa_issues": hipaa_issues,
            "recommendations": self.get_hipaa_recommendations(results)
        }
    
    def check_hipaa_compliance(self, results: dict) -> list:
        """Check for HIPAA-specific compliance issues."""
        issues = []
        
        # Check if tracking cookies are set before consent
        if results["summary"]["total_cookies_initial"] > 0:
            issues.append("Cookies set before user consent - HIPAA violation risk")
        
        # Check for proper consent mechanisms
        if not results["summary"]["banner_detected"]:
            issues.append("No cookie consent banner - HIPAA compliance issue")
        
        return issues
\`\`\`

## Finance Risk Engine Integration

### Financial Services Compliance

\`\`\`python
class FinanceComplianceEngine:
    def __init__(self, api_base: str):
        self.api_base = api_base
    
    def audit_financial_website(self, website_url: str, institution_id: str):
        """Audit financial institution website for compliance."""
        response = requests.post(
            f"{self.api_base}/analyze",
            json={
                "url": website_url,
                "user_id": f"finance_{institution_id}",
                "metadata": {
                    "institution_id": institution_id,
                    "sector": "financial_services",
                    "regulations": ["GDPR", "CCPA", "PCI-DSS"]
                }
            }
        )
        
        task_id = response.json()["task_id"]
        results = self.wait_for_completion(task_id)
        
        # Calculate risk score
        risk_score = self.calculate_risk_score(results)
        
        return {
            "institution_id": institution_id,
            "website": website_url,
            "compliance_score": results["summary"]["compliance_score"],
            "risk_score": risk_score,
            "regulatory_issues": self.identify_regulatory_issues(results),
            "action_required": risk_score > 7.0
        }
    
    def calculate_risk_score(self, results: dict) -> float:
        """Calculate overall risk score."""
        base_score = results["summary"]["compliance_score"]
        critical_issues = results["summary"]["critical_issues_count"]
        
        # Adjust for critical issues
        risk_score = 10 - base_score + (critical_issues * 0.5)
        
        return min(risk_score, 10.0)
\`\`\`

## Webhook Integration

### Webhook Handler

\`\`\`python
from flask import Flask, request

app = Flask(__name__)

@app.route('/webhook/analysis-complete', methods=['POST'])
def handle_analysis_complete():
    """Handle analysis completion webhook."""
    data = request.json
    
    task_id = data.get('task_id')
    status = data.get('status')
    summary = data.get('summary')
    
    if status == 'completed':
        # Process results
        compliance_score = summary.get('compliance_score')
        
        if compliance_score < 5.0:
            # Send alert for non-compliance
            send_alert(f"Website {data['url']} has low compliance score: {compliance_score}")
        
        # Store in database
        store_analysis_result(data)
        
        # Notify stakeholders
        notify_stakeholders(data)
    
    return {'status': 'received'}, 200

def send_alert(message: str):
    """Send compliance alert."""
    # Implement alerting (email, Slack, etc.)
    pass

def store_analysis_result(data: dict):
    """Store analysis result in database."""
    # Store in your database
    pass

def notify_stakeholders(data: dict):
    """Notify relevant stakeholders."""
    # Send notifications
    pass
\`\`\`

### Webhook Configuration

\`\`\`python
# When starting analysis, include webhook URL
response = requests.post(
    f"{API_BASE}/analyze",
    json={
        "url": "https://www.example.com",
        "user_id": "user123",
        "callback_url": "https://your-server.com/webhook/analysis-complete"
    }
)
\`\`\`

## Postman Collection

### Collection JSON

\`\`\`json
{
  "info": {
    "name": "Cookie Consent Analyzer API",
    "schema": "https://schema.getpostman.com/json/collection/v2.1.0/collection.json"
  },
  "item": [
    {
      "name": "Start Analysis",
      "request": {
        "method": "POST",
        "header": [{"key": "Content-Type", "value": "application/json"}],
        "body": {
          "mode": "raw",
          "raw": "{\\n  \\"url\\": \\"https://www.example.com\\",\\n  \\"user_id\\": \\"user123\\"\\n}"
        },
        "url": {
          "raw": "{{base_url}}/api/v1/analyze",
          "host": ["{{base_url}}"],
          "path": ["api", "v1", "analyze"]
        }
      }
    },
    {
      "name": "Check Status",
      "request": {
        "method": "GET",
        "url": {
          "raw": "{{base_url}}/api/v1/status/{{task_id}}",
          "host": ["{{base_url}}"],
          "path": ["api", "v1", "status", "{{task_id}}"]
        }
      }
    },
    {
      "name": "Get Results",
      "request": {
        "method": "GET",
        "url": {
          "raw": "{{base_url}}/api/v1/results/{{task_id}}",
          "host": ["{{base_url}}"],
          "path": ["api", "v1", "results", "{{task_id}}"]
        }
      }
    }
  ],
  "variable": [
    {"key": "base_url", "value": "http://localhost:8080"},
    {"key": "task_id", "value": ""}
  ]
}
\`\`\`

## Error Handling Examples

### Retry Logic

\`\`\`python
from tenacity import retry, stop_after_attempt, wait_exponential

@retry(
    stop=stop_after_attempt(3),
    wait=wait_exponential(multiplier=1, min=4, max=10)
)
def start_analysis_with_retry(url: str, user_id: str):
    response = requests.post(
        f"{API_BASE}/analyze",
        json={"url": url, "user_id": user_id}
    )
    response.raise_for_status()
    return response.json()["task_id"]
\`\`\`

### Timeout Handling

\`\`\`python
import signal

def analyze_with_timeout(url: str, user_id: str, timeout: int = 300):
    def timeout_handler(signum, frame):
        raise TimeoutError("Analysis timed out")
    
    signal.signal(signal.SIGALRM, timeout_handler)
    signal.alarm(timeout)
    
    try:
        results = analyze_website(url, user_id)
        signal.alarm(0)  # Cancel alarm
        return results
    except TimeoutError:
        return None
\`\`\`

## References

- [API Endpoints](./endpoints.html)
- [Data Schemas](./data-schemas.html)
- [Error Codes](./error-codes.html)
- [Authentication](./authentication.html)
`;

// Auto-generated from rate-limits.md
export const CookieConsentBackendDocumentation04ApiDocumentationRateLimits = `# Rate Limits & Quotas

## Overview

API rate limiting and quota management.

## Content

This section needs to be completed with detailed information.

## References

- [Main Documentation](../index.html)
- [Related Documentation](#)

---

*This documentation file is a template and needs to be completed with detailed content.*
`;

// Auto-generated from component-design.md
export const CookieConsentBackendDocumentation05ArchitectureComponentDesign = `# Component-Level Design

## Overview

This document provides detailed component-level design documentation for the Cookie Consent Analyzer backend, covering each major component's responsibilities, interfaces, and interactions.

## Component Architecture

\`\`\`mermaid
graph TB
    A[API Layer] --> B[Analysis Orchestrator]
    B --> C[Browser Automation]
    B --> D[LLM Analysis Engine]
    B --> E[Cloud Storage Manager]
    B --> F[Database Manager]
    C --> G[Browser Session Manager]
    D --> H[OpenAI API]
    E --> I[Google Cloud Storage]
    F --> J[Firebase Firestore]
\`\`\`

## Core Components

### 1. API Layer (\`api.py\`)

**Purpose**: HTTP request handling and API endpoints

**Responsibilities**:
- Request validation
- Task creation and management
- Background task orchestration
- Response formatting
- Error handling

**Key Classes**:
- \`FastAPI\`: Main application instance
- \`TaskStore\`: In-memory task storage
- \`AnalysisRequest\`: Request validation model
- \`AnalysisResponse\`: Response model

**Interfaces**:
\`\`\`python
@app.post("/api/v1/analyze")
async def start_analysis(request: AnalysisRequest) -> AnalysisResponse

@app.get("/api/v1/status/{task_id}")
async def get_status(task_id: str) -> StatusResponse

@app.get("/api/v1/results/{task_id}")
async def get_results(task_id: str) -> ResultsResponse
\`\`\`

**Dependencies**:
- FastAPI framework
- Pydantic for validation
- BackgroundTasks for async processing

### 2. Analysis Orchestrator (\`app.py\`)

**Purpose**: Coordinate the complete analysis workflow

**Responsibilities**:
- Stage management
- Browser automation coordination
- LLM analysis coordination
- Cloud storage coordination
- Result compilation

**Key Functions**:
\`\`\`python
async def analyze_cookie_banner_modular(
    url: str,
    status_callback: Optional[Callable[[int, str], None]] = None,
    user_id: Optional[str] = None
) -> AnalysisResult
\`\`\`

**Workflow Stages**:
1. Initialization
2. Stage 0: Initial page load
3. Stage 1: Banner detection
4. Stages 2-N: Button actions
5. Stage 6: Comprehensive LLM analysis
6. Cloud storage upload
7. Result compilation

**Data Structures**:
- \`CookieStage\`: Represents each analysis stage
- \`AnalysisResult\`: Complete analysis result
- \`BannerDetectionResult\`: Banner detection output

### 3. Browser Automation (\`utility/sessions.py\`)

**Purpose**: Web page interaction and cookie capture

**Responsibilities**:
- Browser instance management
- Page navigation
- Cookie capture via storage_state
- Screenshot capture
- Button interaction

**Key Functions**:
\`\`\`python
async def execute_action_and_capture(
    task: str,
    llm: ChatOpenAI,
    output_dir: Path,
    stage_number: int,
    action_name: str,
    max_steps: int = 10
) -> Tuple[List[Dict[str, Any]], Optional[Path]]
\`\`\`

**Browser Management**:
- Creates fresh browser per action
- Uses storage_state for cookie persistence
- Headless mode for efficiency
- Automatic cleanup

**Dependencies**:
- browser-use library
- Playwright (underlying browser)
- OpenAI LLM for vision

### 4. LLM Analysis Engine (\`utility/llm_analysis.py\`)

**Purpose**: AI-powered analysis using LLMs

**Responsibilities**:
- Banner detection (vision)
- Cookie data analysis
- UI/UX analysis
- Legal compliance analysis
- Executive summary generation

**Key Functions**:
\`\`\`python
async def detect_banner_with_llm(screenshot_path: Path) -> BannerDetectionResult

async def analyze_cookies_data_with_llm(
    cookies: List[Dict[str, Any]],
    stage_name: str
) -> Dict[str, Any]

async def perform_comprehensive_legal_compliance_analysis(
    url: str,
    banner_detection: BannerDetectionResult,
    all_stages: List[CookieStage],
    banner_ui_analysis: Optional[Dict[str, Any]]
) -> Dict[str, Any]
\`\`\`

**Analysis Types**:
1. Banner UI/UX Analysis
2. Cookie Analysis (Before Consent)
3. Cookie Analysis (After Accept)
4. Cookie Analysis (After Reject)
5. Legal Compliance Analysis
6. Overall Executive Summary

**Structured Output**:
- Uses LangChain structured output
- Pydantic models for validation
- Type-safe responses
- No JSON parsing errors

### 5. Cloud Storage Manager (\`utility/gcs_storage.py\`)

**Purpose**: File storage in Google Cloud Storage

**Responsibilities**:
- Screenshot uploads
- Report file uploads
- Public URL generation
- File organization

**Key Classes**:
\`\`\`python
class GCSStorageManager:
    def upload_screenshot(...) -> str
    def upload_json_report(...) -> str
    def batch_upload_screenshots(...) -> dict
\`\`\`

**File Organization**:
\`\`\`
cookie-analysis/
  {url_slug}/
    {timestamp}/
      screenshots/
      reports/
\`\`\`

**Features**:
- Automatic public URL generation
- Organized folder structure
- Batch upload support
- Error handling and retry

### 6. Database Manager (\`utility/firebase_storage.py\`)

**Purpose**: Structured data storage in Firebase Firestore

**Responsibilities**:
- User document management
- Analysis document creation
- Stage updates
- Analysis updates
- Query support

**Key Classes**:
\`\`\`python
class FirebaseManager:
    def create_analysis_document(...) -> str
    def update_stage(...) -> None
    def update_analysis(...) -> None
    def get_analysis_by_domain(...) -> List[Dict]
\`\`\`

**Data Organization**:
\`\`\`
report-user-ids/
  {user_id}/
    cookies-analysis-database/
      {analysis_doc_id}/
\`\`\`

**Features**:
- User-specific subcollections
- Incremental updates
- Statistics tracking
- Query support

## Utility Components

### 7. Data Models (\`utility/data_models.py\`)

**Purpose**: Type-safe data structures

**Key Models**:
- \`BannerDetectionResult\`: Banner detection output
- \`CookieAnalysisResult\`: Cookie analysis output
- \`CookieAnalysisItem\`: Individual cookie analysis
- \`BannerUIAnalysisResult\`: UI/UX analysis output
- \`PreferencesDialogAnalysisResult\`: Preferences dialog analysis

**Benefits**:
- Type safety
- Automatic validation
- Self-documenting
- IDE support

### 8. Prompt Templates (\`utility/prompts.py\`)

**Purpose**: Centralized LLM prompt management

**Key Functions**:
\`\`\`python
def create_initial_load_task(url: str) -> str
def create_accept_task(url: str, button_text: str) -> str
def create_reject_task(url: str, button_text: str) -> str
def create_manage_prefs_reject_task(url: str, button_text: str) -> str
\`\`\`

**Benefits**:
- Consistent formatting
- Easy to modify
- Version control
- A/B testing capability

### 9. Cleanup Manager (\`utility/cleanup.py\`)

**Purpose**: Resource cleanup and disk management

**Responsibilities**:
- Delete old analysis folders
- Enforce size limits
- Enforce folder count limits
- Clean up empty directories

**Key Classes**:
\`\`\`python
class FolderCleanupManager:
    def cleanup_old_folders() -> dict
    def cleanup_excess_folders() -> dict
    def cleanup_by_total_size() -> dict
    def perform_full_cleanup() -> dict
\`\`\`

**Configuration**:
- Max age: 6 hours (configurable)
- Max folders: 50 (configurable)
- Max size: 2GB (configurable)

## Component Interactions

### Analysis Flow

\`\`\`
API Layer
    ↓
Creates Task
    ↓
Background Task
    ↓
Analysis Orchestrator
    ↓
    ├─→ Browser Automation (sessions.py)
    │       ↓
    │   Browser-use Agent
    │       ↓
    │   Cookie Capture
    │       ↓
    │   Screenshot Capture
    │
    ├─→ LLM Analysis (llm_analysis.py)
    │       ↓
    │   OpenAI API
    │       ↓
    │   Structured Output
    │
    ├─→ Cloud Storage (gcs_storage.py)
    │       ↓
    │   GCS Upload
    │       ↓
    │   Public URLs
    │
    └─→ Database (firebase_storage.py)
            ↓
        Firestore Update
            ↓
        User Subcollection
\`\`\`

### Data Flow

\`\`\`
User Request
    ↓
API Validation (Pydantic)
    ↓
Task Creation
    ↓
Analysis Execution
    ↓
    ├─→ Browser → Cookies → Storage State
    ├─→ Screenshot → Base64 → LLM
    ├─→ LLM → Structured Output → Pydantic
    ├─→ Results → GCS Upload
    └─→ Results → Firebase Update
    ↓
Result Compilation
    ↓
Response Return
\`\`\`

## Component Interfaces

### Browser Automation Interface

\`\`\`python
async def execute_action_and_capture(
    task: str,                    # Task instructions
    llm: ChatOpenAI,              # LLM for vision
    output_dir: Path,             # Output directory
    stage_number: int,           # Stage identifier
    action_name: str,            # Action name
    max_steps: int = 10          # Max agent steps
) -> Tuple[
    List[Dict[str, Any]],        # Cookies
    Optional[Path]                # Screenshot path
]
\`\`\`

### LLM Analysis Interface

\`\`\`python
async def detect_banner_with_llm(
    screenshot_path: Path
) -> BannerDetectionResult

async def analyze_cookies_data_with_llm(
    cookies: List[Dict[str, Any]],
    stage_name: str
) -> Dict[str, Any]  # CookieAnalysisResult as dict
\`\`\`

### Storage Interface

\`\`\`python
class GCSStorageManager:
    def upload_screenshot(
        screenshot_path: Path,
        url_slug: str,
        timestamp: str,
        stage_name: str
    ) -> str  # Public URL

class FirebaseManager:
    def create_analysis_document(
        url: str,
        timestamp: str,
        banner_detection: Dict[str, Any]
    ) -> str  # Document ID
\`\`\`

## Design Patterns

### 1. Singleton Pattern

**Usage**: Manager classes (GCS, Firebase)

\`\`\`python
_gcs_manager: Optional[GCSStorageManager] = None

def get_gcs_manager() -> GCSStorageManager:
    global _gcs_manager
    if _gcs_manager is None:
        _gcs_manager = GCSStorageManager()
    return _gcs_manager
\`\`\`

### 2. Factory Pattern

**Usage**: Task creation

\`\`\`python
def create_accept_task(url: str, button_text: str) -> str:
    return f"""
    TASK: Accept cookie consent banner
    INSTRUCTIONS:
    1. Navigate to {url}
    ...
    """
\`\`\`

### 3. Strategy Pattern

**Usage**: Different button action strategies

\`\`\`python
if banner_has_accept and not banner_has_reject:
    # Strategy 1: Reject via preferences
elif banner_has_accept and banner_has_reject:
    # Strategy 2: Analyze preferences
# ...
\`\`\`

### 4. Observer Pattern

**Usage**: Status callbacks

\`\`\`python
def update_progress(progress: int, stage_name: str):
    task_store.update_task(task_id, progress=progress, current_stage=stage_name)

result = await analyze_cookie_banner_modular(
    url,
    status_callback=update_progress
)
\`\`\`

## Component Testing

### Unit Testing

\`\`\`python
# Test browser automation
async def test_execute_action_and_capture():
    cookies, screenshot = await execute_action_and_capture(...)
    assert len(cookies) > 0
    assert screenshot.exists()

# Test LLM analysis
async def test_detect_banner_with_llm():
    result = await detect_banner_with_llm(screenshot_path)
    assert isinstance(result, BannerDetectionResult)
\`\`\`

### Integration Testing

\`\`\`python
# Test full analysis flow
async def test_analysis_flow():
    result = await analyze_cookie_banner_modular(url)
    assert result.banner_detection is not None
    assert len(result.stages) > 0
\`\`\`

## Extensibility

### Adding New Analysis Types

1. Create Pydantic model in \`data_models.py\`
2. Add analysis function in \`llm_analysis.py\`
3. Integrate into orchestrator in \`app.py\`
4. Update Firebase schema if needed

### Adding New Button Actions

1. Create task template in \`prompts.py\`
2. Add execution function in \`app.py\`
3. Integrate into orchestrator flow
4. Update data models

### Adding New Storage Backends

1. Create manager class (e.g., \`aws_storage.py\`)
2. Implement same interface as GCS manager
3. Integrate into upload functions
4. Update error handling

## Performance Considerations

### Component Optimization

1. **Browser Automation**: Use headless, close promptly
2. **LLM Analysis**: Cache responses, batch requests
3. **Cloud Storage**: Use resumable uploads, batch operations
4. **Database**: Use batch writes, optimize queries

### Resource Management

- Browser instances: One per action, closed after use
- LLM clients: Reused across analyses
- Cloud clients: Singleton pattern
- Memory: Cleanup old analysis folders

## References

- [System Overview](./system-overview.html)
- [Data Flow & Storage](./data-flow-storage.html)
- [Code Examples](../01-code-documentation/code-examples.html)
`;

// Auto-generated from data-flow-storage.md
export const CookieConsentBackendDocumentation05ArchitectureDataFlowStorage = `# Data Flow & Storage

## Overview

On-device SLMs, GPU benchmarking, compliance repositories.

## Content

This section needs to be completed with detailed information.

## References

- [Main Documentation](../index.html)
- [Related Documentation](#)

---

*This documentation file is a template and needs to be completed with detailed content.*
`;

// Auto-generated from deployment-architecture.md
export const CookieConsentBackendDocumentation05ArchitectureDeploymentArchitecture = `# Deployment Architecture

## Overview

Multi-cloud, hybrid, on-prem deployment.

## Content

This section needs to be completed with detailed information.

## References

- [Main Documentation](../index.html)
- [Related Documentation](#)

---

*This documentation file is a template and needs to be completed with detailed content.*
`;

// Auto-generated from integration-points.md
export const CookieConsentBackendDocumentation05ArchitectureIntegrationPoints = `# Integration Points

## Overview

Law firms, hospitals, finance risk engines.

## Content

This section needs to be completed with detailed information.

## References

- [Main Documentation](../index.html)
- [Related Documentation](#)

---

*This documentation file is a template and needs to be completed with detailed content.*
`;

// Auto-generated from monitoring-observability.md
export const CookieConsentBackendDocumentation05ArchitectureMonitoringObservability = `# Monitoring & Observability

## Overview

Metrics, dashboards, compliance scoring rubrics.

## Content

This section needs to be completed with detailed information.

## References

- [Main Documentation](../index.html)
- [Related Documentation](#)

---

*This documentation file is a template and needs to be completed with detailed content.*
`;

// Auto-generated from scalability-resilience.md
export const CookieConsentBackendDocumentation05ArchitectureScalabilityResilience = `# Scalability & Resilience

## Overview

Load balancing, failover, continuous improvement.

## Content

This section needs to be completed with detailed information.

## References

- [Main Documentation](../index.html)
- [Related Documentation](#)

---

*This documentation file is a template and needs to be completed with detailed content.*
`;

// Auto-generated from security-privacy.md
export const CookieConsentBackendDocumentation05ArchitectureSecurityPrivacy = `# Security & Privacy Layers

## Overview

Encryption, sandboxing, assurance pipelines.

## Content

This section needs to be completed with detailed information.

## References

- [Main Documentation](../index.html)
- [Related Documentation](#)

---

*This documentation file is a template and needs to be completed with detailed content.*
`;

// Auto-generated from system-overview.md
export const CookieConsentBackendDocumentation05ArchitectureSystemOverview = `# System Overview

## Architecture Diagram

\`\`\`mermaid
graph TB
    subgraph "Client Layer"
        A[Web Browser]
        B[API Client]
        C[Command Line]
    end
    
    subgraph "API Layer"
        D[FastAPI Server]
        E[Task Store]
        F[Background Tasks]
    end
    
    subgraph "Business Logic"
        G[Analysis Orchestrator]
        H[Browser Automation]
        I[LLM Analysis Engine]
    end
    
    subgraph "External Services"
        J[OpenAI API]
        K[Google Cloud Storage]
        L[Firebase Firestore]
    end
    
    subgraph "Storage"
        M[Local File System]
        N[GCS Bucket]
        O[Firestore Database]
    end
    
    A --> D
    B --> D
    C --> G
    D --> E
    D --> F
    F --> G
    G --> H
    G --> I
    H --> J
    I --> J
    G --> K
    G --> L
    H --> M
    G --> N
    G --> O
\`\`\`

## System Components

### 1. API Layer (\`api.py\`)

**Technology**: FastAPI

**Responsibilities**:
- HTTP request handling
- Request validation (Pydantic)
- Task management
- Background task orchestration
- Response formatting

**Key Features**:
- Async request handling
- Automatic OpenAPI documentation
- CORS support
- Health check endpoint

### 2. Analysis Orchestrator (\`app.py\`)

**Technology**: Python AsyncIO

**Responsibilities**:
- Coordinate analysis workflow
- Manage analysis stages
- Integrate browser automation and LLM analysis
- Handle cloud storage operations
- Generate comprehensive reports

**Key Features**:
- Modular stage-based architecture
- Dynamic action execution
- Real-time status updates
- Error handling and recovery

### 3. Browser Automation (\`utility/sessions.py\`)

**Technology**: browser-use, Playwright

**Responsibilities**:
- Web page navigation
- Cookie capture via storage_state
- Screenshot capture
- Button interaction
- Page state management

**Key Features**:
- Automatic cookie persistence
- Vision-based interaction
- Headless browsing
- Fresh browser per action

### 4. LLM Analysis Engine (\`utility/llm_analysis.py\`)

**Technology**: LangChain, OpenAI GPT-4

**Responsibilities**:
- Banner detection (vision)
- Cookie data analysis
- UI/UX analysis
- Legal compliance analysis
- Executive summary generation

**Key Features**:
- Structured output validation
- Type-safe responses
- Multiple analysis types
- Comprehensive compliance checking

### 5. Cloud Storage (\`utility/gcs_storage.py\`)

**Technology**: Google Cloud Storage

**Responsibilities**:
- Screenshot storage
- Report file storage
- Public URL generation
- File organization

**Key Features**:
- Automatic public URL generation
- Organized folder structure
- Batch upload support

### 6. Database (\`utility/firebase_storage.py\`)

**Technology**: Firebase Firestore

**Responsibilities**:
- User-specific data storage
- Analysis result storage
- Real-time updates
- Query support

**Key Features**:
- User subcollections
- Incremental updates
- Document versioning
- Statistics tracking

## Technology Stack

### Backend Framework
- **FastAPI**: Modern async web framework
- **Uvicorn**: ASGI server

### Browser Automation
- **browser-use**: AI-powered browser automation
- **Playwright**: Browser control
- **Selenium**: Fallback option

### AI/ML
- **OpenAI GPT-4**: LLM for analysis
- **LangChain**: LLM framework
- **Pydantic**: Structured output validation

### Cloud Services
- **Google Cloud Storage**: File storage
- **Firebase Firestore**: Database
- **Google Cloud Run**: Deployment platform

### Data Processing
- **Pandas**: Data analysis
- **Pydantic**: Data validation

## Data Flow

### Request Flow

\`\`\`
1. Client Request
   ↓
2. API Endpoint (FastAPI)
   ↓
3. Task Creation (TaskStore)
   ↓
4. Background Task Start
   ↓
5. Analysis Orchestrator
   ↓
6. Browser Automation + LLM Analysis
   ↓
7. Cloud Storage Upload
   ↓
8. Database Update
   ↓
9. Result Return
\`\`\`

### Analysis Flow

\`\`\`
Initial Page Load
    ↓
Banner Detection (LLM Vision)
    ↓
Button Actions (Browser Automation)
    ↓
Cookie Capture (Storage State)
    ↓
LLM Analysis (6 Types)
    ↓
Cloud Storage (GCS + Firebase)
    ↓
Report Generation
\`\`\`

## Component Interaction

### API → Orchestrator

\`\`\`python
# API receives request
@app.post("/api/v1/analyze")
async def start_analysis(request: AnalysisRequest):
    # Create task
    task_id = str(uuid.uuid4())
    task_store.create_task(task_id, ...)
    
    # Start background task
    background_tasks.add_task(
        process_analysis_task,
        task_id, request.url, request.user_id
    )
    
    return AnalysisResponse(task_id=task_id, ...)
\`\`\`

### Orchestrator → Browser Automation

\`\`\`python
# Orchestrator calls browser automation
cookies, screenshot = await execute_action_and_capture(
    task=task,
    llm=llm,
    output_dir=output_dir,
    stage_number=stage_number,
    action_name="accept"
)
\`\`\`

### Orchestrator → LLM Analysis

\`\`\`python
# Orchestrator calls LLM analysis
banner_detection = await detect_banner_with_llm(screenshot)
legal_analysis = await perform_comprehensive_legal_compliance_analysis(...)
\`\`\`

### Orchestrator → Cloud Storage

\`\`\`python
# Upload to GCS
screenshot_url = gcs_manager.upload_screenshot(...)

# Update Firebase
firebase_manager.update_stage(
    doc_id=firebase_doc_id,
    stage_data={"screenshot_url": screenshot_url, ...}
)
\`\`\`

## Scalability Architecture

### Horizontal Scaling

- **Stateless API**: Can run multiple instances
- **Task Store**: Currently in-memory (use Redis for production)
- **Cloud Run**: Auto-scales based on traffic

### Vertical Scaling

- **Memory**: 2GB minimum (browser automation is memory-intensive)
- **CPU**: 2 cores minimum for concurrent operations
- **Storage**: 10GB for temporary files

### Load Balancing

- Use Google Cloud Load Balancer
- Health checks on \`/api/v1/health\`
- Session affinity not required (stateless)

## Security Architecture

### Authentication

- **Current**: user_id in request body
- **Recommended**: OAuth2 or JWT tokens
- **Future**: API keys per client

### Data Privacy

- User-specific subcollections in Firebase
- Encrypted data in transit (HTTPS)
- Access control via Firebase security rules

### Network Security

- HTTPS only in production
- CORS configuration
- Rate limiting (to be implemented)

## Monitoring & Observability

### Health Checks

- Endpoint: \`/api/v1/health\`
- Checks: OpenAI, Firebase, GCS connectivity
- Response: Service status

### Logging

- Structured logging to Cloud Logging
- Log levels: DEBUG, INFO, WARNING, ERROR
- Request/response logging

### Metrics

- Analysis completion rate
- Average analysis time
- Error rates
- API request counts

## Deployment Architecture

### Development

- Local Python environment
- Local file storage
- Optional cloud services

### Staging

- Cloud Run deployment
- Cloud services enabled
- Test data isolation

### Production

- Cloud Run with auto-scaling
- Multiple regions
- CDN for static assets
- Database replication

## Integration Points

### Frontend Integration

- REST API endpoints
- WebSocket for real-time updates (future)
- Polling pattern for status

### External Integrations

- **OpenAI API**: LLM analysis
- **Google Cloud**: Storage and database
- **Webhooks**: Completion notifications

## Performance Characteristics

### Analysis Time

- **Average**: 90-180 seconds
- **Fastest**: 60 seconds (simple banner)
- **Slowest**: 300 seconds (complex preferences)

### Resource Usage

- **Memory**: 1-2GB per analysis
- **CPU**: 1-2 cores per analysis
- **Network**: High bandwidth for LLM calls

### Concurrent Capacity

- **Current**: Limited by in-memory task store
- **With Redis**: 100+ concurrent analyses
- **Cloud Run**: Auto-scales to demand

## Future Architecture Enhancements

### Planned

1. **Queue System**: Celery/RQ for task queuing
2. **Caching**: Redis for LLM response caching
3. **Monitoring**: Prometheus + Grafana
4. **Logging**: ELK stack
5. **Microservices**: Split into separate services

### Considerations

- Event-driven architecture
- GraphQL API layer
- Multiple LLM provider support
- Real-time WebSocket updates

## References

- [Component Design](./component-design.html)
- [Data Flow & Storage](./data-flow-storage.html)
- [Deployment Architecture](./deployment-architecture.html)
- [API Documentation](../04-api-documentation/endpoints.html)
`;

// Auto-generated from core-design-philosophy.md
export const CookieConsentFrontendDocumentation01CodeDocumentationCoreDesignPhilosophy = `# Core Design Philosophy - Frontend

## Overview

The Cookie Consent Analyzer frontend is built with React, TypeScript, and Styled Components, following modern React patterns and best practices. This document outlines the core design principles and architectural decisions.

## Design Principles

### 1. Component-Based Architecture

The application is built using reusable, composable React components:

\`\`\`
src/
├── components/
│   ├── Auth/              # Authentication components
│   ├── Layout/            # Layout components (Sidebar, DashboardLayout)
│   ├── Pages/             # Page-level components
│   └── UI/                # Reusable UI components (Button, Input, Card)
├── services/              # Service layer (API, Auth, Firebase)
├── styles/                # Theme and global styles
└── types/                 # TypeScript type definitions
\`\`\`

**Benefits**:
- Reusability
- Maintainability
- Testability
- Clear separation of concerns

### 2. Type Safety with TypeScript

All components and functions use TypeScript for type safety:

\`\`\`typescript
interface CookieAnalyzerPageProps {
  userSession?: {
    userId: string;
    email: string;
    first_name?: string;
    last_name?: string;
    loggedInAt: Date;
  } | null;
}

export const CookieAnalyzerPage: React.FC<CookieAnalyzerPageProps> = ({ userSession }) => {
  // Component implementation
};
\`\`\`

**Benefits**:
- Catch errors at compile time
- Better IDE support
- Self-documenting code
- Refactoring safety

### 3. Styled Components for Styling

CSS-in-JS using Styled Components with theme support:

\`\`\`typescript
import styled from 'styled-components';
import { theme } from '../../styles/theme';

const PageTitle = styled.h1\`
  font-size: \${theme.typography.fontSizes.xxl};
  font-weight: \${theme.typography.fontWeights.semibold};
  color: \${theme.colors.primary.darker};
  margin-bottom: \${theme.spacing.xl};
\`;
\`\`\`

**Benefits**:
- Scoped styles
- Theme integration
- Dynamic styling
- No CSS conflicts

### 4. Service Layer Pattern

Business logic separated into service classes:

\`\`\`typescript
// services/apiService.ts
export class ApiService {
  async startAnalysis(url: string, options?: AnalysisRequest): Promise<AnalysisResponse> {
    // API call logic
  }
  
  async pollTaskStatus(taskId: string, onProgress: ProgressCallback): Promise<StatusResponse> {
    // Polling logic
  }
}
\`\`\`

**Benefits**:
- Separation of concerns
- Easy to mock for testing
- Centralized API logic
- Reusable across components

## Component Architecture

### Page Components

Page-level components orchestrate the UI:

- **CookieAnalyzerPage**: Main analysis page
- **AnalysisHistoryPage**: History of analyses
- **AnalysisResultsDashboard**: Results display
- **OverviewDisclaimerPage**: Overview and disclaimer

### Layout Components

Layout components provide structure:

- **DashboardLayout**: Main application layout with sidebar
- **Sidebar**: Navigation sidebar
- **Container**: Content container

### UI Components

Reusable UI building blocks:

- **Button**: Multiple variants (primary, secondary, outline, ghost)
- **Input**: Form inputs with validation
- **Card**: Flexible containers
- **ProgressBar**: Progress indicators
- **CookieBannerPreview**: Banner preview component

## State Management

### Local State (useState)

Component-specific state:

\`\`\`typescript
const [websiteUrl, setWebsiteUrl] = useState('https://example.com');
const [analysisState, setAnalysisState] = useState<AnalysisState>('initial');
const [progress, setProgress] = useState(0);
\`\`\`

### Session State (localStorage)

User session persisted in localStorage:

\`\`\`typescript
// services/authService.ts
static saveSession(session: UserSession): void {
  localStorage.setItem(this.SESSION_KEY, JSON.stringify(session));
}

static getSession(): UserSession | null {
  const sessionData = localStorage.getItem(this.SESSION_KEY);
  return sessionData ? JSON.parse(sessionData) : null;
}
\`\`\`

### Server State (API Polling)

Analysis status polled from API:

\`\`\`typescript
const apiService = getApiService();
const taskId = await apiService.analyzeWithPolling(
  url,
  handleStatusUpdate,  // Progress callback
  handleComplete,       // Completion callback
  handleError           // Error callback
);
\`\`\`

## Styling System

### Theme-Based Design

Centralized theme in \`styles/theme.ts\`:

\`\`\`typescript
export const theme = {
  colors: {
    primary: { darker: '#2E4258' },
    neutral: { 100: '#FFFFFF', 300: '#EFF0F6' },
    black: { 100: '#000000' }
  },
  spacing: { xs: '4px', sm: '8px', md: '12px', ... },
  typography: { fontFamily: 'Inter, ...', fontSizes: {...}, ... },
  borderRadius: { sm: '4px', md: '8px', ... },
  shadows: { sm: '...', md: '...', ... }
};
\`\`\`

### Responsive Design

Mobile-first approach with breakpoints:

\`\`\`typescript
const ContentGrid = styled.div\`
  display: grid;
  grid-template-columns: 1fr 400px;
  gap: \${theme.spacing.xl};

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
\`;
\`\`\`

## API Integration Pattern

### Async Task Flow

1. **Start Analysis**: POST to \`/api/v1/analyze\`
2. **Poll Status**: GET \`/api/v1/status/{task_id}\` with exponential backoff
3. **Get Results**: GET \`/api/v1/results/{task_id}\` when complete

\`\`\`typescript
async analyzeWithPolling(
  url: string,
  onProgress: ProgressCallback,
  onComplete: CompletionCallback,
  onError: ErrorCallback
): Promise<string> {
  // Start analysis
  const response = await this.startAnalysis(url);
  const taskId = response.task_id;
  
  // Poll for status
  const finalStatus = await this.pollTaskStatus(taskId, onProgress);
  
  // Get results when complete
  if (finalStatus.status === 'completed') {
    const results = await this.getTaskResults(taskId);
    onComplete(results);
  }
  
  return taskId;
}
\`\`\`

## Authentication Flow

### Login Process

1. User enters email/password
2. Query Firebase \`report-user-ids\` collection
3. Verify password
4. Create user session
5. Store in localStorage
6. Redirect to main app

### Session Management

- Session stored in localStorage
- Restored on page load
- Cleared on logout
- Optional Firebase verification

## Error Handling

### API Errors

\`\`\`typescript
try {
  const results = await apiService.getTaskResults(taskId);
} catch (error) {
  if (error instanceof Error) {
    setError(error.message);
    setAnalysisState('error');
  }
}
\`\`\`

### User-Friendly Error Messages

\`\`\`typescript
const ErrorContainer = styled.div\`
  // Error display styling
\`;

// Display errors to user
{error && (
  <ErrorContainer>
    <ErrorTitle>Analysis Failed</ErrorTitle>
    <ErrorMessage>{error}</ErrorMessage>
    <Button onClick={handleRetry}>Try Again</Button>
  </ErrorContainer>
)}
\`\`\`

## Performance Optimization

### Code Splitting

\`\`\`typescript
// Lazy load components
const AnalysisResultsDashboard = React.lazy(() => 
  import('./AnalysisResultsDashboard')
);

// Use Suspense
<Suspense fallback={<Loading />}>
  <AnalysisResultsDashboard />
</Suspense>
\`\`\`

### Memoization

\`\`\`typescript
const MemoizedComponent = React.memo(({ data }) => {
  // Component that only re-renders when data changes
});
\`\`\`

## Best Practices

### Component Organization

- One component per file
- Co-locate related files
- Clear naming conventions
- Export defaults for pages

### Type Safety

- Define interfaces for all props
- Use TypeScript strict mode
- Avoid \`any\` types
- Use type guards

### Styling

- Use theme values, not hardcoded values
- Responsive design first
- Consistent spacing
- Accessible colors

### State Management

- Keep state as local as possible
- Lift state only when needed
- Use callbacks for parent communication
- Avoid prop drilling

## Testing Strategy

### Unit Tests

Test individual components:

\`\`\`typescript
import { render, screen } from '@testing-library/react';
import Button from './Button';

test('renders button with text', () => {
  render(<Button>Click me</Button>);
  expect(screen.getByText('Click me')).toBeInTheDocument();
});
\`\`\`

### Integration Tests

Test component interactions:

\`\`\`typescript
test('analysis flow', async () => {
  render(<CookieAnalyzerPage />);
  // Simulate user actions
  // Verify API calls
  // Check results display
});
\`\`\`

## Build & Deployment

### Development

\`\`\`bash
npm start
# Runs on http://localhost:3000
\`\`\`

### Production Build

\`\`\`bash
npm run build
# Creates optimized build in build/ directory
\`\`\`

### Environment Variables

\`\`\`env
REACT_APP_API_URL=http://localhost:8080
REACT_APP_FIREBASE_API_KEY=...
REACT_APP_FIREBASE_AUTH_DOMAIN=...
\`\`\`

## References

- [React Documentation](https://react.dev/)
- [TypeScript Handbook](https://www.typescriptlang.org/docs/)
- [Styled Components](https://styled-components.com/)
- [Firebase Documentation](https://firebase.google.com/docs)

`;

// Auto-generated from api-versioning.md
export const MainToolDocumentationApiDocumentationApiVersioning = `# API Versioning & Lifecycle Management

## Versioning Strategy

### Current: Single Version

Currently, the API has a single version (implicit v1).

### Future: URL-Based Versioning

**Format**: \`/api/v1/\`, \`/api/v2/\`

**Example**:
- Current: \`/get_image_metadata\`
- Versioned: \`/api/v1/get_image_metadata\`

## Version Lifecycle

### Version States

1. **Development**: New version in development
2. **Beta**: Testing with select users
3. **Stable**: Production-ready
4. **Deprecated**: Scheduled for removal
5. **Retired**: No longer supported

## Deprecation Policy

### Timeline

- **Announcement**: 3 months before deprecation
- **Deprecation Period**: 6 months
- **Removal**: After deprecation period

### Communication

- Release notes
- API documentation updates
- Email notifications
- In-app warnings

## Migration Guides

When breaking changes occur:
1. Document changes
2. Provide migration guide
3. Support both versions during transition
4. Sunset old version after migration period

## Related Documentation

- [Versioning](../code-documentation/versioning.md)
- [Endpoints](endpoints.md)

`;

// Auto-generated from authentication.md
export const MainToolDocumentationApiDocumentationAuthentication = `# Authentication & Security

## Overview

This document details authentication mechanisms, token management, and security protocols for the Dark Pattern Detection & Compliance Analysis Tool API.

## Firebase OAuth2 Flow

### Authentication Process

1. User clicks "Sign In with Google"
2. Frontend initiates Firebase OAuth
3. User authenticates with Google
4. Firebase returns ID token
5. Frontend verifies email authorization
6. Token used for API requests

### Token Structure

**Firebase ID Token**:
\`\`\`json
{
  "iss": "https://securetoken.google.com/project-id",
  "aud": "project-id",
  "auth_time": 1234567890,
  "user_id": "user123",
  "sub": "user123",
  "email": "user@example.com",
  "email_verified": true,
  "exp": 1234567890,
  "iat": 1234567890
}
\`\`\`

## API Authentication

### Request Headers

**Required Header**:
\`\`\`
Authorization: Bearer <firebase_id_token>
\`\`\`

**Example**:
\`\`\`bash
curl -X POST https://api.example.com/endpoint \\
  -H "Authorization: Bearer eyJhbGciOiJSUzI1NiIs..." \\
  -H "Content-Type: application/json" \\
  -d '{"data": "value"}'
\`\`\`

### Token Verification

**Backend Verification**:
\`\`\`python
from firebase_admin import auth

def verify_token(id_token):
    try:
        decoded_token = auth.verify_id_token(id_token)
        return decoded_token
    except Exception as e:
        raise UnauthorizedError(f"Invalid token: {e}")
\`\`\`

## Session Handling

### Token Refresh

Firebase automatically refreshes tokens:
- Tokens expire after 1 hour
- Automatic refresh before expiration
- Seamless user experience

### Token Storage

**Current**: Local storage (browser)
**Recommended**: HTTP-only cookies for enhanced security

## Privacy-Preserving Protocols

### Data Minimization

- Only collect necessary data
- No PII stored unnecessarily
- Encrypted data transmission

### GDPR Compliance

- User consent for data processing
- Right to access data
- Right to deletion
- Data portability

## Security Best Practices

1. **HTTPS Only**: All API calls use HTTPS
2. **Token Validation**: Server-side token verification
3. **Rate Limiting**: Prevent abuse
4. **Input Validation**: Sanitize all inputs
5. **Error Handling**: Don't expose sensitive info

## Related Documentation

- [Endpoints](endpoints.md)
- [Security & Compliance](../code-documentation/security-compliance.md)

`;

// Auto-generated from data-schemas.md
export const MainToolDocumentationApiDocumentationDataSchemas = `# Data Schemas

## Overview

This document defines all JSON data structures used in API requests and responses.

## Request Schemas

### Image Analysis Request

\`\`\`json
{
  "image_url": "https://storage.googleapis.com/...",
  "url": "https://example.com/page",
  "note": "User notes",
  "session_id": "session_123",
  "journey_id": "journey_456",
  "image_doc_id": "image_789"
}
\`\`\`

### File Upload Request

**Multipart Form Data**:
- \`image\` (file): Image file
- \`url\` (string): Source URL
- \`note\` (string): User notes
- \`session_id\` (string): Session ID
- \`journey_id\` (string): Journey ID
- \`image_doc_id\` (string): Image document ID

## Response Schemas

### Analysis Response

\`\`\`json
{
  "status": "completed",
  "image_id": "image_789",
  "pageName": "Example Page",
  "response": [
    {
      "type": "outcome",
      "text": "Dark pattern description",
      "coordinates": {"x": 100, "y": 200},
      "parent_type": "Coercing",
      "dark_pattern": "Forced Action",
      "dark_pattern_details": {
        "name": "Forced Action",
        "type": "Coercing",
        "context": "...",
        "source_urls": [],
        "reference_urls": []
      },
      "laws": [
        {
          "name": "GDPR",
          "description": "...",
          "relevance": "...",
          "url": "..."
        }
      ],
      "fair_patterns": [
        {
          "name": "Clear Consent",
          "description": "...",
          "implementation": "..."
        }
      ]
    }
  ],
  "processing_metadata": {
    "duration": 220,
    "llm_calls": 45,
    "tokens_used": 50000
  }
}
\`\`\`

### Error Response

\`\`\`json
{
  "status": "error",
  "error": "Error message",
  "error_code": "ERROR_CODE",
  "details": {
    "field": "Additional details"
  }
}
\`\`\`

## Validation Rules

### Required Fields

**Image Analysis**:
- \`url\`: Required, must be valid URL
- \`session_id\`: Required, string
- \`journey_id\`: Required, string
- \`image_doc_id\`: Required, string

### Field Constraints

- **URL**: Must start with http:// or https://
- **File Size**: Maximum 10MB
- **File Types**: PNG, JPG, JPEG, WebP, BMP
- **String Lengths**: Varies by field

## Related Documentation

- [Endpoints](endpoints.md)
- [Error Codes](error-codes.md)

`;

// Auto-generated from endpoints.md
export const MainToolDocumentationApiDocumentationEndpoints = `# API Endpoints & Methods

## Overview

This document provides comprehensive documentation for all API endpoints in the Dark Pattern Detection & Compliance Analysis Tool, including request/response schemas, authentication requirements, and usage examples.

## Base URL

**Production**: \`https://reactserver-757095405629.us-central1.run.app\`

**Development**: \`http://localhost:5000\`

## Authentication

All endpoints require Firebase authentication token in the Authorization header:

\`\`\`
Authorization: Bearer <firebase_id_token>
\`\`\`

## Endpoints

### 1. Get Image Metadata

**Endpoint**: \`POST /get_image_metadata\`

**Description**: Retrieves metadata for an image, processing it if not already processed.

**Request Body**:
\`\`\`json
{
  "image_url": "https://storage.googleapis.com/bucket/image.png",
  "url": "https://example.com/page",
  "note": "User notes about the image",
  "session_id": "session_123",
  "journey_id": "journey_456",
  "image_doc_id": "image_789"
}
\`\`\`

**Response** (200 OK):
\`\`\`json
{
  "status": "completed",
  "image_id": "image_789",
  "pageName": "Example Page",
  "website_brief": "...",
  "webpage_brief": "...",
  "response": [
    {
      "type": "outcome",
      "text": "Dark pattern detected",
      "coordinates": {"x": 100, "y": 200},
      "parent_type": "Coercing",
      "dark_pattern": "Forced Action",
      "laws": [...],
      "fair_patterns": [...]
    }
  ],
  "processing_metadata": {
    "duration": 220,
    "llm_calls": 45,
    "tokens_used": 50000
  }
}
\`\`\`

**Error Response** (400/500):
\`\`\`json
{
  "error": "Error message",
  "status": "error"
}
\`\`\`

### 2. Upload and Process for AI Agents

**Endpoint**: \`POST /upload_and_process_for_aiagents\`

**Description**: Uploads an image and processes Step 1 only (information extraction).

**Request**: Multipart form data
- \`image\` (file, optional): Image file
- \`image_url\` (string, optional): Image URL (if image not provided)
- \`url\` (string, required): Source webpage URL
- \`note\` (string, optional): User notes
- \`session_id\` (string, required): Session ID
- \`journey_id\` (string, required): Journey ID
- \`image_doc_id\` (string, required): Image document ID

**Response** (200 OK):
\`\`\`json
{
  "status": "success",
  "data": {
    "public_url": "https://storage.googleapis.com/...",
    "step1_result": {
      "pageName": "...",
      "website_brief": "...",
      "webpage_brief": "...",
      "context": "..."
    },
    "status": "step1_completed"
  }
}
\`\`\`

### 3. Upload and Process (Full Pipeline)

**Endpoint**: \`POST /upload_and_process\`

**Description**: Uploads an image and processes through all steps.

**Request**: Same as \`/upload_and_process_for_aiagents\`

**Response** (200 OK):
\`\`\`json
{
  "status": "success",
  "data": {
    "public_url": "https://storage.googleapis.com/...",
    "response": [...],
    "status": "completed"
  }
}
\`\`\`

### 4. Process Screen Capture (Background)

**Endpoint**: \`POST /process_screen_capture\`

**Description**: Background processing endpoint triggered by Cloud Tasks.

**Request Body**:
\`\`\`json
{
  "session_id": "session_123",
  "journey_id": "journey_456",
  "image_doc_id": "image_789",
  "public_url": "https://storage.googleapis.com/...",
  "session_name": "session_name",
  "url": "https://example.com",
  "note": "User notes"
}
\`\`\`

**Response** (200 OK):
\`\`\`json
{
  "status": "success"
}
\`\`\`

### 5. Process Figma Images

**Endpoint**: \`POST /process_figma_images\`

**Description**: Simplified analysis for Figma images (steps 1 and 2 only).

**Request**: Multipart form data
- \`image\` (file, required): Figma image file
- \`url\` (string, optional): Source URL

**Response** (200 OK):
\`\`\`json
{
  "status": "success",
  "data": {
    "public_url": "https://storage.googleapis.com/...",
    "response": {
      "ui_features": "...",
      "dark_pattern_analysis": "..."
    },
    "image_id": "img_123"
  }
}
\`\`\`

### 6. Upload Files

**Endpoint**: \`POST /upload-files\`

**Description**: Upload multiple files to Google Cloud Storage.

**Request**: Multipart form data
- \`files\` (files, required): Multiple files
- \`environmentName\` (string, optional): Environment name
- \`documentId\` (string, optional): Document ID
- \`tabNumber\` (string, optional): Tab number (3 or 4)

**Response** (200 OK):
\`\`\`json
{
  "success": true,
  "message": "Successfully uploaded 5 files",
  "documentId": "doc_123",
  "environmentName": "production",
  "tabNumber": "3",
  "uploadedFiles": [
    {
      "id": "file_123",
      "originalName": "document.pdf",
      "fileName": "20240115_123456_abc123.pdf",
      "fileSize": 1024000,
      "mimeType": "application/pdf",
      "gcsPath": "production/doc_123/tab3/20240115_123456_abc123.pdf",
      "publicUrl": "https://storage.googleapis.com/...",
      "uploadedAt": "2024-01-15T12:34:56Z"
    }
  ],
  "failedFiles": [],
  "totalFiles": 5,
  "successfulUploads": 5,
  "failedUploads": 0
}
\`\`\`

### 7. Get XAI Details

**Endpoint**: \`POST /get_xai_details\`

**Description**: Get explainable AI details for a specific point.

**Request Body**:
\`\`\`json
{
  "point": "Dark pattern description",
  "session_id": "session_123",
  "journey_id": "journey_456",
  "image_id": "image_789"
}
\`\`\`

**Response** (200 OK):
\`\`\`json
{
  "status": "success",
  "data": {
    "explanation": "...",
    "reasoning": "...",
    "evidence": [...]
  }
}
\`\`\`

### 8. Chat with Image

**Endpoint**: \`POST /chat_with_image\`

**Description**: Chat with LLM about an image based on its context.

**Request Body**:
\`\`\`json
{
  "session_id": "session_123",
  "journey_id": "journey_456",
  "image_id": "image_789",
  "question": "Why was this pattern detected?"
}
\`\`\`

**Response** (200 OK):
\`\`\`json
{
  "status": "success",
  "data": {
    "response": "The pattern was detected because..."
  }
}
\`\`\`

## Error Codes

### HTTP Status Codes

- \`200 OK\`: Request successful
- \`400 Bad Request\`: Invalid request data
- \`401 Unauthorized\`: Authentication required
- \`403 Forbidden\`: Insufficient permissions
- \`404 Not Found\`: Resource not found
- \`500 Internal Server Error\`: Server error
- \`503 Service Unavailable\`: Service temporarily unavailable

### Error Response Format

\`\`\`json
{
  "status": "error",
  "error": "Error message",
  "code": "ERROR_CODE",
  "details": {
    "field": "Additional error details"
  }
}
\`\`\`

## Rate Limits

**Current**: Managed by LLM API providers

**Recommended**: Implement application-level rate limiting
- 10 requests per minute per user
- 100 requests per hour per user
- 1000 requests per day per user

## Related Documentation

- [Authentication](authentication.md)
- [Data Schemas](data-schemas.md)
- [Error Codes](error-codes.md)

`;

// Auto-generated from error-codes.md
export const MainToolDocumentationApiDocumentationErrorCodes = `# Error Codes & Responses

## HTTP Status Codes

- **200 OK**: Request successful
- **400 Bad Request**: Invalid request data
- **401 Unauthorized**: Authentication required
- **403 Forbidden**: Insufficient permissions
- **404 Not Found**: Resource not found
- **500 Internal Server Error**: Server error
- **503 Service Unavailable**: Service temporarily unavailable

## Custom Error Codes

### INVALID_INPUT
**Status**: 400
**Description**: Invalid or missing required fields
**Example**:
\`\`\`json
{
  "status": "error",
  "error": "Missing required field: url",
  "error_code": "INVALID_INPUT"
}
\`\`\`

### PROCESSING_FAILED
**Status**: 500
**Description**: Analysis processing failed
**Example**:
\`\`\`json
{
  "status": "error",
  "error": "Analysis processing failed",
  "error_code": "PROCESSING_FAILED"
}
\`\`\`

### TIMEOUT
**Status**: 504
**Description**: Request timeout
**Example**:
\`\`\`json
{
  "status": "error",
  "error": "Request timeout",
  "error_code": "TIMEOUT"
}
\`\`\`

### RATE_LIMIT
**Status**: 429
**Description**: Rate limit exceeded
**Example**:
\`\`\`json
{
  "status": "error",
  "error": "Rate limit exceeded",
  "error_code": "RATE_LIMIT",
  "retry_after": 60
}
\`\`\`

### STORAGE_ERROR
**Status**: 500
**Description**: Storage operation failed
**Example**:
\`\`\`json
{
  "status": "error",
  "error": "Failed to upload image",
  "error_code": "STORAGE_ERROR"
}
\`\`\`

### AUTH_ERROR
**Status**: 401
**Description**: Authentication failed
**Example**:
\`\`\`json
{
  "status": "error",
  "error": "Invalid or expired token",
  "error_code": "AUTH_ERROR"
}
\`\`\`

### NOT_FOUND
**Status**: 404
**Description**: Resource not found
**Example**:
\`\`\`json
{
  "status": "error",
  "error": "Session not found",
  "error_code": "NOT_FOUND"
}
\`\`\`

## Error Response Format

All errors follow this structure:
\`\`\`json
{
  "status": "error",
  "error": "Human-readable error message",
  "error_code": "ERROR_CODE",
  "details": {
    "field": "Additional context"
  },
  "suggestion": "What user can do"
}
\`\`\`

## Related Documentation

- [Endpoints](endpoints.md)
- [Data Schemas](data-schemas.md)

`;

// Auto-generated from integration-examples.md
export const MainToolDocumentationApiDocumentationIntegrationExamples = `# Integration Examples

## Overview

Code examples for integrating the Dark Pattern Detection API into various applications.

## Python Integration

### Basic Analysis

\`\`\`python
import requests

def analyze_image(image_url, url, note, session_id, journey_id, image_id):
    url = "https://api.example.com/get_image_metadata"
    headers = {
        "Authorization": f"Bearer {firebase_token}",
        "Content-Type": "application/json"
    }
    data = {
        "image_url": image_url,
        "url": url,
        "note": note,
        "session_id": session_id,
        "journey_id": journey_id,
        "image_doc_id": image_id
    }
    response = requests.post(url, json=data, headers=headers)
    return response.json()
\`\`\`

## JavaScript Integration

### React Component

\`\`\`javascript
import axios from 'axios';

async function analyzeImage(imageFile, url, note, sessionId, journeyId, imageId) {
  const formData = new FormData();
  formData.append('image', imageFile);
  formData.append('url', url);
  formData.append('note', note);
  formData.append('session_id', sessionId);
  formData.append('journey_id', journeyId);
  formData.append('image_doc_id', imageId);

  const response = await axios.post(
    'https://api.example.com/upload_and_process',
    formData,
    {
      headers: {
        'Authorization': \`Bearer \${firebaseToken}\`,
        'Content-Type': 'multipart/form-data'
      }
    }
  );
  
  return response.data;
}
\`\`\`

## Related Documentation

- [Endpoints](endpoints.md)
- [Authentication](authentication.md)

`;

// Auto-generated from rate-limits.md
export const MainToolDocumentationApiDocumentationRateLimits = `# Rate Limits & Quotas

## Overview

Current rate limits are managed by LLM API providers. Application-level rate limiting is recommended for production.

## Current Limits

### LLM API Limits

**OpenAI**:
- Varies by API key tier
- Typically: 3,500 requests/minute
- Distributed across multiple keys

**Google Gemini**:
- Varies by quota
- Typically: 60 requests/minute

**Anthropic**:
- Varies by tier
- Typically: 50 requests/minute

## Recommended Application Limits

### Per User Limits

- **10 requests per minute**
- **100 requests per hour**
- **1000 requests per day**

### Implementation

\`\`\`python
from flask_limiter import Limiter

limiter = Limiter(
    app=app,
    key_func=get_user_id,
    default_limits=["10 per minute", "100 per hour"]
)

@app.route('/api/analyze', methods=['POST'])
@limiter.limit("10 per minute")
def analyze():
    pass
\`\`\`

## Quota Management

### Monitoring

- Track API usage per user
- Monitor quota consumption
- Alert on high usage

### Best Practices

1. Implement request queuing
2. Cache results when possible
3. Batch similar requests
4. Use efficient models

## Related Documentation

- [Endpoints](endpoints.md)
- [Performance Benchmarking](../code-documentation/performance-benchmarking.md)

`;

// Auto-generated from component-design.md
export const MainToolDocumentationArchitectureComponentDesign = `# Component-Level Design

## Overview

Detailed design of major system components.

## Evaluation Engine

### DarkPattern Module

**Location**: \`DarkPattern/version3.py\`

**Responsibilities**:
- Main dark pattern analysis engine
- Coordinates sub-agents
- Manages reasoning storage
- Generates comprehensive results

**Key Functions**:
- \`analyze_dark_patterns()\`: Main analysis function
- \`analyze_dark_patterns_figma()\`: Figma-specific analysis
- \`store_occurrence_reasoning()\`: Store reasoning data

## Benchmarking Module

### Performance Tracking

**Components**:
- LLM call tracking
- Token usage monitoring
- Cost calculation
- Response time measurement

**Storage**: Firebase \`performanceMetrics\` collection

## Audit Trail Manager

### Activity Logging

**Components**:
- User activity tracking
- Error logging
- Feedback collection
- Security event logging

**Storage**: Firebase collections

## Compliance Pipeline

### Regulatory Analysis

**Components**:
- Law fetching (AdvancedLawFetcher)
- Jurisdiction mapping
- Compliance scoring
- Fair pattern generation

## UI Components

### React Components

**Core Pages**:
- \`HomePage.js\`: Dashboard
- \`ExplorePage.js\`: Session browser
- \`ReportPage.js\`: Report viewer

**Editors**:
- \`NewEditor.js\`: Dark pattern editor
- \`DigitalEditor.js\`: Compliance editor

**Related Documentation**

- [System Overview](system-overview.md)
- [Data Flow](data-flow.md)

`;

// Auto-generated from data-flow.md
export const MainToolDocumentationArchitectureDataFlow = `# Data Flow & Storage

## Image Processing Flow

\`\`\`
Image Upload
    ↓
Google Cloud Storage
    ↓
Firebase Document Created (status: initial)
    ↓
Step 1: Information Extraction
    ├─► LLM API Calls
    └─► Firebase Update (status: step1_completed)
    ↓
Step 2: Agent Search
    ├─► LLM API Calls
    └─► Firebase Update (status: step2_completed)
    ↓
Step 3: Sub-Agent Execution (Parallel)
    ├─► Multiple LLM API Calls
    └─► Firebase Update (status: step3_completed)
    ↓
Step 4: Outcome Synthesis
    ├─► LLM API Calls
    ├─► Law Fetching (Perplexity)
    ├─► Coordinate Generation
    └─► Firebase Update (status: completed)
    ↓
Results Returned to User
\`\`\`

## Storage Architecture

### Firestore Structure

\`\`\`
sessions/
  └── {sessionId}/
      └── userJourneys/
          └── {journeyId}/
              └── images/
                  └── {imageId}/
\`\`\`

### Google Cloud Storage

\`\`\`
dd-media-files/
  ├── {session_name}_{timestamp}/
  └── {environmentName}/{documentId}/tab{tabNumber}/
\`\`\`

## Data Transformation Pipeline

1. **Input**: Image + URL + Notes
2. **Extraction**: Context and design analysis
3. **Detection**: Pattern identification
4. **Validation**: Fact-checking and filtering
5. **Enrichment**: Law fetching and coordinates
6. **Output**: Structured analysis results

## Related Documentation

- [Database Schema](../code-documentation/database-schema.md)
- [System Overview](system-overview.md)

`;

// Auto-generated from deployment-architecture.md
export const MainToolDocumentationArchitectureDeploymentArchitecture = `# Deployment Architecture

## Overview

Multi-cloud deployment architecture using Google Cloud Platform and Vercel.

## Backend Deployment (Google Cloud Run)

### Container Architecture

- **Base Image**: Python 3.11-slim
- **Runtime**: Gunicorn (4 workers, 2 threads)
- **Memory**: 4Gi
- **Timeout**: 900 seconds
- **Region**: europe-west1

### Scaling

- **Auto-scaling**: Enabled
- **Min Instances**: 0
- **Max Instances**: Configurable
- **Concurrency**: 80 requests/instance

## Frontend Deployment (Vercel)

### Configuration

- **Framework**: React
- **Build**: \`npm run build\`
- **Routing**: Client-side (SPA)
- **CDN**: Global edge network

## Multi-Cloud Considerations

### Current Setup

- **Frontend**: Vercel (global CDN)
- **Backend**: Google Cloud Run (EU region)
- **Database**: Google Cloud Firestore
- **Storage**: Google Cloud Storage

### Hybrid Options

- On-premise backend (future)
- Multi-region deployment
- Disaster recovery sites

## Related Documentation

- [Deployment Environment](../code-documentation/deployment-environment.md)
- [System Overview](system-overview.md)

`;

// Auto-generated from integrations.md
export const MainToolDocumentationArchitectureIntegrations = `# Integration Points

## External Integrations

### LLM Providers

- **OpenAI**: GPT models
- **Google**: Gemini models
- **Anthropic**: Claude models
- **Perplexity**: Law search

### Cloud Services

- **Google Cloud**: Run, Firestore, Storage, Tasks
- **Firebase**: Auth, Firestore, Analytics
- **Vercel**: Frontend hosting
- **Supabase**: Reasoning storage

## Integration Patterns

### API Integration

- RESTful API
- JSON data format
- Token authentication
- Error handling

### Webhook Integration (Future)

- Event notifications
- Status updates
- Result delivery

## Third-Party Integrations

### Potential Integrations

- **Design Tools**: Figma plugin
- **CI/CD**: Automated testing
- **Analytics**: Usage tracking
- **Support**: Help desk integration

## Related Documentation

- [API Documentation](../api-documentation/endpoints.md)
- [System Overview](system-overview.md)

`;

// Auto-generated from monitoring.md
export const MainToolDocumentationArchitectureMonitoring = `# Monitoring & Observability

## Metrics Collection

### Application Metrics

- Request count
- Response time
- Error rate
- Success rate

### LLM Metrics

- API call count
- Token usage
- Cost per analysis
- Model performance

### Resource Metrics

- CPU usage
- Memory usage
- Network I/O
- Storage usage

## Dashboard Setup

### Google Cloud Monitoring

- Cloud Run metrics
- Firestore metrics
- GCS metrics
- Custom metrics

### Firebase Analytics

- User activity
- Error tracking
- Performance monitoring

## Compliance Scoring Rubrics

### Scoring Metrics

- Pattern detection accuracy
- Compliance score trends
- Jurisdiction coverage
- Remediation effectiveness

## Alerting Mechanisms

### Key Alerts

- High error rate (>5%)
- Slow response time (>5 min)
- High memory usage (>90%)
- API rate limits
- Cost thresholds

## Related Documentation

- [Operational Readiness](../code-documentation/operational-readiness.md)
- [System Overview](system-overview.md)

`;

// Auto-generated from scalability.md
export const MainToolDocumentationArchitectureScalability = `# Scalability & Resilience

## Scalability Strategy

### Horizontal Scaling

**Cloud Run**:
- Automatic scaling based on load
- Multiple instances handle traffic
- Load balancing built-in

### Vertical Scaling

**Resource Allocation**:
- Memory: 4Gi (adjustable)
- CPU: Auto-allocated
- Can increase for heavy workloads

## Load Balancing

### Cloud Run Load Balancing

- Automatic request distribution
- Health check integration
- Regional load balancing

## Failover Mechanisms

### Automatic Failover

- Cloud Run: Automatic instance restart
- Firestore: Multi-region replication (if enabled)
- GCS: Automatic redundancy

### Manual Failover

- Rollback to previous revision
- Switch to backup region
- Use alternative API providers

## Performance Optimization

### Caching

- Airtable prompt caching (5-minute TTL)
- Result caching (future)
- CDN for static assets

### Parallel Processing

- Sub-agents run in parallel
- ThreadPoolExecutor for concurrency
- Async operations for I/O

## Related Documentation

- [Performance Benchmarking](../code-documentation/performance-benchmarking.md)
- [Deployment Architecture](deployment-architecture.md)

`;

// Auto-generated from security-architecture.md
export const MainToolDocumentationArchitectureSecurityArchitecture = `# Security Architecture

## Security Layers

### 1. Authentication Layer

- Firebase OAuth2
- Token-based authentication
- Email authorization

### 2. Authorization Layer

- Role-based access control (planned)
- Email whitelist (current)
- Resource-level permissions

### 3. Data Protection

- Encryption at rest (Firestore, GCS)
- Encryption in transit (HTTPS/TLS)
- Data sanitization

### 4. API Security

- CORS configuration
- Rate limiting (recommended)
- Input validation
- Output sanitization

## Encryption

### At Rest

- **Firestore**: Google-managed encryption
- **GCS**: Google-managed encryption
- **Supabase**: AES-256 encryption

### In Transit

- **HTTPS**: All communications
- **TLS 1.2+**: Minimum version
- **Certificate Management**: Automatic

## Sandboxing

### Container Isolation

- Docker containers
- Process isolation
- Resource limits

## Assurance Pipelines

### Security Checks

- Dependency scanning
- Code review
- Security audits
- Penetration testing

## Related Documentation

- [Security & Compliance](../code-documentation/security-compliance.md)
- [System Overview](system-overview.md)

`;

// Auto-generated from system-overview.md
export const MainToolDocumentationArchitectureSystemOverview = `# System Overview

## Architecture Overview

The Dark Pattern Detection & Compliance Analysis Tool is a cloud-based, AI-powered system for detecting deceptive design patterns and analyzing regulatory compliance.

## High-Level Architecture

\`\`\`
┌─────────────┐
│   Browser   │
│  (React)    │
└──────┬──────┘
       │ HTTPS
       ▼
┌─────────────┐
│   Vercel    │
│  (Frontend) │
└──────┬──────┘
       │
       ▼
┌─────────────┐      ┌──────────────┐
│ Google Cloud│      │   Firebase   │
│     Run     │◄────►│  (Firestore) │
│  (Backend)  │      └──────────────┘
└──────┬──────┘
       │
       ├──► Google Cloud Storage
       ├──► OpenAI API
       ├──► Google Gemini API
       ├──► Anthropic API
       └──► Perplexity AI
\`\`\`

## Technology Stack

### Frontend
- **Framework**: React 18
- **Deployment**: Vercel
- **Authentication**: Firebase Auth
- **UI**: Bootstrap 5, Chart.js, D3.js

### Backend
- **Framework**: Flask (Python)
- **Deployment**: Google Cloud Run
- **WSGI Server**: Gunicorn
- **Database**: Google Cloud Firestore
- **Storage**: Google Cloud Storage

### AI/ML
- **LLM Providers**: OpenAI, Google Gemini, Anthropic
- **Framework**: LangChain
- **Storage**: Supabase (reasoning)

## Component Interaction

### Request Flow

1. User uploads image via React frontend
2. Frontend sends request to Cloud Run backend
3. Backend uploads image to GCS
4. Backend processes through 4-step pipeline
5. Backend stores results in Firestore
6. Backend returns results to frontend
7. Frontend displays results to user

## Related Documentation

- [Component Design](component-design.md)
- [Data Flow](data-flow.md)
- [Deployment Architecture](deployment-architecture.md)

`;

// Auto-generated from code-examples.md
export const MainToolDocumentationCodeDocumentationCodeExamples = `# Code Examples

## Overview

This document provides practical code examples for common use cases, standard workflows, edge cases, and integration scenarios in the Dark Pattern Detection & Compliance Analysis Tool.

## Standard Workflow Examples

### Example 1: Complete Image Analysis Workflow

\`\`\`python
from utility.context import get_context_information
from utility.parentCategoryWithScanner import generate_coercing_scanner
from utility.SubAgents import generate_preselection
from utility.OutcomeSynthesis import generate_key_highlights, validation_outcome
from utility.LlmCalls import call_llm

# Step 1: Information Extraction
def analyze_image(image_url, url, note, image_id):
    # Extract context
    website_brief, webpage_brief, nature_of_design, typography_and_colour_use, \\
        call_to_action, information_organization, footnote_or_information, \\
        scanner, user_benefits, business_success = get_context_information(
            image_url, url, note, image_id
        )
    
    # Step 2: Parent Category Scanning
    coercing_result = generate_coercing_scanner(
        image_url=image_url,
        scanner_context=scanner,
        image_id=image_id
    )
    
    # Step 3: Sub-Agent Analysis (if parent category detected)
    if coercing_result:
        preselection_result = generate_preselection(
            image_url=image_url,
            parent_category="Coercing",
            scanner_context=scanner,
            image_id=image_id
        )
    
    # Step 4: Outcome Synthesis
    key_highlights = generate_key_highlights(
        filter_for_agent=[preselection_result],
        image_id=image_id
    )
    
    validation_results = validation_outcome(
        image_url=image_url,
        actual_measurable_harm=key_highlights,
        note=note,
        call_to_action=call_to_action,
        image_id=image_id
    )
    
    return validation_results
\`\`\`

### Example 2: Using the Centralized LLM Interface

\`\`\`python
from utility.LlmCalls import call_llm

# Simple text-only call
response = call_llm(
    model_name="gpt-4o-mini",
    prompt="Analyze this webpage for dark patterns: ...",
    task_name="Dark Pattern Analysis",
    image_id="img_123"
)

# Multimodal call with image
response = call_llm(
    model_name="gemini-2.0-flash",
    prompt="What dark patterns do you see in this image?",
    task_name="Visual Dark Pattern Detection",
    image_url="https://storage.googleapis.com/bucket/image.png",
    image_id="img_123"
)
\`\`\`

### Example 3: Parallel Sub-Agent Execution

\`\`\`python
from concurrent.futures import ThreadPoolExecutor
from utility.SubAgents import (
    generate_preselection,
    generate_hidden_legalese,
    generate_potential_false_urgency
)

def run_sub_agents_parallel(image_url, parent_categories, context, image_id):
    agent_function_map = {
        'Preselection': generate_preselection,
        'Hidden legalese': generate_hidden_legalese,
        'False urgency': generate_potential_false_urgency,
    }
    
    # Run agents in parallel
    with ThreadPoolExecutor() as executor:
        agent_futures = {
            category: executor.submit(
                agent_function_map[category],
                image_url,
                parent_category=category,
                scanner_context=context,
                image_id=image_id
            )
            for category in parent_categories
            if category in agent_function_map
        }
        
        # Collect results
        agent_responses = {
            category: future.result()
            for category, future in agent_futures.items()
        }
    
    return agent_responses
\`\`\`

### Example 4: Firebase Document Updates with Status Tracking

\`\`\`python
from firebase_admin import firestore
from datetime import datetime

def update_image_status(session_id, journey_id, image_id, status, data):
    db = firestore.client()
    image_doc_ref = db.collection('sessions').document(session_id) \\
                      .collection('userJourneys').document(journey_id) \\
                      .collection('images').document(image_id)
    
    update_data = {
        'status': status,
        'updatedAt': firestore.SERVER_TIMESTAMP,
        **data
    }
    
    if status == 'completed':
        update_data['completedAt'] = firestore.SERVER_TIMESTAMP
    
    image_doc_ref.update(update_data)
    return image_doc_ref.get().to_dict()
\`\`\`

## Edge Cases and Error Handling

### Example 5: Handling Missing Data

\`\`\`python
def safe_get_context(image_data):
    """Safely extract context with default values."""
    return {
        'website_brief': image_data.get('website_brief', ''),
        'webpage_brief': image_data.get('webpage_brief', ''),
        'note': image_data.get('note', ''),
        'context': image_data.get('context', ''),
        'scanner': image_data.get('scanner', '')
    }

# Usage
image_data = fetch_image_data(session_id, journey_id, image_id)
context = safe_get_context(image_data)

if not context['webpage_brief']:
    # Fallback: generate basic context
    context['webpage_brief'] = generate_basic_context(image_data['url'])
\`\`\`

### Example 6: API Failure Handling with Retries

\`\`\`python
import time
from utility.LlmCalls import call_llm

def call_llm_with_retry(prompt, model_name, task_name, max_retries=3):
    """Call LLM with automatic retry on failure."""
    for attempt in range(max_retries):
        try:
            response = call_llm(
                model_name=model_name,
                prompt=prompt,
                task_name=task_name
            )
            return response
        except Exception as e:
            if attempt < max_retries - 1:
                wait_time = 2 ** attempt  # Exponential backoff
                print(f"Attempt {attempt + 1} failed: {e}. Retrying in {wait_time}s...")
                time.sleep(wait_time)
            else:
                raise Exception(f"Failed after {max_retries} attempts: {e}")
    
    return None
\`\`\`

### Example 7: Timeout Handling

\`\`\`python
import signal
from contextlib import contextmanager

@contextmanager
def timeout_handler(seconds):
    """Context manager for timeout handling."""
    def timeout_signal(signum, frame):
        raise TimeoutError(f"Operation timed out after {seconds} seconds")
    
    signal.signal(signal.SIGALRM, timeout_signal)
    signal.alarm(seconds)
    try:
        yield
    finally:
        signal.alarm(0)

# Usage
try:
    with timeout_handler(60):  # 60 second timeout
        result = analyze_image(image_url, url, note, image_id)
except TimeoutError as e:
    print(f"Analysis timed out: {e}")
    # Save partial results or return error
    return {"status": "timeout", "message": str(e)}
\`\`\`

### Example 8: Handling Invalid Image URLs

\`\`\`python
import requests
from io import BytesIO

def validate_and_fetch_image(image_url):
    """Validate image URL and fetch if needed."""
    try:
        # Check if URL is accessible
        response = requests.head(image_url, timeout=10)
        if response.status_code != 200:
            raise ValueError(f"Image URL returned status {response.status_code}")
        
        # Verify content type
        content_type = response.headers.get('Content-Type', '')
        if not content_type.startswith('image/'):
            raise ValueError(f"URL does not point to an image: {content_type}")
        
        return image_url
    except requests.RequestException as e:
        raise ValueError(f"Failed to validate image URL: {e}")

# Usage
try:
    validated_url = validate_and_fetch_image(image_url)
except ValueError as e:
    # Handle invalid URL
    return {"error": str(e), "status": "invalid_url"}
\`\`\`

## Integration Examples

### Example 9: Integrating with External API

\`\`\`python
import requests
from utility.LlmCalls import call_llm

def analyze_with_external_api(image_url, external_api_url, api_key):
    """Integrate with external analysis API."""
    # First, get internal analysis
    internal_result = analyze_image(image_url, url, note, image_id)
    
    # Then, enrich with external API
    external_payload = {
        "image_url": image_url,
        "analysis": internal_result
    }
    
    headers = {
        "Authorization": f"Bearer {api_key}",
        "Content-Type": "application/json"
    }
    
    try:
        response = requests.post(
            external_api_url,
            json=external_payload,
            headers=headers,
            timeout=30
        )
        response.raise_for_status()
        external_result = response.json()
        
        # Merge results
        merged_result = {
            "internal": internal_result,
            "external": external_result,
            "combined": merge_analysis_results(internal_result, external_result)
        }
        
        return merged_result
    except requests.RequestException as e:
        # Fallback to internal result only
        return {"internal": internal_result, "error": str(e)}
\`\`\`

### Example 10: Batch Processing Multiple Images

\`\`\`python
from concurrent.futures import ThreadPoolExecutor, as_completed

def batch_analyze_images(image_list, max_workers=5):
    """Process multiple images in parallel."""
    results = {}
    
    with ThreadPoolExecutor(max_workers=max_workers) as executor:
        # Submit all tasks
        future_to_image = {
            executor.submit(
                analyze_image,
                image['image_url'],
                image['url'],
                image.get('note', ''),
                image['image_id']
            ): image
            for image in image_list
        }
        
        # Collect results as they complete
        for future in as_completed(future_to_image):
            image = future_to_image[future]
            try:
                result = future.result()
                results[image['image_id']] = {
                    "status": "success",
                    "result": result
                }
            except Exception as e:
                results[image['image_id']] = {
                    "status": "error",
                    "error": str(e)
                }
    
    return results
\`\`\`

### Example 11: Custom Agent Implementation

\`\`\`python
from utility.LlmCalls import call_llm

def generate_custom_dark_pattern_detector(
    image_url,
    parent_category,
    scanner_context,
    overall_context,
    image_id
):
    """Example of implementing a custom dark pattern detector."""
    
    prompt = f"""
    Analyze the following image for {parent_category} dark patterns.
    
    Context: {overall_context}
    Scanner Context: {scanner_context}
    
    Identify any instances of:
    1. [Specific pattern 1]
    2. [Specific pattern 2]
    3. [Specific pattern 3]
    
    Provide your analysis in the following format:
    - Pattern name
    - Location description
    - Severity (low/medium/high)
    - Evidence
    """
    
    response = call_llm(
        model_name="gpt-4o-mini",
        prompt=prompt,
        task_name="Custom Dark Pattern Detection",
        image_url=image_url,
        image_id=image_id
    )
    
    # Parse and structure response
    structured_response = parse_custom_response(response)
    
    return structured_response
\`\`\`

### Example 12: Law Fetching Integration

\`\`\`python
from utility.AdvancedLawFetcher import advance_law_fetch_function

def get_compliance_info(dark_pattern_point, jurisdictions=['EU', 'US']):
    """Fetch compliance information for a dark pattern."""
    compliance_data = {}
    
    for jurisdiction in jurisdictions:
        try:
            law_result = advance_law_fetch_function(
                point=dark_pattern_point,
                selected_jurisdictions=[jurisdiction]
            )
            
            compliance_data[jurisdiction] = {
                "laws": law_result.get("laws", []),
                "fair_patterns": law_result.get("fair_patterns", []),
                "dark_pattern_details": law_result.get("dark_pattern_details", {})
            }
        except Exception as e:
            compliance_data[jurisdiction] = {
                "error": str(e)
            }
    
    return compliance_data
\`\`\`

## Frontend Integration Examples

### Example 13: React Component for Image Upload

\`\`\`javascript
import React, { useState } from 'react';
import axios from 'axios';

function ImageUpload({ sessionId, journeyId, onUploadComplete }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  const handleUpload = async (file, url, note) => {
    setUploading(true);
    setError(null);

    const formData = new FormData();
    formData.append('image', file);
    formData.append('url', url);
    formData.append('note', note);
    formData.append('session_id', sessionId);
    formData.append('journey_id', journeyId);
    formData.append('image_doc_id', \`img_\${Date.now()}\`);

    try {
      const response = await axios.post(
        'https://your-api-url.com/upload_and_process',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          timeout: 300000, // 5 minutes
        }
      );

      if (response.data.status === 'success') {
        onUploadComplete(response.data);
      } else {
        setError(response.data.message || 'Upload failed');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      {/* Upload form UI */}
      {error && <div className="error">{error}</div>}
      {uploading && <div>Uploading and processing...</div>}
    </div>
  );
}
\`\`\`

### Example 14: Polling for Processing Status

\`\`\`javascript
async function pollProcessingStatus(sessionId, journeyId, imageId) {
  const maxAttempts = 60; // 5 minutes with 5-second intervals
  let attempts = 0;

  while (attempts < maxAttempts) {
    try {
      const response = await axios.post(
        'https://your-api-url.com/get_image_metadata',
        {
          session_id: sessionId,
          journey_id: journeyId,
          image_doc_id: imageId,
        }
      );

      const status = response.data.status;

      if (status === 'completed') {
        return response.data;
      } else if (status === 'error') {
        throw new Error(response.data.error || 'Processing failed');
      }

      // Wait before next poll
      await new Promise(resolve => setTimeout(resolve, 5000));
      attempts++;
    } catch (error) {
      throw error;
    }
  }

  throw new Error('Processing timeout');
}
\`\`\`

## Best Practices

### Error Handling Pattern

\`\`\`python
def robust_analysis_function(image_url, url, note, image_id):
    """Example of robust error handling."""
    try:
        # Step 1
        step1_result = step1_information_extraction(image_url, url, note, image_id)
        
        # Step 2
        step2_result = step2_agents_search(
            image_url,
            step1_result['scanner'],
            step1_result['Scanner_with_context'],
            image_id
        )
        
        # Step 3
        step3_result = step3_agents_run(
            image_url,
            step2_result['agent_responses'],
            step1_result['context'],
            image_id
        )
        
        # Step 4
        step4_result = step4_outcome_synthesis(
            image_url,
            step3_result['revised_agent_responses'],
            step1_result['website_brief'],
            step1_result['webpage_brief'],
            note,
            step1_result['call_to_action'],
            image_id
        )
        
        return {
            "status": "success",
            "result": step4_result
        }
        
    except Exception as e:
        # Log error
        log_error_to_firebase(
            function_name="robust_analysis_function",
            error=str(e),
            image_id=image_id
        )
        
        # Return error response
        return {
            "status": "error",
            "error": str(e),
            "image_id": image_id
        }
\`\`\`

### Logging Pattern

\`\`\`python
import logging
from datetime import datetime

logger = logging.getLogger(__name__)

def logged_function(image_id, *args, **kwargs):
    """Example function with comprehensive logging."""
    start_time = datetime.now()
    logger.info(f"Starting analysis for image_id: {image_id}")
    
    try:
        result = perform_analysis(*args, **kwargs)
        
        duration = (datetime.now() - start_time).total_seconds()
        logger.info(
            f"Analysis completed for image_id: {image_id} "
            f"in {duration:.2f} seconds"
        )
        
        return result
        
    except Exception as e:
        duration = (datetime.now() - start_time).total_seconds()
        logger.error(
            f"Analysis failed for image_id: {image_id} "
            f"after {duration:.2f} seconds: {e}",
            exc_info=True
        )
        raise
\`\`\`

## Related Documentation

- [Design Philosophy](design-philosophy.md)
- [Performance Benchmarking](performance-benchmarking.md)
- [API Documentation - Endpoints](../api-documentation/endpoints.md)

`;

// Auto-generated from continuous-learning.md
export const MainToolDocumentationCodeDocumentationContinuousLearning = `# Continuous Learning / Improvement Loops

## Overview

The Dark Pattern Detection & Compliance Analysis Tool implements continuous learning mechanisms to improve accuracy, reduce hallucinations, and adapt to new dark pattern types. This document details the feedback loops, reasoning storage, model improvement processes, and meta-learning capabilities.

## Feedback Loop Mechanisms

### 1. User Feedback Collection

**Implementation**: Firebase \`FeedbackLogs\` collection

\`\`\`python
def log_user_feedback(user_id, session_id, image_id, feedback_type, feedback_text, rating):
    """Log user feedback for continuous improvement."""
    db = firestore.client()
    db.collection('FeedbackLogs').add({
        'userId': user_id,
        'sessionId': session_id,
        'imageId': image_id,
        'feedbackType': feedback_type,  # 'positive', 'negative', 'suggestion', 'bug'
        'feedbackText': feedback_text,
        'rating': rating,  # 1-5 scale
        'timestamp': firestore.SERVER_TIMESTAMP
    })
\`\`\`

**Feedback Types**:
- **Positive**: Confirms accurate detection
- **Negative**: Indicates false positive or missed pattern
- **Suggestion**: User suggestions for improvement
- **Bug**: Technical issues or errors

### 2. Outcome Validation Feedback

**Implementation**: Users can mark outcomes as:
- **Correct**: Valid detection
- **Hallucination**: False positive
- **Doubtful**: Uncertain, needs review

\`\`\`python
def update_outcome_feedback(image_id, outcome_id, feedback_type):
    """Update feedback on specific outcome."""
    db = firestore.client()
    image_ref = db.collection('sessions').document(session_id) \\
                  .collection('userJourneys').document(journey_id) \\
                  .collection('images').document(image_id)
    
    image_ref.update({
        f'response.{outcome_id}.feedback': feedback_type,
        f'response.{outcome_id}.feedback_timestamp': firestore.SERVER_TIMESTAMP
    })
\`\`\`

## Reasoning Storage in Supabase

### Architecture

The system stores LLM reasoning in Supabase for analysis and improvement:

**Storage Module**: \`utility/reasoning_storage.py\`

**Storage Types**:
1. **Occurrence Reasoning**: Why a pattern was detected
2. **Validation Reasoning**: Why an outcome was validated/invalidated
3. **Pattern Validation Reasoning**: Pattern-specific validation logic

### Implementation

\`\`\`python
from utility.reasoning_storage import ReasoningStorage

storage = ReasoningStorage()
storage.initialize()

# Store occurrence reasoning
await store_occurrence_reasoning(
    image_id=image_id,
    webpage_category=webpage_category,
    website_category=website_category,
    reasoning_response=reasoning_response
)

# Store validation reasoning
await store_validation_reasoning(
    image_id=image_id,
    webpage_category=webpage_category,
    website_category=website_category,
    prompt=prompt,
    task_name='pattern_validation',
    response=response
)
\`\`\`

### Reasoning Analysis

**Purpose**: Analyze stored reasoning to:
- Identify common patterns in reasoning
- Find reasoning gaps
- Improve prompt quality
- Reduce hallucinations

**Process**:
1. Collect reasoning for similar patterns
2. Analyze reasoning quality
3. Identify improvement opportunities
4. Update prompts based on findings

## Model Improvement Processes

### 1. Prompt Optimization

**Current Approach**: Prompts stored in Airtable for easy updates

**Improvement Process**:
1. Collect feedback on analysis quality
2. Identify prompt weaknesses
3. Test improved prompts
4. Deploy validated improvements

**Example**:
\`\`\`python
# Original prompt
prompt = "Analyze this image for dark patterns."

# Improved prompt (based on feedback)
prompt = """
Analyze this image for dark patterns with the following criteria:
1. Focus on user manipulation techniques
2. Identify specific UI elements that mislead users
3. Provide evidence for each detection
4. Rate severity (low/medium/high)
"""
\`\`\`

### 2. Model Selection Optimization

**Process**:
1. Track performance by model
2. Compare accuracy vs. cost
3. Adjust model selection based on results
4. A/B test different models

**Metrics Tracked**:
- Accuracy rate
- False positive rate
- Cost per analysis
- Response time

### 3. Agent Refinement

**Process**:
1. Monitor agent performance
2. Identify low-performing agents
3. Refine agent logic
4. Test improvements
5. Deploy updates

**Example**:
\`\`\`python
# Monitor agent performance
def track_agent_performance(agent_name, results, feedback):
    """Track how well each agent performs."""
    accuracy = calculate_accuracy(results, feedback)
    db.collection('agentPerformance').document(agent_name).set({
        'accuracy': accuracy,
        'total_runs': firestore.Increment(1),
        'last_updated': firestore.SERVER_TIMESTAMP
    }, merge=True)
\`\`\`

## Meta-Learning from User Feedback

### 1. Image Metadata Collection

**Storage**: Firebase \`images\` collection

**Data Collected**:
- Image URL
- Desired outcome (user-provided)
- Actual outcome (system-generated)
- Analysis data
- Feedback

**Purpose**: Build training dataset for future improvements

\`\`\`python
def save_image_for_meta_learning(image_id, image_url, desired_outcome, actual_outcome, analysis_data):
    """Save image data for meta-learning."""
    db = firestore.client()
    db.collection('images').document(image_id).set({
        'imageUrl': image_url,
        'desiredOutcome': desired_outcome,
        'actualOutcome': actual_outcome,
        'analysisData': analysis_data,
        'createdAt': firestore.SERVER_TIMESTAMP,
        'updatedAt': firestore.SERVER_TIMESTAMP
    }, merge=True)
\`\`\`

### 2. Pattern Learning

**Process**:
1. Collect examples of each dark pattern type
2. Analyze common characteristics
3. Update detection logic
4. Improve accuracy

**Implementation**:
\`\`\`python
def learn_from_feedback():
    """Analyze feedback to improve pattern detection."""
    # Get all feedback
    feedback_docs = db.collection('FeedbackLogs').stream()
    
    # Group by pattern type
    pattern_feedback = {}
    for doc in feedback_docs:
        data = doc.to_dict()
        pattern = data.get('pattern_type')
        if pattern not in pattern_feedback:
            pattern_feedback[pattern] = []
        pattern_feedback[pattern].append(data)
    
    # Analyze and improve
    for pattern, feedback_list in pattern_feedback.items():
        accuracy = calculate_accuracy(feedback_list)
        if accuracy < 0.8:  # Below 80% accuracy
            improve_pattern_detection(pattern, feedback_list)
\`\`\`

### 3. Continuous Prompt Refinement

**Process**:
1. Collect reasoning for failed detections
2. Identify prompt weaknesses
3. Generate improved prompts
4. Test and validate
5. Deploy improvements

**Automated Refinement**:
\`\`\`python
def refine_prompts_from_feedback():
    """Automatically refine prompts based on feedback."""
    # Get negative feedback
    negative_feedback = db.collection('FeedbackLogs') \\
                          .where('feedbackType', '==', 'negative') \\
                          .stream()
    
    # Analyze common issues
    common_issues = identify_common_issues(negative_feedback)
    
    # Generate improved prompts
    for issue in common_issues:
        improved_prompt = generate_improved_prompt(issue)
        test_prompt(improved_prompt)
        if validate_prompt(improved_prompt):
            deploy_prompt(improved_prompt)
\`\`\`

## Feedback Analysis Pipeline

### 1. Feedback Collection

**Sources**:
- User feedback forms
- Outcome validation
- Error reports
- Usage analytics

### 2. Feedback Processing

**Steps**:
1. **Categorization**: Classify feedback type
2. **Sentiment Analysis**: Positive/negative/neutral
3. **Pattern Extraction**: Identify common themes
4. **Priority Assignment**: High/medium/low priority

### 3. Improvement Generation

**Process**:
1. Analyze feedback patterns
2. Identify root causes
3. Generate improvement proposals
4. Test improvements
5. Deploy validated changes

## A/B Testing Framework

### Implementation

\`\`\`python
def ab_test_prompts(prompt_a, prompt_b, test_size=100):
    """A/B test two prompt versions."""
    # Randomly assign to A or B
    for i in range(test_size):
        version = 'A' if random.random() < 0.5 else 'B'
        prompt = prompt_a if version == 'A' else prompt_b
        
        result = analyze_with_prompt(prompt)
        track_result(version, result)
    
    # Compare results
    return compare_results('A', 'B')
\`\`\`

### Metrics

- **Accuracy**: Correct detections / Total detections
- **Precision**: True positives / (True positives + False positives)
- **Recall**: True positives / (True positives + False negatives)
- **F1 Score**: Harmonic mean of precision and recall

## Improvement Tracking

### Performance Metrics

**Tracked Metrics**:
- Overall accuracy
- Per-pattern accuracy
- False positive rate
- False negative rate
- User satisfaction score
- Cost per analysis

### Dashboard

\`\`\`python
def generate_improvement_dashboard():
    """Generate dashboard showing improvement metrics."""
    metrics = {
        'overall_accuracy': calculate_overall_accuracy(),
        'pattern_accuracy': calculate_per_pattern_accuracy(),
        'false_positive_rate': calculate_false_positive_rate(),
        'user_satisfaction': calculate_user_satisfaction(),
        'cost_trend': calculate_cost_trend(),
        'improvement_timeline': get_improvement_timeline()
    }
    return metrics
\`\`\`

## Automated Improvement Pipeline

### 1. Daily Analysis

**Process**:
1. Collect previous day's feedback
2. Analyze patterns
3. Generate improvement suggestions
4. Queue for review

### 2. Weekly Review

**Process**:
1. Review improvement suggestions
2. Test high-priority improvements
3. Deploy validated changes
4. Monitor results

### 3. Monthly Optimization

**Process**:
1. Comprehensive performance review
2. Model comparison
3. Prompt optimization
4. Agent refinement
5. Cost optimization

## Best Practices

### 1. Feedback Quality

- **Specific**: Detailed feedback is more valuable
- **Contextual**: Include relevant context
- **Actionable**: Feedback should lead to improvements

### 2. Improvement Validation

- **Test Before Deploy**: Always test improvements
- **Monitor After Deploy**: Track impact of changes
- **Rollback if Needed**: Revert if performance degrades

### 3. Continuous Monitoring

- **Track Metrics**: Monitor key performance indicators
- **Alert on Degradation**: Set up alerts for performance drops
- **Regular Reviews**: Schedule regular improvement reviews

## Related Documentation

- [Model Integrations](model-integrations.md)
- [Performance Benchmarking](performance-benchmarking.md)
- [Database Schema](database-schema.md)

`;

// Auto-generated from database-schema.md
export const MainToolDocumentationCodeDocumentationDatabaseSchema = `# Database Management & Schema

## Overview

The Dark Pattern Detection & Compliance Analysis Tool uses Google Cloud Firestore as its primary database and Google Cloud Storage for file storage. This document details the database schema, data relationships, indexing strategies, and backup procedures.

## Firestore Database Structure

### Collections Overview

Firestore is organized into collections and subcollections with the following hierarchy:

\`\`\`
sessions/
  └── {sessionId}/
      ├── (session metadata)
      └── userJourneys/
          └── {journeyId}/
              ├── (journey metadata)
              └── images/
                  └── {imageId}/
                      └── (image processing data)

userActivityLogs/
  └── {logId}/
      └── (activity data)

errorLogs/
  └── {errorId}/
      └── (error data)

FeedbackLogs/
  └── {feedbackId}/
      └── (feedback data)

images/
  └── {imageId}/
      └── (image metadata for meta-learning)
\`\`\`

## Collection Schemas

### 1. Sessions Collection

**Path**: \`sessions/{sessionId}\`

**Purpose**: Top-level container for user analysis sessions

**Document Structure**:
\`\`\`typescript
{
  sessionId: string,              // Document ID
  createdAt: Timestamp,            // Server timestamp
  updatedAt: Timestamp,            // Server timestamp
  userId: string,                  // Firebase Auth user ID
  sessionName: string,             // User-provided session name
  status: string,                  // 'active' | 'completed' | 'archived'
  metadata: {
    // Additional session metadata
  }
}
\`\`\`

**Subcollections**:
- \`userJourneys/\` - User journey data within this session

### 2. User Journeys Collection

**Path**: \`sessions/{sessionId}/userJourneys/{journeyId}\`

**Purpose**: Represents a user journey or workflow within a session

**Document Structure**:
\`\`\`typescript
{
  journeyId: string,              // Document ID
  sessionId: string,              // Parent session ID
  journeyName: string,            // User-provided journey name
  createdAt: Timestamp,           // Server timestamp
  updatedAt: Timestamp,           // Server timestamp
  images: string[],               // Array of image document IDs
  metadata: {
    // Journey-specific metadata
  }
}
\`\`\`

**Subcollections**:
- \`images/\` - Image processing data for this journey

### 3. Images Collection

**Path**: \`sessions/{sessionId}/userJourneys/{journeyId}/images/{imageId}\`

**Purpose**: Stores processing data for individual images in a journey

**Document Structure**:
\`\`\`typescript
{
  imageId: string,                // Document ID
  sessionId: string,              // Parent session ID
  journeyId: string,              // Parent journey ID
  status: string,                 // 'initial' | 'step1_completed' | 'step2_completed' | 'step3_completed' | 'completed' | 'error'
  url: string,                    // Source webpage URL
  image_url: string,              // Google Cloud Storage public URL
  note: string,                   // User-provided notes
  createdAt: Timestamp,           // Server timestamp
  completedAt: Timestamp,        // Completion timestamp
  
  // Step 1: Information Extraction
  pageName: string,
  website_brief: string,
  webpage_brief: string,
  nature_of_design: string,
  typography_and_colour_use: string,
  call_to_action: string,
  information_organization: string,
  footnote_or_information: string,
  scanner: string,
  Scanner_with_context: string,
  context: string,
  prompt_context: string,
  
  // Step 2: Agent Search Results
  parent_category_results: {
    // Parent category scanner results
  },
  
  // Step 3: Sub-Agent Results
  agent_responses: {
    // Sub-agent analysis results
  },
  revised_agent_responses: {
    // Filtered and validated agent responses
  },
  
  // Step 4: Outcome Synthesis
  key_highlights: string[],
  actual_measurable_harm: string,
  outcome: string[],
  Hallucination: string[],
  doubtful: string[],
  outcome_validation_result: string,
  outcome_fact_check_response: string,
  response: Array<{
    type: 'outcome' | 'hallucination' | 'doubtful',
    text: string,
    coordinates: { x: number, y: number },
    parent_type: string,
    dark_pattern: string,
    dark_pattern_details: {
      name: string,
      type: string,
      context: string,
      source_urls: string[],
      reference_urls: string[]
    },
    laws: Array<{
      name: string,
      description: string,
      relevance: string,
      url: string
    }>,
    fair_patterns: Array<{
      name: string,
      description: string,
      implementation: string
    }>
  }>,
  
  // Comprehensive Result (from DarkPattern/version3.py)
  comprehensive_result: {
    // Full analysis result structure
  },
  
  // Error Handling
  error: string,                  // Error message if status is 'error'
  
  // Metadata
  processing_metadata: {
    start_time: Timestamp,
    end_time: Timestamp,
    processing_duration: number,  // seconds
    llm_calls_count: number,
    tokens_used: number
  }
}
\`\`\`

**Status Flow**:
\`\`\`
initial → step1_completed → step2_completed → step3_completed → completed
                                                      ↓
                                                   error
\`\`\`

### 4. User Activity Logs Collection

**Path**: \`userActivityLogs/{logId}\`

**Purpose**: Track user activities for auditing and analytics

**Document Structure**:
\`\`\`typescript
{
  logId: string,                 // Document ID (auto-generated)
  userId: string,                 // Firebase Auth user ID
  displayName: string,           // User display name
  activity: string,               // Activity description
  timestamp: Timestamp,           // Activity timestamp
  metadata: {
    endpoint: string,             // API endpoint if applicable
    sessionId: string,            // Related session if applicable
    // Additional context
  }
}
\`\`\`

**Example Activities**:
- "Authorized login: user@example.com"
- "Image analysis started: session_123"
- "Report generated: session_123"

### 5. Error Logs Collection

**Path**: \`errorLogs/{errorId}\`

**Purpose**: Centralized error tracking and debugging

**Document Structure**:
\`\`\`typescript
{
  errorId: string,                // Document ID (auto-generated)
  error: string,                 // Error message
  userId: string | null,         // User ID if available
  displayName: string | null,    // User display name if available
  timestamp: Timestamp,          // Error timestamp
  api_name: string,              // API endpoint name
  payload: {
    // Sanitized request payload
  },
  additional_info: {
    url: string,
    method: string,
    remote_addr: string,
    user_agent: string,
    endpoint: string
  },
  stack_trace: string,           // Full stack trace if available
  severity: 'low' | 'medium' | 'high' | 'critical'
}
\`\`\`

### 6. Feedback Logs Collection

**Path**: \`FeedbackLogs/{feedbackId}\`

**Purpose**: Store user feedback for continuous improvement

**Document Structure**:
\`\`\`typescript
{
  feedbackId: string,             // Document ID (auto-generated)
  userId: string,                // Firebase Auth user ID
  sessionId: string,             // Related session ID
  imageId: string,               // Related image ID
  feedbackType: 'positive' | 'negative' | 'suggestion' | 'bug',
  feedbackText: string,          // User feedback text
  rating: number,                // 1-5 rating if applicable
  timestamp: Timestamp,          // Feedback timestamp
  metadata: {
    // Additional feedback context
  }
}
\`\`\`

### 7. Images Collection (Meta-Learning)

**Path**: \`images/{imageId}\`

**Purpose**: Store image metadata for meta-learning and improvement

**Document Structure**:
\`\`\`typescript
{
  imageId: string,               // Document ID
  imageUrl: string,              // Image URL
  desiredOutcome: string,        // User-provided desired outcome
  actualOutcome: string,         // System-generated outcome
  analysisData: {
    // Full analysis data
  },
  createdAt: Timestamp,          // Creation timestamp
  updatedAt: Timestamp          // Last update timestamp
}
\`\`\`

## Google Cloud Storage Structure

### Bucket Organization

**Primary Bucket**: \`dd-media-files\`
- Stores uploaded images and screenshots
- Organized by session and timestamp

**Structure**:
\`\`\`
dd-media-files/
  ├── {session_name}_{timestamp}/
  │   └── {filename}.png
  ├── analysis_{timestamp}/
  │   └── analysis_{timestamp}.png
  └── {environmentName}/{documentId}/tab{tabNumber}/
      └── {timestamp}_{unique_id}.{ext}
\`\`\`

**Secondary Bucket**: \`dd-saved-result\`
- Stores saved analysis results and exports

### File Naming Conventions

- **Screenshots**: \`{session_id}_{journey_id}_{image_doc_id}_{timestamp}.png\`
- **Analysis Images**: \`analysis_{timestamp}.png\`
- **Uploaded Files**: \`{timestamp}_{unique_id}.{ext}\`

### Access Control

- **Public URLs**: Generated for uploaded images (for analysis)
- **Private Access**: Service account credentials for backend access
- **CORS**: Configured for frontend access

## Data Relationships

### Hierarchical Relationships

\`\`\`
Session (1) ──→ (N) User Journeys
User Journey (1) ──→ (N) Images
Session (1) ──→ (N) Activity Logs
Image (1) ──→ (N) Feedback Logs
\`\`\`

### Referential Integrity

- **Session IDs**: Referenced in userJourneys, activityLogs
- **Journey IDs**: Referenced in images
- **Image IDs**: Referenced in feedback, meta-learning images
- **User IDs**: Referenced in all user-related collections

## Indexing Strategy

### Firestore Indexes

**Recommended Composite Indexes**:

1. **Sessions by User and Status**:
   - Collection: \`sessions\`
   - Fields: \`userId\` (Ascending), \`status\` (Ascending), \`createdAt\` (Descending)

2. **Images by Status and Timestamp**:
   - Collection: \`sessions/{sessionId}/userJourneys/{journeyId}/images\`
   - Fields: \`status\` (Ascending), \`createdAt\` (Descending)

3. **Error Logs by Timestamp and Severity**:
   - Collection: \`errorLogs\`
   - Fields: \`timestamp\` (Descending), \`severity\` (Ascending)

4. **Activity Logs by User and Timestamp**:
   - Collection: \`userActivityLogs\`
   - Fields: \`userId\` (Ascending), \`timestamp\` (Descending)

### Query Optimization

- Use indexed fields in WHERE clauses
- Limit result sets with \`.limit()\`
- Use pagination for large result sets
- Cache frequently accessed data

## Backup and Recovery

### Backup Strategy

**Current Approach**:
- Firestore automatic backups (if enabled)
- GCS versioning for critical files
- Manual exports via Firestore console

**Recommended Approach**:
1. **Automated Backups**: Schedule daily Firestore exports to GCS
2. **Point-in-Time Recovery**: Enable Firestore point-in-time recovery
3. **Cross-Region Replication**: Replicate critical data to secondary region
4. **Export Scripts**: Automated export scripts for manual backups

### Recovery Procedures

**Data Restoration**:
\`\`\`bash
# Export Firestore data
gcloud firestore export gs://backup-bucket/firestore-export

# Import Firestore data
gcloud firestore import gs://backup-bucket/firestore-export
\`\`\`

**File Restoration**:
- GCS versioning allows restoration of previous file versions
- Use \`gsutil cp\` to restore from backups

## Data Retention Policies

### Retention Periods

- **Sessions**: Retain for 1 year, then archive
- **Activity Logs**: Retain for 90 days
- **Error Logs**: Retain for 180 days
- **Feedback Logs**: Retain indefinitely
- **Images (Meta-Learning)**: Retain indefinitely

### Archival Strategy

- Move old data to separate "archive" collections
- Compress archived data
- Store in cheaper storage tier (GCS Nearline/Coldline)

## Data Privacy and Compliance

### GDPR Considerations

- **Right to Access**: Users can request their data
- **Right to Deletion**: Implement data deletion endpoints
- **Data Minimization**: Only collect necessary data
- **Consent**: User consent for data collection

### Data Sanitization

- **Logs**: Sanitize sensitive data before logging
- **Exports**: Remove PII from exported data
- **Backups**: Encrypt backup data

## Performance Optimization

### Query Optimization

- Use composite indexes for complex queries
- Limit result sets
- Use pagination
- Cache frequently accessed data

### Storage Optimization

- Compress large documents
- Use references instead of duplicating data
- Archive old data
- Optimize image storage (compression, formats)

## Monitoring and Maintenance

### Monitoring Metrics

- Document read/write counts
- Query performance
- Storage usage
- Error rates

### Maintenance Tasks

- Regular index optimization
- Cleanup of old data
- Storage cost optimization
- Performance tuning

## Related Documentation

- [Deployment Environment](deployment-environment.md)
- [Operational Readiness](operational-readiness.md)
- [Architecture - Data Flow](../architecture/data-flow.md)

`;

// Auto-generated from deployment-environment.md
export const MainToolDocumentationCodeDocumentationDeploymentEnvironment = `# Deployment Environment

## Overview

The Dark Pattern Detection & Compliance Analysis Tool is deployed using a containerized architecture on Google Cloud Platform (GCP) for the backend and Vercel for the frontend. This document details the deployment configuration, environment setup, and operational procedures.

## Backend Deployment (Google Cloud Run)

### Container Configuration

The backend is containerized using Docker and deployed to Google Cloud Run. The containerization is defined in \`Dockerfile\`.

#### Base Image
- **Image**: \`python:3.11-slim\`
- **Working Directory**: \`/app\`
- **Port**: \`8080\`

#### Dependencies Installation
1. System packages: \`wget\`, \`gnupg\`, \`unzip\`, \`curl\`
2. Python packages: Installed from \`requirements.txt\`
3. Google Chrome: Installed for Selenium-based web scraping
4. ChromeDriver: Automatically fetched latest compatible version

#### Key Dockerfile Features
\`\`\`dockerfile
FROM python:3.11-slim
WORKDIR /app
COPY requirements.txt ./
RUN apt-get update && apt-get install -y wget gnupg unzip curl && \\
    pip install --no-cache-dir -r requirements.txt
RUN apt-get update && apt-get install -y wget gnupg && \\
    wget -q -O - https://dl.google.com/linux/linux_signing_key.pub | gpg --dearmor > /usr/share/keyrings/google-linux-signing-key.gpg && \\
    echo "deb [arch=amd64 signed-by=/usr/share/keyrings/google-linux-signing-key.gpg] http://dl.google.com/linux/chrome/deb/ stable main" > /etc/apt/sources.list.d/google.list && \\
    apt-get update && \\
    apt-get install -y google-chrome-stable
RUN CHROME_DRIVER_VERSION=$(curl -sS https://chromedriver.storage.googleapis.com/LATEST_RELEASE) && \\
    wget -O /tmp/chromedriver.zip https://chromedriver.storage.googleapis.com/$CHROME_DRIVER_VERSION/chromedriver_linux64.zip && \\
    unzip /tmp/chromedriver.zip -d /usr/local/bin/
COPY kinetic-mile-439413-k0-bc4aef71e45c.json /app/kinetic-mile-439413-k0-bc4aef71e45c.json
ENV GOOGLE_APPLICATION_CREDENTIALS="/app/kinetic-mile-439413-k0-bc4aef71e45c.json"
COPY . .
ENV PORT=8080
ENV PYTHONUNBUFFERED=True
EXPOSE 8080
CMD ["gunicorn", "app:app", "--bind", "0.0.0.0:8080", "--workers", "4", "--threads", "2", "--timeout", "0"]
\`\`\`

### Gunicorn Configuration

The application runs using Gunicorn as the WSGI HTTP server with the following configuration:
- **Bind Address**: \`0.0.0.0:8080\` (listens on all interfaces)
- **Workers**: 4 worker processes
- **Threads**: 2 threads per worker (8 total concurrent requests)
- **Timeout**: 0 (no worker timeout, suitable for long-running LLM calls)

### Google Cloud Run Deployment

#### Project Configuration
- **Project ID**: \`kinetic-mile-439413-k0\`
- **Region**: \`europe-west1\`
- **Platform**: Managed
- **Memory**: 4Gi
- **Timeout**: 900 seconds (15 minutes)

#### Deployment Commands

**Build Docker Image:**
\`\`\`bash
docker build -t deceptive-design-server .
\`\`\`

**Test Locally:**
\`\`\`bash
docker run -p 8080:8080 --env-file .env deceptive-design-server
\`\`\`

**Tag for Google Container Registry:**
\`\`\`bash
docker tag deceptive-design-server gcr.io/kinetic-mile-439413-k0/deceptive-design-server
\`\`\`

**Push to GCR:**
\`\`\`bash
docker push gcr.io/kinetic-mile-439413-k0/deceptive-design-server
\`\`\`

**Deploy to Cloud Run (PowerShell):**
\`\`\`powershell
gcloud run deploy deceptive-design-server \`
--image gcr.io/kinetic-mile-439413-k0/deceptive-design-server \`
--region europe-west1 \`
--platform managed \`
--memory 4Gi \`
--env-vars-file env.yaml \`
--timeout=900
\`\`\`

**Deploy to Cloud Run (Command Prompt):**
\`\`\`cmd
gcloud run deploy deceptive-design-server ^
--image gcr.io/kinetic-mile-439413-k0/deceptive-design-server ^
--region europe-west1 ^
--platform managed ^
--memory 4Gi ^
--env-vars-file env.yaml
\`\`\`

### Environment Variables

Environment variables are managed through \`env.yaml\` file for Cloud Run deployment. Key variables include:

#### Google Cloud Configuration
- \`GOOGLE_APPLICATION_CREDENTIALS\` - Path to service account JSON (container path)
- \`GOOGLE_APPLICATION_CREDENTIALS_2\` - Firebase service account credentials path
- \`GCP_PROJECT\` - Google Cloud Project ID
- \`CLOUD_TASKS_QUEUE\` - Cloud Tasks queue name
- \`CLOUD_TASKS_LOCATION\` - Cloud Tasks region location
- \`PROCESS_SCREEN_CAPTURE_URL\` - URL for background processing endpoint

#### API Keys (Multiple Keys for Load Distribution)
- \`OPENAI_API_KEY\` - Default OpenAI API key
- \`OPENAI_API_KEY_USER_INFO\` - OpenAI key for user information extraction tasks
- \`OPENAI_API_KEY_PARENT_CATEGORY\` - OpenAI key for parent category analysis
- \`OPENAI_API_KEY_AGENTS\` - OpenAI key for sub-agent tasks
- \`OPENAI_API_KEY_SCANNER\` - OpenAI key for scanner analysis
- \`OPENAI_API_KEY_HIGHLIGHTS\` - OpenAI key for highlights generation
- \`OPENAI_API_KEY_HARM_MEASURE\` - OpenAI key for harm measurement
- \`OPENAI_API_KEY_VALIDATION\` - OpenAI key for validation tasks
- \`OPENAI_API_KEY_FACT_CHECK\` - OpenAI key for fact-checking
- \`GOOGLE_API_KEY\` - Google Generative AI (Gemini) API key

#### External Services
- \`PERPLEXITY_API_KEY\` - Perplexity AI API key for law fetching
- \`AIRTABLE_API_KEY\` - Airtable API key
- \`BASE_ID\` - Airtable base ID
- \`PROMPT_TABLE_ID\` - Airtable prompt table ID
- \`PROHIBITIONS_TABLE_ID\` - Airtable prohibitions table ID
- \`SUPABASE_URL\` - Supabase project URL
- \`SUPABASE_KEY\` - Supabase API key

#### Model Configuration
- \`SUBAGENTS_MODEL\` - Model for sub-agents (default: \`gpt-4o-mini\`)
- \`OUTCOME_SYNTHESIS_MODEL_NAME\` - Model for outcome synthesis (default: \`gpt-4o-mini\`)
- \`REGULATORY_PREVENTION_MODEL\` - Model for regulatory prevention (default: \`gpt-4o-mini\`)

### Secrets Management

**Current Approach:**
- Service account JSON files are copied into the Docker image
- Environment variables stored in \`env.yaml\` for Cloud Run
- API keys managed through environment variables

**Best Practices (Recommended):**
- Use Google Secret Manager for sensitive credentials
- Rotate API keys regularly
- Never commit credentials to version control
- Use IAM roles and service accounts with minimal permissions

## Frontend Deployment (Vercel)

### Configuration

The frontend is deployed on Vercel with configuration in \`vercel.json\`:

\`\`\`json
{
  "rewrites": [
    { "source": "/(.*)", "destination": "/" }
  ]
}
\`\`\`

This configuration enables client-side routing for the React application.

### Build Process

1. **Install Dependencies:**
   \`\`\`bash
   npm install
   \`\`\`

2. **Build for Production:**
   \`\`\`bash
   npm run build
   \`\`\`

3. **Deploy to Vercel:**
   - Automatic deployment via Git integration, or
   - Manual deployment: \`vercel --prod\`

### Environment Variables (Frontend)

Set in Vercel dashboard or via CLI:

- \`REACT_APP_FIREBASE_API_KEY\`
- \`REACT_APP_FIREBASE_AUTH_DOMAIN\`
- \`REACT_APP_FIREBASE_PROJECT_ID\`
- \`REACT_APP_FIREBASE_STORAGE_BUCKET\`
- \`REACT_APP_FIREBASE_MESSAGING_SENDER_ID\`
- \`REACT_APP_FIREBASE_APP_ID\`
- \`REACT_APP_FIREBASE_MEASUREMENT_ID\`
- \`REACT_APP_AUTHORIZED_EMAILS\` - Comma-separated authorized emails

## Google Cloud Services

### Cloud Storage
- **Bucket**: \`dd-media-files\` - For image and file storage
- **Bucket**: \`dd-saved-result\` - For saved analysis results
- **Access**: Public URLs generated for uploaded files

### Cloud Firestore
- **Database**: NoSQL document database
- **Collections**:
  - \`sessions\` - User session data
  - \`userActivityLogs\` - Activity tracking
  - \`errorLogs\` - Error logging
  - \`FeedbackLogs\` - User feedback
  - \`images\` - Image metadata

### Cloud Tasks
- **Purpose**: Asynchronous background job processing
- **Queue**: Configured via \`CLOUD_TASKS_QUEUE\` environment variable
- **Location**: Configured via \`CLOUD_TASKS_LOCATION\` environment variable

## Local Development Setup

### Backend

1. **Prerequisites:**
   - Python 3.11+
   - Docker (for containerized testing)
   - Google Cloud SDK (\`gcloud\` CLI)

2. **Setup:**
   \`\`\`bash
   # Install dependencies
   pip install -r requirements.txt
   
   # Set environment variables
   cp .env.example .env
   # Edit .env with your credentials
   
   # Run locally
   python app.py
   # Or with Gunicorn
   gunicorn app:app --bind 0.0.0.0:5000 --reload
   \`\`\`

3. **Docker Local Testing:**
   \`\`\`bash
   docker build -t deceptive-design-server .
   docker run -p 8080:8080 --env-file .env deceptive-design-server
   \`\`\`

### Frontend

1. **Prerequisites:**
   - Node.js 18+
   - npm or yarn

2. **Setup:**
   \`\`\`bash
   # Install dependencies
   npm install
   
   # Set environment variables
   # Create .env file with REACT_APP_* variables
   
   # Run development server
   npm start
   # Runs on http://localhost:3000
   \`\`\`

3. **Build for Production:**
   \`\`\`bash
   npm run build
   # Output in build/ directory
   \`\`\`

## Monitoring and Logging

### Application Logs
- **Backend**: Logs to stdout/stderr, captured by Cloud Run
- **Frontend**: Console logs in browser, production errors logged to Firebase
- **Error Tracking**: Firebase \`errorLogs\` collection
- **Activity Tracking**: Firebase \`userActivityLogs\` collection

### Cloud Run Monitoring
- View logs: \`gcloud logging read "resource.type=cloud_run_revision"\`
- Metrics available in Google Cloud Console:
  - Request count
  - Latency
  - Error rate
  - Memory usage
  - CPU utilization

## Scaling Configuration

### Cloud Run Auto-Scaling
- **Min Instances**: 0 (scales to zero when idle)
- **Max Instances**: Configurable (default: 100)
- **Concurrency**: 80 requests per instance (default)
- **CPU**: Allocated based on memory (4Gi = ~4 vCPU)

### Performance Tuning
- **Memory**: 4Gi allocated for LLM processing
- **Timeout**: 900 seconds for long-running analyses
- **Workers**: 4 Gunicorn workers with 2 threads each

## Health Checks

### Backend Health Endpoint
Currently, health checks can be performed by:
- Checking Cloud Run service status
- Monitoring error rates in logs
- Implementing a \`/health\` endpoint (recommended)

### Recommended Health Endpoint Implementation
\`\`\`python
@app.route('/health', methods=['GET'])
def health_check():
    return jsonify({
        "status": "healthy",
        "timestamp": datetime.now().isoformat(),
        "version": "1.0.0"
    }), 200
\`\`\`

## Rollback Procedures

### Cloud Run Rollback
\`\`\`bash
# List revisions
gcloud run revisions list --service deceptive-design-server --region europe-west1

# Rollback to previous revision
gcloud run services update-traffic deceptive-design-server \\
  --to-revisions REVISION_NAME=100 \\
  --region europe-west1
\`\`\`

### Vercel Rollback
- Use Vercel dashboard to revert to previous deployment
- Or via CLI: \`vercel rollback\`

## Troubleshooting

### Common Issues

1. **Container Build Failures**
   - Check Dockerfile syntax
   - Verify all dependencies in requirements.txt
   - Ensure service account JSON is available

2. **Environment Variable Issues**
   - Verify \`env.yaml\` format
   - Check variable names match code expectations
   - Ensure secrets are properly set

3. **Memory Issues**
   - Increase Cloud Run memory allocation
   - Monitor memory usage in Cloud Console
   - Optimize LLM call patterns

4. **Timeout Issues**
   - Increase Cloud Run timeout
   - Implement async processing for long operations
   - Use Cloud Tasks for background jobs

## Related Documentation

- [Libraries & Dependencies](libraries-dependencies.md)
- [Operational Readiness](operational-readiness.md)
- [Architecture - Deployment](../architecture/deployment-architecture.md)

`;

// Auto-generated from design-philosophy.md
export const MainToolDocumentationCodeDocumentationDesignPhilosophy = `# Core Code Approach & Design Philosophy

## Overview

The Dark Pattern Detection & Compliance Analysis Tool is built on a multi-agent AI architecture that processes web interfaces through a phased pipeline. This document outlines the core design principles, architectural patterns, and development philosophy that guide the codebase.

## Design Principles

### 1. Multi-Agent AI Architecture

The system employs a hierarchical agent-based approach for dark pattern detection:

- **Parent Category Agents**: 6 high-level scanners (Coercing, Falsification, Misdirection, Obstructing, Sneaking, Omitting)
- **Sub-Agents**: 30+ specialized detectors that run in parallel
- **Synthesis Agents**: Outcome generation, validation, and fact-checking agents

**Rationale**: This hierarchical approach allows for comprehensive analysis while maintaining modularity and extensibility.

### 2. Phased Processing Pipeline

The system processes images through a well-defined 4-step pipeline:

\`\`\`
Step 1: Information Extraction
  ↓
Step 2: Agent Search (Parent Categories)
  ↓
Step 3: Sub-Agent Execution (Parallel)
  ↓
Step 4: Outcome Synthesis
\`\`\`

**Benefits**:
- Allows resumable processing (status tracked in Firebase)
- Enables incremental updates to user interface
- Facilitates error recovery and retry logic
- Supports partial results display

### 3. Asynchronous and Parallel Processing

- **ThreadPoolExecutor**: Sub-agents run in parallel for performance
- **Async/Await**: Used for I/O-bound operations (LLM calls, storage)
- **Cloud Tasks**: Background processing for long-running operations

**Performance Impact**: Reduces total processing time from sequential ~5 minutes to ~2-3 minutes for typical analysis.

### 4. Task-Based API Key Distribution

The system distributes LLM calls across multiple API keys based on task type:

\`\`\`python
TASK_API_MAPPING = {
    "Page Name Extraction": "OPENAI_API_KEY_USER_INFO",
    "Coercing Scanner Analysis": "OPENAI_API_KEY_PARENT_CATEGORY",
    "Subagent 1 Analysis": "OPENAI_API_KEY_AGENTS",
    # ... etc
}
\`\`\`

**Benefits**:
- Load balancing across API keys
- Rate limit management
- Cost optimization
- Fault tolerance (if one key fails, others continue)

### 5. Centralized LLM Abstraction

All LLM calls go through \`utility/LlmCalls.py\`:

- Unified interface for different providers (OpenAI, Anthropic, Google)
- Automatic logging to Firebase
- Token usage tracking
- Error handling and retries
- Timing metrics

**Code Pattern**:
\`\`\`python
response = call_llm(
    model_name="gpt-4o-mini",
    prompt=prompt_text,
    task_name="Task Description",
    image_id=image_id
)
\`\`\`

## Architecture Patterns

### 1. Modular Utility Structure

The codebase is organized into focused utility modules:

- \`utility/LlmCalls.py\` - LLM abstraction layer
- \`utility/SubAgents.py\` - Sub-agent implementations
- \`utility/OutcomeSynthesis.py\` - Outcome generation and validation
- \`utility/RegulatoryPrevention.py\` - Compliance mapping
- \`utility/AdvancedLawFetcher.py\` - Law retrieval
- \`utility/context.py\` - Context extraction
- \`utility/OutcomeCordinates.py\` - UI coordinate generation

**Principle**: Single Responsibility - each module has a clear, focused purpose.

### 2. Status-Based State Management

Processing state is tracked in Firebase documents:

- \`initial\` - Document created, processing not started
- \`step1_completed\` - Information extraction complete
- \`step2_completed\` - Agent search complete
- \`step3_completed\` - Sub-agents execution complete
- \`completed\` - Full analysis complete
- \`error\` - Error state with error message

**Benefits**:
- Resumable processing
- Progress tracking
- Error recovery
- User feedback

### 3. Decorator Pattern for Error Logging

API endpoints use a decorator for automatic error logging:

\`\`\`python
@log_api_failures
def my_endpoint():
    # Endpoint logic
    pass
\`\`\`

**Features**:
- Automatic exception capture
- Payload sanitization
- Firebase error logging
- Request metadata capture

### 4. Factory Pattern for Agent Selection

Agents are selected dynamically based on parent category results:

\`\`\`python
agent_function_map = {
    'Preselection v0.1': generate_preselection,
    'Hidden legalese': generate_hidden_legalese,
    # ... etc
}
\`\`\`

## Dark Pattern Detection Methodology

### Detection Approach

1. **Context Extraction**: Analyze image, URL, and user notes to understand the interface
2. **Parent Category Scanning**: Identify high-level dark pattern categories
3. **Sub-Agent Analysis**: Deep dive into specific patterns within identified categories
4. **Validation**: Fact-check and validate findings
5. **Synthesis**: Generate outcomes, coordinates, and legal references

### Pattern Categories

**Coercing**: Patterns that force or pressure users into actions
- Commercial coercion
- Privacy coercion
- Platform/service coercion

**Falsification**: Patterns that present false or misleading information
- False social proof
- False urgency
- False scarcity
- False endorsements
- False trust marks

**Misdirection**: Patterns that distract or mislead users
- Disguised ads
- Trick wording
- Misleading pricing

**Obstructing**: Patterns that make actions difficult
- General obstruction
- Visual interference
- Comparison prevention
- Hard to cancel

**Sneaking**: Patterns that hide information or actions
- Hidden costs
- Hidden subscription
- Hidden legalese
- Bait and switch

**Omitting**: Patterns that fail to disclose important information
- Misleading omissions
- Unclear terms
- Delayed acknowledgments

## Compliance Analysis Framework

### Regulatory Framework Integration

The system integrates with multiple regulatory frameworks:

- **EU**: Digital Content Directive, AVMSD, DMA, CRD, UCPD, GDPR, DSA, etc.
- **US**: FTC Act, CCPA, CPRA, COPPA, CAN-SPAM, etc.
- **UK**: UK GDPR, Consumer Rights Act, Online Safety Act, etc.

### Law Fetching Process

1. **Pattern Identification**: Identify dark pattern from analysis
2. **Jurisdiction Selection**: User or system selects jurisdiction(s)
3. **Law Retrieval**: Query Perplexity AI for relevant laws
4. **Validation**: Cross-reference with Airtable database
5. **Fair Pattern Suggestions**: Generate compliant alternatives

### Compliance Scoring

- **Outcome**: Confident violations (high score)
- **Doubtful**: Uncertain violations (medium score)
- **Hallucination**: Invalid detections (low/zero score)

## Error Handling Strategy

### Multi-Layer Error Handling

1. **Try-Except Blocks**: Catch and handle exceptions at function level
2. **Decorator Logging**: Automatic error logging for API endpoints
3. **Firebase Error Collection**: Centralized error tracking
4. **User-Friendly Messages**: Sanitized error messages for frontend
5. **Retry Logic**: Automatic retries for transient failures

### Error Categories

- **API Errors**: LLM API failures, rate limits
- **Storage Errors**: Firebase, GCS failures
- **Validation Errors**: Invalid input data
- **Processing Errors**: Analysis failures
- **Network Errors**: Timeouts, connection issues

### Recovery Mechanisms

- **Status Tracking**: Resume from last completed step
- **Partial Results**: Return partial data when possible
- **Fallback Models**: Use alternative LLM providers if primary fails
- **Graceful Degradation**: Continue processing with available data

## Logging Strategy

### Logging Levels

1. **Info**: Normal operation flow
2. **Warning**: Non-critical issues
3. **Error**: Exceptions and failures
4. **Debug**: Detailed diagnostic information

### Logging Destinations

- **Console/Stdout**: Development and Cloud Run logs
- **Firebase Firestore**: Structured error logs (\`errorLogs\` collection)
- **Firebase Firestore**: User activity logs (\`userActivityLogs\` collection)
- **Firebase Firestore**: LLM usage logs (via \`LlmCalls.py\`)

### Logged Information

- **API Calls**: Endpoint, method, payload (sanitized), response time
- **LLM Calls**: Model, task name, tokens used, cost, duration
- **Errors**: Exception type, message, stack trace, context
- **User Actions**: Login, analysis requests, feedback

## Code Organization Principles

### 1. Separation of Concerns

- **Backend (\`app.py\`)**: API endpoints and request handling
- **Utilities (\`utility/\`)**: Business logic and reusable functions
- **Modules (\`DarkPattern/\`, \`DigitalViolation/\`)**: Domain-specific analysis
- **Frontend (\`src/components/\`)**: UI components and user interaction

### 2. DRY (Don't Repeat Yourself)

- Centralized LLM calling
- Reusable utility functions
- Shared data structures
- Common error handling patterns

### 3. Configuration Over Code

- Environment variables for all configuration
- Airtable for prompt management
- JSON files for compliance prompts
- Firebase for dynamic data

### 4. Extensibility

- Easy to add new sub-agents
- Pluggable LLM providers
- Configurable jurisdictions
- Modular compliance frameworks

## Performance Considerations

### Optimization Strategies

1. **Parallel Processing**: Sub-agents run concurrently
2. **Caching**: TTL caches for Airtable data (5-minute TTL)
3. **Async Operations**: Non-blocking I/O for LLM calls
4. **Background Jobs**: Long operations via Cloud Tasks
5. **Efficient Data Structures**: Optimized for Firebase queries

### Resource Management

- **Memory**: 4Gi allocated for LLM processing
- **CPU**: 4 workers × 2 threads = 8 concurrent requests
- **Timeout**: 900 seconds for long analyses
- **Rate Limiting**: Distributed across multiple API keys

## Security Philosophy

### Defense in Depth

1. **Authentication**: Firebase OAuth2
2. **Authorization**: Email-based access control
3. **Input Validation**: Sanitize all user inputs
4. **Output Sanitization**: Remove sensitive data from logs
5. **API Security**: CORS, rate limiting, error handling

### Data Privacy

- **Minimal Data Collection**: Only necessary data stored
- **Data Sanitization**: Sensitive fields redacted in logs
- **Secure Storage**: Firebase and GCS with proper access controls
- **GDPR Compliance**: User data handling considerations

## Testing Philosophy

### Testing Approach

- **Unit Tests**: Individual function testing (to be implemented)
- **Integration Tests**: API endpoint testing (to be implemented)
- **Error Injection**: Test failure scenarios
- **Performance Tests**: Load and stress testing

### Quality Assurance

- **Code Review**: Peer review process
- **Error Logging**: Monitor production errors
- **User Feedback**: Continuous improvement based on feedback
- **A/B Testing**: Model and prompt optimization

## Continuous Improvement

### Learning Mechanisms

1. **Reasoning Storage**: Store LLM reasoning in Supabase for analysis
2. **Feedback Loops**: User feedback on analysis accuracy
3. **Meta-Learning**: Improve prompts based on historical data
4. **Model Comparison**: Compare different LLM providers

### Version Control

- **Git Strategy**: Feature branches, main branch for production
- **Versioning**: Semantic versioning (major.minor.patch)
- **Release Notes**: Document changes and breaking changes
- **Migration Guides**: Support for version upgrades

## Related Documentation

- [Code Examples](code-examples.md)
- [Performance Benchmarking](performance-benchmarking.md)
- [Developer Experience](developer-experience.md)
- [Architecture - System Overview](../architecture/system-overview.md)

`;

// Auto-generated from developer-experience.md
export const MainToolDocumentationCodeDocumentationDeveloperExperience = `# Developer Experience

## Overview

This document provides guidelines for developers contributing to the Dark Pattern Detection & Compliance Analysis Tool, including contribution guidelines, code structure, extensibility points, and development workflows.

## Contribution Guidelines

### Getting Started

1. **Fork the Repository**
2. **Clone Your Fork**:
   \`\`\`bash
   git clone https://github.com/your-username/deceptive-design-tool.git
   cd deceptive-design-tool
   \`\`\`

3. **Set Up Development Environment**:
   \`\`\`bash
   # Backend
   cd Tool-Application-Backend-main
   python -m venv venv
   source venv/bin/activate  # On Windows: venv\\Scripts\\activate
   pip install -r requirements.txt
   
   # Frontend
   cd Tool-Application-Frontend-main
   npm install
   \`\`\`

4. **Create Feature Branch**:
   \`\`\`bash
   git checkout -b feature/your-feature-name
   \`\`\`

### Code Style

**Python**:
- Follow PEP 8 style guide
- Use type hints where possible
- Maximum line length: 100 characters
- Use Black for formatting (recommended)

**JavaScript**:
- Follow ESLint configuration
- Use Prettier for formatting
- Follow React best practices

### Commit Guidelines

**Format**: \`<type>(<scope>): <subject>\`

**Types**:
- \`feat\`: New feature
- \`fix\`: Bug fix
- \`docs\`: Documentation
- \`style\`: Code style
- \`refactor\`: Refactoring
- \`test\`: Tests
- \`chore\`: Maintenance

**Example**:
\`\`\`
feat(agents): Add new dark pattern detection agent
fix(api): Resolve timeout in image processing
docs(readme): Update installation instructions
\`\`\`

### Pull Request Process

1. **Update Documentation**: Ensure all changes are documented
2. **Add Tests**: Include tests for new features
3. **Run Tests**: Ensure all tests pass
4. **Update Changelog**: Add entry to CHANGELOG.md
5. **Create PR**: Submit pull request with description
6. **Code Review**: Address review comments
7. **Merge**: After approval, merge to develop

### Pull Request Template

\`\`\`markdown
## Description
Brief description of changes

## Type of Change
- [ ] Bug fix
- [ ] New feature
- [ ] Breaking change
- [ ] Documentation update

## Testing
- [ ] Unit tests added/updated
- [ ] Integration tests added/updated
- [ ] Manual testing completed

## Checklist
- [ ] Code follows style guidelines
- [ ] Self-review completed
- [ ] Comments added for complex code
- [ ] Documentation updated
- [ ] No new warnings generated
\`\`\`

## Code Structure and Organization

### Backend Structure

\`\`\`
Tool-Application-Backend-main/
├── app.py                 # Main Flask application
├── requirements.txt       # Python dependencies
├── Dockerfile            # Container configuration
├── utility/              # Utility modules
│   ├── LlmCalls.py       # LLM abstraction
│   ├── SubAgents.py      # Sub-agent implementations
│   ├── OutcomeSynthesis.py # Outcome generation
│   ├── RegulatoryPrevention.py # Compliance mapping
│   └── ...
├── DarkPattern/          # Dark pattern detection
│   ├── version3.py       # Main analysis engine
│   ├── agents.py         # Agent definitions
│   └── ...
├── DigitalViolation/     # Compliance analysis
│   └── compliance_analysis.py
└── tests/               # Test files
    ├── unit/
    ├── integration/
    └── e2e/
\`\`\`

### Frontend Structure

\`\`\`
Tool-Application-Frontend-main/
├── src/
│   ├── App.js           # Main app component
│   ├── firebase.js      # Firebase configuration
│   ├── components/      # React components
│   │   ├── Dashboard.js
│   │   ├── ExplorePage.js
│   │   └── ...
│   ├── config/          # Configuration
│   │   └── auth.js      # Auth configuration
│   └── ...
├── public/              # Static assets
└── package.json         # Dependencies
\`\`\`

### Module Organization Principles

1. **Single Responsibility**: Each module has one clear purpose
2. **Separation of Concerns**: Business logic separated from API layer
3. **Dependency Injection**: Dependencies passed as parameters
4. **Interface Abstraction**: Use interfaces for testability

## Extensibility Points

### Adding New Dark Pattern Detectors

**Step 1**: Create agent function in \`utility/SubAgents.py\`

\`\`\`python
def generate_new_pattern_detector(
    image_url,
    parent_category,
    scanner_context,
    overall_context,
    prompt_context,
    call_to_action,
    webpage_brief,
    note,
    image_id
):
    """Detect new dark pattern type."""
    prompt = f"""
    Analyze for [new pattern type]:
    Context: {overall_context}
    Scanner: {scanner_context}
    """
    
    response = call_llm(
        model_name="gpt-4o-mini",
        prompt=prompt,
        task_name="New Pattern Detection",
        image_url=image_url,
        image_id=image_id
    )
    
    return response
\`\`\`

**Step 2**: Add to agent function map in \`app.py\`

\`\`\`python
agent_function_map = {
    # ... existing agents
    'New Pattern v0.1': generate_new_pattern_detector,
}
\`\`\`

**Step 3**: Update documentation

### Adding New LLM Providers

**Step 1**: Add provider support in \`utility/LlmCalls.py\`

\`\`\`python
def call_custom_llm(model_name, prompt, task_name, image_url=None, image_id=None):
    """Call custom LLM provider."""
    # Implementation
    pass
\`\`\`

**Step 2**: Add to provider selection logic

\`\`\`python
if model_name.startswith('custom-'):
    return call_custom_llm(model_name, prompt, task_name, image_url, image_id)
\`\`\`

### Adding New Compliance Frameworks

**Step 1**: Add jurisdiction config in \`utility/AdvancedLawFetcher.py\`

\`\`\`python
JURISDICTION_CONFIGS = {
    # ... existing jurisdictions
    "NEW_JURISDICTION": {
        "name": "New Jurisdiction",
        "key_laws": ["Law 1", "Law 2"],
        "description": "Description"
    }
}
\`\`\`

**Step 2**: Update law fetching logic

### Adding New API Endpoints

**Step 1**: Create endpoint function

\`\`\`python
@app.route('/api/new_endpoint', methods=['POST'])
@log_api_failures
def new_endpoint():
    """New endpoint description."""
    try:
        data = request.get_json()
        # Process request
        return jsonify({"status": "success", "data": result}), 200
    except Exception as e:
        return jsonify({"status": "error", "message": str(e)}), 500
\`\`\`

**Step 2**: Add to API documentation

## Development Workflows

### Local Development

**Backend**:
\`\`\`bash
# Start development server
python app.py

# Or with auto-reload
flask run --reload

# Run tests
pytest tests/
\`\`\`

**Frontend**:
\`\`\`bash
# Start development server
npm start

# Run tests
npm test

# Build for production
npm run build
\`\`\`

### Testing Workflow

1. **Write Tests First** (TDD recommended)
2. **Run Tests Frequently**
3. **Ensure All Tests Pass Before Committing**
4. **Run Full Test Suite Before PR**

### Debugging

**Backend Debugging**:
\`\`\`python
import pdb; pdb.set_trace()  # Breakpoint

# Or use logging
import logging
logging.basicConfig(level=logging.DEBUG)
logger = logging.getLogger(__name__)
logger.debug("Debug message")
\`\`\`

**Frontend Debugging**:
- Use React DevTools
- Browser console
- Network tab for API calls

### Code Review Guidelines

**For Reviewers**:
- Be constructive and respectful
- Focus on code quality and correctness
- Check for security issues
- Verify tests are adequate

**For Authors**:
- Respond to all comments
- Make requested changes
- Explain design decisions if needed
- Update documentation

## Automated Documentation Generation

### API Documentation

**Tool**: Swagger/OpenAPI (recommended)

\`\`\`python
from flask_restx import Api, Resource

api = Api(app, doc='/api-docs/')

@api.route('/get_image_metadata')
class ImageMetadata(Resource):
    @api.doc('get_image_metadata')
    @api.expect(image_metadata_model)
    @api.marshal_with(result_model)
    def post(self):
        """Get image metadata."""
        pass
\`\`\`

### Code Documentation

**Python**: Sphinx or pydoc

\`\`\`bash
# Generate documentation
sphinx-build -b html docs/ docs/_build/html/
\`\`\`

**JavaScript**: JSDoc

\`\`\`bash
# Generate documentation
npm run docs
\`\`\`

## Development Tools

### Recommended Tools

**Backend**:
- **IDE**: VS Code or PyCharm
- **Linter**: pylint or flake8
- **Formatter**: Black
- **Type Checker**: mypy

**Frontend**:
- **IDE**: VS Code
- **Linter**: ESLint
- **Formatter**: Prettier
- **Package Manager**: npm or yarn

### VS Code Extensions

**Recommended**:
- Python
- ESLint
- Prettier
- GitLens
- Docker
- Firebase

## Common Development Tasks

### Adding a New Feature

1. Create feature branch
2. Implement feature
3. Write tests
4. Update documentation
5. Create PR
6. Address review comments
7. Merge

### Fixing a Bug

1. Create bugfix branch
2. Reproduce bug
3. Write failing test
4. Fix bug
5. Verify test passes
6. Update documentation if needed
7. Create PR
8. Merge

### Refactoring

1. Create refactor branch
2. Ensure tests exist
3. Refactor code
4. Run tests frequently
5. Update documentation
6. Create PR with explanation
7. Merge

## Best Practices

### Code Quality

- **Write Clean Code**: Readable, maintainable
- **Follow DRY**: Don't repeat yourself
- **Use Meaningful Names**: Clear variable/function names
- **Keep Functions Small**: Single responsibility
- **Add Comments**: Explain why, not what

### Testing

- **Test Edge Cases**: Boundary conditions
- **Test Error Handling**: Failure scenarios
- **Maintain Test Coverage**: Aim for 80%+
- **Keep Tests Fast**: Unit tests should be quick
- **Isolate Tests**: No dependencies between tests

### Documentation

- **Document Public APIs**: All public functions
- **Update README**: Keep it current
- **Write Clear Commit Messages**: Descriptive
- **Document Complex Logic**: Explain reasoning

## Getting Help

### Resources

- **Documentation**: This documentation set
- **Code Comments**: Inline documentation
- **GitHub Issues**: Report bugs, ask questions
- **Team Communication**: Slack/Discord (if available)

### Asking Questions

When asking for help:
1. **Describe the Problem**: What are you trying to do?
2. **Show What You've Tried**: Code snippets, error messages
3. **Provide Context**: Environment, versions
4. **Be Specific**: Clear, concise questions

## Related Documentation

- [Code Examples](code-examples.md)
- [Testing & QA](testing-qa.md)
- [Versioning](versioning.md)
- [API Documentation](../api-documentation/endpoints.md)

`;

// Auto-generated from libraries-dependencies.md
export const MainToolDocumentationCodeDocumentationLibrariesDependencies = `# Libraries and Dependencies

## Overview

This document provides a comprehensive overview of all libraries, frameworks, and dependencies used in the Dark Pattern Detection & Compliance Analysis Tool. The application consists of a Flask-based backend and a React-based frontend, with extensive integrations for AI/ML, cloud services, and data processing.

## Backend Dependencies

### Core Framework
- **Flask** - Lightweight Python web framework for building REST APIs
- **Flask-CORS** - Cross-Origin Resource Sharing support for API endpoints
- **gunicorn** - Python WSGI HTTP Server for production deployment (4 workers, 2 threads per worker)

### Cloud Services & Storage
- **google-cloud-storage** - Google Cloud Storage client for file/image storage
- **google-cloud-firestore** - Firestore NoSQL database client
- **firebase-admin** - Firebase Admin SDK for server-side operations
- **google-cloud-aiplatform>=1.38** - Google Cloud AI Platform integration
- **google-cloud-tasks** - Google Cloud Tasks for asynchronous job processing
- **vertexai** - Vertex AI SDK for machine learning operations
- **google-generativeai** - Google Generative AI (Gemini) client library

### AI/ML & LLM Integrations
- **openai** - OpenAI API client for GPT models
- **langchain** - Framework for developing applications powered by language models
- **langchain-openai** - LangChain integration for OpenAI models
- **langchain-google-genai** - LangChain integration for Google Generative AI
- **langchain-google-genai** (duplicate) - Additional Google AI integration
- **langchain-community** - Community-contributed LangChain integrations
- **langchain-anthropic** - LangChain integration for Anthropic Claude models
- **langchain-huggingface** - LangChain integration for HuggingFace models
- **huggingface_hub** - HuggingFace Hub client library (appears twice in requirements)
- **agent-squad[openai]** - Agent framework with OpenAI support
- **agent-squad[sql]** - Agent framework with SQL support

### Data Processing & Web Scraping
- **beautifulsoup4** - HTML/XML parsing library (appears twice in requirements)
- **selenium** - Web browser automation for dynamic content scraping
- **webdriver-manager** - Automatic management of browser drivers
- **requests** - HTTP library for API calls (appears twice in requirements)
- **tldextract** - Domain name extraction and parsing

### Image Processing
- **Pillow** - Python Imaging Library for image manipulation

### Data Management
- **gspread** - Google Sheets API client
- **oauth2client** - OAuth 2.0 client library
- **chromadb** - ChromaDB vector database for embeddings
- **supabase>=2.0.0** - Supabase client for reasoning storage
- **unstructured** - Unstructured data processing library

### Utilities
- **python-dotenv>=0.19.0** - Environment variable management
- **cachetools** - Caching utilities
- **jq** - JSON query processor
- **boto3** - AWS SDK for Python (for potential AWS integrations)

### Development Tools
- **streamlit** - Rapid web app development framework (may be used for admin tools)

## Frontend Dependencies

### Core Framework
- **react** (^18.3.1) - React library for building user interfaces
- **react-dom** (^18.3.1) - React DOM renderer
- **react-router-dom** (^6.28.0) - Declarative routing for React applications
- **react-scripts** (5.0.1) - Create React App scripts and configuration

### UI Components & Styling
- **bootstrap** (^5.3.3) - CSS framework for responsive design
- **react-bootstrap** (^2.10.4) - Bootstrap components for React
- **@fortawesome/fontawesome-svg-core** (^6.6.0) - Font Awesome icon library core
- **@fortawesome/free-solid-svg-icons** (^6.6.0) - Font Awesome solid icons
- **@fortawesome/react-fontawesome** (^0.2.2) - Font Awesome React component

### Data Visualization
- **chart.js** (^4.4.4) - Charting library
- **react-chartjs-2** (^5.2.0) - React wrapper for Chart.js
- **d3** (^7.9.0) - Data visualization library
- **react-google-charts** (^5.2.1) - Google Charts React wrapper
- **react-d3-tree** (^3.6.2) - React component for D3 tree visualizations
- **react-flow-renderer** (^10.3.17) - React component for flow diagrams
- **reactflow** (^11.11.4) - Modern React flow library
- **react-force-graph-2d** (^1.27.1) - 2D force-directed graph component
- **dagre** (^0.8.5) - Directed graph layout library

### Firebase Integration
- **firebase** (^10.14.0) - Firebase JavaScript SDK
- **react-firebase-hooks** (^5.1.1) - React hooks for Firebase

### Data Processing
- **axios** (^1.7.7) - HTTP client for API requests
- **papaparse** (^5.5.3) - CSV parser
- **xlsx** (^0.18.5) - Excel file parser/writer
- **airtable** (^0.12.2) - Airtable API client

### UI Utilities
- **react-toastify** (^11.0.5) - Toast notification library
- **react-syntax-highlighter** (^15.6.1) - Syntax highlighting component

### Testing
- **@testing-library/jest-dom** (^5.17.0) - Custom Jest matchers for DOM testing
- **@testing-library/react** (^13.4.0) - React testing utilities
- **@testing-library/user-event** (^13.5.0) - User event simulation for testing

### Performance & Analytics
- **web-vitals** (^2.1.4) - Web performance metrics

## Dependency Management

### Backend
- Dependencies are managed via \`requirements.txt\`
- Some packages appear multiple times (requests, beautifulsoup4, huggingface_hub, gunicorn) - these should be deduplicated
- Version pinning is minimal - most packages use latest compatible versions
- Specific version constraints:
  - \`python-dotenv>=0.19.0\`
  - \`google-cloud-aiplatform>=1.38\`
  - \`supabase>=2.0.0\`

### Frontend
- Dependencies managed via \`package.json\` and \`package-lock.json\`
- Version ranges use semantic versioning (^) for minor/patch updates
- All dependencies are production-ready with no development-only dependencies listed separately

## Compatibility Matrix

### Python Version
- Recommended: Python 3.11+ (as specified in Dockerfile: \`python:3.11-slim\`)
- Minimum: Python 3.9+ (for LangChain and modern async features)

### Node.js Version
- Recommended: Node.js 18+ (for React 18 compatibility)
- Minimum: Node.js 16+

### Browser Compatibility
- Modern browsers (Chrome, Firefox, Safari, Edge)
- Production build targets: \`>0.2%\`, \`not dead\`, \`not op_mini all\`
- Development targets: Latest versions of Chrome, Firefox, Safari

## Security Considerations

### Known Vulnerabilities
- Regular dependency audits should be performed using:
  - \`npm audit\` for frontend dependencies
  - \`pip-audit\` or \`safety\` for Python dependencies

### Authentication & Authorization
- Firebase Admin SDK handles server-side authentication
- OAuth2 client library for Google services
- Email-based authorization implemented in frontend

### API Keys Management
- All API keys stored in environment variables
- Task-based API key distribution for load balancing across multiple OpenAI keys
- Keys never committed to version control

## Version Pinning Recommendations

### Critical Dependencies (Should Be Pinned)
- \`firebase-admin\` - Breaking changes in Firebase API
- \`langchain\` - Rapid development, potential breaking changes
- \`openai\` - API version compatibility
- \`google-cloud-*\` - Cloud service API compatibility

### Recommended Actions
1. Deduplicate repeated dependencies in \`requirements.txt\`
2. Pin major versions for critical cloud service libraries
3. Implement automated dependency updates with testing
4. Regular security audits and updates

## Installation

### Backend
\`\`\`bash
pip install -r requirements.txt
\`\`\`

### Frontend
\`\`\`bash
npm install
\`\`\`

## Environment Variables Required

### Backend
- \`GOOGLE_APPLICATION_CREDENTIALS\` - Path to Google Cloud service account JSON
- \`GOOGLE_APPLICATION_CREDENTIALS_2\` - Firebase service account credentials
- \`GCP_PROJECT\` - Google Cloud Project ID
- \`CLOUD_TASKS_QUEUE\` - Cloud Tasks queue name
- \`CLOUD_TASKS_LOCATION\` - Cloud Tasks region
- \`OPENAI_API_KEY\` - OpenAI API key (multiple keys for load distribution)
- \`GOOGLE_API_KEY\` - Google Generative AI API key
- \`PERPLEXITY_API_KEY\` - Perplexity AI API key
- \`AIRTABLE_API_KEY\` - Airtable API key
- \`BASE_ID\` - Airtable base ID
- \`SUPABASE_URL\` - Supabase project URL
- \`SUPABASE_KEY\` - Supabase API key

### Frontend
- \`REACT_APP_FIREBASE_API_KEY\` - Firebase API key
- \`REACT_APP_FIREBASE_AUTH_DOMAIN\` - Firebase auth domain
- \`REACT_APP_FIREBASE_PROJECT_ID\` - Firebase project ID
- \`REACT_APP_FIREBASE_STORAGE_BUCKET\` - Firebase storage bucket
- \`REACT_APP_FIREBASE_MESSAGING_SENDER_ID\` - Firebase messaging sender ID
- \`REACT_APP_FIREBASE_APP_ID\` - Firebase app ID
- \`REACT_APP_FIREBASE_MEASUREMENT_ID\` - Firebase analytics measurement ID
- \`REACT_APP_AUTHORIZED_EMAILS\` - Comma-separated list of authorized email addresses

## Troubleshooting

### Common Issues

1. **Version Conflicts**: If encountering version conflicts, check for duplicate entries in \`requirements.txt\`
2. **Firebase Initialization Errors**: Ensure \`GOOGLE_APPLICATION_CREDENTIALS_2\` points to valid service account JSON
3. **LangChain Compatibility**: Some LangChain versions may have breaking changes - check release notes
4. **Node Module Issues**: Delete \`node_modules\` and \`package-lock.json\`, then run \`npm install\` fresh

## Related Documentation

- [Deployment Environment](deployment-environment.md)
- [Developer Experience](developer-experience.md)
- [Security & Compliance](security-compliance.md)

`;

// Auto-generated from model-integrations.md
export const MainToolDocumentationCodeDocumentationModelIntegrations = `# Model Integrations

## Overview

The Dark Pattern Detection & Compliance Analysis Tool integrates with multiple Large Language Model (LLM) providers to enable flexible, robust, and cost-effective AI-powered analysis. This document details the model integrations, API key management, model selection strategies, and provider-specific configurations.

## Supported LLM Providers

### 1. OpenAI (GPT Models)

**Primary Models Used**:
- \`gpt-4o-mini\` - Default model for most tasks (cost-effective, fast)
- \`gpt-4o\` - Higher accuracy tasks when needed
- \`gpt-4-turbo\` - Complex reasoning tasks

**Integration**:
- **Library**: \`langchain-openai\`, \`openai\`
- **Configuration**: Via \`OPENAI_API_KEY\` environment variables
- **Usage**: Primary provider for most analysis tasks

**Task Distribution**:
- User information extraction
- Parent category scanning
- Sub-agent analysis
- Outcome synthesis
- Validation and fact-checking

### 2. Google Generative AI (Gemini)

**Primary Models Used**:
- \`gemini-2.0-flash\` - Fast, efficient model for analysis
- \`gemini-pro\` - Higher quality when needed
- \`gemini-pro-vision\` - Vision tasks

**Integration**:
- **Library**: \`langchain-google-genai\`, \`google-generativeai\`
- **Configuration**: Via \`GOOGLE_API_KEY\` environment variable
- **Usage**: Primary model for dark pattern analysis (\`DarkPattern/version3.py\`)

**Task Distribution**:
- Dark pattern analysis (main engine)
- Vision-based analysis
- Compliance analysis
- Page type identification

### 3. Anthropic (Claude)

**Primary Models Used**:
- \`claude-3-opus\` - High-quality analysis
- \`claude-3-sonnet\` - Balanced performance
- \`claude-3-haiku\` - Fast, cost-effective

**Integration**:
- **Library**: \`langchain-anthropic\`
- **Configuration**: Via \`ANTHROPIC_API_KEY\` environment variable
- **Usage**: Alternative provider for high-quality analysis

**Task Distribution**:
- Complex reasoning tasks
- High-accuracy requirements
- Fallback for OpenAI failures

### 4. HuggingFace Models

**Integration**:
- **Library**: \`langchain-huggingface\`, \`huggingface_hub\`
- **Configuration**: Via HuggingFace API tokens
- **Usage**: Specialized models for specific tasks

**Task Distribution**:
- Embedding models
- Specialized NLP tasks
- On-device inference (future)

## Task-Based API Key Distribution

### Architecture

The system uses a task-based API key mapping to distribute load across multiple API keys:

\`\`\`python
TASK_API_MAPPING = {
    # User information extraction tasks
    "Page Name Extraction": "OPENAI_API_KEY_USER_INFO",
    "User Notes Filtering": "OPENAI_API_KEY_USER_INFO",
    "Footnote Information Analysis": "OPENAI_API_KEY_USER_INFO",
    "Information Organization Analysis": "OPENAI_API_KEY_USER_INFO",
    "Call to Action Analysis": "OPENAI_API_KEY_USER_INFO",
    "Typography and Color Analysis": "OPENAI_API_KEY_USER_INFO",
    "Nature of Design Analysis": "OPENAI_API_KEY_USER_INFO",
    "Webpage Brief Analysis": "OPENAI_API_KEY_USER_INFO",
    
    # Parent category tasks
    "Coercing Scanner Analysis": "OPENAI_API_KEY_PARENT_CATEGORY",
    "Falsification Scanner Analysis": "OPENAI_API_KEY_PARENT_CATEGORY",
    "Misdirection Scanner Analysis": "OPENAI_API_KEY_PARENT_CATEGORY",
    "Obstructing Scanner Analysis": "OPENAI_API_KEY_PARENT_CATEGORY",
    "Sneaking Scanner Analysis": "OPENAI_API_KEY_PARENT_CATEGORY",
    "Omiting Scanner Analysis": "OPENAI_API_KEY_PARENT_CATEGORY",
    
    # Sub-agent tasks
    "Prompt Filtering for SubAgents": "OPENAI_API_KEY_AGENTS",
    "Subagent 1 Analysis": "OPENAI_API_KEY_AGENTS",
    "Subagent 2 Evaluation": "OPENAI_API_KEY_AGENTS",
    "Subagent 3 Example Analysis": "OPENAI_API_KEY_AGENTS",
    "Subagent 4 Analysis Process": "OPENAI_API_KEY_AGENTS",
    "Subagent 5 Compilation": "OPENAI_API_KEY_AGENTS",
    "Master Agent Response": "OPENAI_API_KEY_AGENTS",
    
    # Scanner Analysis
    "Scanner Analysis": "OPENAI_API_KEY_SCANNER",
    "Scanner Context": "OPENAI_API_KEY_SCANNER",
    "Developer Design Guidance": "OPENAI_API_KEY_SCANNER",
    
    # Outcome synthesis tasks
    "Generate Key Highlights": "OPENAI_API_KEY_HIGHLIGHTS",
    "Generate Actual Measurable Harm": "OPENAI_API_KEY_HARM_MEASURE",
    "Validation Outcome": "OPENAI_API_KEY_VALIDATION",
    "Fact Check Validation": "OPENAI_API_KEY_FACT_CHECK",
    
    # Gemini-specific tasks
    "Gemini Analysis": "GOOGLE_API_KEY",
    "Gemini Vision Analysis": "GOOGLE_API_KEY",
    
    # Default
    "default": "OPENAI_API_KEY"
}
\`\`\`

### Benefits

1. **Load Balancing**: Distribute requests across multiple API keys
2. **Rate Limit Management**: Avoid hitting rate limits on single keys
3. **Cost Optimization**: Use different keys for different cost tiers
4. **Fault Tolerance**: If one key fails, others continue working
5. **Usage Tracking**: Track usage per task type

## Centralized LLM Interface

### Implementation

All LLM calls go through \`utility/LlmCalls.py\`:

\`\`\`python
def call_llm(
    model_name: str,
    prompt: str,
    task_name: str,
    image_url: Optional[str] = None,
    image_id: Optional[str] = None,
    temperature: float = 0,
    max_tokens: Optional[int] = None
) -> str:
    """
    Unified interface for calling LLM models.
    
    Features:
    - Automatic API key selection based on task
    - Token usage tracking
    - Firebase logging
    - Error handling and retries
    - Timing metrics
    """
\`\`\`

### Features

1. **Automatic Key Selection**: Selects API key based on task name
2. **Provider Abstraction**: Unified interface for all providers
3. **Logging**: Automatic logging to Firebase
4. **Metrics**: Token usage, cost, duration tracking
5. **Error Handling**: Automatic retries and fallbacks
6. **Multimodal Support**: Text and image inputs

## Model Selection Strategy

### Selection Criteria

1. **Task Type**: Different models for different tasks
2. **Accuracy Requirements**: Higher accuracy = more expensive models
3. **Speed Requirements**: Fast tasks = lighter models
4. **Cost Constraints**: Balance cost vs. quality
5. **Provider Availability**: Fallback if primary fails

### Default Model Configuration

\`\`\`python
# Sub-agents
SUBAGENTS_MODEL = "gpt-4o-mini"

# Outcome synthesis
OUTCOME_SYNTHESIS_MODEL_NAME = "gpt-4o-mini"

# Regulatory prevention
REGULATORY_PREVENTION_MODEL = "gpt-4o-mini"

# Dark pattern analysis (main engine)
MODEL_NAME = "gemini-2.0-flash"
\`\`\`

### Model Selection by Task

| Task Category | Primary Model | Fallback Model | Rationale |
|--------------|--------------|----------------|-----------|
| Information Extraction | gpt-4o-mini | gpt-4o | Fast, cost-effective |
| Parent Category Scanning | gpt-4o-mini | gemini-2.0-flash | Balanced performance |
| Sub-Agent Analysis | gpt-4o-mini | claude-3-haiku | Parallel processing |
| Dark Pattern Analysis | gemini-2.0-flash | gpt-4o | Vision capabilities |
| Outcome Synthesis | gpt-4o-mini | gpt-4o | Quality important |
| Validation | gpt-4o-mini | gpt-4o | Accuracy critical |
| Compliance Analysis | gemini-2.0-flash | gpt-4o | Complex reasoning |

## Provider-Specific Configurations

### OpenAI Configuration

\`\`\`python
from langchain_openai import ChatOpenAI

model = ChatOpenAI(
    model_name="gpt-4o-mini",
    temperature=0,
    max_tokens=None,  # Use model default
    timeout=60
)
\`\`\`

**Features**:
- Structured outputs (Pydantic models)
- Function calling
- Streaming support
- Token usage tracking

### Google Gemini Configuration

\`\`\`python
from langchain_google_genai import ChatGoogleGenerativeAI

model = ChatGoogleGenerativeAI(
    model="gemini-2.0-flash",
    temperature=0,
    max_output_tokens=None
)
\`\`\`

**Features**:
- Multimodal (text + images)
- Fast inference
- Cost-effective
- Vision capabilities

### Anthropic Claude Configuration

\`\`\`python
from langchain_anthropic import ChatAnthropic

model = ChatAnthropic(
    model="claude-3-haiku",
    temperature=0,
    max_tokens=4096
)
\`\`\`

**Features**:
- High-quality outputs
- Long context windows
- Strong reasoning capabilities

## Multimodal Support

### Image Input

The system supports image inputs for vision-based analysis:

\`\`\`python
response = call_llm(
    model_name="gemini-2.0-flash",
    prompt=prompt_text,
    task_name="Dark Pattern Analysis",
    image_url="https://storage.googleapis.com/...",
    image_id=image_id
)
\`\`\`

**Supported Providers**:
- Google Gemini (native support)
- OpenAI GPT-4 Vision (via API)
- Anthropic Claude (via API)

**Use Cases**:
- Screenshot analysis
- UI element detection
- Visual pattern recognition
- Coordinate generation

## Usage Tracking and Logging

### Firebase Logging

All LLM calls are logged to Firebase with:

\`\`\`python
{
    "task_name": str,
    "model_name": str,
    "provider": str,
    "tokens_used": int,
    "cost": float,
    "duration": float,  # seconds
    "timestamp": Timestamp,
    "image_id": str,
    "success": bool,
    "error": str  # if failed
}
\`\`\`

### Metrics Collected

- **Token Usage**: Input and output tokens
- **Cost**: Estimated cost per call
- **Duration**: Response time
- **Success Rate**: Percentage of successful calls
- **Error Rate**: Error frequency by provider

## Error Handling and Retries

### Retry Strategy

1. **Transient Errors**: Automatic retry with exponential backoff
2. **Rate Limits**: Retry after rate limit window
3. **Provider Failures**: Fallback to alternative provider
4. **Timeout Errors**: Retry with longer timeout

### Fallback Chain

\`\`\`
Primary Model (e.g., gpt-4o-mini)
  ↓ (on failure)
Fallback Model (e.g., gemini-2.0-flash)
  ↓ (on failure)
Alternative Provider (e.g., claude-3-haiku)
  ↓ (on failure)
Error logged, user notified
\`\`\`

## Cost Optimization

### Strategies

1. **Model Selection**: Use cost-effective models for simple tasks
2. **Prompt Optimization**: Shorter prompts = lower costs
3. **Caching**: Cache similar queries
4. **Batch Processing**: Group similar requests
5. **Rate Limiting**: Distribute load to avoid peak pricing

### Cost Tracking

- Track costs per task type
- Monitor total monthly spend
- Set budget alerts
- Optimize high-cost operations

## Performance Optimization

### Parallel Processing

Sub-agents run in parallel using ThreadPoolExecutor:

\`\`\`python
with ThreadPoolExecutor() as executor:
    agent_futures = {
        response: executor.submit(
            agent_function_map[response],
            image_url,
            # ... parameters
        )
        for response in combined_responses
    }
    agent_responses = {
        response: future.result() 
        for response, future in agent_futures.items()
    }
\`\`\`

### Async Operations

Long-running operations use async/await:

\`\`\`python
async def analyze_dark_patterns(image_url, image_id):
    # Async LLM calls
    result = await process_async(image_url)
    return result
\`\`\`

## Future Enhancements

### Planned Integrations

1. **Local Models**: On-device inference with HuggingFace
2. **Model Fine-Tuning**: Custom models for specific tasks
3. **Ensemble Methods**: Combine multiple model outputs
4. **A/B Testing**: Compare model performance
5. **Auto-Model Selection**: Automatic model selection based on task

## Related Documentation

- [Libraries & Dependencies](libraries-dependencies.md)
- [Performance Benchmarking](performance-benchmarking.md)
- [Continuous Learning](continuous-learning.md)
- [Architecture - Component Design](../architecture/component-design.md)

`;

// Auto-generated from operational-readiness.md
export const MainToolDocumentationCodeDocumentationOperationalReadiness = `# Operational Readiness

## Overview

This document covers configuration management, monitoring and logging, rollback procedures, health checks, and operational best practices for the Dark Pattern Detection & Compliance Analysis Tool.

## Configuration Management

### Environment Variables

**Backend Configuration** (\`env.yaml\` for Cloud Run):

\`\`\`yaml
GOOGLE_APPLICATION_CREDENTIALS: "/app/kinetic-mile-439413-k0-bc4aef71e45c.json"
GOOGLE_APPLICATION_CREDENTIALS_2: "/path/to/firebase-credentials.json"
GCP_PROJECT: "kinetic-mile-439413-k0"
CLOUD_TASKS_QUEUE: "deceptive-design-queue"
CLOUD_TASKS_LOCATION: "europe-west1"
PROCESS_SCREEN_CAPTURE_URL: "https://your-service.run.app/process_screen_capture"
OPENAI_API_KEY: "sk-..."
OPENAI_API_KEY_USER_INFO: "sk-..."
OPENAI_API_KEY_PARENT_CATEGORY: "sk-..."
# ... additional API keys
GOOGLE_API_KEY: "..."
PERPLEXITY_API_KEY: "..."
AIRTABLE_API_KEY: "..."
BASE_ID: "..."
SUPABASE_URL: "..."
SUPABASE_KEY: "..."
\`\`\`

**Frontend Configuration** (Vercel environment variables):

\`\`\`
REACT_APP_FIREBASE_API_KEY=...
REACT_APP_FIREBASE_AUTH_DOMAIN=...
REACT_APP_FIREBASE_PROJECT_ID=...
REACT_APP_FIREBASE_STORAGE_BUCKET=...
REACT_APP_FIREBASE_MESSAGING_SENDER_ID=...
REACT_APP_FIREBASE_APP_ID=...
REACT_APP_FIREBASE_MEASUREMENT_ID=...
REACT_APP_AUTHORIZED_EMAILS=email1@example.com,email2@example.com
\`\`\`

### Configuration Validation

\`\`\`python
def validate_configuration():
    """Validate all required configuration is present."""
    required_vars = [
        'GOOGLE_APPLICATION_CREDENTIALS',
        'GCP_PROJECT',
        'OPENAI_API_KEY',
        'GOOGLE_API_KEY'
    ]
    
    missing = []
    for var in required_vars:
        if not os.getenv(var):
            missing.append(var)
    
    if missing:
        raise ValueError(f"Missing required environment variables: {missing}")
    
    return True
\`\`\`

## Monitoring and Logging

### Application Logs

**Backend Logging**:

\`\`\`python
import logging
from datetime import datetime

# Configure logging
logging.basicConfig(
    level=logging.INFO,
    format='%(asctime)s - %(name)s - %(levelname)s - %(message)s'
)

logger = logging.getLogger(__name__)

def log_analysis_start(image_id):
    """Log analysis start."""
    logger.info(f"Analysis started for image_id: {image_id}")

def log_analysis_complete(image_id, duration):
    """Log analysis completion."""
    logger.info(f"Analysis completed for image_id: {image_id} in {duration:.2f}s")
\`\`\`

**Cloud Run Logs**:

\`\`\`bash
# View logs
gcloud logging read "resource.type=cloud_run_revision" --limit 50

# Filter by severity
gcloud logging read "resource.type=cloud_run_revision AND severity>=ERROR" --limit 50

# Real-time logs
gcloud logging tail "resource.type=cloud_run_revision"
\`\`\`

### Error Logging

**Firebase Error Collection**:

\`\`\`python
def log_error_to_firebase(error, context):
    """Log error to Firebase."""
    db = firestore.client()
    db.collection('errorLogs').add({
        'error': str(error),
        'context': context,
        'timestamp': firestore.SERVER_TIMESTAMP,
        'severity': 'high'
    })
\`\`\`

### Metrics Collection

**Key Metrics**:

1. **Request Metrics**:
   - Request count
   - Response time
   - Error rate
   - Success rate

2. **LLM Metrics**:
   - API call count
   - Token usage
   - Cost per analysis
   - Model performance

3. **Resource Metrics**:
   - CPU usage
   - Memory usage
   - Network I/O

**Implementation**:

\`\`\`python
def track_metric(metric_name, value, tags=None):
    """Track custom metric."""
    # Log to Firebase or monitoring service
    db.collection('metrics').add({
        'metric_name': metric_name,
        'value': value,
        'tags': tags or {},
        'timestamp': firestore.SERVER_TIMESTAMP
    })
\`\`\`

### Monitoring Dashboards

**Google Cloud Monitoring**:

- View in Cloud Console
- Set up custom dashboards
- Configure alerts

**Firebase Analytics**:

- User activity tracking
- Error rate monitoring
- Performance metrics

## Health Checks

### Health Check Endpoint

**Current**: Not implemented (recommended)

**Recommended Implementation**:

\`\`\`python
@app.route('/health', methods=['GET'])
def health_check():
    """Health check endpoint."""
    health_status = {
        'status': 'healthy',
        'timestamp': datetime.now().isoformat(),
        'version': '1.0.0',
        'checks': {}
    }
    
    # Check Firebase connection
    try:
        db = firestore.client()
        db.collection('health').document('check').get()
        health_status['checks']['firestore'] = 'ok'
    except Exception as e:
        health_status['checks']['firestore'] = f'error: {str(e)}'
        health_status['status'] = 'unhealthy'
    
    # Check GCS connection
    try:
        storage_client = storage.Client()
        storage_client.bucket('dd-media-files').exists()
        health_status['checks']['gcs'] = 'ok'
    except Exception as e:
        health_status['checks']['gcs'] = f'error: {str(e)}'
        health_status['status'] = 'unhealthy'
    
    # Check LLM API (lightweight)
    try:
        # Quick test call
        test_response = call_llm(
            model_name="gpt-4o-mini",
            prompt="test",
            task_name="Health Check"
        )
        health_status['checks']['llm'] = 'ok'
    except Exception as e:
        health_status['checks']['llm'] = f'error: {str(e)}'
        health_status['status'] = 'degraded'  # Not critical
    
    status_code = 200 if health_status['status'] == 'healthy' else 503
    return jsonify(health_status), status_code
\`\`\`

### Readiness Check

\`\`\`python
@app.route('/ready', methods=['GET'])
def readiness_check():
    """Readiness check for Kubernetes/Cloud Run."""
    # Check if service is ready to accept traffic
    try:
        # Quick checks
        db = firestore.client()
        storage_client = storage.Client()
        
        return jsonify({'status': 'ready'}), 200
    except Exception as e:
        return jsonify({'status': 'not ready', 'error': str(e)}), 503
\`\`\`

## Rollback Procedures

### Cloud Run Rollback

**List Revisions**:

\`\`\`bash
gcloud run revisions list \\
  --service deceptive-design-server \\
  --region europe-west1
\`\`\`

**Rollback to Previous Revision**:

\`\`\`bash
# Get current traffic allocation
gcloud run services describe deceptive-design-server \\
  --region europe-west1 \\
  --format="value(status.traffic)"

# Rollback to specific revision
gcloud run services update-traffic deceptive-design-server \\
  --to-revisions REVISION_NAME=100 \\
  --region europe-west1
\`\`\`

**Gradual Rollback**:

\`\`\`bash
# Route 50% to previous revision
gcloud run services update-traffic deceptive-design-server \\
  --to-revisions \\
    CURRENT_REVISION=50,PREVIOUS_REVISION=50 \\
  --region europe-west1
\`\`\`

### Vercel Rollback

**Via Dashboard**:
1. Go to Vercel dashboard
2. Select project
3. Go to Deployments
4. Click "..." on previous deployment
5. Select "Promote to Production"

**Via CLI**:

\`\`\`bash
# List deployments
vercel ls

# Rollback to specific deployment
vercel rollback [deployment-url]
\`\`\`

### Database Rollback

**Firestore**:
- Use point-in-time recovery (if enabled)
- Restore from backups

\`\`\`bash
# Export before changes
gcloud firestore export gs://backup-bucket/backup-$(date +%Y%m%d)

# Import if rollback needed
gcloud firestore import gs://backup-bucket/backup-YYYYMMDD
\`\`\`

## Alerting

### Alert Configuration

**Google Cloud Monitoring Alerts**:

\`\`\`bash
# Create alert policy
gcloud alpha monitoring policies create \\
  --notification-channels=CHANNEL_ID \\
  --display-name="High Error Rate" \\
  --condition-threshold-value=0.05 \\
  --condition-threshold-duration=300s
\`\`\`

**Key Alerts**:

1. **High Error Rate**: > 5% errors
2. **Slow Response Time**: > 5 minutes average
3. **High Memory Usage**: > 90% memory
4. **API Rate Limit**: Approaching rate limits
5. **Cost Threshold**: Daily cost > budget

### Alert Channels

- **Email**: For critical alerts
- **Slack**: For team notifications
- **PagerDuty**: For on-call escalation
- **SMS**: For critical issues

## Backup Procedures

### Data Backups

**Firestore Backups**:

\`\`\`bash
# Automated daily backup (recommended)
gcloud firestore export gs://backup-bucket/firestore-backup-$(date +%Y%m%d) \\
  --collection-ids=sessions,userActivityLogs,errorLogs
\`\`\`

**Cloud Storage Backups**:

\`\`\`bash
# Copy to backup bucket
gsutil -m cp -r gs://dd-media-files/* gs://dd-media-files-backup/
\`\`\`

### Backup Retention

- **Daily Backups**: Retain for 30 days
- **Weekly Backups**: Retain for 12 weeks
- **Monthly Backups**: Retain for 12 months

## Disaster Recovery

### Recovery Procedures

1. **Service Failure**:
   - Cloud Run auto-scales and restarts
   - Check logs for root cause
   - Rollback if needed

2. **Database Failure**:
   - Restore from latest backup
   - Verify data integrity
   - Resume operations

3. **Storage Failure**:
   - Restore from backup
   - Re-upload critical files
   - Verify accessibility

### Recovery Time Objectives (RTO)

- **Service Recovery**: < 5 minutes (auto-recovery)
- **Data Recovery**: < 1 hour (from backups)
- **Full System Recovery**: < 4 hours

## Operational Checklist

### Daily

- [ ] Check error logs
- [ ] Monitor error rates
- [ ] Review performance metrics
- [ ] Check API usage/costs

### Weekly

- [ ] Review system performance
- [ ] Analyze error patterns
- [ ] Check backup status
- [ ] Review security logs

### Monthly

- [ ] Performance optimization review
- [ ] Cost analysis
- [ ] Security audit
- [ ] Capacity planning

## Related Documentation

- [Deployment Environment](deployment-environment.md)
- [Monitoring](../architecture/monitoring.md)
- [Security & Compliance](security-compliance.md)

`;

// Auto-generated from performance-benchmarking.md
export const MainToolDocumentationCodeDocumentationPerformanceBenchmarking = `# Performance Benchmarking & Profiling

## Overview

This document details performance optimization strategies, benchmarking results, profiling techniques, and resource usage patterns for the Dark Pattern Detection & Compliance Analysis Tool.

## Performance Metrics

### Key Performance Indicators (KPIs)

1. **Response Time**: Time from request to complete analysis
2. **Throughput**: Number of analyses per minute
3. **Resource Usage**: CPU, memory, and network utilization
4. **Cost per Analysis**: Total cost including LLM API calls
5. **Error Rate**: Percentage of failed requests

### Target Performance

- **Average Analysis Time**: 2-3 minutes for complete analysis
- **Step 1 (Information Extraction)**: 30-60 seconds
- **Step 2 (Agent Search)**: 20-40 seconds
- **Step 3 (Sub-Agent Execution)**: 60-90 seconds (parallel)
- **Step 4 (Outcome Synthesis)**: 30-60 seconds

## LLM Call Optimization

### Parallel Processing

**Strategy**: Run sub-agents in parallel using ThreadPoolExecutor

\`\`\`python
from concurrent.futures import ThreadPoolExecutor

# Before: Sequential (5+ minutes)
for agent in agents:
    result = agent(image_url)
    results.append(result)

# After: Parallel (2-3 minutes)
with ThreadPoolExecutor(max_workers=10) as executor:
    futures = {executor.submit(agent, image_url): agent for agent in agents}
    results = {agent: future.result() for future, agent in futures.items()}
\`\`\`

**Performance Gain**: ~60% reduction in processing time

### API Key Distribution

**Strategy**: Distribute LLM calls across multiple API keys

**Benefits**:
- Avoid rate limits (higher throughput)
- Load balancing
- Fault tolerance

**Impact**: Enables 3-5x higher concurrent request rate

### Model Selection

**Strategy**: Use cost-effective models for appropriate tasks

| Task | Model | Tokens/Second | Cost/1K Tokens |
|------|-------|---------------|----------------|
| Information Extraction | gpt-4o-mini | ~50 | $0.15 |
| Parent Category | gpt-4o-mini | ~50 | $0.15 |
| Sub-Agents | gpt-4o-mini | ~50 | $0.15 |
| Dark Pattern Analysis | gemini-2.0-flash | ~100 | $0.075 |
| Outcome Synthesis | gpt-4o-mini | ~50 | $0.15 |

**Cost Optimization**: ~40% cost reduction vs. using gpt-4o for all tasks

### Caching Strategy

**Implementation**: TTL cache for Airtable prompts

\`\`\`python
from cachetools import TTLCache

# 5-minute TTL cache
airtable_cache = TTLCache(maxsize=100, ttl=300)

def get_prompt_cached(function_name):
    if function_name in airtable_cache:
        return airtable_cache[function_name]
    
    prompt = fetch_from_airtable(function_name)
    airtable_cache[function_name] = prompt
    return prompt
\`\`\`

**Impact**: Reduces Airtable API calls by ~80%

## Response Time Metrics

### Typical Analysis Timeline

\`\`\`
Request Received
  ↓ (0s)
Image Upload to GCS
  ↓ (5-10s)
Step 1: Information Extraction
  ├─ Page Name: 5s
  ├─ Context Extraction: 20s
  ├─ Scanner Generation: 15s
  └─ Total: ~40s
  ↓
Step 2: Agent Search (Parallel)
  ├─ 6 Parent Categories: 30s (parallel)
  └─ Total: ~30s
  ↓
Step 3: Sub-Agent Execution (Parallel)
  ├─ 30+ Sub-Agents: 90s (parallel)
  └─ Total: ~90s
  ↓
Step 4: Outcome Synthesis
  ├─ Key Highlights: 10s
  ├─ Harm Calculation: 15s
  ├─ Validation: 20s
  ├─ Law Fetching: 15s
  └─ Total: ~60s
  ↓
Response Sent
Total: ~220s (3.7 minutes)
\`\`\`

### Optimization Opportunities

1. **Step 1**: Can be reduced to ~25s with prompt optimization
2. **Step 3**: Can be reduced to ~60s with better parallelization
3. **Step 4**: Can be reduced to ~40s with async law fetching

**Potential Total**: ~125s (2 minutes)

## Resource Usage Patterns

### Memory Usage

**Current Configuration**: 4Gi per Cloud Run instance

**Breakdown**:
- Python runtime: ~200MB
- Dependencies: ~500MB
- LLM processing: ~2-3GB (peak)
- Buffer: ~1GB

**Optimization**:
- Use streaming for large responses
- Clear intermediate results
- Optimize data structures

### CPU Usage

**Configuration**: 4 workers × 2 threads = 8 concurrent requests

**Usage Pattern**:
- Idle: ~5% CPU
- Processing: ~60-80% CPU
- Peak (parallel agents): ~90% CPU

**Bottlenecks**:
- LLM API calls (network I/O bound)
- Image processing (CPU bound)
- Firebase writes (network I/O bound)

### Network Usage

**Outbound** (per analysis):
- LLM API calls: ~50-100 requests
- Average: ~5MB total
- Peak: ~10MB

**Inbound** (per analysis):
- Image upload: ~1-5MB
- Firebase reads: ~100KB
- Total: ~1-5MB

## Profiling Techniques

### Python Profiling

\`\`\`python
import cProfile
import pstats

def profile_analysis():
    profiler = cProfile.Profile()
    profiler.enable()
    
    # Run analysis
    result = analyze_image(image_url, url, note, image_id)
    
    profiler.disable()
    stats = pstats.Stats(profiler)
    stats.sort_stats('cumulative')
    stats.print_stats(20)  # Top 20 functions
\`\`\`

### Timing Decorator

\`\`\`python
import time
from functools import wraps

def timing_decorator(func):
    @wraps(func)
    def wrapper(*args, **kwargs):
        start = time.time()
        result = func(*args, **kwargs)
        duration = time.time() - start
        print(f"{func.__name__} took {duration:.2f} seconds")
        return result
    return wrapper

@timing_decorator
def step1_information_extraction(image_url, url, note, image_id):
    # ... implementation
    pass
\`\`\`

### Firebase Performance Logging

\`\`\`python
def log_performance_metrics(function_name, duration, tokens_used, cost):
    db = firestore.client()
    db.collection('performanceMetrics').add({
        'function_name': function_name,
        'duration': duration,
        'tokens_used': tokens_used,
        'cost': cost,
        'timestamp': firestore.SERVER_TIMESTAMP
    })
\`\`\`

## Benchmarking Results

### Analysis Time by Step

| Step | Average Time | Min | Max | Std Dev |
|------|--------------|-----|-----|---------|
| Step 1 | 42s | 28s | 65s | 12s |
| Step 2 | 32s | 20s | 48s | 8s |
| Step 3 | 88s | 55s | 125s | 20s |
| Step 4 | 58s | 35s | 85s | 15s |
| **Total** | **220s** | **138s** | **323s** | **45s** |

### Throughput

- **Single Instance**: ~0.27 analyses/minute
- **4 Workers**: ~1.08 analyses/minute
- **Scaled (10 instances)**: ~10.8 analyses/minute

### Cost per Analysis

**Breakdown**:
- Step 1 LLM calls: ~$0.05
- Step 2 LLM calls: ~$0.03
- Step 3 LLM calls: ~$0.15
- Step 4 LLM calls: ~$0.07
- **Total**: ~$0.30 per analysis

**Optimization Potential**: ~$0.20 per analysis (33% reduction)

## Optimization Strategies

### 1. Prompt Optimization

**Current**: Verbose prompts with redundant context
**Optimized**: Concise, focused prompts

**Impact**: 20-30% reduction in tokens, faster responses

### 2. Batch Processing

**Strategy**: Group similar requests

\`\`\`python
def batch_llm_calls(prompts, model_name, task_name):
    """Batch multiple prompts into single API call."""
    combined_prompt = "\\n\\n---\\n\\n".join(prompts)
    response = call_llm(model_name, combined_prompt, task_name)
    return split_response(response)
\`\`\`

**Impact**: 40-50% reduction in API calls

### 3. Async Operations

**Strategy**: Use async/await for I/O-bound operations

\`\`\`python
import asyncio

async def async_analysis(image_url, url, note, image_id):
    # Run steps concurrently where possible
    step1_task = asyncio.create_task(step1_async(...))
    step2_task = asyncio.create_task(step2_async(...))
    
    step1_result, step2_result = await asyncio.gather(
        step1_task, step2_task
    )
\`\`\`

**Impact**: 15-20% reduction in total time

### 4. Result Caching

**Strategy**: Cache similar analysis results

\`\`\`python
from cachetools import TTLCache

analysis_cache = TTLCache(maxsize=1000, ttl=3600)  # 1 hour

def get_cached_analysis(image_hash):
    if image_hash in analysis_cache:
        return analysis_cache[image_hash]
    return None
\`\`\`

**Impact**: Near-instant results for duplicate images

## Monitoring and Alerting

### Key Metrics to Monitor

1. **Average Response Time**: Alert if > 5 minutes
2. **Error Rate**: Alert if > 5%
3. **API Rate Limits**: Alert if approaching limits
4. **Memory Usage**: Alert if > 90%
5. **Cost per Day**: Alert if > budget threshold

### Cloud Run Metrics

\`\`\`bash
# View metrics
gcloud monitoring time-series list \\
  --filter='resource.type="cloud_run_revision"' \\
  --project=kinetic-mile-439413-k0
\`\`\`

### Custom Metrics

\`\`\`python
from google.cloud import monitoring_v3

def record_custom_metric(metric_name, value):
    client = monitoring_v3.MetricServiceClient()
    project_name = f"projects/kinetic-mile-439413-k0"
    
    series = monitoring_v3.TimeSeries()
    series.metric.type = f"custom.googleapis.com/{metric_name}"
    series.resource.type = "global"
    
    point = monitoring_v3.Point()
    point.value.double_value = value
    point.interval.end_time.seconds = int(time.time())
    
    series.points = [point]
    client.create_time_series(name=project_name, time_series=[series])
\`\`\`

## Load Testing

### Test Scenarios

1. **Single Request**: Baseline performance
2. **Concurrent Requests**: 10 simultaneous analyses
3. **Sustained Load**: 100 requests over 10 minutes
4. **Peak Load**: 50 requests in 1 minute

### Load Testing Tools

\`\`\`python
import asyncio
import aiohttp

async def load_test(num_requests=100):
    async with aiohttp.ClientSession() as session:
        tasks = [
            send_analysis_request(session, i)
            for i in range(num_requests)
        ]
        results = await asyncio.gather(*tasks)
    return analyze_results(results)
\`\`\`

## Performance Tuning Checklist

- [ ] Optimize prompts (reduce token count)
- [ ] Implement result caching
- [ ] Use async/await for I/O operations
- [ ] Batch similar requests
- [ ] Monitor and optimize slow queries
- [ ] Implement connection pooling
- [ ] Use CDN for static assets
- [ ] Optimize database queries
- [ ] Implement rate limiting
- [ ] Set up performance monitoring

## Related Documentation

- [Design Philosophy](design-philosophy.md)
- [Code Examples](code-examples.md)
- [Architecture - Scalability](../architecture/scalability.md)

`;

// Auto-generated from security-compliance.md
export const MainToolDocumentationCodeDocumentationSecurityCompliance = `# Security & Compliance

## Overview

This document covers security measures, threat modeling, encryption, role-based access control, and compliance considerations (GDPR, HIPAA) for the Dark Pattern Detection & Compliance Analysis Tool.

## Threat Modeling

### Threat Categories

#### 1. Authentication & Authorization Threats

**Threats**:
- Unauthorized access to the application
- Session hijacking
- Privilege escalation

**Mitigations**:
- Firebase OAuth2 authentication
- Email-based authorization whitelist
- Session management via Firebase Auth
- Token expiration and refresh

#### 2. Data Exposure Threats

**Threats**:
- Sensitive data in logs
- Unauthorized data access
- Data leakage through APIs

**Mitigations**:
- Payload sanitization before logging
- Firestore security rules
- CORS configuration
- Input validation and sanitization

#### 3. API Security Threats

**Threats**:
- API key exposure
- Rate limiting bypass
- Injection attacks
- DDoS attacks

**Mitigations**:
- Environment variable management
- API key rotation
- Rate limiting (via API providers)
- Input validation
- Cloud Run auto-scaling for DDoS protection

#### 4. Infrastructure Threats

**Threats**:
- Container vulnerabilities
- Unauthorized cloud resource access
- Service account key exposure

**Mitigations**:
- Regular dependency updates
- Least privilege IAM roles
- Service account key rotation
- Container image scanning

## Encryption

### Data at Rest

**Firestore**: Encrypted by default by Google Cloud
- **Encryption**: AES-256
- **Key Management**: Google-managed keys
- **Compliance**: Meets various compliance standards

**Google Cloud Storage**: Encrypted by default
- **Encryption**: AES-256
- **Key Management**: Google-managed or customer-managed keys
- **Access Control**: IAM and bucket policies

**Supabase**: Encrypted at rest
- **Encryption**: AES-256
- **Key Management**: Supabase-managed

### Data in Transit

**HTTPS/TLS**: All communications encrypted
- **Backend API**: HTTPS only (Cloud Run default)
- **Frontend**: HTTPS only (Vercel default)
- **Firebase**: TLS 1.2+
- **Cloud Storage**: HTTPS for all operations

**Certificate Management**:
- Cloud Run: Google-managed certificates
- Vercel: Automatic SSL certificates

## Role-Based Access Control (RBAC)

### Current Implementation

**Email-Based Authorization**:

\`\`\`javascript
// Frontend: src/config/auth.js
export const isEmailAuthorized = (email) => {
  if (!email) return false;
  return AUTHORIZED_EMAILS.includes(email.toLowerCase().trim());
};
\`\`\`

**Authorized Emails**: Managed via environment variable
- \`REACT_APP_AUTHORIZED_EMAILS\` - Comma-separated list
- Fallback to hardcoded list for development

### Access Levels

**Current**: Single access level (authorized/unauthorized)

**Recommended**: Multi-level access
- **Admin**: Full access, user management
- **Analyst**: Analysis creation, report generation
- **Viewer**: Read-only access

### Implementation Example

\`\`\`python
# Backend: Role-based endpoint protection
from functools import wraps
from firebase_admin import auth

def require_role(required_role):
    def decorator(f):
        @wraps(f)
        def wrapper(*args, **kwargs):
            # Get user from token
            token = request.headers.get('Authorization')
            decoded_token = auth.verify_id_token(token)
            user_id = decoded_token['uid']
            
            # Check user role
            user_role = get_user_role(user_id)
            if user_role != required_role and user_role != 'admin':
                return jsonify({"error": "Insufficient permissions"}), 403
            
            return f(*args, **kwargs)
        return wrapper
    return decorator

@app.route('/admin/users', methods=['GET'])
@require_role('admin')
def list_users():
    # Admin-only endpoint
    pass
\`\`\`

## Data Sanitization

### Payload Sanitization

**Implementation**: \`sanitize_payload()\` function in \`app.py\`

\`\`\`python
def sanitize_payload(payload):
    """Remove or mask sensitive data from payload before logging."""
    if not payload or not isinstance(payload, dict):
        return {"payload_type": str(type(payload))}
    
    sanitized = payload.copy()
    
    # List of sensitive fields to mask
    sensitive_fields = ['password', 'token', 'api_key', 'secret', 'auth']
    
    # Mask sensitive fields
    for key in sanitized:
        for sensitive in sensitive_fields:
            if sensitive in key.lower():
                sanitized[key] = "***REDACTED***"
    
    return sanitized
\`\`\`

### Input Validation

**Backend Validation**:
\`\`\`python
def validate_image_upload(request):
    """Validate image upload request."""
    # Check required fields
    required_fields = ['url', 'session_id', 'journey_id', 'image_doc_id']
    for field in required_fields:
        if not request.form.get(field):
            return False, f"Missing required field: {field}"
    
    # Validate URL format
    url = request.form.get('url')
    if not url.startswith(('http://', 'https://')):
        return False, "Invalid URL format"
    
    # Validate file type
    if 'image' in request.files:
        file = request.files['image']
        if not allowed_file(file.filename):
            return False, "Invalid file type"
    
    return True, None
\`\`\`

## GDPR Considerations

### Data Collection

**Principles**:
- **Minimal Collection**: Only collect necessary data
- **Purpose Limitation**: Use data only for stated purposes
- **Storage Limitation**: Retain data only as long as necessary

**Data Collected**:
- User email (for authentication)
- Analysis results (user-generated)
- Feedback (voluntary)
- Activity logs (for security)

### User Rights

**Right to Access**:
\`\`\`python
@app.route('/api/user/data', methods=['GET'])
def get_user_data():
    """Retrieve all user data."""
    user_id = get_current_user_id()
    
    # Collect all user data
    user_data = {
        'sessions': get_user_sessions(user_id),
        'activity_logs': get_user_activity_logs(user_id),
        'feedback': get_user_feedback(user_id)
    }
    
    return jsonify(user_data)
\`\`\`

**Right to Deletion**:
\`\`\`python
@app.route('/api/user/data', methods=['DELETE'])
def delete_user_data():
    """Delete all user data."""
    user_id = get_current_user_id()
    
    # Delete user data
    delete_user_sessions(user_id)
    delete_user_activity_logs(user_id)
    delete_user_feedback(user_id)
    
    return jsonify({"status": "deleted"})
\`\`\`

**Right to Rectification**:
- Users can update their profile information
- Users can correct analysis results

**Right to Portability**:
- Export user data in JSON format
- Transfer data to another service

### Data Processing

**Legal Basis**: User consent (via account creation)

**Data Processing Activities**:
- Analysis processing (user-initiated)
- Feedback processing (user-provided)
- Activity logging (legitimate interest - security)

### Data Protection Officer (DPO)

**Contact**: [To be specified]

**Responsibilities**:
- Monitor GDPR compliance
- Handle data subject requests
- Conduct privacy impact assessments

## HIPAA Considerations (If Applicable)

### Healthcare Data

**Note**: Current implementation does not handle PHI (Protected Health Information). If healthcare use cases are added:

**Requirements**:
- Business Associate Agreement (BAA) with cloud providers
- Encryption of PHI at rest and in transit
- Access controls and audit logs
- Data breach notification procedures

**Implementation**:
\`\`\`python
# Example: HIPAA-compliant data handling
def handle_phi_data(phi_data, user_id):
    """Handle PHI data with HIPAA compliance."""
    # Encrypt before storage
    encrypted_data = encrypt_phi(phi_data)
    
    # Store with access controls
    store_with_access_controls(encrypted_data, user_id)
    
    # Log access
    log_phi_access(user_id, 'store')
    
    return encrypted_data
\`\`\`

## API Security

### CORS Configuration

**Backend**: Flask-CORS

\`\`\`python
from flask_cors import CORS

app = Flask(__name__)
CORS(app)  # Configure appropriately for production

# Production configuration
CORS(app, resources={
    r"/api/*": {
        "origins": ["https://your-frontend-domain.com"],
        "methods": ["GET", "POST"],
        "allow_headers": ["Content-Type", "Authorization"]
    }
})
\`\`\`

### Rate Limiting

**Current**: Managed by LLM API providers

**Recommended**: Implement application-level rate limiting

\`\`\`python
from flask_limiter import Limiter
from flask_limiter.util import get_remote_address

limiter = Limiter(
    app=app,
    key_func=get_remote_address,
    default_limits=["200 per day", "50 per hour"]
)

@app.route('/api/analyze', methods=['POST'])
@limiter.limit("10 per minute")
def analyze():
    # Rate-limited endpoint
    pass
\`\`\`

### API Key Management

**Best Practices**:
- Store in environment variables
- Never commit to version control
- Rotate regularly
- Use different keys for different environments
- Monitor key usage

**Rotation Process**:
1. Generate new key
2. Update environment variable
3. Deploy with new key
4. Monitor for issues
5. Revoke old key after verification

## Security Monitoring

### Logging

**Security Events Logged**:
- Failed authentication attempts
- Unauthorized access attempts
- API errors
- Suspicious activity

**Implementation**:
\`\`\`python
def log_security_event(event_type, user_id, details):
    """Log security events."""
    db.collection('securityLogs').add({
        'event_type': event_type,
        'user_id': user_id,
        'details': details,
        'timestamp': firestore.SERVER_TIMESTAMP,
        'ip_address': request.remote_addr
    })
\`\`\`

### Alerting

**Alerts Configured**:
- Multiple failed login attempts
- Unusual API usage patterns
- Error rate spikes
- Unauthorized access attempts

## Security Best Practices

### Code Security

1. **Input Validation**: Validate all user inputs
2. **Output Encoding**: Encode outputs to prevent XSS
3. **SQL Injection Prevention**: Use parameterized queries (N/A for Firestore)
4. **Dependency Updates**: Regularly update dependencies
5. **Secret Management**: Use secret management services

### Infrastructure Security

1. **Least Privilege**: Minimal IAM permissions
2. **Network Security**: VPC, firewall rules
3. **Container Security**: Scan images for vulnerabilities
4. **Backup Security**: Encrypt backups
5. **Monitoring**: Continuous security monitoring

## Compliance Checklist

### GDPR Compliance

- [ ] Data minimization implemented
- [ ] User consent obtained
- [ ] Right to access implemented
- [ ] Right to deletion implemented
- [ ] Data portability implemented
- [ ] Privacy policy published
- [ ] DPO appointed (if required)
- [ ] Data processing agreements in place

### Security Compliance

- [ ] Encryption at rest enabled
- [ ] Encryption in transit enabled
- [ ] Access controls implemented
- [ ] Audit logging enabled
- [ ] Security monitoring active
- [ ] Incident response plan documented
- [ ] Regular security audits conducted

## Related Documentation

- [Deployment Environment](deployment-environment.md)
- [Operational Readiness](operational-readiness.md)
- [Architecture - Security](../architecture/security-architecture.md)

`;

// Auto-generated from testing-qa.md
export const MainToolDocumentationCodeDocumentationTestingQa = `# Testing & QA

## Overview

This document outlines the testing strategy, test structure, quality assurance processes, and testing tools for the Dark Pattern Detection & Compliance Analysis Tool.

## Testing Strategy

### Testing Pyramid

\`\`\`
        /\\
       /  \\  E2E Tests (Few)
      /____\\
     /      \\  Integration Tests (Some)
    /________\\
   /          \\  Unit Tests (Many)
  /____________\\
\`\`\`

### Test Types

1. **Unit Tests**: Test individual functions and modules
2. **Integration Tests**: Test API endpoints and component interactions
3. **End-to-End Tests**: Test complete user workflows
4. **Performance Tests**: Load and stress testing
5. **Security Tests**: Vulnerability and penetration testing

## Unit Testing

### Test Structure

**Location**: \`tests/unit/\`

**Structure**:
\`\`\`
tests/
├── unit/
│   ├── test_llm_calls.py
│   ├── test_sub_agents.py
│   ├── test_outcome_synthesis.py
│   ├── test_context.py
│   └── test_utilities.py
├── integration/
│   ├── test_api_endpoints.py
│   └── test_firebase_integration.py
└── e2e/
    └── test_user_workflows.py
\`\`\`

### Example Unit Test

\`\`\`python
import unittest
from unittest.mock import patch, MagicMock
from utility.LlmCalls import call_llm

class TestLlmCalls(unittest.TestCase):
    
    @patch('utility.LlmCalls.ChatOpenAI')
    def test_call_llm_success(self, mock_chat):
        """Test successful LLM call."""
        # Mock response
        mock_response = MagicMock()
        mock_response.content = "Test response"
        mock_chat.return_value.invoke.return_value = mock_response
        
        # Call function
        result = call_llm(
            model_name="gpt-4o-mini",
            prompt="Test prompt",
            task_name="Test Task"
        )
        
        # Assertions
        self.assertEqual(result, "Test response")
        mock_chat.assert_called_once()
    
    @patch('utility.LlmCalls.ChatOpenAI')
    def test_call_llm_failure(self, mock_chat):
        """Test LLM call failure handling."""
        # Mock exception
        mock_chat.return_value.invoke.side_effect = Exception("API Error")
        
        # Call function and expect exception
        with self.assertRaises(Exception):
            call_llm(
                model_name="gpt-4o-mini",
                prompt="Test prompt",
                task_name="Test Task"
            )
\`\`\`

### Testing Utilities

**Example**: Test helper functions

\`\`\`python
def create_mock_image_data():
    """Create mock image data for testing."""
    return {
        'image_id': 'test_img_123',
        'url': 'https://example.com',
        'image_url': 'https://storage.googleapis.com/test/image.png',
        'note': 'Test note',
        'status': 'initial'
    }

def create_mock_llm_response():
    """Create mock LLM response."""
    return {
        'content': 'Mock LLM response',
        'tokens_used': 100,
        'cost': 0.001
    }
\`\`\`

## Integration Testing

### API Endpoint Testing

**Framework**: pytest with Flask test client

\`\`\`python
import pytest
from app import app

@pytest.fixture
def client():
    """Create test client."""
    app.config['TESTING'] = True
    with app.test_client() as client:
        yield client

def test_get_image_metadata(client):
    """Test image metadata endpoint."""
    response = client.post('/get_image_metadata', json={
        'image_url': 'https://example.com/image.png',
        'url': 'https://example.com',
        'note': 'Test note',
        'session_id': 'test_session',
        'journey_id': 'test_journey',
        'image_doc_id': 'test_image'
    })
    
    assert response.status_code == 200
    data = response.get_json()
    assert 'status' in data

def test_upload_and_process(client):
    """Test upload and process endpoint."""
    with open('test_image.png', 'rb') as f:
        response = client.post('/upload_and_process', data={
            'image': (f, 'test_image.png'),
            'url': 'https://example.com',
            'session_id': 'test_session',
            'journey_id': 'test_journey',
            'image_doc_id': 'test_image'
        })
    
    assert response.status_code == 200
\`\`\`

### Firebase Integration Testing

**Mock Firebase**: Use Firebase emulator or mocks

\`\`\`python
from unittest.mock import patch, MagicMock

@patch('firebase_admin.firestore.client')
def test_firebase_update(mock_firestore):
    """Test Firebase document update."""
    # Mock Firestore
    mock_doc = MagicMock()
    mock_doc.update.return_value = None
    mock_doc.get.return_value.to_dict.return_value = {'status': 'completed'}
    
    mock_collection = MagicMock()
    mock_collection.document.return_value.collection.return_value.document.return_value = mock_doc
    mock_firestore.return_value.collection.return_value = mock_collection
    
    # Test function
    result = update_image_status('session', 'journey', 'image', 'completed', {})
    
    assert result['status'] == 'completed'
\`\`\`

## End-to-End Testing

### User Workflow Testing

**Framework**: Selenium or Playwright

\`\`\`python
from selenium import webdriver
from selenium.webdriver.common.by import By
import time

def test_complete_analysis_workflow():
    """Test complete user workflow."""
    driver = webdriver.Chrome()
    
    try:
        # 1. Login
        driver.get('https://your-app.com/login')
        driver.find_element(By.ID, 'login-button').click()
        time.sleep(2)  # Wait for OAuth
        
        # 2. Upload image
        driver.get('https://your-app.com/upload')
        driver.find_element(By.ID, 'file-input').send_keys('test_image.png')
        driver.find_element(By.ID, 'url-input').send_keys('https://example.com')
        driver.find_element(By.ID, 'submit-button').click()
        
        # 3. Wait for processing
        time.sleep(180)  # Wait for analysis
        
        # 4. Verify results
        results = driver.find_element(By.ID, 'results')
        assert results.is_displayed()
        
    finally:
        driver.quit()
\`\`\`

## Error Injection Testing

### Failure Mode Testing

**Purpose**: Test system behavior under failure conditions

\`\`\`python
def test_llm_api_failure():
    """Test behavior when LLM API fails."""
    with patch('utility.LlmCalls.call_llm') as mock_llm:
        mock_llm.side_effect = Exception("API Error")
        
        # Should handle gracefully
        result = analyze_image('url', 'note', 'image_id')
        
        assert result['status'] == 'error'
        assert 'error' in result

def test_firebase_connection_failure():
    """Test behavior when Firebase is unavailable."""
    with patch('firebase_admin.firestore.client') as mock_firestore:
        mock_firestore.side_effect = Exception("Connection Error")
        
        # Should handle gracefully
        with pytest.raises(Exception):
            save_to_firebase({})

def test_timeout_handling():
    """Test timeout handling."""
    with patch('utility.LlmCalls.call_llm') as mock_llm:
        mock_llm.side_effect = TimeoutError("Request timeout")
        
        result = analyze_image('url', 'note', 'image_id')
        
        assert result['status'] == 'error'
\`\`\`

## Coverage Metrics

### Coverage Goals

- **Unit Tests**: 80%+ code coverage
- **Integration Tests**: 70%+ API endpoint coverage
- **Critical Paths**: 100% coverage

### Coverage Tools

**Python**: pytest-cov

\`\`\`bash
# Run tests with coverage
pytest --cov=utility --cov=app --cov-report=html

# View coverage report
open htmlcov/index.html
\`\`\`

**JavaScript**: Jest with coverage

\`\`\`bash
# Run tests with coverage
npm test -- --coverage

# View coverage report
open coverage/lcov-report/index.html
\`\`\`

### Coverage Reports

**Track**:
- Line coverage
- Branch coverage
- Function coverage
- Statement coverage

## Test Data Management

### Test Fixtures

**Location**: \`tests/fixtures/\`

**Types**:
- Sample images
- Mock API responses
- Test database snapshots
- Configuration files

### Test Data Generation

\`\`\`python
def generate_test_image_data(count=10):
    """Generate test image data."""
    return [
        {
            'image_id': f'test_img_{i}',
            'url': f'https://example.com/page_{i}',
            'note': f'Test note {i}',
            'status': 'initial'
        }
        for i in range(count)
    ]

def generate_mock_llm_responses():
    """Generate mock LLM responses."""
    return {
        'context_extraction': 'Mock context',
        'scanner_result': 'Mock scanner result',
        'agent_response': 'Mock agent response'
    }
\`\`\`

## Performance Testing

### Load Testing

**Tool**: Locust or Apache JMeter

\`\`\`python
from locust import HttpUser, task, between

class AnalysisUser(HttpUser):
    wait_time = between(1, 3)
    
    @task
    def analyze_image(self):
        """Simulate image analysis request."""
        self.client.post('/upload_and_process', files={
            'image': ('test.png', open('test.png', 'rb'), 'image/png')
        }, data={
            'url': 'https://example.com',
            'session_id': 'test_session',
            'journey_id': 'test_journey',
            'image_doc_id': 'test_image'
        })
\`\`\`

### Stress Testing

**Purpose**: Test system limits

\`\`\`python
def stress_test_concurrent_requests(num_requests=100):
    """Test system with concurrent requests."""
    import concurrent.futures
    
    with concurrent.futures.ThreadPoolExecutor(max_workers=50) as executor:
        futures = [
            executor.submit(analyze_image, f'url_{i}', f'note_{i}', f'img_{i}')
            for i in range(num_requests)
        ]
        
        results = [future.result() for future in concurrent.futures.as_completed(futures)]
    
    return results
\`\`\`

## Regression Testing

### Test Suite

**Automated**: Run on every commit

\`\`\`bash
# Run all tests
pytest tests/

# Run specific test category
pytest tests/unit/
pytest tests/integration/
pytest tests/e2e/
\`\`\`

### Continuous Integration

**GitHub Actions Example**:

\`\`\`yaml
name: Tests

on: [push, pull_request]

jobs:
  test:
    runs-on: ubuntu-latest
    steps:
      - uses: actions/checkout@v2
      - name: Set up Python
        uses: actions/setup-python@v2
        with:
          python-version: '3.11'
      - name: Install dependencies
        run: pip install -r requirements.txt
      - name: Run tests
        run: pytest tests/ --cov
\`\`\`

## Quality Assurance Checklist

### Pre-Deployment

- [ ] All unit tests passing
- [ ] All integration tests passing
- [ ] Code coverage above threshold
- [ ] Performance tests passing
- [ ] Security tests passing
- [ ] Manual testing completed
- [ ] Documentation updated

### Post-Deployment

- [ ] Smoke tests passing
- [ ] Monitoring alerts configured
- [ ] Error rates within acceptable range
- [ ] Performance metrics normal
- [ ] User acceptance testing completed

## Related Documentation

- [Code Examples](code-examples.md)
- [Performance Benchmarking](performance-benchmarking.md)
- [Operational Readiness](operational-readiness.md)

`;

// Auto-generated from versioning.md
export const MainToolDocumentationCodeDocumentationVersioning = `# Versioning & Change Management

## Overview

This document outlines the versioning strategy, Git workflow, release management, backward compatibility considerations, and migration procedures for the Dark Pattern Detection & Compliance Analysis Tool.

## Git Strategy

### Branching Model

**Main Branches**:
- \`main\` - Production-ready code
- \`develop\` - Integration branch for features
- \`release/*\` - Release preparation branches
- \`hotfix/*\` - Critical bug fixes

**Feature Branches**:
- \`feature/*\` - New features
- \`bugfix/*\` - Bug fixes
- \`refactor/*\` - Code refactoring

### Workflow

\`\`\`
main
  │
  ├── develop
  │     │
  │     ├── feature/new-agent
  │     ├── feature/ui-improvements
  │     └── bugfix/fix-error-handling
  │
  ├── release/v1.2.0
  │
  └── hotfix/critical-security-fix
\`\`\`

### Commit Message Convention

**Format**: \`<type>(<scope>): <subject>\`

**Types**:
- \`feat\`: New feature
- \`fix\`: Bug fix
- \`docs\`: Documentation changes
- \`style\`: Code style changes
- \`refactor\`: Code refactoring
- \`test\`: Test additions/changes
- \`chore\`: Maintenance tasks

**Examples**:
\`\`\`
feat(agents): Add new dark pattern detection agent
fix(api): Resolve timeout issue in image processing
docs(readme): Update installation instructions
refactor(llm): Optimize LLM call interface
\`\`\`

## Semantic Versioning

### Version Format

**Format**: \`MAJOR.MINOR.PATCH\`

- **MAJOR**: Breaking changes
- **MINOR**: New features (backward compatible)
- **PATCH**: Bug fixes (backward compatible)

**Examples**:
- \`1.0.0\` - Initial release
- \`1.1.0\` - New features added
- \`1.1.1\` - Bug fixes
- \`2.0.0\` - Breaking changes

### Version Numbering

**Current Version**: \`1.0.0\` (to be determined)

**Version Components**:
- **Backend**: Tracked in \`app.py\` or \`__version__.py\`
- **Frontend**: Tracked in \`package.json\`
- **API**: Versioned in URL or headers

## Release Notes Format

### Structure

\`\`\`markdown
# Release Notes - Version X.Y.Z

## Release Date
YYYY-MM-DD

## Summary
Brief overview of the release

## New Features
- Feature 1 description
- Feature 2 description

## Improvements
- Improvement 1
- Improvement 2

## Bug Fixes
- Fixed issue #123
- Resolved timeout errors

## Breaking Changes
- Change 1 (migration guide link)
- Change 2 (migration guide link)

## Deprecations
- Deprecated feature X (removal in vX+1)

## Security
- Security fix description

## Migration Guide
Link to detailed migration guide if needed
\`\`\`

### Example Release Notes

\`\`\`markdown
# Release Notes - Version 1.2.0

## Release Date
2024-01-15

## Summary
This release introduces new dark pattern detection agents and improves analysis accuracy.

## New Features
- Added "Bait and Switch" detection agent
- Implemented batch image processing
- Added compliance scoring dashboard

## Improvements
- Reduced average analysis time by 30%
- Improved LLM response caching
- Enhanced error messages

## Bug Fixes
- Fixed memory leak in parallel processing
- Resolved Firebase connection timeout issues
- Fixed coordinate generation for mobile screenshots

## Breaking Changes
None

## Migration Guide
No migration required for this release.
\`\`\`

## Backward Compatibility

### Compatibility Policy

**Major Versions**: May include breaking changes
**Minor Versions**: Backward compatible
**Patch Versions**: Backward compatible

### API Compatibility

**Versioning Strategy**:
- URL-based: \`/api/v1/\`, \`/api/v2/\`
- Header-based: \`API-Version: 1.0\`
- Query parameter: \`?version=1.0\`

**Current**: Single version (implicit v1)

**Future**: Multi-version support

\`\`\`python
@app.route('/api/v1/get_image_metadata', methods=['POST'])
def get_image_metadata_v1():
    # Version 1 implementation
    pass

@app.route('/api/v2/get_image_metadata', methods=['POST'])
def get_image_metadata_v2():
    # Version 2 implementation (may have breaking changes)
    pass
\`\`\`

### Database Schema Compatibility

**Strategy**: Additive changes only in minor versions

**Breaking Changes**:
- Field removals → Major version
- Type changes → Major version
- Required field additions → Major version

**Safe Changes**:
- New optional fields → Minor version
- New collections → Minor version
- Index additions → Minor version

### Frontend Compatibility

**Strategy**: Support multiple API versions

\`\`\`javascript
const API_VERSION = 'v1';

async function callAPI(endpoint, data) {
  const response = await fetch(\`/api/\${API_VERSION}/\${endpoint}\`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  });
  return response.json();
}
\`\`\`

## Migration Guides

### Database Migration

**Example**: Adding new field

\`\`\`python
def migrate_add_new_field():
    """Migrate existing documents to include new field."""
    db = firestore.client()
    
    # Get all sessions
    sessions = db.collection('sessions').stream()
    
    for session in sessions:
        # Check if field exists
        data = session.to_dict()
        if 'new_field' not in data:
            # Add default value
            session.reference.update({
                'new_field': 'default_value'
            })
\`\`\`

### API Migration

**Example**: Changing response format

\`\`\`python
# Old format (v1)
{
    "result": "analysis result"
}

# New format (v2)
{
    "analysis": {
        "result": "analysis result",
        "metadata": {...}
    }
}

# Migration: Support both formats during transition
def get_image_metadata_v2():
    result = process_analysis()
    
    # Return new format
    return {
        "analysis": {
            "result": result,
            "metadata": get_metadata()
        }
    }
\`\`\`

### Configuration Migration

**Example**: Environment variable changes

\`\`\`python
def migrate_environment_variables():
    """Migrate old environment variable names."""
    import os
    
    # Old variable name
    old_key = os.getenv('OLD_API_KEY')
    
    # New variable name
    new_key = os.getenv('NEW_API_KEY')
    
    # Use new key, fallback to old for compatibility
    api_key = new_key or old_key
    
    if old_key and not new_key:
        print("Warning: Using deprecated OLD_API_KEY. Please update to NEW_API_KEY.")
    
    return api_key
\`\`\`

## Release Process

### Pre-Release Checklist

- [ ] All tests passing
- [ ] Code review completed
- [ ] Documentation updated
- [ ] Version numbers updated
- [ ] Release notes prepared
- [ ] Migration guide written (if needed)
- [ ] Changelog updated

### Release Steps

1. **Create Release Branch**:
   \`\`\`bash
   git checkout -b release/v1.2.0 develop
   \`\`\`

2. **Update Version Numbers**:
   - Backend: Update \`__version__.py\`
   - Frontend: Update \`package.json\`
   - Documentation: Update version references

3. **Final Testing**:
   \`\`\`bash
   pytest tests/
   npm test
   \`\`\`

4. **Merge to Main**:
   \`\`\`bash
   git checkout main
   git merge release/v1.2.0
   git tag v1.2.0
   \`\`\`

5. **Merge to Develop**:
   \`\`\`bash
   git checkout develop
   git merge release/v1.2.0
   \`\`\`

6. **Deploy**:
   - Deploy backend to Cloud Run
   - Deploy frontend to Vercel
   - Verify deployment

7. **Announce**:
   - Publish release notes
   - Notify users
   - Update documentation

### Hotfix Process

1. **Create Hotfix Branch**:
   \`\`\`bash
   git checkout -b hotfix/critical-fix main
   \`\`\`

2. **Fix Issue**:
   - Implement fix
   - Add tests
   - Update documentation

3. **Merge to Main and Develop**:
   \`\`\`bash
   git checkout main
   git merge hotfix/critical-fix
   git tag v1.1.1
   
   git checkout develop
   git merge hotfix/critical-fix
   \`\`\`

4. **Deploy Immediately**

## Change Log

### Format

\`\`\`markdown
# Changelog

All notable changes to this project will be documented in this file.

The format is based on [Keep a Changelog](https://keepachangelog.com/en/1.0.0/),
and this project adheres to [Semantic Versioning](https://semver.org/spec/v2.0.0.html).

## [Unreleased]

## [1.2.0] - 2024-01-15
### Added
- New dark pattern detection agents
- Batch processing capability

### Changed
- Improved analysis accuracy
- Reduced processing time

### Fixed
- Memory leak in parallel processing
- Firebase timeout issues

## [1.1.0] - 2023-12-01
### Added
- User feedback system
- Compliance scoring

## [1.0.0] - 2023-11-01
### Added
- Initial release
- Core dark pattern detection
- Compliance analysis
\`\`\`

## Deprecation Policy

### Deprecation Process

1. **Announce**: Mark as deprecated in documentation
2. **Warn**: Log warnings when deprecated features are used
3. **Maintain**: Keep deprecated features for at least one major version
4. **Remove**: Remove in next major version

### Example

\`\`\`python
import warnings

def deprecated_function():
    """This function is deprecated."""
    warnings.warn(
        "deprecated_function is deprecated. Use new_function instead.",
        DeprecationWarning,
        stacklevel=2
    )
    # Old implementation
    pass

def new_function():
    """New recommended function."""
    # New implementation
    pass
\`\`\`

## Related Documentation

- [Developer Experience](developer-experience.md)
- [Operational Readiness](operational-readiness.md)
- [API Documentation - Versioning](../api-documentation/api-versioning.md)

`;

// Auto-generated from compliance-integration.md
export const MainToolDocumentationFunctionalFlowsComplianceIntegration = `# Compliance & Audit Trail Integration

## Overview

This document details how the Dark Pattern Detection & Compliance Analysis Tool integrates with regulatory frameworks, generates compliance reports, and maintains audit trails.

## Regulatory Framework Mapping

### Supported Jurisdictions

**EU (European Union)**:
- Digital Content Directive
- AVMSD (Audiovisual Media Services Directive)
- DMA (Digital Markets Act)
- CRD (Consumer Rights Directive)
- UCPD (Unfair Commercial Practices Directive)
- GDPR (General Data Protection Regulation)
- DSA (Digital Services Act)

**US (United States)**:
- Federal Trade Commission Act
- CCPA (California Consumer Privacy Act)
- CPRA (California Privacy Rights Act)
- COPPA (Children's Online Privacy Protection Act)
- CAN-SPAM Act

**UK (United Kingdom)**:
- UK GDPR
- Consumer Rights Act 2015
- Online Safety Act
- Digital Markets, Competition and Consumers Act

## Law Fetching Process

### Advanced Law Fetcher

**Implementation**: \`utility/AdvancedLawFetcher.py\`

**Process**:
1. Identify dark pattern from analysis
2. Select jurisdiction(s)
3. Query Perplexity AI for relevant laws
4. Validate with Airtable database
5. Match specific regulations
6. Generate fair pattern suggestions

**Code Example**:
\`\`\`python
from utility.AdvancedLawFetcher import advance_law_fetch_function

law_result = advance_law_fetch_function(
    point="Dark pattern description",
    selected_jurisdictions=['EU', 'US']
)

# Returns:
# {
#     "parent_type": "Coercing",
#     "dark_pattern": "Forced Action",
#     "dark_pattern_details": {...},
#     "laws": [...],
#     "fair_patterns": [...]
# }
\`\`\`

## Compliance Scoring

### Scoring Methodology

**Outcome Categories**:
- **Outcome**: Confident violations (score: 1.0)
- **Doubtful**: Uncertain violations (score: 0.5)
- **Hallucination**: Invalid detections (score: 0.0)

**Compliance Score Calculation**:
\`\`\`python
def calculate_compliance_score(analysis_results):
    total_patterns = len(analysis_results)
    confident_violations = sum(1 for r in analysis_results if r['type'] == 'outcome')
    doubtful_violations = sum(1 for r in analysis_results if r['type'] == 'doubtful')
    
    violation_score = confident_violations * 1.0 + doubtful_violations * 0.5
    compliance_score = 1.0 - (violation_score / total_patterns) if total_patterns > 0 else 1.0
    
    return {
        'compliance_score': compliance_score,
        'violation_count': confident_violations,
        'doubtful_count': doubtful_violations,
        'total_patterns': total_patterns
    }
\`\`\`

## Audit Trail Generation

### Activity Logging

**Firebase Collections**:
- \`userActivityLogs\`: User actions
- \`errorLogs\`: System errors
- \`FeedbackLogs\`: User feedback
- Session documents: Analysis history

### Audit Trail Structure

\`\`\`python
{
    'timestamp': Timestamp,
    'user_id': str,
    'action': str,  # 'analysis_created', 'report_generated', etc.
    'resource_type': str,  # 'session', 'image', 'report'
    'resource_id': str,
    'details': dict,
    'ip_address': str,
    'user_agent': str
}
\`\`\`

### Compliance Report Generation

**Report Contents**:
1. Executive Summary
2. Detected Dark Patterns
3. Legal References
4. Compliance Scores
5. Remediation Recommendations
6. Fair Pattern Alternatives
7. Evidence Screenshots

## Related Documentation

- [Advanced Law Fetcher](../code-documentation/model-integrations.md)
- [Use Cases](use-cases.md)
- [Security & Compliance](../code-documentation/security-compliance.md)

`;

// Auto-generated from error-handling.md
export const MainToolDocumentationFunctionalFlowsErrorHandling = `# Error Handling & Recovery Paths

## Overview

This document details error handling strategies, error types, recovery mechanisms, and debugging procedures for the Dark Pattern Detection & Compliance Analysis Tool.

## Error Categories

### 1. API Errors

**LLM API Failures**:
- Rate limit exceeded
- API key invalid/expired
- Network timeout
- Service unavailable

**Handling**:
\`\`\`python
def handle_llm_error(error, retry_count=0):
    if isinstance(error, RateLimitError):
        # Wait and retry
        time.sleep(2 ** retry_count)
        return retry_llm_call()
    elif isinstance(error, AuthenticationError):
        # Try alternative API key
        return try_alternative_key()
    elif isinstance(error, TimeoutError):
        # Retry with longer timeout
        return retry_with_timeout()
    else:
        # Log and return error
        log_error(error)
        return None
\`\`\`

### 2. Storage Errors

**Firebase Errors**:
- Connection timeout
- Permission denied
- Document not found
- Quota exceeded

**GCS Errors**:
- Upload failure
- Access denied
- Bucket not found

**Handling**:
\`\`\`python
def handle_storage_error(error, operation):
    if isinstance(error, ConnectionError):
        # Retry connection
        return retry_operation(operation)
    elif isinstance(error, PermissionError):
        # Check credentials
        verify_credentials()
        return retry_operation(operation)
    else:
        log_error(error)
        return None
\`\`\`

### 3. Validation Errors

**Input Validation**:
- Missing required fields
- Invalid data types
- Out of range values

**Handling**:
\`\`\`python
def validate_request(data):
    required_fields = ['url', 'session_id', 'journey_id']
    for field in required_fields:
        if field not in data:
            return {
                'error': f'Missing required field: {field}',
                'status': 400
            }
    return None
\`\`\`

### 4. Processing Errors

**Analysis Failures**:
- Step processing timeout
- Invalid image format
- Insufficient context

**Handling**:
\`\`\`python
def handle_processing_error(step_number, error):
    # Save partial results
    save_partial_results(step_number - 1)
    
    # Update status
    update_status('error', {
        'error_step': step_number,
        'error_message': str(error)
    })
    
    # Notify user
    notify_user_of_error(error)
\`\`\`

## Error Recovery Mechanisms

### Automatic Retry

**Exponential Backoff**:
\`\`\`python
def retry_with_backoff(func, max_retries=3):
    for attempt in range(max_retries):
        try:
            return func()
        except RetryableError as e:
            if attempt < max_retries - 1:
                wait_time = 2 ** attempt
                time.sleep(wait_time)
                continue
            else:
                raise
\`\`\`

### Resume from Last State

**State-Based Recovery**:
\`\`\`python
def resume_processing(image_id):
    current_status = get_status(image_id)
    
    if current_status == 'step1_completed':
        return continue_from_step2(image_id)
    elif current_status == 'step2_completed':
        return continue_from_step3(image_id)
    elif current_status == 'step3_completed':
        return continue_from_step4(image_id)
    else:
        return start_from_beginning(image_id)
\`\`\`

### Fallback Strategies

**Model Fallback**:
\`\`\`python
def call_llm_with_fallback(prompt, primary_model, fallback_model):
    try:
        return call_llm(prompt, primary_model)
    except Exception as e:
        log_error(f"Primary model failed: {e}")
        return call_llm(prompt, fallback_model)
\`\`\`

**Provider Fallback**:
\`\`\`python
def call_with_provider_fallback(prompt):
    providers = ['openai', 'google', 'anthropic']
    
    for provider in providers:
        try:
            return call_llm_provider(prompt, provider)
        except Exception as e:
            log_error(f"{provider} failed: {e}")
            continue
    
    raise Exception("All providers failed")
\`\`\`

## Error Logging

### Firebase Error Collection

**Structure**:
\`\`\`python
{
    'error': str,              # Error message
    'error_type': str,         # Error category
    'error_step': int,         # Processing step
    'user_id': str,            # User ID
    'session_id': str,         # Session ID
    'image_id': str,           # Image ID
    'timestamp': Timestamp,    # Error timestamp
    'stack_trace': str,        # Full stack trace
    'context': dict            # Additional context
}
\`\`\`

### Error Logging Function

\`\`\`python
def log_error_to_firebase(error, context):
    db = firestore.client()
    db.collection('errorLogs').add({
        'error': str(error),
        'error_type': type(error).__name__,
        'timestamp': firestore.SERVER_TIMESTAMP,
        'context': context,
        'stack_trace': traceback.format_exc()
    })
\`\`\`

## User-Facing Error Messages

### Error Message Format

\`\`\`json
{
    "status": "error",
    "error": "User-friendly error message",
    "error_code": "ERROR_CODE",
    "details": {
        "field": "Additional details"
    },
    "suggestion": "What the user can do"
}
\`\`\`

### Error Codes

- \`INVALID_INPUT\`: Invalid request data
- \`PROCESSING_FAILED\`: Analysis processing failed
- \`TIMEOUT\`: Request timeout
- \`RATE_LIMIT\`: Rate limit exceeded
- \`STORAGE_ERROR\`: Storage operation failed
- \`AUTH_ERROR\`: Authentication failed
- \`NOT_FOUND\`: Resource not found

## Debugging Procedures

### 1. Check Error Logs

\`\`\`bash
# View recent errors
gcloud logging read "resource.type=cloud_run_revision AND severity>=ERROR" --limit 50

# Filter by error type
gcloud logging read "jsonPayload.error_type=ProcessingError" --limit 50
\`\`\`

### 2. Check Firebase Error Collection

\`\`\`python
def get_recent_errors(limit=10):
    db = firestore.client()
    errors = db.collection('errorLogs') \\
               .order_by('timestamp', direction=firestore.Query.DESCENDING) \\
               .limit(limit) \\
               .stream()
    return [doc.to_dict() for doc in errors]
\`\`\`

### 3. Reproduce Error

\`\`\`python
def reproduce_error(error_id):
    error = get_error_by_id(error_id)
    context = error['context']
    
    # Reproduce with same context
    try:
        result = process_image(
            context['image_url'],
            context['url'],
            context['note']
        )
    except Exception as e:
        # Compare with original error
        compare_errors(error, e)
\`\`\`

## Common Error Scenarios

### Scenario 1: LLM API Rate Limit

**Symptoms**: 429 status code, rate limit error

**Recovery**:
1. Wait for rate limit window
2. Use alternative API key
3. Reduce request frequency
4. Implement request queuing

### Scenario 2: Firebase Connection Timeout

**Symptoms**: Connection timeout, document not updated

**Recovery**:
1. Retry with exponential backoff
2. Check network connectivity
3. Verify Firebase credentials
4. Use offline mode if available

### Scenario 3: Image Processing Timeout

**Symptoms**: Step processing exceeds timeout

**Recovery**:
1. Increase timeout for long operations
2. Process in background (Cloud Tasks)
3. Optimize processing steps
4. Implement checkpointing

### Scenario 4: Invalid Image Format

**Symptoms**: Image cannot be processed

**Recovery**:
1. Validate image format before processing
2. Convert to supported format
3. Return clear error message
4. Suggest alternative formats

## Error Prevention

### Input Validation

\`\`\`python
def validate_image_upload(request):
    """Validate image upload before processing."""
    errors = []
    
    # Check required fields
    if not request.form.get('url'):
        errors.append('URL is required')
    
    # Validate URL format
    url = request.form.get('url')
    if not url.startswith(('http://', 'https://')):
        errors.append('Invalid URL format')
    
    # Validate image file
    if 'image' in request.files:
        file = request.files['image']
        if not allowed_file(file.filename):
            errors.append('Invalid file type')
    
    return errors
\`\`\`

### Pre-flight Checks

\`\`\`python
def preflight_checks():
    """Check system health before processing."""
    checks = {
        'firebase': check_firebase_connection(),
        'gcs': check_gcs_connection(),
        'llm': check_llm_availability(),
        'storage_quota': check_storage_quota()
    }
    
    if not all(checks.values()):
        raise SystemError(f"Pre-flight checks failed: {checks}")
    
    return True
\`\`\`

## Related Documentation

- [Process States](process-states.md)
- [Workflow Diagrams](workflow-diagrams.md)
- [Operational Readiness](../code-documentation/operational-readiness.md)

`;

// Auto-generated from interactive-flows.md
export const MainToolDocumentationFunctionalFlowsInteractiveFlows = `# Interactive Simulation/Game/Dashboard Flows

## Overview

This document describes the interactive user flows, dashboard navigation, and user interface interactions in the Dark Pattern Detection & Compliance Analysis Tool.

## Dashboard Navigation

### Main Dashboard Flow

\`\`\`mermaid
graph TD
    A[Login] --> B[Dashboard]
    B --> C[Create New Session]
    B --> D[View Existing Sessions]
    B --> E[Reports]
    B --> F[Settings]
    
    C --> G[Session Creation Form]
    G --> H[Add Images]
    H --> I[Analysis View]
    
    D --> J[Session List]
    J --> K[Session Details]
    K --> I
    
    E --> L[Report List]
    L --> M[Report View]
\`\`\`

### Dashboard Components

**HomePage** (\`src/components/HomePage.js\`):
- Welcome message
- Quick actions
- Recent sessions
- Statistics

**ExplorePage** (\`src/components/ExplorePage.js\`):
- Session browser
- Filter and search
- Session cards
- Quick actions

**Dashboard** (\`src/components/Dashboard.js\`):
- Overview statistics
- Recent activity
- Quick access buttons
- Notifications

## Report Generation Flow

### Report Creation

\`\`\`mermaid
sequenceDiagram
    participant User
    participant Frontend
    participant API
    participant Firebase
    
    User->>Frontend: Select Session
    Frontend->>API: Get Session Data
    API->>Firebase: Fetch Session
    Firebase-->>API: Session Data
    API-->>Frontend: Session with Results
    Frontend->>Frontend: Generate Report
    Frontend->>User: Display Report
    User->>Frontend: Export Report
    Frontend->>User: Download PDF/JSON
\`\`\`

### Report Components

**ReportPage** (\`src/components/ReportPage.js\`):
- Executive summary
- Pattern detections
- Legal references
- Visualizations
- Export options

**ReportsPage** (\`src/components/ReportsPage.js\`):
- Report list
- Filter by date/status
- Search functionality
- Bulk actions

## Interactive Editor Flows

### Image Editor Flow

**NewEditor** (\`src/components/NewEditor.js\`):
1. Upload/select image
2. Add URL and notes
3. Submit for analysis
4. View real-time progress
5. Review results
6. Provide feedback

**DigitalEditor** (\`src/components/DigitalEditor.js\`):
- Similar flow for digital compliance analysis
- Compliance-specific fields
- Jurisdiction selection

### JSON Editor Flow

**JsonEditor** (\`src/components/JsonEditor.js\`):
1. View analysis JSON
2. Edit if needed
3. Validate structure
4. Save changes
5. Regenerate report

## Data Visualization Flows

### Chart Generation

**Components Used**:
- Chart.js
- React Google Charts
- D3.js

**Visualizations**:
- Pattern distribution charts
- Compliance score trends
- Timeline of detections
- Jurisdiction comparison

### Interactive Charts

\`\`\`javascript
// Example: Pattern distribution
const patternData = {
  labels: ['Coercing', 'Falsification', 'Misdirection', ...],
  datasets: [{
    data: [5, 3, 2, ...],
    backgroundColor: ['#FF6384', '#36A2EB', '#FFCE56', ...]
  }]
};
\`\`\`

## User Journey Tracking

### Journey Visualization

**FlowChart** (\`src/components/FlowChart.js\`):
- Visual journey representation
- Step-by-step flow
- Status indicators
- Interactive navigation

### Journey Management

1. Create journey
2. Add images to journey
3. Track processing status
4. View journey results
5. Generate journey report

## Related Documentation

- [Workflow Diagrams](workflow-diagrams.md)
- [Process States](process-states.md)
- [User Manual - Feature Walkthroughs](../user-manual/feature-walkthroughs.md)

`;

// Auto-generated from process-states.md
export const MainToolDocumentationFunctionalFlowsProcessStates = `# Process States & Transitions

## Overview

This document details all process states, state transitions, and state management in the Dark Pattern Detection & Compliance Analysis Tool.

## Image Processing States

### State Diagram

\`\`\`mermaid
stateDiagram-v2
    [*] --> initial: Image Uploaded
    initial --> step1_completed: Step 1 Success
    step1_completed --> step2_completed: Step 2 Success
    step2_completed --> step3_completed: Step 3 Success
    step3_completed --> completed: Step 4 Success
    
    initial --> error: Step 1 Failure
    step1_completed --> error: Step 2 Failure
    step2_completed --> error: Step 3 Failure
    step3_completed --> error: Step 4 Failure
    
    error --> initial: Manual Retry
    error --> step1_completed: Resume from Step 1
    error --> step2_completed: Resume from Step 2
    error --> step3_completed: Resume from Step 3
    
    completed --> [*]
\`\`\`

### State Definitions

#### \`initial\`
- **Description**: Document created, processing not started
- **Firebase Field**: \`status: 'initial'\`
- **Data Present**: Basic metadata (url, image_url, note)
- **Next States**: \`step1_completed\`, \`error\`

#### \`step1_completed\`
- **Description**: Information extraction complete
- **Firebase Field**: \`status: 'step1_completed'\`
- **Data Present**: 
  - All Step 1 fields (pageName, website_brief, webpage_brief, etc.)
  - Context information
  - Scanner results
- **Next States**: \`step2_completed\`, \`error\`

#### \`step2_completed\`
- **Description**: Parent category scanning complete
- **Firebase Field**: \`status: 'step2_completed'\`
- **Data Present**:
  - All Step 1 data
  - Parent category results
  - Selected categories for Step 3
- **Next States**: \`step3_completed\`, \`error\`

#### \`step3_completed\`
- **Description**: Sub-agent execution complete
- **Firebase Field**: \`status: 'step3_completed'\`
- **Data Present**:
  - All previous step data
  - Sub-agent responses
  - Filtered agent responses
- **Next States**: \`completed\`, \`error\`

#### \`completed\`
- **Description**: Full analysis complete
- **Firebase Field**: \`status: 'completed'\`
- **Data Present**:
  - All processing data
  - Final outcomes
  - Validation results
  - Law references
  - Coordinates
- **Next States**: None (terminal state)

#### \`error\`
- **Description**: Error occurred during processing
- **Firebase Field**: \`status: 'error'\`
- **Data Present**:
  - All data from last successful step
  - Error message in \`error\` field
  - Partial results if available
- **Next States**: \`initial\` (retry), or resume from last completed step

## Session Lifecycle

### Session States

\`\`\`mermaid
stateDiagram-v2
    [*] --> created: User Creates Session
    created --> active: First Journey Added
    active --> processing: Images Being Analyzed
    processing --> active: More Images Added
    processing --> completed: All Images Done
    active --> archived: User Archives
    completed --> archived: Auto-Archive (30 days)
    archived --> [*]
\`\`\`

### Session State Fields

**Created**:
- \`createdAt\`: Timestamp
- \`status\`: 'active'
- \`userJourneys\`: Empty array

**Active**:
- \`status\`: 'active'
- \`userJourneys\`: Array of journey IDs
- \`lastActivity\`: Timestamp

**Completed**:
- \`status\`: 'completed'
- \`completedAt\`: Timestamp
- All journeys processed

**Archived**:
- \`status\`: 'archived'
- \`archivedAt\`: Timestamp
- Read-only access

## User Journey States

### Journey States

\`\`\`mermaid
stateDiagram-v2
    [*] --> created: Journey Created
    created --> active: Images Added
    active --> processing: Analysis Started
    processing --> active: More Images Added
    processing --> completed: All Images Analyzed
    active --> archived: User Archives
    completed --> archived: Auto-Archive
    archived --> [*]
\`\`\`

## State Transition Logic

### Automatic Transitions

**Step Completion**:
\`\`\`python
def update_status_on_step_completion(image_doc_ref, step_number, step_data):
    """Update status when step completes."""
    status_map = {
        1: 'step1_completed',
        2: 'step2_completed',
        3: 'step3_completed',
        4: 'completed'
    }
    
    update_data = {
        'status': status_map[step_number],
        **step_data
    }
    
    if step_number == 4:
        update_data['completedAt'] = firestore.SERVER_TIMESTAMP
    
    image_doc_ref.update(update_data)
\`\`\`

### Manual Transitions

**Retry from Error**:
\`\`\`python
def retry_from_error(image_doc_ref, resume_from_step=None):
    """Retry processing from error state."""
    current_data = image_doc_ref.get().to_dict()
    last_status = current_data.get('status')
    
    if resume_from_step:
        # Resume from specific step
        status_map = {
            1: 'step1_completed',
            2: 'step2_completed',
            3: 'step3_completed'
        }
        new_status = status_map.get(resume_from_step, 'initial')
    else:
        # Resume from last completed step
        status_map = {
            'step1_completed': 'step1_completed',
            'step2_completed': 'step2_completed',
            'step3_completed': 'step3_completed',
            'error': 'initial'
        }
        new_status = status_map.get(last_status, 'initial')
    
    image_doc_ref.update({
        'status': new_status,
        'error': firestore.DELETE_FIELD  # Remove error
    })
\`\`\`

## State Persistence

### Firebase Document Structure

\`\`\`typescript
{
  // Status tracking
  status: 'initial' | 'step1_completed' | 'step2_completed' | 'step3_completed' | 'completed' | 'error',
  
  // Timestamps
  createdAt: Timestamp,
  updatedAt: Timestamp,
  completedAt: Timestamp,  // Only when completed
  
  // Step data (populated as processing progresses)
  // Step 1 data
  pageName: string,
  website_brief: string,
  // ... other Step 1 fields
  
  // Step 2 data
  parent_category_results: {...},
  
  // Step 3 data
  agent_responses: {...},
  revised_agent_responses: {...},
  
  // Step 4 data
  key_highlights: string[],
  actual_measurable_harm: string,
  outcome: string[],
  response: Array<{...}>,
  
  // Error handling
  error: string,  // Only when status is 'error'
  
  // Metadata
  processing_metadata: {
    start_time: Timestamp,
    end_time: Timestamp,
    processing_duration: number,
    llm_calls_count: number,
    tokens_used: number
  }
}
\`\`\`

## State Queries

### Querying by Status

\`\`\`python
def get_images_by_status(session_id, journey_id, status):
    """Get all images with specific status."""
    db = firestore.client()
    images_ref = db.collection('sessions').document(session_id) \\
                   .collection('userJourneys').document(journey_id) \\
                   .collection('images')
    
    query = images_ref.where('status', '==', status)
    return [doc.to_dict() for doc in query.stream()]
\`\`\`

### Status Statistics

\`\`\`python
def get_status_statistics(session_id, journey_id):
    """Get statistics for all statuses."""
    db = firestore.client()
    images_ref = db.collection('sessions').document(session_id) \\
                   .collection('userJourneys').document(journey_id) \\
                   .collection('images')
    
    all_images = images_ref.stream()
    stats = {
        'initial': 0,
        'step1_completed': 0,
        'step2_completed': 0,
        'step3_completed': 0,
        'completed': 0,
        'error': 0
    }
    
    for doc in all_images:
        status = doc.to_dict().get('status', 'initial')
        stats[status] = stats.get(status, 0) + 1
    
    return stats
\`\`\`

## Error States and Recovery

### Error State Handling

**Error Types**:
- **Transient Errors**: Network timeouts, rate limits (retryable)
- **Permanent Errors**: Invalid input, API failures (not retryable)
- **Partial Errors**: Some steps succeed, others fail

**Error Recovery**:
\`\`\`python
def handle_error(image_doc_ref, error, step_number):
    """Handle error and update state."""
    error_data = {
        'status': 'error',
        'error': str(error),
        'error_step': step_number,
        'error_timestamp': firestore.SERVER_TIMESTAMP
    }
    
    # Preserve data from completed steps
    current_data = image_doc_ref.get().to_dict()
    if current_data:
        # Keep all completed step data
        error_data.update({
            k: v for k, v in current_data.items()
            if k not in ['status', 'error', 'error_step', 'error_timestamp']
        })
    
    image_doc_ref.update(error_data)
\`\`\`

## State Monitoring

### Status Polling

**Frontend Implementation**:
\`\`\`javascript
async function pollImageStatus(sessionId, journeyId, imageId) {
  const maxAttempts = 60; // 5 minutes
  let attempts = 0;
  
  while (attempts < maxAttempts) {
    const response = await fetch('/api/get_image_metadata', {
      method: 'POST',
      body: JSON.stringify({
        session_id: sessionId,
        journey_id: journeyId,
        image_doc_id: imageId
      })
    });
    
    const data = await response.json();
    const status = data.status;
    
    if (status === 'completed') {
      return data;
    } else if (status === 'error') {
      throw new Error(data.error || 'Processing failed');
    }
    
    // Wait before next poll
    await new Promise(resolve => setTimeout(resolve, 5000));
    attempts++;
  }
  
  throw new Error('Timeout waiting for completion');
}
\`\`\`

## Related Documentation

- [Workflow Diagrams](workflow-diagrams.md)
- [Error Handling](error-handling.md)
- [API Documentation - Endpoints](../api-documentation/endpoints.md)

`;

// Auto-generated from role-based-access.md
export const MainToolDocumentationFunctionalFlowsRoleBasedAccess = `# Role-Based Access Flows

## Overview

This document details the authentication, authorization, and role-based access control (RBAC) implementation in the Dark Pattern Detection & Compliance Analysis Tool.

## Authentication Flow

### Firebase OAuth2 Flow

\`\`\`mermaid
sequenceDiagram
    participant User
    participant Frontend
    participant Firebase Auth
    participant Backend
    
    User->>Frontend: Click "Sign In"
    Frontend->>Firebase Auth: Initiate OAuth
    Firebase Auth->>User: Google Sign-In Popup
    User->>Firebase Auth: Authenticate
    Firebase Auth->>Frontend: Return ID Token
    Frontend->>Frontend: Check Email Authorization
    Frontend->>Backend: API Request with Token
    Backend->>Firebase Auth: Verify Token
    Firebase Auth-->>Backend: User Info
    Backend-->>Frontend: Authorized Response
\`\`\`

### Implementation

**Frontend** (\`src/firebase.js\`):
\`\`\`javascript
const signInWithGoogle = async () => {
  try {
    const result = await signInWithPopup(auth, new GoogleAuthProvider());
    const user = result.user;
    
    // Check email authorization
    if (!isEmailAuthorized(user.email)) {
      await signOut(auth);
      throw new Error('Unauthorized email');
    }
    
    return user;
  } catch (error) {
    console.error("Sign-in error:", error);
    throw error;
  }
};
\`\`\`

**Backend Verification**:
\`\`\`python
from firebase_admin import auth

def verify_token(id_token):
    """Verify Firebase ID token."""
    try:
        decoded_token = auth.verify_id_token(id_token)
        return decoded_token
    except Exception as e:
        raise UnauthorizedError(f"Invalid token: {e}")
\`\`\`

## Email-Based Authorization

### Current Implementation

**Configuration** (\`src/config/auth.js\`):
\`\`\`javascript
const AUTHORIZED_EMAILS = [
  'sinu2441@gmail.com',
  'marie@amurabi.eu',
  'sundar@amurabi.eu',
  // ... more emails
];

export const isEmailAuthorized = (email) => {
  if (!email) return false;
  return AUTHORIZED_EMAILS.includes(email.toLowerCase().trim());
};
\`\`\`

**Environment Variable Support**:
\`\`\`javascript
const getAuthorizedEmails = () => {
  if (process.env.REACT_APP_AUTHORIZED_EMAILS) {
    return process.env.REACT_APP_AUTHORIZED_EMAILS
      .split(',')
      .map(email => email.trim())
      .filter(email => email.length > 0);
  }
  return AUTHORIZED_EMAILS; // Fallback
};
\`\`\`

### Authorization Check Flow

\`\`\`mermaid
graph TD
    A[User Authenticates] --> B{Email in Authorized List?}
    B -->|Yes| C[Grant Access]
    B -->|No| D[Deny Access]
    D --> E[Show Error Message]
    E --> F[Sign Out User]
\`\`\`

## Permission Levels

### Current: Single Level

**Authorized Users**: Full access to all features

### Recommended: Multi-Level Access

**Admin**:
- Full system access
- User management
- System configuration
- All analysis features

**Analyst**:
- Create and manage sessions
- Run analyses
- Generate reports
- View own data

**Viewer**:
- View reports (read-only)
- Export data
- No analysis creation

### Implementation Example

\`\`\`python
# Backend role checking
from functools import wraps

def require_role(required_role):
    def decorator(f):
        @wraps(f)
        def wrapper(*args, **kwargs):
            token = request.headers.get('Authorization')
            if not token:
                return jsonify({"error": "Unauthorized"}), 401
            
            decoded_token = auth.verify_id_token(token.replace('Bearer ', ''))
            user_id = decoded_token['uid']
            user_role = get_user_role(user_id)
            
            if user_role != required_role and user_role != 'admin':
                return jsonify({"error": "Insufficient permissions"}), 403
            
            return f(*args, **kwargs)
        return wrapper
    return decorator

@app.route('/admin/users', methods=['GET'])
@require_role('admin')
def list_users():
    # Admin-only endpoint
    pass
\`\`\`

## Access Control Implementation

### Frontend Route Protection

**Current** (\`src/App.js\`):
\`\`\`javascript
// Authentication Guard
if (authLoading) {
  return <LoadingSpinner />;
}

if (!user) {
  return <LoginPage />;
}

if (user && !isEmailAuthorized(user.email)) {
  return <AccessDenied />;
}
\`\`\`

### Backend Endpoint Protection

**Current**: Email-based check in frontend

**Recommended**: Token verification in backend

\`\`\`python
@app.route('/api/protected', methods=['POST'])
def protected_endpoint():
    # Verify token
    token = request.headers.get('Authorization')
    if not token:
        return jsonify({"error": "Unauthorized"}), 401
    
    try:
        decoded_token = auth.verify_id_token(token.replace('Bearer ', ''))
        user_id = decoded_token['uid']
        email = decoded_token.get('email')
        
        # Check authorization
        if not is_email_authorized(email):
            return jsonify({"error": "Unauthorized"}), 403
        
        # Process request
        return jsonify({"status": "success"})
    except Exception as e:
        return jsonify({"error": str(e)}), 401
\`\`\`

## Session Management

### Firebase Session

**Automatic**: Firebase handles session management

**Persistence**: 
- Local storage (browser)
- Session cookies
- Automatic token refresh

### Session Lifecycle

\`\`\`mermaid
stateDiagram-v2
    [*] --> Unauthenticated
    Unauthenticated --> Authenticating: Sign In
    Authenticating --> Authorized: Email Check Pass
    Authenticating --> Unauthenticated: Email Check Fail
    Authorized --> Active: Access Granted
    Active --> Inactive: Timeout/Idle
    Inactive --> Active: Activity
    Active --> Unauthenticated: Sign Out
    Inactive --> Unauthenticated: Sign Out
\`\`\`

## User Activity Tracking

### Activity Logging

**Implementation**:
\`\`\`javascript
const logUserActivity = async (userId, displayName, activity) => {
  try {
    await addDoc(collection(db, "userActivityLogs"), {
      userId,
      displayName,
      activity,
      timestamp: new Date(),
    });
  } catch (error) {
    console.error("Error logging activity:", error);
  }
};
\`\`\`

**Logged Activities**:
- Login/logout
- Session creation
- Analysis requests
- Report generation
- Settings changes

## Access Control Flows

### Analysis Creation Flow

\`\`\`mermaid
graph TD
    A[User Requests Analysis] --> B{Authenticated?}
    B -->|No| C[Redirect to Login]
    B -->|Yes| D{Email Authorized?}
    D -->|No| E[Show Access Denied]
    D -->|Yes| F[Check Permissions]
    F --> G{Has Create Permission?}
    G -->|Yes| H[Create Analysis]
    G -->|No| I[Show Permission Error]
    H --> J[Log Activity]
\`\`\`

### Report Viewing Flow

\`\`\`mermaid
graph TD
    A[User Requests Report] --> B{Authenticated?}
    B -->|No| C[Redirect to Login]
    B -->|Yes| D{Email Authorized?}
    D -->|No| E[Show Access Denied]
    D -->|Yes| F{Owns Report or Admin?}
    F -->|Yes| G[Show Report]
    F -->|No| H[Show Access Denied]
    G --> I[Log Activity]
\`\`\`

## Security Considerations

### Token Security

- **Storage**: Secure HTTP-only cookies (recommended)
- **Transmission**: HTTPS only
- **Expiration**: Automatic token refresh
- **Validation**: Server-side verification

### Email Authorization

**Current Limitations**:
- Hardcoded list (development)
- Environment variable (production)
- No dynamic management

**Recommended Improvements**:
- Firebase Firestore collection for authorized emails
- Admin interface for management
- Role-based access levels
- Audit trail for access changes

## Related Documentation

- [Security & Compliance](../code-documentation/security-compliance.md)
- [Authentication](../api-documentation/authentication.md)
- [Error Handling](error-handling.md)

`;

// Auto-generated from use-cases.md
export const MainToolDocumentationFunctionalFlowsUseCases = `# Use Case Scenarios

## Overview

This document provides detailed use case scenarios for the Dark Pattern Detection & Compliance Analysis Tool across different industries and applications.

## Legal Tech Compliance Checking

### Use Case: Law Firm Compliance Audit

**Scenario**: A law firm needs to audit client websites for dark patterns before regulatory review.

**Workflow**:
1. Lawyer creates session for client website audit
2. Uploads screenshots of key pages (checkout, signup, privacy policy)
3. System analyzes each page for dark patterns
4. Generates compliance report with:
   - Detected dark patterns
   - Relevant laws (EU GDPR, US CCPA)
   - Risk assessment
   - Remediation recommendations
5. Lawyer reviews report and provides to client

**Benefits**:
- Proactive compliance checking
- Comprehensive legal analysis
- Actionable recommendations
- Time savings vs. manual review

### Use Case: Regulatory Compliance Preparation

**Scenario**: E-commerce company preparing for GDPR audit.

**Workflow**:
1. Compliance officer creates audit session
2. Uploads screenshots of all user-facing pages
3. System identifies dark patterns and compliance issues
4. Generates detailed compliance report
5. Company addresses issues before audit

**Key Features Used**:
- Multi-jurisdiction law fetching (EU focus)
- Compliance scoring
- Fair pattern suggestions
- Exportable reports

## E-commerce Dark Pattern Detection

### Use Case: Shopping Cart Analysis

**Scenario**: Analyze checkout flow for deceptive practices.

**Workflow**:
1. User uploads screenshots of checkout process
2. System detects:
   - Hidden costs
   - False urgency ("Only 2 left!")
   - Preselection of paid options
   - Hard-to-cancel subscriptions
3. Generates report with specific violations
4. Provides fair pattern alternatives

**Detected Patterns**:
- Hidden costs in checkout
- False scarcity indicators
- Preselected add-ons
- Subscription traps

### Use Case: Product Page Analysis

**Scenario**: Review product pages for misleading information.

**Workflow**:
1. Upload product page screenshots
2. System analyzes:
   - False social proof
   - Misleading pricing
   - Disguised ads
   - Fake reviews indicators
3. Provides detailed analysis

## Healthcare Digital Compliance

### Use Case: Patient Portal Review

**Scenario**: Hospital reviewing patient portal for HIPAA compliance and user experience.

**Workflow**:
1. Healthcare administrator creates session
2. Uploads patient portal screenshots
3. System checks for:
   - Privacy coercion
   - Unclear consent mechanisms
   - Hidden data sharing
   - Misleading privacy notices
4. Generates HIPAA-focused compliance report

**Compliance Focus**:
- Patient privacy rights
- Consent clarity
- Data sharing transparency
- Access controls

### Use Case: Telemedicine Platform Audit

**Scenario**: Telemedicine platform ensuring ethical design.

**Workflow**:
1. Upload telemedicine interface screenshots
2. Analyze for:
   - Forced consent
   - Hidden costs
   - Misleading service claims
3. Generate healthcare-specific compliance report

## Financial Services Compliance

### Use Case: Banking App Review

**Scenario**: Bank reviewing mobile app for regulatory compliance.

**Workflow**:
1. Compliance team uploads app screenshots
2. System analyzes for:
   - Hidden fees
   - Misleading terms
   - Forced actions
   - Unclear disclosures
3. Generates financial services compliance report

**Regulatory Focus**:
- Truth in Lending Act
- Fair Credit Reporting Act
- Consumer Financial Protection Bureau guidelines

### Use Case: Investment Platform Analysis

**Scenario**: Investment platform ensuring transparent disclosures.

**Workflow**:
1. Upload investment platform screenshots
2. Detect:
   - Misleading risk disclosures
   - Hidden fees
   - False performance claims
   - Unclear terms
3. Generate SEC-focused compliance report

## User Experience Research

### Use Case: UX Team Pattern Analysis

**Scenario**: UX team analyzing competitor interfaces.

**Workflow**:
1. UX researcher creates analysis session
2. Uploads competitor website screenshots
3. System identifies dark patterns used
4. Team uses insights for:
   - Competitive analysis
   - Design guidelines
   - Ethical design principles

### Use Case: Design System Review

**Scenario**: Design team reviewing design system components.

**Workflow**:
1. Upload design system component screenshots
2. Analyze for potential dark patterns
3. Get recommendations for ethical alternatives
4. Update design system guidelines

## Compliance Training

### Use Case: Team Education

**Scenario**: Training team on dark patterns and compliance.

**Workflow**:
1. Trainer uploads example screenshots
2. System provides detailed explanations
3. Team learns:
   - Pattern identification
   - Legal implications
   - Ethical alternatives
4. Interactive learning experience

## Regulatory Reporting

### Use Case: Regulatory Submission

**Scenario**: Company preparing regulatory submission.

**Workflow**:
1. Create comprehensive audit session
2. Upload all relevant pages
3. Generate detailed compliance report
4. Export for regulatory submission
5. Include remediation evidence

**Report Contents**:
- Executive summary
- Detailed findings
- Legal references
- Remediation plan
- Evidence screenshots

## Continuous Monitoring

### Use Case: Ongoing Compliance

**Scenario**: Regular compliance monitoring.

**Workflow**:
1. Schedule regular audits
2. Upload new/changed pages
3. Compare with previous audits
4. Track compliance improvements
5. Generate trend reports

## Integration Use Cases

### Use Case: CI/CD Integration

**Scenario**: Automated testing in development pipeline.

**Workflow**:
1. Automated screenshot capture
2. API call to analysis service
3. Fail build if dark patterns detected
4. Generate developer feedback

### Use Case: Design Tool Integration

**Scenario**: Figma plugin for design-time checking.

**Workflow**:
1. Designer uses Figma plugin
2. Selects frames to analyze
3. Plugin calls analysis API
4. Shows results in Figma
5. Designer adjusts design

## Related Documentation

- [Workflow Diagrams](workflow-diagrams.md)
- [Compliance Integration](compliance-integration.md)
- [API Documentation - Integration Examples](../api-documentation/integration-examples.md)

`;

// Auto-generated from workflow-diagrams.md
export const MainToolDocumentationFunctionalFlowsWorkflowDiagrams = `# End-to-End Workflow Diagrams

## Overview

This document provides comprehensive workflow diagrams for all major processes in the Dark Pattern Detection & Compliance Analysis Tool, including image processing, dark pattern detection, compliance analysis, and user journey tracking.

## Image Upload and Processing Flow

### High-Level Flow

\`\`\`mermaid
graph TD
    A[User Uploads Image] --> B[Image Uploaded to GCS]
    B --> C[Create Firebase Document]
    C --> D[Status: initial]
    D --> E[Step 1: Information Extraction]
    E --> F[Status: step1_completed]
    F --> G[Step 2: Agent Search]
    G --> H[Status: step2_completed]
    H --> I[Step 3: Sub-Agent Execution]
    I --> J[Status: step3_completed]
    J --> K[Step 4: Outcome Synthesis]
    K --> L[Status: completed]
    L --> M[Return Results to User]
    
    E --> N{Error?}
    N -->|Yes| O[Status: error]
    O --> P[Log Error]
    P --> Q[Notify User]
\`\`\`

### Detailed Step Flow

\`\`\`mermaid
sequenceDiagram
    participant User
    participant Frontend
    participant API
    participant GCS
    participant Firebase
    participant LLM
    
    User->>Frontend: Upload Image + URL + Note
    Frontend->>API: POST /upload_and_process
    API->>GCS: Upload Image
    GCS-->>API: Public URL
    API->>Firebase: Create Document (status: initial)
    
    API->>LLM: Step 1: Extract Context
    LLM-->>API: Context Data
    API->>Firebase: Update (status: step1_completed)
    
    API->>LLM: Step 2: Parent Category Scan
    LLM-->>API: Parent Categories
    API->>Firebase: Update (status: step2_completed)
    
    par Parallel Sub-Agents
        API->>LLM: Sub-Agent 1
        API->>LLM: Sub-Agent 2
        API->>LLM: Sub-Agent N
    end
    LLM-->>API: Sub-Agent Results
    API->>Firebase: Update (status: step3_completed)
    
    API->>LLM: Step 4: Synthesize Outcomes
    LLM-->>API: Final Results
    API->>Firebase: Update (status: completed)
    API-->>Frontend: Return Results
    Frontend-->>User: Display Results
\`\`\`

## Dark Pattern Detection Pipeline

### Detection Flow

\`\`\`mermaid
graph LR
    A[Image Input] --> B[Context Extraction]
    B --> C[Parent Category Scanning]
    C --> D{Coercing?}
    C --> E{Falsification?}
    C --> F{Misdirection?}
    C --> G{Obstructing?}
    C --> H{Sneaking?}
    C --> I{Omitting?}
    
    D --> J[Coercing Sub-Agents]
    E --> K[Falsification Sub-Agents]
    F --> L[Misdirection Sub-Agents]
    G --> M[Obstructing Sub-Agents]
    H --> N[Sneaking Sub-Agents]
    I --> O[Omitting Sub-Agents]
    
    J --> P[Parallel Execution]
    K --> P
    L --> P
    M --> P
    N --> P
    O --> P
    
    P --> Q[Filter & Validate]
    Q --> R[Generate Outcomes]
    R --> S[Fetch Laws]
    S --> T[Final Results]
\`\`\`

### Agent Hierarchy

\`\`\`mermaid
graph TD
    A[Image Analysis] --> B[Parent Categories]
    
    B --> C[Coercing]
    B --> D[Falsification]
    B --> E[Misdirection]
    B --> F[Obstructing]
    B --> G[Sneaking]
    B --> H[Omitting]
    
    C --> C1[Commercial Coercion]
    C --> C2[Privacy Coercion]
    C --> C3[Platform Coercion]
    
    D --> D1[False Social Proof]
    D --> D2[False Urgency]
    D --> D3[False Scarcity]
    D --> D4[False Endorsements]
    
    E --> E1[Disguised Ads]
    E --> E2[Trick Wording]
    E --> E3[Misleading Pricing]
    
    F --> F1[General Obstruction]
    F --> F2[Visual Interference]
    F --> F3[Comparison Prevention]
    F --> F4[Hard to Cancel]
    
    G --> G1[Hidden Costs]
    G --> G2[Hidden Subscription]
    G --> G3[Hidden Legalese]
    G --> G4[Bait and Switch]
    
    H --> H1[Misleading Omissions]
    H --> H2[Unclear Terms]
    H --> H3[Delayed Acknowledgement]
\`\`\`

## Compliance Analysis Workflow

### Compliance Flow

\`\`\`mermaid
graph TD
    A[Dark Pattern Detected] --> B[Extract Pattern Details]
    B --> C[Select Jurisdiction]
    C --> D{EU?}
    C --> E{US?}
    C --> F{UK?}
    
    D --> G[Query EU Laws]
    E --> H[Query US Laws]
    F --> I[Query UK Laws]
    
    G --> J[Perplexity AI Search]
    H --> J
    I --> J
    
    J --> K[Validate with Airtable]
    K --> L[Match Relevant Laws]
    L --> M[Generate Fair Patterns]
    M --> N[Compliance Report]
\`\`\`

### Law Fetching Process

\`\`\`mermaid
sequenceDiagram
    participant System
    participant Perplexity
    participant Airtable
    participant Database
    
    System->>Perplexity: Query Laws for Pattern
    Perplexity-->>System: Law Candidates
    System->>Airtable: Validate Laws
    Airtable-->>System: Validated Laws
    System->>Database: Store Law References
    System->>System: Generate Fair Patterns
    System-->>User: Compliance Report
\`\`\`

## User Journey Tracking

### Journey Flow

\`\`\`mermaid
graph TD
    A[User Creates Session] --> B[Session Created in Firebase]
    B --> C[User Creates Journey]
    C --> D[Journey Created in Firebase]
    D --> E[User Adds Images]
    E --> F[Image 1 Processing]
    E --> G[Image 2 Processing]
    E --> H[Image N Processing]
    
    F --> I[All Images Complete?]
    G --> I
    H --> I
    
    I -->|No| J[Wait for Completion]
    J --> I
    I -->|Yes| K[Generate Journey Report]
    K --> L[Display Results]
\`\`\`

### Session Lifecycle

\`\`\`mermaid
stateDiagram-v2
    [*] --> Created: User Creates Session
    Created --> Active: First Image Added
    Active --> Processing: Images Being Analyzed
    Processing --> Active: More Images Added
    Processing --> Completed: All Images Done
    Active --> Archived: User Archives
    Completed --> Archived: Auto-Archive
    Archived --> [*]
\`\`\`

## Background Processing Flow

### Cloud Tasks Flow

\`\`\`mermaid
graph TD
    A[API Receives Request] --> B[Upload Image to GCS]
    B --> C[Create Cloud Task]
    C --> D[Return Immediately]
    D --> E[User Receives Response]
    
    C --> F[Cloud Tasks Queue]
    F --> G[Background Worker]
    G --> H[Process Image]
    H --> I[Update Firebase]
    I --> J[Notify User if Needed]
\`\`\`

### Phased Processing with Status Updates

\`\`\`mermaid
sequenceDiagram
    participant User
    participant API
    participant CloudTask
    participant Firebase
    participant LLM
    
    User->>API: Upload Request
    API->>Firebase: Create (status: initial)
    API->>CloudTask: Queue Background Job
    API-->>User: Accepted (status: initial)
    
    CloudTask->>LLM: Step 1
    LLM-->>CloudTask: Context
    CloudTask->>Firebase: Update (status: step1_completed)
    
    CloudTask->>LLM: Step 2
    LLM-->>CloudTask: Categories
    CloudTask->>Firebase: Update (status: step2_completed)
    
    CloudTask->>LLM: Step 3
    LLM-->>CloudTask: Sub-Agents
    CloudTask->>Firebase: Update (status: step3_completed)
    
    CloudTask->>LLM: Step 4
    LLM-->>CloudTask: Outcomes
    CloudTask->>Firebase: Update (status: completed)
    
    User->>API: Poll Status
    API->>Firebase: Get Status
    Firebase-->>API: Status
    API-->>User: Current Status
\`\`\`

## Error Handling Flow

### Error Recovery

\`\`\`mermaid
graph TD
    A[Process Step] --> B{Success?}
    B -->|Yes| C[Update Status]
    B -->|No| D[Log Error]
    D --> E{Retryable?}
    E -->|Yes| F[Retry with Backoff]
    F --> B
    E -->|No| G[Set Status: error]
    G --> H[Notify User]
    H --> I[Store Partial Results]
    I --> J[Allow Manual Retry]
\`\`\`

## Related Documentation

- [Process States](process-states.md)
- [Error Handling](error-handling.md)
- [Use Cases](use-cases.md)

`;

// Auto-generated from accessibility.md
export const MainToolDocumentationUserManualAccessibility = `# Accessibility & Localization

## Accessibility

### WCAG Compliance

The application aims for WCAG 2.1 Level AA compliance.

### Keyboard Navigation

- Tab: Navigate between elements
- Enter/Space: Activate buttons
- Arrow keys: Navigate menus
- Escape: Close modals

### Screen Reader Support

- Semantic HTML elements
- ARIA labels where needed
- Alt text for images
- Descriptive link text

### Visual Accessibility

- High contrast mode support
- Resizable text
- Color not sole indicator
- Focus indicators

## Localization

### Current Status

- Primary language: English
- Localization: Planned for future

### Future Support

Planned languages:
- French
- German
- Spanish
- Additional languages based on demand

## Related Documentation

- [Getting Started](getting-started.md)
- [User Manual Index](index.md)

`;

// Auto-generated from feature-walkthroughs.md
export const MainToolDocumentationUserManualFeatureWalkthroughs = `# Feature Walkthroughs

## Overview

This guide provides detailed walkthroughs of all major features in the Dark Pattern Detection & Compliance Analysis Tool.

## Framework Generation

### Creating Analysis Sessions

**Purpose**: Organize your analyses into sessions

**Steps**:
1. Navigate to "New Session" or click "Create Session"
2. Enter descriptive session name
3. (Optional) Add description
4. Click "Create"
5. Session appears in your session list

**Tips**:
- Use descriptive names (e.g., "E-commerce Checkout Flow")
- Group related analyses in same session
- Use sessions for different projects/clients

### Managing User Journeys

**Purpose**: Track user flows within a session

**Steps**:
1. Open a session
2. Click "Add Journey"
3. Name the journey (e.g., "Checkout Process")
4. Add images to the journey
5. Track journey completion

## Benchmarking Features

### Analysis Comparison

**Purpose**: Compare analyses over time

**Steps**:
1. View session history
2. Select multiple sessions
3. Click "Compare"
4. Review:
   - Pattern trends
   - Compliance score changes
   - Improvement metrics

### Compliance Scoring

**Purpose**: Understand compliance levels

**Score Interpretation**:
- **0.9-1.0**: Excellent compliance
- **0.7-0.9**: Good compliance
- **0.5-0.7**: Needs improvement
- **<0.5**: Significant issues

**Viewing Scores**:
1. Open completed analysis
2. View "Compliance Score" section
3. Review breakdown by pattern type
4. See jurisdiction-specific scores

## Reporting Capabilities

### Generating Reports

**Steps**:
1. Open completed session
2. Click "Generate Report"
3. Select report format:
   - Summary report
   - Detailed report
   - Compliance report
4. Choose jurisdictions to include
5. Click "Generate"
6. Download or view report

### Report Types

**Summary Report**:
- Executive overview
- Key findings
- Compliance score
- Top recommendations

**Detailed Report**:
- All detected patterns
- Full legal references
- Evidence screenshots
- Fair pattern suggestions

**Compliance Report**:
- Jurisdiction-specific analysis
- Legal compliance status
- Risk assessment
- Remediation plan

### Exporting Reports

**Formats Available**:
- PDF (for sharing/presentation)
- JSON (for integration)
- CSV (for data analysis)

**Steps**:
1. Open report
2. Click "Export"
3. Select format
4. Download file

## Dashboard Usage

### Dashboard Overview

**Main Sections**:
- **Statistics**: Total analyses, compliance scores
- **Recent Activity**: Latest sessions and analyses
- **Quick Actions**: Create session, view reports
- **Notifications**: System updates, errors

### Navigation

**Sidebar Menu**:
- Home: Dashboard
- Explore: Browse sessions
- Reports: View all reports
- Settings: Application settings
- Help: Documentation and support

### Session Management

**Viewing Sessions**:
1. Click "Explore" or "Sessions"
2. See list of all sessions
3. Filter by:
   - Date range
   - Status
   - Name
4. Click session to open

**Session Actions**:
- View details
- Edit name/description
- Add images
- Generate report
- Archive/delete

## Image Analysis Features

### Uploading Images

**Methods**:
1. **File Upload**: Select image from device
2. **URL**: Provide image URL
3. **Drag & Drop**: Drag image to upload area

**Supported Formats**:
- PNG, JPG, JPEG
- WebP, BMP
- Maximum size: 10MB

### Analysis Options

**Analysis Types**:
- **Standard Analysis**: Full 4-step pipeline
- **Quick Analysis**: Step 1 only (faster)
- **Compliance Focus**: Emphasize legal compliance

**Configuration**:
- Select jurisdictions (EU, US, UK)
- Add context notes
- Set analysis priority

### Viewing Analysis Results

**Results Display**:
1. **Overview**: Summary of findings
2. **Patterns**: List of detected patterns
3. **Visual**: Image with pattern highlights
4. **Legal**: Relevant laws and regulations
5. **Recommendations**: Fair pattern alternatives

**Interactive Features**:
- Click patterns to see details
- Hover over highlights for info
- Expand/collapse sections
- Filter by pattern type

## Feedback and Improvement

### Providing Feedback

**On Analysis Results**:
1. View analysis results
2. Click "Feedback" on specific pattern
3. Select feedback type:
   - Accurate detection
   - False positive
   - Needs clarification
4. Add comments
5. Submit feedback

**Impact**:
- Helps improve system accuracy
- Contributes to model training
- Improves future analyses

## Related Documentation

- [Getting Started](getting-started.md)
- [Tutorials](tutorials.md)
- [Troubleshooting](troubleshooting.md)

`;

// Auto-generated from getting-started.md
export const MainToolDocumentationUserManualGettingStarted = `# Getting Started Guide

## Overview

Welcome to the Dark Pattern Detection & Compliance Analysis Tool! This guide will help you get started with the application, from account setup to your first analysis.

## Prerequisites

### System Requirements

**Browser**:
- Chrome (recommended), Firefox, Safari, or Edge
- Latest version for best compatibility

**Internet Connection**:
- Stable connection required for analysis
- Upload speeds: Minimum 1 Mbps

**Account**:
- Google account for authentication
- Authorized email address

## Account Creation and Authentication

### Step 1: Access the Application

1. Navigate to the application URL
2. You'll be redirected to the login page if not authenticated

### Step 2: Sign In with Google

1. Click "Sign In with Google" button
2. Select your Google account
3. Grant necessary permissions
4. You'll be redirected to the application

### Step 3: Authorization Check

- If your email is authorized, you'll see the dashboard
- If not authorized, you'll see an access denied message
- Contact your administrator to request access

## First-Time User Onboarding

### Dashboard Overview

After logging in, you'll see:

1. **Header**: Navigation and user menu
2. **Sidebar**: Main navigation menu
3. **Main Content**: Dashboard or current page
4. **Quick Actions**: Create session, view reports

### Creating Your First Session

1. Click "Create New Session" or navigate to "New Session"
2. Enter session name (e.g., "Website Audit - January 2024")
3. Click "Create"
4. You'll be redirected to the session page

### Adding Your First Image

1. In your session, click "Add Image" or "Upload"
2. Choose one of:
   - Upload image file (PNG, JPG, etc.)
   - Provide image URL
3. Enter the source webpage URL
4. (Optional) Add notes about what to focus on
5. Click "Analyze" or "Submit"

### Understanding Analysis Status

**Status Indicators**:
- **Initial**: Image uploaded, processing not started
- **Processing**: Analysis in progress
- **Step 1 Complete**: Information extracted
- **Step 2 Complete**: Categories identified
- **Step 3 Complete**: Patterns detected
- **Completed**: Full analysis ready
- **Error**: Processing failed (can retry)

### Viewing Results

1. Wait for status to show "Completed"
2. Click on the image or "View Results"
3. Review:
   - Detected dark patterns
   - Pattern locations (coordinates)
   - Relevant laws
   - Fair pattern suggestions
   - Compliance score

## Environment Setup

### For Developers

See [Developer Experience Guide](../code-documentation/developer-experience.md) for:
- Local development setup
- Environment variables
- Testing procedures

### For End Users

No additional setup required! The application runs in your browser.

## Quick Start Checklist

- [ ] Access application URL
- [ ] Sign in with Google account
- [ ] Verify authorization
- [ ] Explore dashboard
- [ ] Create first session
- [ ] Upload first image
- [ ] Review analysis results
- [ ] Generate first report

## Next Steps

1. **Learn Features**: Read [Feature Walkthroughs](feature-walkthroughs.md)
2. **Try Tutorials**: Follow [Interactive Tutorials](tutorials.md)
3. **Get Help**: Check [Troubleshooting](troubleshooting.md) if needed

## Related Documentation

- [Feature Walkthroughs](feature-walkthroughs.md)
- [Tutorials](tutorials.md)
- [Troubleshooting](troubleshooting.md)

`;

// Auto-generated from glossary.md
export const MainToolDocumentationUserManualGlossary = `# Glossary of Terms

## Dark Pattern Terms

### Coercing
Patterns that force or pressure users into actions they might not want to take.

### Falsification
Patterns that present false or misleading information to users.

### Misdirection
Patterns that distract or mislead users from important information.

### Obstructing
Patterns that make it difficult for users to complete desired actions.

### Sneaking
Patterns that hide information or actions from users.

### Omitting
Patterns that fail to disclose important information users need.

## Compliance Terms

### GDPR (General Data Protection Regulation)
EU regulation governing data protection and privacy.

### CCPA (California Consumer Privacy Act)
California state law providing privacy rights to consumers.

### Compliance Score
Numerical rating (0-1) indicating compliance level with regulations.

### Fair Pattern
Ethical alternative to a dark pattern that achieves business goals without manipulation.

## AI/ML Concepts

### LLM (Large Language Model)
AI model that processes and generates human-like text.

### Agent
Specialized AI component that detects specific types of patterns.

### Sub-Agent
Detailed detector for specific dark pattern subtypes.

### Context Extraction
Process of analyzing image and webpage to understand interface.

## Technical Terms

### Session
Container for organizing multiple analyses.

### Journey
User flow or workflow within a session.

### Status
Current state of analysis processing (initial, processing, completed, error).

### Firebase
Google's backend platform used for data storage and authentication.

## Related Documentation

- [Getting Started](getting-started.md)
- [Feature Walkthroughs](feature-walkthroughs.md)

`;

// Auto-generated from troubleshooting.md
export const MainToolDocumentationUserManualTroubleshooting = `# Troubleshooting & FAQs

## Common Issues and Solutions

### Authentication Issues

**Problem**: Cannot sign in
- **Solution**: Check internet connection, try different browser, clear cache

**Problem**: "Access Denied" message
- **Solution**: Your email may not be authorized. Contact administrator.

**Problem**: Sign-in popup blocked
- **Solution**: Allow popups for the application domain

### Upload Issues

**Problem**: Image upload fails
- **Check**: File size (max 10MB), format (PNG/JPG), internet connection
- **Solution**: Compress image, try different format, check connection

**Problem**: Upload timeout
- **Solution**: Use smaller image, check internet speed, retry

### Analysis Issues

**Problem**: Analysis stuck in "Processing"
- **Check**: Wait 5-10 minutes (normal processing time)
- **Solution**: Refresh page, check status, contact support if persists

**Problem**: Analysis shows "Error"
- **Solution**: Click "Retry", check image quality, verify URL if using image URL

**Problem**: No patterns detected
- **Possible Reasons**: 
  - Image doesn't contain dark patterns
  - Image quality too low
  - Context insufficient
- **Solution**: Try different image, add more context notes

### Performance Issues

**Problem**: Slow loading
- **Solution**: Check internet connection, clear browser cache, try different browser

**Problem**: Timeout errors
- **Solution**: Analysis may be complex. Wait longer or contact support.

## FAQ

### General Questions

**Q: How long does analysis take?**
A: Typically 2-5 minutes per image, depending on complexity.

**Q: What image formats are supported?**
A: PNG, JPG, JPEG, WebP, BMP. Maximum size: 10MB.

**Q: Can I analyze multiple images at once?**
A: Yes, add multiple images to a session. They process sequentially.

**Q: How accurate are the detections?**
A: System uses advanced AI with high accuracy. Provide feedback to improve.

### Analysis Questions

**Q: What if I disagree with a detection?**
A: Use the feedback feature to report false positives. This helps improve the system.

**Q: Can I customize which patterns to detect?**
A: Currently analyzes all patterns. Focus on specific categories in your notes.

**Q: How are compliance scores calculated?**
A: Based on number and severity of detected patterns. See documentation for details.

### Technical Questions

**Q: Do I need to install anything?**
A: No, the application runs in your browser.

**Q: Is my data secure?**
A: Yes, we use industry-standard security. See Security documentation.

**Q: Can I export my data?**
A: Yes, export reports in PDF, JSON, or CSV formats.

## Getting Help

### Support Channels

1. **Documentation**: Check this documentation first
2. **FAQ**: Review FAQ section above
3. **Contact Support**: Email support team
4. **Feedback**: Use in-app feedback feature

### Reporting Bugs

When reporting issues, include:
- Description of problem
- Steps to reproduce
- Screenshots if possible
- Browser and version
- Error messages

## Related Documentation

- [Getting Started](getting-started.md)
- [Feature Walkthroughs](feature-walkthroughs.md)
- [Glossary](glossary.md)

`;

// Auto-generated from tutorials.md
export const MainToolDocumentationUserManualTutorials = `# Interactive Tutorials

## Overview

Step-by-step tutorials to help you master the Dark Pattern Detection & Compliance Analysis Tool.

## Tutorial 1: Your First Analysis

### Step 1: Create a Session
1. Log in to the application
2. Click "Create New Session"
3. Name it "Tutorial Session"
4. Click "Create"

### Step 2: Upload an Image
1. Click "Add Image"
2. Choose a screenshot of a webpage
3. Enter the webpage URL
4. Add a note: "Check for dark patterns"
5. Click "Analyze"

### Step 3: Review Results
1. Wait for analysis to complete (2-5 minutes)
2. Click on the image to view results
3. Review detected patterns
4. Check compliance score
5. Read legal references

## Tutorial 2: Compliance Report

### Steps
1. Complete Tutorial 1 first
2. In your session, click "Generate Report"
3. Select "Compliance Report"
4. Choose jurisdictions (EU, US, UK)
5. Click "Generate"
6. Review the report
7. Export as PDF

## Tutorial 3: Batch Analysis

### Steps
1. Create a new session
2. Upload multiple images (5-10)
3. Wait for all to process
4. Review each analysis
5. Generate comprehensive report
6. Compare patterns across pages

## Related Documentation

- [Getting Started](getting-started.md)
- [Feature Walkthroughs](feature-walkthroughs.md)

`;

// Auto-generated from version-history.md
export const MainToolDocumentationUserManualVersionHistory = `# Version History & Release Notes

## Version 1.0.0

**Release Date**: [To be determined]

### Initial Release

**Features**:
- Dark pattern detection
- Compliance analysis
- Multi-jurisdiction support (EU, US, UK)
- Report generation
- User feedback system

**Known Issues**:
- [List any known issues]

## Future Versions

### Planned Features

- Enhanced pattern detection
- Additional jurisdictions
- Improved reporting
- API enhancements
- Performance optimizations

## Related Documentation

- [Versioning](../code-documentation/versioning.md)
- [Getting Started](getting-started.md)

`;

// Auto-generated from api-documentation.md
export const ReportDocumentationApiDocumentation = `# API Documentation

## Report Application Backend - API Documentation

### Table of Contents
1. [Authentication & Security](#authentication--security)
2. [Endpoints & Methods](#endpoints--methods)
3. [Data Schemas](#data-schemas)
4. [Rate Limits & Quotas](#rate-limits--quotas)
5. [Error Codes & Responses](#error-codes--responses)
6. [Integration Examples](#integration-examples)
7. [Version Control & Lifecycle Management](#version-control--lifecycle-management)

---

## Authentication & Security

### Current Implementation

#### Password-Based Authentication
The application currently uses password-based authentication stored in Firestore.

**Security Note**: ⚠️ Passwords are stored in plaintext. This is a security risk and should be addressed in production.

#### Authentication Flow

1. **User Registration**
   - Endpoint: \`POST /register_user\`
   - Creates user with email and password
   - Returns user data (excluding password)

2. **User Authentication**
   - Endpoint: \`POST /authenticate_user\`
   - Validates email and password
   - Returns user data with role information

### Recommended Improvements

#### OAuth2 Integration
For production, implement OAuth2:
- Google OAuth2
- Microsoft Azure AD
- Custom OAuth2 provider

#### JWT Token Management
Replace password-based auth with JWT:
- Token generation on authentication
- Token validation middleware
- Token refresh mechanism

#### Privacy-Preserving Protocols
- End-to-end encryption for sensitive data
- Zero-knowledge proofs for compliance verification
- Differential privacy for analytics

### Role-Based Access Control

#### Roles
- **Admin**: Full access to all endpoints and data
- **Client**: Limited access to assigned domains and own data

#### Access Control Implementation
\`\`\`python
# Example: Admin-only endpoint
if request.user.get('role') != 'Admin':
    return jsonify({"status": "error", "message": "Unauthorized"}), 403
\`\`\`

---

## Endpoints & Methods

### Base URL
\`\`\`
http://localhost:8080
\`\`\`
For production, replace with your deployed URL.

### Session Management Endpoints

#### 1. List All Sessions
**Endpoint**: \`GET /list_all_sessions_data\`

**Description**: Retrieves all sessions from all three collections (sessions, digital-sessions, influencer-marketing)

**Authentication**: Required (Admin only)

**Request**:
\`\`\`http
GET /list_all_sessions_data HTTP/1.1
Host: localhost:8080
\`\`\`

**Response** (200 OK):
\`\`\`json
{
  "status": "success",
  "data": [
    {
      "sessionName": "E-commerce Audit",
      "sessionId": "session123",
      "createdBy": "admin@example.com",
      "domainName": "example.com",
      "journeyCount": 5,
      "source": "main-sessions",
      "createdAt": "2024-01-15T10:30:00Z"
    },
    {
      "sessionName": "Digital Violation Check",
      "sessionId": "digital456",
      "createdBy": "admin@example.com",
      "domainName": "test.com",
      "journeyCount": 3,
      "source": "digital-violation",
      "createdAt": "2024-01-15T11:00:00Z"
    }
  ]
}
\`\`\`

**Response** (403 Forbidden):
\`\`\`json
{
  "status": "error",
  "message": "Unauthorized access"
}
\`\`\`

#### 2. Get Session Journey Images Details
**Endpoint**: \`POST /get_session_user_journey_images_details\`

**Description**: Retrieves all journeys and images for a specific session

**Authentication**: Required

**Request Body**:
\`\`\`json
{
  "sessionId": "session123"
}
\`\`\`

**Response** (200 OK):
\`\`\`json
{
  "status": "success",
  "data": [
    {
      "journeyId": "journey1",
      "journeyName": "Checkout Flow",
      "totalDarkPatterns": 3,
      "images": [
        {
          "imageId": "img1",
          "pageName": "Cart Page",
          "imageUrl": "https://storage.googleapis.com/...",
          "darkPatternCount": 2
        }
      ]
    }
  ]
}
\`\`\`

**Response** (404 Not Found):
\`\`\`json
{
  "status": "error",
  "message": "No session found with ID: session123"
}
\`\`\`

#### 3. Get Image Information
**Endpoint**: \`POST /get_image_Information\`

**Description**: Retrieves detailed metadata for a specific image/video

**Authentication**: Required

**Request Body**:
\`\`\`json
{
  "sessionId": "session123",
  "journeyId": "journey1",
  "imageId": "img1"
}
\`\`\`

**Note**: For video sessions, \`journeyId\` may be optional or ignored.

**Response** (200 OK):
\`\`\`json
{
  "status": "success",
  "data": {
    "imageId": "img1",
    "imageUrl": "https://storage.googleapis.com/...",
    "responseCount": 2,
    "response": [
      {
        "text": "**Excerpt:-**\\n\\tUrgency messaging...\\n\\n**Findings:-**\\n\\tCreates false urgency",
        "dark_pattern": "Urgency",
        "laws": [
          {
            "clause_name": "Article 5(1) GDPR",
            "jurisdiction": "EU",
            "law_name": "General Data Protection Regulation",
            "link": "https://eur-lex.europa.eu/...",
            "type": "regulation"
          }
        ]
      }
    ]
  }
}
\`\`\`

### Domain Management Endpoints

#### 4. List All Domains
**Endpoint**: \`GET /list_all_domains\`

**Description**: Retrieves all unique domain names from all collections

**Authentication**: Required

**Request**:
\`\`\`http
GET /list_all_domains HTTP/1.1
Host: localhost:8080
\`\`\`

**Response** (200 OK):
\`\`\`json
{
  "status": "success",
  "data": [
    "example.com",
    "test.com",
    "demo.com"
  ]
}
\`\`\`

#### 5. Fetch Sessions by Domains
**Endpoint**: \`POST /fetch_sessions_by_domains\`

**Description**: Retrieves all sessions matching any of the specified domains

**Authentication**: Required

**Request Body**:
\`\`\`json
{
  "domains": ["example.com", "test.com"]
}
\`\`\`

**Response** (200 OK):
\`\`\`json
{
  "status": "success",
  "data": [
    {
      "sessionName": "E-commerce Audit",
      "sessionId": "session123",
      "createdBy": "admin@example.com",
      "domainName": "example.com",
      "journeyCount": 5,
      "source": "main-sessions",
      "createdAt": "2024-01-15T10:30:00Z"
    }
  ]
}
\`\`\`

**Response** (400 Bad Request):
\`\`\`json
{
  "status": "error",
  "message": "domains must be provided as a non-empty list"
}
\`\`\`

### User Management Endpoints

#### 6. Register User
**Endpoint**: \`POST /register_user\`

**Description**: Creates a new user account

**Authentication**: Not required (but should be restricted in production)

**Request Body**:
\`\`\`json
{
  "email": "user@example.com",
  "password": "secure-password",
  "first_name": "John",
  "last_name": "Doe",
  "role": "Client",
  "domains": ["example.com"],
  "sessions": []
}
\`\`\`

**Required Fields**:
- \`email\`: User's email address (must be unique)
- \`password\`: User's password
- \`first_name\`: User's first name
- \`last_name\`: User's last name
- \`role\`: "Admin" or "Client"

**Optional Fields**:
- \`domains\`: Array of domain names (default: [])
- \`sessions\`: Array of session IDs (default: [])

**Response** (201 Created):
\`\`\`json
{
  "status": "success",
  "message": "User registered successfully",
  "data": {
    "email": "user@example.com",
    "role": "Client",
    "domains": ["example.com"],
    "sessions": []
  }
}
\`\`\`

**Response** (400 Bad Request):
\`\`\`json
{
  "status": "error",
  "message": "Missing required field: email"
}
\`\`\`

**Response** (409 Conflict):
\`\`\`json
{
  "status": "error",
  "message": "User with this email already exists"
}
\`\`\`

#### 7. Authenticate User
**Endpoint**: \`POST /authenticate_user\`

**Description**: Validates user credentials and returns user data

**Authentication**: Not required

**Request Body**:
\`\`\`json
{
  "email": "user@example.com",
  "password": "secure-password"
}
\`\`\`

**Response** (200 OK):
\`\`\`json
{
  "status": "success",
  "message": "Authentication successful",
  "data": {
    "user_id": "abc123",
    "first_name": "John",
    "last_name": "Doe",
    "email": "user@example.com",
    "role": "Client",
    "domains": ["example.com"]
  }
}
\`\`\`

**Response** (401 Unauthorized):
\`\`\`json
{
  "status": "error",
  "message": "Invalid credentials"
}
\`\`\`

**Response** (404 Not Found):
\`\`\`json
{
  "status": "error",
  "message": "User not found"
}
\`\`\`

#### 8. Update User
**Endpoint**: \`PUT /update_user\`

**Description**: Updates user information

**Authentication**: Required

**Request Body**:
\`\`\`json
{
  "user_id": "abc123",
  "first_name": "Updated",
  "last_name": "Name",
  "email": "updated@example.com",
  "role": "Client",
  "domains": ["example.com", "newdomain.com"],
  "sessions": ["session1"],
  "password": "new-password"
}
\`\`\`

**Required Fields**:
- \`user_id\`: Document ID of the user to update

**Optional Fields** (all other fields):
- \`first_name\`, \`last_name\`, \`email\`, \`role\`, \`domains\`, \`sessions\`, \`password\`

**Response** (200 OK):
\`\`\`json
{
  "status": "success",
  "message": "User updated successfully",
  "data": {
    "user_id": "abc123",
    "updated_fields": ["first_name", "domains"]
  }
}
\`\`\`

**Response** (404 Not Found):
\`\`\`json
{
  "status": "error",
  "message": "User with ID abc123 not found"
}
\`\`\`

**Response** (409 Conflict):
\`\`\`json
{
  "status": "error",
  "message": "Email updated@example.com is already in use by another user"
}
\`\`\`

#### 9. List Users
**Endpoint**: \`POST /list_users\`

**Description**: Lists all users (Admin only)

**Authentication**: Required (Admin only)

**Request Body**:
\`\`\`json
{
  "user_id": "admin_user_id"
}
\`\`\`

**Response** (200 OK):
\`\`\`json
{
  "status": "success",
  "data": {
    "users": [
      {
        "user_id": "abc123",
        "email": "user@example.com",
        "first_name": "John",
        "last_name": "Doe",
        "role": "Client",
        "domains": ["example.com"],
        "created_at": "2024-01-15T10:00:00Z"
      }
    ],
    "total_users": 1
  }
}
\`\`\`

**Response** (403 Forbidden):
\`\`\`json
{
  "status": "error",
  "message": "Unauthorized: Only Admin users can list all users"
}
\`\`\`

#### 10. Get User Details
**Endpoint**: \`GET /get_user_details/<user_id>\`

**Description**: Retrieves complete user information by user ID

**Authentication**: Required

**Request**:
\`\`\`http
GET /get_user_details/abc123 HTTP/1.1
Host: localhost:8080
\`\`\`

**Response** (200 OK):
\`\`\`json
{
  "status": "success",
  "data": {
    "user_id": "abc123",
    "email": "user@example.com",
    "first_name": "John",
    "last_name": "Doe",
    "role": "Client",
    "domains": ["example.com"],
    "sessions": [],
    "created_at": "2024-01-15T10:00:00Z",
    "last_updated": "2024-01-16T14:30:00Z"
  }
}
\`\`\`

**Response** (404 Not Found):
\`\`\`json
{
  "status": "error",
  "message": "User not found"
}
\`\`\`

### Priority Description Endpoint

#### 11. Get Priority Description
**Endpoint**: \`POST /get_priority_description\`

**Description**: Retrieves priority descriptions for a session from Airtable

**Authentication**: Required

**Request Body**:
\`\`\`json
{
  "session_id": "session123"
}
\`\`\`

**Response** (200 OK):
\`\`\`json
{
  "status": "success",
  "data": {
    "session_id": "session123",
    "session_name": "E-commerce Audit",
    "priorities": {
      "highest": "Critical dark patterns requiring immediate action",
      "high": "Significant compliance issues",
      "medium": "Moderate concerns",
      "low": "Minor issues",
      "lowest": "Informational findings"
    }
  }
}
\`\`\`

**Response** (404 Not Found):
\`\`\`json
{
  "status": "error",
  "message": "Session not found"
}
\`\`\`

**Response** (500 Internal Server Error):
\`\`\`json
{
  "status": "error",
  "message": "Failed to fetch data from Airtable: 401"
}
\`\`\`

---

## Data Schemas

### Session Schema
\`\`\`json
{
  "sessionName": "string",
  "sessionId": "string",
  "createdBy": "string",
  "domainName": "string",
  "journeyCount": "number",
  "source": "main-sessions" | "digital-violation" | "video-dark-pattern",
  "createdAt": "ISO 8601 timestamp"
}
\`\`\`

### Journey Schema
\`\`\`json
{
  "journeyId": "string",
  "journeyName": "string",
  "totalDarkPatterns": "number",
  "images": [
    {
      "imageId": "string",
      "pageName": "string",
      "imageUrl": "string",
      "darkPatternCount": "number"
    }
  ]
}
\`\`\`

### Image Metadata Schema
\`\`\`json
{
  "imageId": "string",
  "imageUrl": "string",
  "responseCount": "number",
  "response": [
    {
      "text": "string",
      "dark_pattern": "string",
      "laws": [
        {
          "clause_name": "string",
          "jurisdiction": "string",
          "law_name": "string",
          "link": "string",
          "type": "string"
        }
      ]
    }
  ]
}
\`\`\`

### User Schema
\`\`\`json
{
  "user_id": "string",
  "email": "string",
  "password": "string",
  "first_name": "string",
  "last_name": "string",
  "role": "Admin" | "Client",
  "domains": ["string"],
  "sessions": ["string"],
  "created_at": "ISO 8601 timestamp",
  "last_updated": "ISO 8601 timestamp"
}
\`\`\`

### Error Response Schema
\`\`\`json
{
  "status": "error",
  "message": "string"
}
\`\`\`

### Success Response Schema
\`\`\`json
{
  "status": "success",
  "message": "string (optional)",
  "data": "object | array"
}
\`\`\`

---

## Rate Limits & Quotas

### Current Implementation
- **No rate limiting implemented**
- **Recommendation**: Implement rate limiting for production

### Recommended Rate Limits

#### Per Endpoint
- **Authentication endpoints**: 5 requests per minute per IP
- **Data retrieval endpoints**: 100 requests per minute per user
- **User management endpoints**: 10 requests per minute per user

#### Per User
- **Client users**: 1000 requests per hour
- **Admin users**: 5000 requests per hour

### Quota Management
- **Firestore**: Monitor read/write quotas
- **Airtable**: Monitor API call limits
- **Cloud Storage**: Monitor storage and bandwidth

### Rate Limit Headers
Recommended response headers:
\`\`\`
X-RateLimit-Limit: 100
X-RateLimit-Remaining: 95
X-RateLimit-Reset: 1642248000
\`\`\`

---

## Error Codes & Responses

### HTTP Status Codes

| Status Code | Meaning | Description |
|------------|---------|-------------|
| 200 | OK | Request successful |
| 201 | Created | Resource created successfully |
| 400 | Bad Request | Invalid request parameters |
| 401 | Unauthorized | Authentication required or failed |
| 403 | Forbidden | Insufficient permissions |
| 404 | Not Found | Resource not found |
| 409 | Conflict | Resource conflict (e.g., duplicate email) |
| 500 | Internal Server Error | Server error |

### Error Response Format
All errors follow this format:
\`\`\`json
{
  "status": "error",
  "message": "Human-readable error message"
}
\`\`\`

### Common Error Messages

#### Authentication Errors
- \`"User not found"\` - Email doesn't exist (404)
- \`"Invalid credentials"\` - Wrong password (401)
- \`"Unauthorized access"\` - Insufficient permissions (403)

#### Validation Errors
- \`"Missing required field: {field}"\` - Required field missing (400)
- \`"Role must be either 'Admin' or 'Client'"\` - Invalid role value (400)
- \`"domains must be provided as a list"\` - Invalid data type (400)

#### Resource Errors
- \`"No session found with ID: {sessionId}"\` - Session doesn't exist (404)
- \`"User with this email already exists"\` - Duplicate email (409)
- \`"No image found with ID: {imageId}"\` - Image doesn't exist (404)

#### External Service Errors
- \`"Failed to fetch data from Airtable: {status_code}"\` - Airtable API error (500)

---

## Integration Examples

### Legal Case Tools Integration

#### Example: Fetching Compliance Data for Case Management
\`\`\`python
import requests

# Authenticate
auth_response = requests.post(
    "http://api.example.com/authenticate_user",
    json={"email": "lawyer@firm.com", "password": "password"}
)
user_data = auth_response.json()["data"]
user_id = user_data["user_id"]

# Get sessions for specific domain
sessions_response = requests.post(
    "http://api.example.com/fetch_sessions_by_domains",
    json={"domains": ["defendant-website.com"]},
    headers={"Authorization": f"Bearer {user_id}"}
)

# Process compliance violations
for session in sessions_response.json()["data"]:
    # Get detailed violations
    images_response = requests.post(
        "http://api.example.com/get_session_user_journey_images_details",
        json={"sessionId": session["sessionId"]}
    )
    
    # Extract law references for legal brief
    violations = extract_law_references(images_response.json())
    generate_legal_brief(violations)
\`\`\`

### EMR (Electronic Medical Records) Integration

#### Example: Healthcare Compliance Monitoring
\`\`\`python
# Monitor healthcare website compliance
healthcare_domains = ["hospital-website.com", "clinic-portal.com"]

for domain in healthcare_domains:
    sessions = fetch_sessions_by_domains([domain])
    
    for session in sessions:
        # Check for HIPAA violations
        violations = check_hipaa_compliance(session)
        if violations:
            alert_compliance_team(violations)
\`\`\`

### Finance Dashboard Integration

#### Example: Financial Services Compliance Dashboard
\`\`\`javascript
// Frontend integration example
async function loadComplianceData(domain) {
  // Authenticate
  const authResponse = await fetch('/authenticate_user', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      email: 'analyst@bank.com',
      password: 'password'
    })
  });
  
  const { data: user } = await authResponse.json();
  
  // Fetch sessions
  const sessionsResponse = await fetch('/fetch_sessions_by_domains', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ domains: [domain] })
  });
  
  const { data: sessions } = await sessionsResponse.json();
  
  // Display in dashboard
  updateDashboard(sessions);
}
\`\`\`

---

## Version Control & Lifecycle Management

### API Versioning Strategy

#### Current Implementation
- **No versioning**: All endpoints are unversioned
- **Recommendation**: Implement versioning for future changes

#### Recommended Versioning Approach

**URL-based versioning**:
\`\`\`
/api/v1/list_all_sessions_data
/api/v2/list_all_sessions_data
\`\`\`

**Header-based versioning**:
\`\`\`
Accept: application/vnd.api.v1+json
\`\`\`

### API Lifecycle Stages

1. **Development**: Internal testing
2. **Beta**: Limited external testing
3. **Stable**: Production-ready
4. **Deprecated**: Scheduled for removal
5. **Retired**: No longer available

### Deprecation Policy

1. **Announcement**: 6 months before deprecation
2. **Documentation**: Mark endpoints as deprecated
3. **Support**: Maintain for 12 months after deprecation
4. **Removal**: Remove after support period

### Backward Compatibility

- **Additive changes**: New fields added (backward compatible)
- **Breaking changes**: Documented with migration guides
- **Removal**: Follow deprecation policy

### Change Log

#### Version 1.0.0 (Current)
- Initial API release
- 11 endpoints
- Multi-collection support
- Role-based access control

---

## Conclusion

This API documentation provides comprehensive information about all endpoints, data schemas, error handling, and integration examples. For implementation details, refer to the [Code Documentation](code-documentation.md) and [Architecture Documentation](architecture.html).

For questions or support, please refer to the [User Manual](user-manual.html) or contact your system administrator.

`;

// Auto-generated from architecture.md
export const ReportDocumentationArchitecture = `# Architecture Documentation

## Report Application Backend - Architecture Documentation

### Table of Contents
1. [System Overview](#system-overview)
2. [Component-Level Design](#component-level-design)
3. [Data Flow & Storage](#data-flow--storage)
4. [Deployment Architecture](#deployment-architecture)
5. [Scalability & Resilience](#scalability--resilience)
6. [Security & Privacy Layers](#security--privacy-layers)
7. [Integration Points](#integration-points)
8. [Monitoring & Observability](#monitoring--observability)

---

## System Overview

### High-Level Architecture

\`\`\`mermaid
graph TB
    subgraph "Client Layer"
        Web[Web Frontend]
        Mobile[Mobile App]
        API_Client[API Clients]
    end
    
    subgraph "API Layer"
        Flask[Flask Application]
        Gunicorn[Gunicorn WSGI Server]
    end
    
    subgraph "Data Layer"
        Firestore[(Firestore Database)]
        GCS[Google Cloud Storage]
    end
    
    subgraph "External Services"
        Airtable[Airtable API]
        GCP[Google Cloud Platform]
    end
    
    Web --> Flask
    Mobile --> Flask
    API_Client --> Flask
    Flask --> Gunicorn
    Gunicorn --> Firestore
    Gunicorn --> GCS
    Gunicorn --> Airtable
    Firestore --> GCP
    GCS --> GCP
\`\`\`

### System Components

#### 1. **UI Layer** (Frontend)
- Web-based dashboard
- Mobile applications
- Third-party integrations

#### 2. **API Layer**
- **Framework**: Flask (Python)
- **Server**: Gunicorn with 4 workers, 2 threads each
- **Port**: 8080 (configurable)
- **Protocol**: HTTP/HTTPS
- **Format**: JSON request/response

#### 3. **Model Layer**
- Dark pattern detection models
- Compliance scoring algorithms
- Regulatory law matching

#### 4. **Database Layer**
- **Primary**: Google Cloud Firestore (NoSQL)
- **Collections**:
  - \`sessions\`: Main web sessions
  - \`digital-sessions\`: Digital violation sessions
  - \`influencer-marketing\`: Video dark pattern sessions
  - \`report-user-ids\`: User management

#### 5. **Compliance Pipeline**
- GDPR compliance hooks
- Audit trail generation
- Data access logging
- Regulatory law references

### Technology Stack

| Layer | Technology |
|-------|-----------|
| Web Framework | Flask 2.x |
| WSGI Server | Gunicorn |
| Database | Google Cloud Firestore |
| Storage | Google Cloud Storage |
| External API | Airtable |
| Container | Docker |
| Language | Python 3.10 |

---

## Component-Level Design

### 1. Evaluation Engine

#### Purpose
Processes images and detects dark patterns with regulatory compliance matching.

#### Components
- **Image Analyzer**: Processes screenshots and extracts UI elements
- **Pattern Detector**: Identifies dark pattern types (urgency, scarcity, etc.)
- **Law Matcher**: Matches detected patterns to regulatory laws
- **Confidence Scorer**: Calculates confidence scores for detections

#### Data Flow
\`\`\`
Image → Analyzer → Pattern Detector → Law Matcher → Confidence Scorer → Result
\`\`\`

### 2. Benchmarking Module

#### Purpose
Provides performance metrics and comparison capabilities.

#### Components
- **Session Comparator**: Compares sessions across domains
- **Trend Analyzer**: Tracks compliance trends over time
- **Performance Metrics**: Calculates processing times and accuracy

#### Implementation
- Parallel processing with ThreadPoolExecutor
- Batch operations for efficiency
- Caching for frequently accessed data

### 3. Audit Trail Manager

#### Purpose
Maintains comprehensive audit logs for compliance and security.

#### Components
- **Event Logger**: Logs all user actions and system events
- **Timestamp Manager**: Ensures consistent timestamp handling
- **Data Lineage Tracker**: Tracks data origin and transformations

#### Features
- GDPR-compliant logging
- User action tracking
- Data access logging
- Error event capture

### 4. Session Manager

#### Purpose
Manages session data across multiple Firestore collections.

#### Components
- **Collection Router**: Routes requests to appropriate collection
- **Session Aggregator**: Combines data from multiple collections
- **Journey Counter**: Counts journeys in parallel for performance

#### Key Functions
\`\`\`python
def determine_session_collection(session_id):
    """Routes to correct collection"""
    
def fetch_all_sessions():
    """Aggregates sessions from all collections"""
    
def count_journeys_for_session(session_id):
    """Parallel journey counting"""
\`\`\`

### 5. User Manager

#### Purpose
Handles user authentication, authorization, and management.

#### Components
- **Authentication Handler**: Validates credentials
- **Role Manager**: Manages Admin/Client roles
- **Access Controller**: Enforces role-based access control
- **User CRUD**: Create, read, update user operations

#### Security Features
- Password validation (should be hashed in production)
- Role-based access control
- Domain-based filtering for clients

---

## Data Flow & Storage

### Data Flow Architecture

\`\`\`mermaid
sequenceDiagram
    participant Client
    participant API
    participant Router
    participant Firestore
    participant ThreadPool
    
    Client->>API: Request
    API->>Router: Route Request
    Router->>Firestore: Query Collection
    Firestore-->>Router: Documents
    Router->>ThreadPool: Parallel Processing
    ThreadPool->>Firestore: Count Journeys
    Firestore-->>ThreadPool: Results
    ThreadPool-->>Router: Aggregated Data
    Router-->>API: Formatted Response
    API-->>Client: JSON Response
\`\`\`

### Firestore Collections Structure

#### 1. \`sessions\` Collection
\`\`\`
sessions/
  {sessionId}/
    - sessionName: string
    - createdBy: string
    - domainName: string
    - createdAt: timestamp
    userJourneys/
      {journeyId}/
        - journeyName: string
        images/
          {imageId}/
            - imageUrl: string
            - pageName: string
            - response: array
            - comprehensive_result: object
\`\`\`

#### 2. \`digital-sessions\` Collection
Similar structure to \`sessions\` with different page name extraction logic.

#### 3. \`influencer-marketing\` Collection
\`\`\`
influencer-marketing/
  {sessionId}/
    - sessionName: string
    - video_count: number
    - platform: string
    - analysis_data: object
      - videos: array
        - video_id: string
        - raw_analysis: array
\`\`\`

#### 4. \`report-user-ids\` Collection
\`\`\`
report-user-ids/
  {userId}/
    - email: string
    - password: string
    - first_name: string
    - last_name: string
    - role: string
    - domains: array
    - sessions: array
    - created_at: timestamp
    - last_updated: timestamp
\`\`\`

### Data Storage Patterns

#### On-Device SLMs
- **Current**: Not implemented
- **Recommendation**: Deploy lightweight models for real-time analysis
- **Storage**: Model files in Cloud Storage, loaded on-demand

#### GPU Benchmarking
- **Current**: Not implemented
- **Recommendation**: Use Vertex AI for GPU-accelerated processing
- **Use Case**: Large batch processing of images/videos

#### Compliance Repositories
- **Storage**: Firestore collections
- **Structure**: Hierarchical (sessions → journeys → images)
- **Indexing**: Email, domain name, timestamps

---

## Deployment Architecture

### Multi-Cloud Deployment

#### Primary: Google Cloud Platform

**Components**:
- **Compute**: Cloud Run, GKE, or Compute Engine
- **Database**: Firestore
- **Storage**: Cloud Storage
- **Container Registry**: GCR or Artifact Registry

#### Deployment Options

##### Option 1: Cloud Run (Serverless)
\`\`\`yaml
service: report-backend
runtime: python310
instance_class: F1
autoscaling:
  min_instances: 1
  max_instances: 10
\`\`\`

##### Option 2: GKE (Kubernetes)
\`\`\`yaml
apiVersion: apps/v1
kind: Deployment
metadata:
  name: report-backend
spec:
  replicas: 3
  template:
    spec:
      containers:
      - name: app
        image: gcr.io/project/report-backend
        ports:
        - containerPort: 8080
\`\`\`

##### Option 3: Compute Engine (VM)
- Standard VM instance
- Docker container deployment
- Load balancer for traffic distribution

### Hybrid Deployment

#### On-Premises Components
- **Database Replica**: Local Firestore emulator for development
- **Storage Cache**: Local cache for frequently accessed files
- **Monitoring**: Local logging and metrics collection

#### Cloud Components
- **Primary Database**: Cloud Firestore
- **Object Storage**: Cloud Storage
- **External APIs**: Airtable integration

### Containerization

#### Docker Configuration
\`\`\`dockerfile
FROM python:3.10-slim
WORKDIR /app
COPY requirements.txt ./
RUN pip install --no-cache-dir -r requirements.txt
COPY . .
CMD ["gunicorn", "app:app", "--bind", "0.0.0.0:8080"]
\`\`\`

#### Container Features
- **Base Image**: Python 3.10 slim
- **Working Directory**: \`/app\`
- **Port**: 8080
- **Process Manager**: Gunicorn
- **Workers**: 4
- **Threads**: 2 per worker

---

## Scalability & Resilience

### Load Balancing

#### Horizontal Scaling
- **Stateless Design**: Application is stateless, enabling easy scaling
- **Load Balancer**: Distributes traffic across multiple instances
- **Auto-scaling**: Cloud Run/GKE auto-scales based on traffic

#### Vertical Scaling
- **Worker Configuration**: Adjust Gunicorn workers based on CPU
- **Thread Configuration**: Adjust threads per worker
- **Resource Limits**: Set CPU and memory limits

### Failover Strategies

#### Database Failover
- **Firestore**: Automatic failover built-in
- **Multi-region**: Deploy in multiple regions
- **Backup**: Regular backups to Cloud Storage

#### Application Failover
- **Health Checks**: Implement health check endpoints
- **Circuit Breaker**: Implement circuit breaker pattern
- **Retry Logic**: Automatic retry with exponential backoff

#### External Service Failover
- **Airtable**: Graceful degradation if Airtable is unavailable
- **Fallback**: Return cached data or default values
- **Monitoring**: Alert on external service failures

### Continuous Improvement

#### Performance Optimization
- **Parallel Processing**: ThreadPoolExecutor for concurrent operations
- **Caching**: Cache frequently accessed data
- **Query Optimization**: Optimize Firestore queries
- **Batch Operations**: Batch Firestore operations

#### Monitoring & Feedback
- **Performance Metrics**: Track response times
- **Error Rates**: Monitor error rates
- **User Feedback**: Collect user feedback for improvements
- **A/B Testing**: Test different algorithms and approaches

---

## Security & Privacy Layers

### Encryption

#### Data in Transit
- **HTTPS/TLS**: All API communication over HTTPS
- **Certificate Management**: Automated certificate rotation
- **TLS Version**: Minimum TLS 1.2

#### Data at Rest
- **Firestore Encryption**: Automatic encryption by GCP
- **Cloud Storage Encryption**: Automatic encryption
- **Key Management**: Google Cloud KMS for key management

### Sandboxing

#### Application Sandboxing
- **Container Isolation**: Docker containers provide isolation
- **Resource Limits**: CPU and memory limits per container
- **Network Isolation**: Network policies for container communication

#### Data Sandboxing
- **Collection Isolation**: Separate collections for different data types
- **Access Control**: Role-based access to collections
- **Data Segregation**: Client data isolated by domain

### Assurance Pipelines

#### Security Scanning
- **Container Scanning**: Scan Docker images for vulnerabilities
- **Dependency Scanning**: Scan Python dependencies
- **Code Analysis**: Static code analysis for security issues

#### Compliance Validation
- **GDPR Compliance**: Regular compliance audits
- **Data Access Logging**: Log all data access
- **Audit Trails**: Maintain comprehensive audit trails

### Role-Based Access Control (RBAC)

#### Admin Role
- Full access to all endpoints
- User management capabilities
- System configuration access

#### Client Role
- Limited to assigned domains
- Read-only access to own data
- Profile management only

---

## Integration Points

### Law Firms Integration

#### Use Case
Legal teams need compliance data for case preparation.

#### Integration Pattern
\`\`\`python
# Law firm integration example
def get_compliance_report_for_case(domain):
    sessions = fetch_sessions_by_domains([domain])
    violations = extract_violations(sessions)
    return generate_legal_brief(violations)
\`\`\`

#### Data Format
- JSON API responses
- Export to PDF/Word for legal documents
- Law reference links included

### Hospitals / EMR Integration

#### Use Case
Healthcare organizations monitor website compliance for HIPAA.

#### Integration Pattern
\`\`\`python
# Healthcare compliance monitoring
def monitor_hipaa_compliance(healthcare_domains):
    for domain in healthcare_domains:
        sessions = fetch_sessions_by_domains([domain])
        violations = check_hipaa_violations(sessions)
        if violations:
            alert_compliance_officer(violations)
\`\`\`

#### Features
- HIPAA-specific violation detection
- Automated alerting
- Compliance reporting

### Finance Risk Engines Integration

#### Use Case
Financial institutions integrate compliance data into risk assessment.

#### Integration Pattern
\`\`\`javascript
// Finance dashboard integration
async function assessComplianceRisk(domain) {
  const sessions = await fetchSessionsByDomains([domain]);
  const riskScore = calculateRiskScore(sessions);
  updateRiskDashboard(riskScore);
}
\`\`\`

#### Data Requirements
- Real-time compliance scores
- Historical trend data
- Regulatory law references

### Airtable Integration

#### Current Implementation
- **Purpose**: Priority descriptions for sessions
- **Endpoint**: \`/get_priority_description\`
- **Authentication**: Bearer token
- **Table**: \`tblevlgLvZNOyVW4E\`

#### Integration Flow
\`\`\`
API Request → Airtable API → Priority Data → Response
\`\`\`

---

## Monitoring & Observability

### Metrics

#### Application Metrics
- **Response Times**: Track endpoint response times
- **Request Rates**: Monitor requests per second
- **Error Rates**: Track error percentages
- **Throughput**: Monitor requests processed

#### System Metrics
- **CPU Usage**: Monitor CPU utilization
- **Memory Usage**: Track memory consumption
- **Network I/O**: Monitor network traffic
- **Disk I/O**: Track storage operations

#### Business Metrics
- **Sessions Processed**: Count of sessions analyzed
- **Dark Patterns Detected**: Number of violations found
- **User Activity**: Active users and usage patterns
- **Compliance Scores**: Average compliance scores

### Dashboards

#### Operational Dashboard
- Real-time system health
- Error rates and alerts
- Performance metrics
- Resource utilization

#### Compliance Dashboard
- Compliance scores by domain
- Violation trends
- Regulatory law matches
- Audit trail summary

### Compliance Scoring Rubrics

#### Scoring Methodology
1. **Dark Pattern Severity**: Weight violations by severity
2. **Regulatory Impact**: Consider regulatory law importance
3. **Frequency**: Account for pattern frequency
4. **Domain Context**: Adjust for domain type

#### Score Calculation
\`\`\`
Compliance Score = 100 - (Severity × Frequency × Regulatory Weight)
\`\`\`

#### Score Categories
- **90-100**: Excellent compliance
- **75-89**: Good compliance
- **60-74**: Needs improvement
- **Below 60**: Critical issues

### Logging Strategy

#### Log Levels
- **ERROR**: System errors and exceptions
- **WARN**: Warning conditions
- **INFO**: Informational messages
- **DEBUG**: Debug information

#### Log Destinations
- **Cloud Logging**: GCP Cloud Logging
- **Local Logs**: Container logs
- **Audit Logs**: Firestore audit collection

#### Log Format
\`\`\`json
{
  "timestamp": "2024-01-15T10:30:00Z",
  "level": "ERROR",
  "message": "Session not found",
  "endpoint": "/get_session_user_journey_images_details",
  "session_id": "session123",
  "user_id": "user456"
}
\`\`\`

---

## Conclusion

This architecture documentation provides a comprehensive overview of the Report Application Backend system. The architecture is designed for scalability, security, and compliance with GDPR requirements.

Key strengths:
- Multi-collection support for flexible data management
- Parallel processing for performance
- Role-based access control for security
- Comprehensive audit trails for compliance

Areas for improvement:
- Implement password hashing
- Add comprehensive monitoring
- Enhance error handling
- Implement rate limiting

For implementation details, refer to the [Code Documentation](code-documentation.md) and [API Documentation](api-documentation.md).

`;

// Auto-generated from code-documentation.md
export const ReportDocumentationCodeDocumentation = `# Code Documentation

## Report Application Backend - Comprehensive Code Documentation

### Table of Contents
1. [Libraries and Dependencies](#libraries-and-dependencies)
2. [Deployment Environment](#deployment-environment)
3. [Core Code Approach & Design Philosophy](#core-code-approach--design-philosophy)
4. [Examples](#examples)
5. [Performance Benchmarking & Profiling](#performance-benchmarking--profiling)
6. [Database Management & Schema Notes](#database-management--schema-notes)
7. [Model Integrations](#model-integrations)
8. [Continuous Learning / Improvement Loops](#continuous-learning--improvement-loops)
9. [Security & Compliance](#security--compliance)
10. [Testing & QA](#testing--qa)
11. [Versioning & Change Management](#versioning--change-management)
12. [Operational Readiness](#operational-readiness)
13. [Developer Experience](#developer-experience)

---

## Libraries and Dependencies

### Core Framework
- **Flask** (v2.x): Primary web framework for REST API endpoints
- **Flask-CORS**: Cross-Origin Resource Sharing support for frontend integration
- **gunicorn**: Production WSGI HTTP server with 4 workers and 2 threads per worker

### Google Cloud Services
- **google-cloud-storage**: Google Cloud Storage client for file operations
- **google-cloud-firestore**: NoSQL document database for session, user, and journey data
- **firebase-admin**: Firebase Admin SDK for authentication and Firestore operations
- **google-cloud-tasks**: Cloud Tasks for asynchronous job processing
- **google-cloud-aiplatform** (>=1.38): Vertex AI platform integration for ML models

### Data Processing
- **python-dotenv**: Environment variable management from \`.env\` files
- **requests**: HTTP library for external API calls (Airtable integration)
- **jq**: JSON processor (if needed for data transformation)

### Development & Testing
- **gspread**: Google Sheets API client
- **oauth2client**: OAuth 2.0 authentication for Google services
- **cachetools**: Caching utilities for performance optimization

### Dependency Management
All dependencies are listed in \`requirements.txt\`. Installation:
\`\`\`bash
pip install -r requirements.txt
\`\`\`

---

## Deployment Environment

### Containerization
- **Base Image**: \`python:3.10-slim\`
- **Container Runtime**: Docker
- **Working Directory**: \`/app\`
- **Port**: 8080 (configurable via \`PORT\` environment variable)

### Production Server Configuration
- **WSGI Server**: Gunicorn
- **Workers**: 4
- **Threads per Worker**: 2
- **Timeout**: 0 (unlimited)
- **Bind Address**: \`0.0.0.0:8080\`

### Environment Variables
Required environment variables:
- \`GOOGLE_APPLICATION_CREDENTIALS\`: Path to Google Cloud service account JSON
- \`GOOGLE_APPLICATION_CREDENTIALS_2\`: Alternative credentials path (used in code)
- \`AIRTABLE_API_KEY\`: Airtable API key for priority descriptions
- \`BASE_ID\`: Airtable base ID
- \`PORT\`: Server port (default: 8080)
- \`PYTHONUNBUFFERED\`: Set to \`True\` for real-time logging

### Deployment Platforms
- **Primary**: Google Cloud Platform (GCP)
- **Container Registry**: Google Container Registry (GCR) or Artifact Registry
- **Compute Options**: Cloud Run, GKE, or Compute Engine

---

## Core Code Approach & Design Philosophy

### Multi-Collection Architecture
The application uses a unified interface to manage three distinct Firestore collections:
1. **sessions**: Main web session data with user journeys and images
2. **digital-sessions**: Digital violation detection sessions
3. **influencer-marketing**: Video dark pattern analysis sessions

### Design Patterns

#### 1. Collection Abstraction
Functions like \`determine_session_collection()\` and \`fetch_all_sessions()\` abstract collection differences, providing a unified API.

\`\`\`python
def determine_session_collection(session_id):
    """Determines which collection a session belongs to"""
    # Checks all three collections in order
\`\`\`

#### 2. Parallel Processing
Uses \`ThreadPoolExecutor\` for concurrent journey counting:
- **Max Workers**: 20 threads
- **Use Case**: Counting journeys across multiple sessions simultaneously
- **Performance Gain**: Reduces total processing time from O(n) sequential to O(1) parallel

#### 3. Error Handling Decorator
The \`@log_api_failures\` decorator provides centralized error logging:
- Catches all exceptions
- Logs to Firebase (currently commented out)
- Sanitizes sensitive data before logging
- Maintains original error response flow

#### 4. Functional Modularity
Separate modules for different collection types:
- \`digitalviolation.py\`: Digital violation session handlers
- \`videodarkpattern.py\`: Video dark pattern session handlers
- \`app.py\`: Main application and routing logic

### Code Organization Principles
1. **Separation of Concerns**: Business logic separated from routing
2. **DRY (Don't Repeat Yourself)**: Shared utilities for common operations
3. **Fail-Safe Defaults**: Graceful degradation when data is missing
4. **Type Safety**: Explicit type checking and validation

---

## Examples

### Standard Use Cases

#### Example 1: Fetching All Sessions
\`\`\`python
# Standard flow - fetches from all three collections
sessions, error = fetch_all_sessions()
if error:
    return jsonify({"status": "error", "message": error}), 500
return jsonify({"status": "success", "data": sessions}), 200
\`\`\`

#### Example 2: Getting Session Images
\`\`\`python
# Handles all three collection types automatically
journeys, error = fetch_images_by_session(session_id)
# Returns journey data with images and dark pattern counts
\`\`\`

#### Example 3: User Authentication
\`\`\`python
# Password-based authentication with role checking
user_data = authenticate_user(email, password)
if user_data.get('role') == 'Admin':
    # Admin-specific operations
\`\`\`

### Edge Cases

#### Edge Case 1: Missing Collection Data
\`\`\`python
# Handles missing createdAt timestamps
created_at = session_data.get("createdAt")
if created_at:
    try:
        session_info["createdAt"] = created_at.isoformat()
    except AttributeError:
        session_info["createdAt"] = str(created_at)
else:
    session_info["createdAt"] = None
\`\`\`

#### Edge Case 2: Empty Journey Lists
\`\`\`python
# Returns empty list instead of error
if not sessions:
    return jsonify({"status": "success", "data": []}), 200
\`\`\`

#### Edge Case 3: Video Session Structure Variations
\`\`\`python
# Handles nested analysis_data structures
analysis_data = session_data.get("analysis_data", {})
if not analysis_data:
    analysis_data = session_data.get("data", {}).get("analysis_data", {})
    if not analysis_data:
        analysis_data = session_data.get("data", {})
\`\`\`

#### Edge Case 4: Regulatory Violation Reference Formats
\`\`\`python
# Handles both list and dict formats for regulatory violations
if isinstance(regulatory_violation, list):
    for violation_item in regulatory_violation:
        # Process each item
elif isinstance(regulatory_violation, dict):
    # Process single dict
\`\`\`

---

## Performance Benchmarking & Profiling

### Optimization Strategies

#### 1. Parallel Journey Counting
**Before**: Sequential counting - O(n) time complexity
\`\`\`python
# Sequential approach (not used)
for session_id in session_ids:
    count = count_journeys_for_session(session_id)  # Blocking
\`\`\`

**After**: Parallel execution - O(1) time complexity
\`\`\`python
# Parallel approach (current)
with ThreadPoolExecutor(max_workers=20) as executor:
    future_to_session = {
        executor.submit(count_journeys_for_session, session_id): session_id 
        for session_id in session_data_map.keys()
    }
\`\`\`

**Performance Impact**: 
- 20 sessions: ~10x faster
- 100 sessions: ~50x faster

#### 2. Batch Firestore Queries
Uses \`stream()\` for efficient document retrieval:
\`\`\`python
session_docs = list(sessions_ref.stream())  # Single query
\`\`\`

#### 3. Limit Queries
Prevents excessive data retrieval:
\`\`\`python
journey_count = len(list(journeys_ref.limit(1000).stream()))
\`\`\`

### Profiling Recommendations
1. **Use cProfile**: Profile endpoint execution times
2. **Monitor Firestore Queries**: Track query latency
3. **Thread Pool Metrics**: Monitor thread pool utilization
4. **Memory Profiling**: Track memory usage for large session lists

### Performance Benchmarks
- **Session List Endpoint**: ~500ms for 100 sessions (with parallel counting)
- **Image Metadata Fetch**: ~50-100ms per image
- **Domain Filtering**: ~200ms for 50 domains

---

## Database Management & Schema Notes

### Firestore Collections

#### 1. \`sessions\` Collection
**Structure**:
\`\`\`
sessions/{sessionId}
  - sessionName: string
  - createdBy: string
  - domainName: string
  - createdAt: timestamp
  - userJourneys/{journeyId}
    - journeyName: string
    - images/{imageId}
      - imageUrl: string
      - pageName: string
      - response: array[object]
        - type: "outcome"
        - text: string
        - dark_pattern: string
        - laws: array[object]
      - comprehensive_result: object
\`\`\`

#### 2. \`digital-sessions\` Collection
**Structure**: Similar to \`sessions\` but with different page name extraction:
- Uses \`comprehensive_result.agent_results.step_1_page_type_identification.page_type\`

#### 3. \`influencer-marketing\` Collection
**Structure**:
\`\`\`
influencer-marketing/{sessionId}
  - sessionName: string
  - createdBy: string
  - video_count: number
  - platform: string
  - search_type: string
  - overall_confidence_score: string
  - analysis_data: object
    - videos: array[object]
      - video_id: string
      - url: string
      - product_names: string
      - raw_analysis: array[object]
        - excerpt: string
        - reasoning: string
        - category: string
        - regulatoryViolationReference: object|array
\`\`\`

#### 4. \`report-user-ids\` Collection
**Structure**:
\`\`\`
report-user-ids/{userId}
  - email: string (unique)
  - password: string (should be hashed in production)
  - first_name: string
  - last_name: string
  - role: "Admin" | "Client"
  - domains: array[string]
  - sessions: array[string]
  - created_at: timestamp
  - last_updated: timestamp
\`\`\`

### Indexing Strategy
- **Email**: Indexed for user lookup queries
- **Domain Name**: Consider indexing for domain-based filtering
- **Created At**: Indexed for sorting operations

### Data Consistency
- **Timestamps**: Use \`firestore.SERVER_TIMESTAMP\` for consistency
- **Unique Constraints**: Email uniqueness enforced at application level
- **Referential Integrity**: Session IDs validated before use

---

## Model Integrations

### Current Integrations

#### 1. Airtable Integration
- **Purpose**: Fetch priority descriptions for sessions
- **Endpoint**: \`/get_priority_description\`
- **Table**: \`tblevlgLvZNOyVW4E\` (Meaning of priority table)
- **Authentication**: Bearer token via \`AIRTABLE_API_KEY\`

#### 2. Google Cloud AI Platform
- **Package**: \`google-cloud-aiplatform>=1.38\`
- **Status**: Installed but not actively used in current codebase
- **Potential Use**: ML model inference for dark pattern detection

### Recommended Integrations

#### 1. Small Language Models (SLMs)
- **Use Case**: On-device dark pattern detection
- **Recommendation**: Integrate lightweight models for real-time analysis
- **Implementation**: Use Vertex AI or local model inference

#### 2. External Compliance APIs
- **Use Case**: Real-time regulatory compliance checking
- **Recommendation**: Integrate legal database APIs for law references

#### 3. AutoML Integration
- **Use Case**: Continuous model improvement
- **Recommendation**: Use Vertex AI AutoML for pattern recognition

---

## Continuous Learning / Improvement Loops

### Current Implementation

#### 1. Error Logging Infrastructure
The \`@log_api_failures\` decorator provides hooks for error tracking:
\`\`\`python
@log_api_failures
def endpoint_function():
    # Errors automatically logged
\`\`\`

**Note**: Firebase logging is currently commented out but infrastructure exists.

#### 2. Payload Sanitization
Sensitive data is sanitized before logging:
\`\`\`python
def sanitize_payload(payload):
    sensitive_fields = ['password', 'token', 'credentials', 'secret']
    # Redacts sensitive information
\`\`\`

### Recommended Improvements

#### 1. Analytics Collection
- Track endpoint usage patterns
- Monitor performance metrics
- Collect user behavior data

#### 2. A/B Testing Framework
- Test different algorithms
- Compare performance optimizations
- Validate user experience improvements

#### 3. Feedback Loops
- User feedback collection
- Model accuracy tracking
- Compliance score validation

---

## Security & Compliance

### GDPR Compliance

#### Data Protection Measures
1. **Data Minimization**: Only collect necessary user data
2. **Access Control**: Role-based access (Admin/Client)
3. **Data Sanitization**: Sensitive data redaction in logs
4. **Audit Trails**: Timestamps on all user actions

#### User Rights Implementation
- **Right to Access**: \`/get_user_details/<user_id>\` endpoint
- **Right to Rectification**: \`/update_user\` endpoint
- **Right to Erasure**: Should be implemented for GDPR compliance
- **Data Portability**: Export functionality recommended

### Authentication & Authorization

#### Current Implementation
- **Method**: Password-based authentication
- **Storage**: Passwords stored in plaintext (⚠️ **Security Risk**)
- **Roles**: Admin and Client with different access levels

#### Security Recommendations
1. **Password Hashing**: Use bcrypt or Argon2
2. **OAuth2 Integration**: Implement OAuth2 for third-party authentication
3. **JWT Tokens**: Replace password-based auth with JWT
4. **Rate Limiting**: Implement rate limiting on authentication endpoints
5. **Multi-Factor Authentication**: Add MFA for admin accounts

### Encryption
- **In Transit**: HTTPS/TLS (handled by deployment platform)
- **At Rest**: Firestore encryption (handled by GCP)
- **Credentials**: Service account keys stored securely

### Role-Based Access Control (RBAC)
\`\`\`python
# Admin-only endpoints
if request.user.get('role') != 'Admin':
    return jsonify({"status": "error", "message": "Unauthorized"}), 403
\`\`\`

### Threat Modeling

#### Identified Threats
1. **SQL Injection**: Not applicable (NoSQL database)
2. **NoSQL Injection**: Mitigated by Firestore parameterized queries
3. **XSS**: Mitigated by JSON-only responses
4. **CSRF**: Mitigated by CORS configuration
5. **Brute Force**: Not currently mitigated (recommend rate limiting)
6. **Credential Theft**: Mitigated by secure credential storage

---

## Testing & QA

### Current Testing

#### Test File: \`test_api_responses.py\`
- **Purpose**: Integration testing of API endpoints
- **Coverage**: Tests all major endpoints
- **Method**: HTTP request-based testing

### Test Structure
\`\`\`python
def test_api(endpoint, method="GET", data=None, description=""):
    # Records response time
    # Validates status codes
    # Displays response data
\`\`\`

### Recommended Testing Strategy

#### 1. Unit Tests
- Test individual functions in isolation
- Mock Firestore and external API calls
- Use pytest framework

#### 2. Integration Tests
- Test endpoint-to-database flows
- Test multi-collection operations
- Validate data transformations

#### 3. Regression Tests
- Maintain test suite for all endpoints
- Run before each deployment
- Track test coverage metrics

#### 4. Failure Mode Injection
- Test error handling paths
- Simulate network failures
- Test with invalid data

#### 5. Coverage Metrics
- Target: 80%+ code coverage
- Focus on critical paths
- Include edge cases

### Test Examples

#### Unit Test Example
\`\`\`python
def test_count_journeys_for_session():
    # Mock Firestore
    # Test journey counting logic
    assert count == expected_count
\`\`\`

#### Integration Test Example
\`\`\`python
def test_fetch_all_sessions_integration():
    # Test actual Firestore connection
    # Validate multi-collection merging
    assert len(sessions) > 0
\`\`\`

---

## Versioning & Change Management

### Git Strategy Recommendations

#### Branching Strategy
- **main**: Production-ready code
- **develop**: Integration branch
- **feature/**: Feature development
- **hotfix/**: Critical bug fixes

#### Commit Conventions
- Use conventional commits: \`feat:\`, \`fix:\`, \`docs:\`, \`refactor:\`
- Include issue numbers in commits
- Write descriptive commit messages

### Release Notes Template
\`\`\`markdown
## Version X.Y.Z - Release Date

### Added
- New features

### Changed
- Modified functionality

### Fixed
- Bug fixes

### Deprecated
- Features to be removed

### Security
- Security updates
\`\`\`

### Backward Compatibility
- Maintain API versioning (e.g., \`/v1/\`, \`/v2/\`)
- Document breaking changes
- Provide migration guides

---

## Operational Readiness

### Configuration Management

#### Environment-Specific Configs
- **Development**: Local \`.env\` file
- **Staging**: GCP Secret Manager
- **Production**: GCP Secret Manager with rotation

#### Configuration Files
- \`requirements.txt\`: Python dependencies
- \`Dockerfile\`: Container configuration
- \`.env\`: Environment variables (not in repo)

### Containerization

#### Docker Configuration
\`\`\`dockerfile
FROM python:3.10-slim
WORKDIR /app
COPY requirements.txt ./
RUN pip install --no-cache-dir -r requirements.txt
CMD ["gunicorn", "app:app", "--bind", "0.0.0.0:8080"]
\`\`\`

#### Container Best Practices
- Use multi-stage builds for smaller images
- Scan images for vulnerabilities
- Use specific version tags

### Monitoring & Logging

#### Current Logging
- Print statements for errors
- Traceback printing for debugging
- Error decorator for API failures

#### Recommended Monitoring
1. **Application Logs**: Cloud Logging integration
2. **Metrics**: Cloud Monitoring for performance
3. **Alerts**: Set up alerts for errors and latency
4. **Dashboards**: Create operational dashboards

### Rollback Procedures
1. **Container Rollback**: Revert to previous container image
2. **Database Rollback**: Firestore point-in-time recovery
3. **Configuration Rollback**: Revert environment variables

---

## Developer Experience

### Contribution Guidelines

#### Code Style
- Follow PEP 8 Python style guide
- Use type hints where possible
- Write docstrings for all functions

#### Pull Request Process
1. Create feature branch
2. Write tests for new features
3. Update documentation
4. Submit PR with description
5. Code review required

### Extensibility Notes

#### Adding New Collections
1. Create new module (e.g., \`newcollection.py\`)
2. Implement fetch functions following existing patterns
3. Update \`determine_session_collection()\` function
4. Add to \`fetch_all_sessions()\` aggregation

#### Adding New Endpoints
1. Define route with \`@app.route()\`
2. Add \`@log_api_failures\` decorator
3. Implement error handling
4. Update API documentation
5. Add tests

### Automated Documentation Generation

#### Recommended Tools
- **Sphinx**: Python documentation generator
- **Swagger/OpenAPI**: API documentation
- **MkDocs**: Markdown-based documentation

#### Documentation Workflow
1. Write docstrings in code
2. Generate API docs automatically
3. Update manual documentation
4. Publish to documentation site

### Development Setup
\`\`\`bash
# Clone repository
git clone <repo-url>
cd Report-Application-Backend-main

# Create virtual environment
python -m venv venv
source venv/bin/activate  # On Windows: venv\\Scripts\\activate

# Install dependencies
pip install -r requirements.txt

# Set up environment variables
cp .env.example .env
# Edit .env with your credentials

# Run locally
python app.py

# Or with Docker
docker build -t report-backend .
docker run -p 8080:8080 report-backend
\`\`\`

---

## Conclusion

This codebase demonstrates a well-structured Flask application with multi-collection support, parallel processing, and comprehensive error handling. Key areas for improvement include security enhancements (password hashing, OAuth2), comprehensive testing, and monitoring integration.

For questions or contributions, please refer to the Developer Experience section and follow the contribution guidelines.

`;

// Auto-generated from functional-flow.md
export const ReportDocumentationFunctionalFlow = `# Application Functional Flow Documentation

## Report Application Backend - Functional Flow Documentation

### Table of Contents
1. [End-to-End Workflow Diagrams](#end-to-end-workflow-diagrams)
2. [Use Case Scenarios](#use-case-scenarios)
3. [Process States & Transitions](#process-states--transitions)
4. [Error Handling & Recovery Paths](#error-handling--recovery-paths)
5. [Role-Based Access Flows](#role-based-access-flows)
6. [Compliance & Audit Trail Integration](#compliance--audit-trail-integration)
7. [Interactive Simulation Flows](#interactive-simulation-flows)

---

## End-to-End Workflow Diagrams

### Session Management Workflow

\`\`\`mermaid
graph TD
    A[Client Request] --> B{Session Type?}
    B -->|Main Session| C[sessions Collection]
    B -->|Digital Violation| D[digital-sessions Collection]
    B -->|Video Dark Pattern| E[influencer-marketing Collection]
    C --> F[Fetch Session Data]
    D --> F
    E --> F
    F --> G[Count Journeys in Parallel]
    G --> H[Return Session List]
    H --> I[Client Receives Data]
\`\`\`

### User Journey Flow

\`\`\`mermaid
sequenceDiagram
    participant Client
    participant API
    participant Firestore
    participant ThreadPool
    
    Client->>API: GET /list_all_sessions_data
    API->>Firestore: Query all collections
    Firestore-->>API: Session documents
    API->>ThreadPool: Submit journey counting tasks
    ThreadPool->>Firestore: Count journeys (parallel)
    Firestore-->>ThreadPool: Journey counts
    ThreadPool-->>API: Aggregated results
    API->>API: Merge collection data
    API-->>Client: Combined session list
\`\`\`

### Image Metadata Retrieval Flow

\`\`\`mermaid
graph LR
    A[Session ID] --> B{Determine Collection}
    B --> C[sessions]
    B --> D[digital-sessions]
    B --> E[influencer-marketing]
    C --> F[Fetch Journey Images]
    D --> F
    E --> F
    F --> G[Filter Dark Patterns]
    G --> H[Count Outcomes]
    H --> I[Return Image Metadata]
\`\`\`

### Domain Filtering Workflow

\`\`\`mermaid
flowchart TD
    Start[Client Request with Domains] --> Validate{Validate Domains}
    Validate -->|Invalid| Error[Return 400 Error]
    Validate -->|Valid| Query1[Query sessions Collection]
    Validate -->|Valid| Query2[Query digital-sessions Collection]
    Validate -->|Valid| Query3[Query influencer-marketing Collection]
    Query1 --> Filter1[Filter by Domain]
    Query2 --> Filter2[Filter by Domain]
    Query3 --> Filter3[Filter by Domain]
    Filter1 --> Count1[Count Journeys]
    Filter2 --> Count2[Count Journeys]
    Filter3 --> Count3[Count Journeys]
    Count1 --> Merge[Merge Results]
    Count2 --> Merge
    Count3 --> Merge
    Merge --> Return[Return Filtered Sessions]
\`\`\`

---

## Use Case Scenarios

### Legal Tech: Dark Pattern Detection

#### Scenario 1: Compliance Audit for E-commerce Website
**Actor**: Compliance Officer (Admin role)

**Steps**:
1. Admin authenticates via \`/authenticate_user\`
2. Admin requests all sessions via \`/list_all_sessions_data\`
3. System fetches sessions from all three collections
4. Admin filters by domain using \`/fetch_sessions_by_domains\`
5. Admin retrieves detailed image metadata via \`/get_session_user_journey_images_details\`
6. Admin reviews dark pattern violations with law references
7. Admin exports compliance report

**Expected Outcome**: Complete audit trail of dark patterns with regulatory references

#### Scenario 2: Real-time Dark Pattern Monitoring
**Actor**: Automated Monitoring System

**Steps**:
1. System periodically calls \`/list_all_sessions_data\`
2. System identifies new sessions with high dark pattern counts
3. System triggers alerts for compliance team
4. System logs findings for audit trail

**Expected Outcome**: Proactive detection of compliance issues

### Compliance Reporting

#### Scenario 3: Generate Compliance Report
**Actor**: Client User

**Steps**:
1. Client authenticates and receives role-based access
2. Client requests sessions for their assigned domains
3. System filters sessions based on user's domain list
4. Client retrieves priority descriptions via \`/get_priority_description\`
5. Client generates report with dark pattern counts and law references

**Expected Outcome**: Domain-specific compliance report

---

## Process States & Transitions

### Session Lifecycle States

\`\`\`mermaid
stateDiagram-v2
    [*] --> Created: Session Created
    Created --> Processing: Images Uploaded
    Processing --> Analyzed: Dark Pattern Analysis Complete
    Analyzed --> Reported: Report Generated
    Reported --> Archived: Session Archived
    Archived --> [*]
    
    Processing --> Error: Analysis Failed
    Error --> Processing: Retry Analysis
    Error --> [*]: Abandon Session
\`\`\`

### User Journey States

\`\`\`mermaid
stateDiagram-v2
    [*] --> JourneyStarted: User Journey Initiated
    JourneyStarted --> ImagesCaptured: Screenshots Taken
    ImagesCaptured --> AnalysisPending: Images Queued
    AnalysisPending --> AnalysisComplete: Dark Patterns Detected
    AnalysisComplete --> ReviewReady: Ready for Review
    ReviewReady --> [*]: Review Complete
    
    AnalysisPending --> AnalysisFailed: Analysis Error
    AnalysisFailed --> AnalysisPending: Retry
\`\`\`

### Image Analysis States

\`\`\`mermaid
stateDiagram-v2
    [*] --> ImageUploaded: Image Stored
    ImageUploaded --> Processing: Analysis Started
    Processing --> OutcomeDetected: Dark Pattern Found
    Processing --> NoViolation: No Dark Pattern
    OutcomeDetected --> LawMatched: Regulatory Law Matched
    LawMatched --> MetadataComplete: Metadata Ready
    NoViolation --> MetadataComplete
    MetadataComplete --> [*]
    
    Processing --> Error: Analysis Failed
    Error --> Processing: Retry
\`\`\`

---

## Error Handling & Recovery Paths

### Error Handling Strategy

#### 1. API Error Decorator Pattern
The \`@log_api_failures\` decorator provides centralized error handling:

\`\`\`python
@log_api_failures
def endpoint_function():
    try:
        # Business logic
    except Exception as e:
        # Error automatically logged
        # Original error re-raised
\`\`\`

**Error Flow**:
1. Exception caught by decorator
2. Payload sanitized (sensitive data removed)
3. Error logged to Firebase (currently commented out)
4. Error printed to server logs
5. Original exception re-raised
6. Standard error response returned to client

#### 2. Collection-Specific Error Handling

**Session Not Found**:
\`\`\`mermaid
graph TD
    A[Session ID Requested] --> B{Check Collections}
    B -->|Not in sessions| C{Check digital-sessions}
    B -->|Not in digital-sessions| D{Check influencer-marketing}
    C -->|Not found| D
    D -->|Not found| E[Return 404 Error]
    D -->|Found| F[Process Request]
    C -->|Found| F
    B -->|Found| F
\`\`\`

**Recovery Actions**:
- Return clear error message: "No session found with ID: {session_id}"
- Log error for debugging
- Maintain API contract (consistent error format)

#### 3. Firestore Connection Errors

**Error Scenarios**:
- Network timeout
- Authentication failure
- Quota exceeded
- Invalid query

**Recovery Paths**:
\`\`\`mermaid
graph TD
    A[Firestore Query] --> B{Query Success?}
    B -->|Yes| C[Return Data]
    B -->|No| D{Error Type?}
    D -->|Timeout| E[Retry with Backoff]
    D -->|Auth Error| F[Return 401]
    D -->|Quota| G[Return 503]
    D -->|Invalid| H[Return 400]
    E --> B
\`\`\`

#### 4. Data Validation Errors

**Validation Flow**:
1. Request received
2. Validate required fields
3. Validate field types
4. Validate business rules (e.g., role must be Admin/Client)
5. Return 400 with specific validation errors

**Example**:
\`\`\`json
{
  "status": "error",
  "message": "Missing required field: sessionId"
}
\`\`\`

### Recovery Mechanisms

#### Automatic Retry
- **Journey Counting**: ThreadPoolExecutor handles retries internally
- **Firestore Queries**: Application-level retry logic recommended

#### Graceful Degradation
- **Missing Data**: Return empty arrays instead of errors
- **Partial Failures**: Return available data with warnings
- **Collection Errors**: Continue with other collections if one fails

---

## Role-Based Access Flows

### Admin Role Flow

\`\`\`mermaid
graph TD
    A[Admin Login] --> B[Authenticate User]
    B --> C{Role Check}
    C -->|Admin| D[Full Access Granted]
    D --> E[Access All Sessions]
    D --> F[Access All Domains]
    D --> G[Manage Users]
    D --> H[View All Data]
    E --> I[Admin Dashboard]
    F --> I
    G --> I
    H --> I
\`\`\`

**Admin Capabilities**:
- View all sessions from all collections
- Access all domains
- List all users (\`/list_users\`)
- Update any user
- Access priority descriptions
- Full audit trail access

### Client Role Flow

\`\`\`mermaid
graph TD
    A[Client Login] --> B[Authenticate User]
    B --> C{Role Check}
    C -->|Client| D[Limited Access Granted]
    D --> E[Filter by Assigned Domains]
    D --> F[View Own Sessions Only]
    D --> G[Update Own Profile]
    E --> H[Client Dashboard]
    F --> H
    G --> H
\`\`\`

**Client Capabilities**:
- View sessions for assigned domains only
- Access domain-filtered data
- Update own user profile
- View own session details
- Generate reports for assigned domains

### Access Control Implementation

\`\`\`python
# Admin-only endpoint example
@app.route('/list_all_sessions_data', methods=['GET'])
@log_api_failures
def list_sessions():
    if request.user.get('role') != 'Admin':
        return jsonify({"status": "error", "message": "Unauthorized access"}), 403
    # Admin-only logic
\`\`\`

**Access Control Matrix**:

| Endpoint | Admin | Client |
|----------|-------|--------|
| \`/list_all_sessions_data\` | ✅ | ❌ |
| \`/list_all_domains\` | ✅ | ✅ |
| \`/fetch_sessions_by_domains\` | ✅ | ✅ (filtered) |
| \`/get_session_user_journey_images_details\` | ✅ | ✅ (own domains) |
| \`/register_user\` | ✅ | ❌ |
| \`/list_users\` | ✅ | ❌ |
| \`/update_user\` | ✅ | ✅ (own profile) |

---

## Compliance & Audit Trail Integration

### Audit Trail Components

#### 1. Timestamp Tracking
- **Session Creation**: \`createdAt\` timestamp in Firestore
- **User Actions**: \`created_at\` and \`last_updated\` timestamps
- **Data Modification**: Server timestamps via \`firestore.SERVER_TIMESTAMP\`

#### 2. User Action Logging
- **Authentication Events**: Login attempts logged
- **Data Access**: Session access tracked
- **Modifications**: User updates logged with timestamps

#### 3. Data Lineage
- **Session Origin**: \`createdBy\` field tracks creator
- **Domain Association**: Domain-to-session mapping
- **Journey Tracking**: Journey-to-image relationships

### GDPR Compliance Integration

#### Data Access Logging
\`\`\`mermaid
graph LR
    A[User Request] --> B[Log Request]
    B --> C[Sanitize Payload]
    C --> D[Store in Audit Log]
    D --> E[Process Request]
    E --> F[Log Response]
\`\`\`

#### Right to Access Implementation
- Endpoint: \`/get_user_details/<user_id>\`
- Returns: Complete user data (excluding sensitive fields in production)
- Logged: Access event with timestamp

#### Right to Rectification
- Endpoint: \`/update_user\`
- Logged: All field changes with before/after values
- Timestamp: \`last_updated\` field updated

#### Recommended: Right to Erasure
- **Not Currently Implemented**
- **Recommendation**: Add \`/delete_user\` endpoint with:
  - Soft delete (mark as deleted)
  - Data anonymization
  - Audit log entry

### Audit Trail Data Structure

\`\`\`json
{
  "audit_entry": {
    "timestamp": "2024-01-15T10:30:00Z",
    "user_id": "user123",
    "action": "session_access",
    "resource": "session456",
    "ip_address": "192.168.1.1",
    "user_agent": "Mozilla/5.0...",
    "result": "success"
  }
}
\`\`\`

---

## Interactive Simulation Flows

### Session Browsing Flow

\`\`\`mermaid
graph TD
    Start[User Opens Dashboard] --> Auth{Authenticated?}
    Auth -->|No| Login[Login Page]
    Auth -->|Yes| Role{Role?}
    Login --> Auth
    Role -->|Admin| AllSessions[View All Sessions]
    Role -->|Client| FilteredSessions[View Domain-Filtered Sessions]
    AllSessions --> SessionList[Session List Displayed]
    FilteredSessions --> SessionList
    SessionList --> Select[User Selects Session]
    Select --> JourneyView[Journey Details]
    JourneyView --> ImageView[Image Details]
    ImageView --> Metadata[Dark Pattern Metadata]
    Metadata --> Report[Generate Report]
\`\`\`

### Domain Filtering Flow

\`\`\`mermaid
sequenceDiagram
    participant User
    participant Frontend
    participant API
    participant Firestore
    
    User->>Frontend: Select Domains
    Frontend->>API: POST /fetch_sessions_by_domains
    API->>Firestore: Query sessions (domain filter)
    Firestore-->>API: Matching sessions
    API->>Firestore: Query digital-sessions (domain filter)
    Firestore-->>API: Matching sessions
    API->>Firestore: Query influencer-marketing (domain filter)
    Firestore-->>API: Matching sessions
    API->>API: Merge and count journeys
    API-->>Frontend: Filtered session list
    Frontend-->>User: Display filtered results
\`\`\`

### Image Metadata Retrieval Flow

\`\`\`mermaid
flowchart TD
    A[User Clicks Image] --> B[Extract Session/Journey/Image IDs]
    B --> C[POST /get_image_Information]
    C --> D{Determine Collection}
    D -->|sessions| E[Fetch from sessions collection]
    D -->|digital-sessions| F[Fetch from digital-sessions]
    D -->|influencer-marketing| G[Fetch from influencer-marketing]
    E --> H[Extract Response Data]
    F --> H
    G --> H
    H --> I[Filter Outcome Types]
    I --> J[Transform Law References]
    J --> K[Return Metadata]
    K --> L[Display Dark Patterns]
    L --> M[Show Law References]
\`\`\`

### Priority Description Flow

\`\`\`mermaid
graph LR
    A[Session Selected] --> B[Get Session ID]
    B --> C[POST /get_priority_description]
    C --> D[Airtable API Call]
    D --> E{Record Found?}
    E -->|Yes| F[Extract Priorities]
    E -->|No| G[Return 404]
    F --> H[Display Priority Levels]
    H --> I[Highest Priority]
    H --> J[High Priority]
    H --> K[Medium Priority]
    H --> L[Low Priority]
    H --> M[Lowest Priority]
\`\`\`

### User Management Flow

\`\`\`mermaid
stateDiagram-v2
    [*] --> Register: New User Registration
    Register --> Authenticate: User Created
    Authenticate --> Dashboard: Login Success
    Dashboard --> UpdateProfile: User Updates Profile
    UpdateProfile --> Dashboard: Profile Updated
    Dashboard --> ViewSessions: User Views Sessions
    ViewSessions --> Dashboard: Return to Dashboard
    Dashboard --> Logout: User Logs Out
    Logout --> [*]
    
    Authenticate --> Register: Login Failed
\`\`\`

---

## Conclusion

This functional flow documentation provides a comprehensive view of how the Report Application Backend processes requests, handles errors, manages user access, and maintains compliance. The workflows demonstrate the multi-collection architecture's flexibility and the system's ability to handle various use cases in legal tech and compliance reporting.

For implementation details, refer to the [Code Documentation](code-documentation.md) and [API Documentation](api-documentation.md).

`;

// Auto-generated from README.md
export const ReportDocumentationReadme = `# Report Application Backend - Documentation

## Overview

This directory contains comprehensive documentation for the Report Application Backend, covering all aspects of the system including code documentation, functional flows, user guides, API references, and architecture details.

## Documentation Structure

### 1. Code Documentation
- **File**: \`code-documentation.md\` / \`code-documentation.html\`
- **Content**: Libraries, dependencies, deployment environment, core code approach, examples, performance, database management, security, testing, versioning, operational readiness, developer experience

### 2. Functional Flow Documentation
- **File**: \`functional-flow.md\` / \`functional-flow.html\`
- **Content**: End-to-end workflow diagrams, use case scenarios, process states & transitions, error handling, role-based access flows, compliance integration, interactive simulation flows

### 3. User Manual
- **File**: \`user-manual.md\` / \`user-manual.html\`
- **Content**: Getting started guide, feature walkthroughs, interactive tutorials, troubleshooting & FAQs, glossary, accessibility & localization, versioning & release notes

### 4. API Documentation
- **File**: \`api-documentation.md\` / \`api-documentation.html\`
- **Content**: Authentication & security, endpoints & methods, data schemas, rate limits & quotas, error codes & responses, integration examples, version control & lifecycle management

### 5. Architecture Documentation
- **File**: \`architecture.md\` / \`architecture.html\`
- **Content**: System overview, component-level design, data flow & storage, deployment architecture, scalability & resilience, security & privacy layers, integration points, monitoring & observability

## File Formats

All documentation is available in two formats:

1. **Markdown (\`.md\`)**: For version control, easy editing, and plain text viewing
2. **HTML (\`.html\`)**: Interactive version with navigation, search, syntax highlighting, and diagrams

## Navigation

### Main Index
- **File**: \`index.html\`
- **Purpose**: Central navigation hub for all documentation

### Supporting Files
- **CSS**: \`assets/style.css\` - Styling for HTML documentation
- **JavaScript**: \`assets/script.js\` - Interactive features (search, navigation, etc.)

## Quick Links

### For Users
- [User Manual](user-manual.html) - Installation, setup, and usage
- [API Documentation](api-documentation.html) - API reference and examples

### For Developers
- [Code Documentation](code-documentation.html) - Code structure and patterns
- [Architecture](architecture.html) - System architecture and design
- [Functional Flow](functional-flow.html) - Application workflows

## Features

### Interactive HTML Documentation
- ✅ Sidebar navigation
- ✅ Search functionality
- ✅ Code syntax highlighting
- ✅ Mermaid diagrams
- ✅ Responsive design
- ✅ Table of contents

### Markdown Documentation
- ✅ Version control friendly
- ✅ Easy to edit
- ✅ Plain text readable
- ✅ GitHub/GitLab compatible

## Usage

### Viewing HTML Documentation
1. Open \`index.html\` in a web browser
2. Navigate using the sidebar menu
3. Use search to find specific topics
4. Click on diagrams to view full size

### Viewing Markdown Documentation
1. Open \`.md\` files in any markdown viewer
2. Use GitHub/GitLab web interface
3. Use markdown editors (VS Code, Typora, etc.)
4. Convert to PDF using pandoc or similar tools

## Documentation Standards

### Code Examples
- All code examples are syntax highlighted
- Examples include both standard and edge cases
- Error handling is demonstrated

### Diagrams
- Mermaid syntax for diagrams
- Flowcharts, sequence diagrams, state diagrams
- Architecture diagrams

### Writing Style
- Clear, professional language
- Consistent terminology
- Comprehensive coverage
- Both current state and recommendations

## Maintenance

### Updating Documentation
1. Edit markdown files for content changes
2. HTML files are generated from markdown (or maintained separately)
3. Update diagrams in Mermaid syntax
4. Keep examples up to date with code changes

### Version Control
- All documentation is version controlled
- Changes tracked in git history
- Release notes updated with documentation changes

## Contributing

When contributing to documentation:

1. Follow the existing structure and style
2. Update both markdown and HTML versions
3. Include code examples where relevant
4. Add diagrams for complex flows
5. Keep terminology consistent
6. Test all links and examples

## Support

For questions about the documentation:
- Check the relevant documentation section
- Review code examples
- Refer to the troubleshooting sections
- Contact the development team

## License

Documentation follows the same license as the main project.

---

**Last Updated**: 2024-01-15
**Version**: 1.0.0

`;

// Auto-generated from user-manual.md
export const ReportDocumentationUserManual = `# User Manual

## Report Application Backend - User Manual

### Table of Contents
1. [Getting Started](#getting-started)
2. [Feature Walkthroughs](#feature-walkthroughs)
3. [Interactive Tutorials](#interactive-tutorials)
4. [Troubleshooting & FAQs](#troubleshooting--faqs)
5. [Glossary of Terms](#glossary-of-terms)
6. [Accessibility & Localization](#accessibility--localization)
7. [Versioning & Release Notes](#versioning--release-notes)

---

## Getting Started

### Installation

#### Prerequisites
- Python 3.10 or higher
- Docker (for containerized deployment)
- Google Cloud Platform account
- Firestore database access
- Airtable account (for priority descriptions)

#### Installation via Docker (Recommended)

1. **Clone the Repository**
   \`\`\`bash
   git clone <repository-url>
   cd Report-Application-Backend-main
   \`\`\`

2. **Build the Docker Image**
   \`\`\`bash
   docker build -t report-backend .
   \`\`\`

3. **Set Up Environment Variables**
   Create a \`.env\` file or set environment variables:
   \`\`\`bash
   export GOOGLE_APPLICATION_CREDENTIALS_2="/path/to/service-account.json"
   export AIRTABLE_API_KEY="your-airtable-api-key"
   export BASE_ID="your-airtable-base-id"
   export PORT=8080
   \`\`\`

4. **Run the Container**
   \`\`\`bash
   docker run -p 8080:8080 \\
     -e GOOGLE_APPLICATION_CREDENTIALS_2="/app/credentials.json" \\
     -v /path/to/credentials.json:/app/kinetic-mile-439413-k0-bc4aef71e45c.json \\
     report-backend
   \`\`\`

#### Local Development Setup

1. **Create Virtual Environment**
   \`\`\`bash
   python -m venv venv
   source venv/bin/activate  # On Windows: venv\\Scripts\\activate
   \`\`\`

2. **Install Dependencies**
   \`\`\`bash
   pip install -r requirements.txt
   \`\`\`

3. **Configure Environment**
   \`\`\`bash
   cp .env.example .env
   # Edit .env with your credentials
   \`\`\`

4. **Run the Application**
   \`\`\`bash
   python app.py
   \`\`\`

The application will start on \`http://localhost:8080\`

### Environment Setup

#### Google Cloud Platform (GCP) Credentials

1. **Create Service Account**
   - Go to GCP Console → IAM & Admin → Service Accounts
   - Create a new service account
   - Grant necessary roles:
     - Cloud Datastore User (for Firestore)
     - Storage Object Viewer (for Cloud Storage)
     - Cloud Tasks Enqueuer (if using Cloud Tasks)

2. **Download Credentials**
   - Create a JSON key for the service account
   - Save it securely (e.g., \`service-account.json\`)
   - Set the path in \`GOOGLE_APPLICATION_CREDENTIALS_2\`

3. **Firestore Setup**
   - Create a Firestore database in GCP Console
   - Note the project ID
   - Ensure the service account has access

#### Airtable Configuration

1. **Get API Key**
   - Go to Airtable Account → Developer Hub
   - Create a new personal access token
   - Copy the API key

2. **Get Base ID**
   - Open your Airtable base
   - Go to Help → API documentation
   - Copy the Base ID

3. **Set Environment Variables**
   \`\`\`bash
   export AIRTABLE_API_KEY="pat..."
   export BASE_ID="app..."
   \`\`\`

### Onboarding

#### First-Time User Registration

1. **Admin Registration** (if no admin exists)
   \`\`\`bash
   curl -X POST http://localhost:8080/register_user \\
     -H "Content-Type: application/json" \\
     -d '{
       "email": "admin@example.com",
       "password": "secure-password",
       "first_name": "Admin",
       "last_name": "User",
       "role": "Admin",
       "domains": [],
       "sessions": []
     }'
   \`\`\`

2. **Client Registration** (by Admin)
   \`\`\`bash
   curl -X POST http://localhost:8080/register_user \\
     -H "Content-Type: application/json" \\
     -d '{
       "email": "client@example.com",
       "password": "client-password",
       "first_name": "Client",
       "last_name": "User",
       "role": "Client",
       "domains": ["example.com", "test.com"],
       "sessions": []
     }'
   \`\`\`

#### User Authentication

\`\`\`bash
curl -X POST http://localhost:8080/authenticate_user \\
  -H "Content-Type: application/json" \\
  -d '{
    "email": "admin@example.com",
    "password": "secure-password"
  }'
\`\`\`

Response:
\`\`\`json
{
  "status": "success",
  "message": "Authentication successful",
  "data": {
    "user_id": "abc123",
    "first_name": "Admin",
    "last_name": "User",
    "email": "admin@example.com",
    "role": "Admin",
    "domains": []
  }
}
\`\`\`

---

## Feature Walkthroughs

### Session Management

#### Viewing All Sessions (Admin Only)

**Endpoint**: \`GET /list_all_sessions_data\`

**Request**:
\`\`\`bash
curl -X GET http://localhost:8080/list_all_sessions_data
\`\`\`

**Response**:
\`\`\`json
{
  "status": "success",
  "data": [
    {
      "sessionName": "E-commerce Audit",
      "sessionId": "session123",
      "createdBy": "admin@example.com",
      "domainName": "example.com",
      "journeyCount": 5,
      "source": "main-sessions",
      "createdAt": "2024-01-15T10:30:00Z"
    },
    {
      "sessionName": "Digital Violation Check",
      "sessionId": "digital456",
      "createdBy": "admin@example.com",
      "domainName": "test.com",
      "journeyCount": 3,
      "source": "digital-violation",
      "createdAt": "2024-01-15T11:00:00Z"
    }
  ]
}
\`\`\`

**Features**:
- Combines sessions from all three collections
- Includes journey counts (calculated in parallel)
- Shows source collection for each session
- Includes creation timestamps

### Domain Filtering

#### Listing All Domains

**Endpoint**: \`GET /list_all_domains\`

**Request**:
\`\`\`bash
curl -X GET http://localhost:8080/list_all_domains
\`\`\`

**Response**:
\`\`\`json
{
  "status": "success",
  "data": [
    "example.com",
    "test.com",
    "demo.com"
  ]
}
\`\`\`

#### Filtering Sessions by Domains

**Endpoint**: \`POST /fetch_sessions_by_domains\`

**Request**:
\`\`\`bash
curl -X POST http://localhost:8080/fetch_sessions_by_domains \\
  -H "Content-Type: application/json" \\
  -d '{
    "domains": ["example.com", "test.com"]
  }'
\`\`\`

**Response**:
\`\`\`json
{
  "status": "success",
  "data": [
    {
      "sessionName": "E-commerce Audit",
      "sessionId": "session123",
      "domainName": "example.com",
      "journeyCount": 5,
      "source": "main-sessions"
    }
  ]
}
\`\`\`

**Use Cases**:
- Filter sessions for specific domains
- Generate domain-specific reports
- Client users see only their assigned domains

### Journey and Image Details

#### Getting Session Journey Images

**Endpoint**: \`POST /get_session_user_journey_images_details\`

**Request**:
\`\`\`bash
curl -X POST http://localhost:8080/get_session_user_journey_images_details \\
  -H "Content-Type: application/json" \\
  -d '{
    "sessionId": "session123"
  }'
\`\`\`

**Response**:
\`\`\`json
{
  "status": "success",
  "data": [
    {
      "journeyId": "journey1",
      "journeyName": "Checkout Flow",
      "totalDarkPatterns": 3,
      "images": [
        {
          "imageId": "img1",
          "pageName": "Cart Page",
          "imageUrl": "https://storage.googleapis.com/...",
          "darkPatternCount": 2
        },
        {
          "imageId": "img2",
          "pageName": "Payment Page",
          "imageUrl": "https://storage.googleapis.com/...",
          "darkPatternCount": 1
        }
      ]
    }
  ]
}
\`\`\`

#### Getting Image Metadata

**Endpoint**: \`POST /get_image_Information\`

**Request**:
\`\`\`bash
curl -X POST http://localhost:8080/get_image_Information \\
  -H "Content-Type: application/json" \\
  -d '{
    "sessionId": "session123",
    "journeyId": "journey1",
    "imageId": "img1"
  }'
\`\`\`

**Response**:
\`\`\`json
{
  "status": "success",
  "data": {
    "imageId": "img1",
    "imageUrl": "https://storage.googleapis.com/...",
    "responseCount": 2,
    "response": [
      {
        "text": "**Excerpt:-**\\n\\tUrgency messaging...\\n\\n**Findings:-**\\n\\tCreates false urgency",
        "dark_pattern": "Urgency",
        "laws": [
          {
            "clause_name": "Article 5(1) GDPR",
            "jurisdiction": "EU",
            "law_name": "General Data Protection Regulation",
            "link": "https://...",
            "type": "regulation"
          }
        ]
      }
    ]
  }
}
\`\`\`

### User Management

#### Registering a New User

**Endpoint**: \`POST /register_user\`

**Request**:
\`\`\`bash
curl -X POST http://localhost:8080/register_user \\
  -H "Content-Type: application/json" \\
  -d '{
    "email": "newuser@example.com",
    "password": "password123",
    "first_name": "New",
    "last_name": "User",
    "role": "Client",
    "domains": ["example.com"],
    "sessions": []
  }'
\`\`\`

**Validation**:
- Email must be unique
- Role must be "Admin" or "Client"
- Domains and sessions must be arrays

#### Updating User Details

**Endpoint**: \`PUT /update_user\`

**Request**:
\`\`\`bash
curl -X PUT http://localhost:8080/update_user \\
  -H "Content-Type: application/json" \\
  -d '{
    "user_id": "user123",
    "first_name": "Updated",
    "domains": ["example.com", "newdomain.com"]
  }'
\`\`\`

#### Listing All Users (Admin Only)

**Endpoint**: \`POST /list_users\`

**Request**:
\`\`\`bash
curl -X POST http://localhost:8080/list_users \\
  -H "Content-Type: application/json" \\
  -d '{
    "user_id": "admin_user_id"
  }'
\`\`\`

#### Getting User Details

**Endpoint**: \`GET /get_user_details/<user_id>\`

**Request**:
\`\`\`bash
curl -X GET http://localhost:8080/get_user_details/user123
\`\`\`

### Priority Descriptions

#### Getting Priority Description for Session

**Endpoint**: \`POST /get_priority_description\`

**Request**:
\`\`\`bash
curl -X POST http://localhost:8080/get_priority_description \\
  -H "Content-Type: application/json" \\
  -d '{
    "session_id": "session123"
  }'
\`\`\`

**Response**:
\`\`\`json
{
  "status": "success",
  "data": {
    "session_id": "session123",
    "session_name": "E-commerce Audit",
    "priorities": {
      "highest": "Critical dark patterns requiring immediate action",
      "high": "Significant compliance issues",
      "medium": "Moderate concerns",
      "low": "Minor issues",
      "lowest": "Informational findings"
    }
  }
}
\`\`\`

---

## Interactive Tutorials

### Tutorial 1: Complete Session Analysis Workflow

**Objective**: Analyze a session from start to finish

**Steps**:

1. **Authenticate as Admin**
   \`\`\`bash
   curl -X POST http://localhost:8080/authenticate_user \\
     -H "Content-Type: application/json" \\
     -d '{"email": "admin@example.com", "password": "password"}'
   \`\`\`
   Save the \`user_id\` from the response.

2. **List All Sessions**
   \`\`\`bash
   curl -X GET http://localhost:8080/list_all_sessions_data
   \`\`\`
   Note a \`sessionId\` from the response.

3. **Get Journey Details**
   \`\`\`bash
   curl -X POST http://localhost:8080/get_session_user_journey_images_details \\
     -H "Content-Type: application/json" \\
     -d '{"sessionId": "your-session-id"}'
   \`\`\`
   Note a \`journeyId\` and \`imageId\`.

4. **Get Image Metadata**
   \`\`\`bash
   curl -X POST http://localhost:8080/get_image_Information \\
     -H "Content-Type: application/json" \\
     -d '{
       "sessionId": "your-session-id",
       "journeyId": "your-journey-id",
       "imageId": "your-image-id"
     }'
   \`\`\`

5. **Get Priority Description**
   \`\`\`bash
   curl -X POST http://localhost:8080/get_priority_description \\
     -H "Content-Type: application/json" \\
     -d '{"session_id": "your-session-id"}'
   \`\`\`

### Tutorial 2: Domain-Specific Reporting

**Objective**: Generate a report for specific domains

**Steps**:

1. **List Available Domains**
   \`\`\`bash
   curl -X GET http://localhost:8080/list_all_domains
   \`\`\`

2. **Filter Sessions by Domains**
   \`\`\`bash
   curl -X POST http://localhost:8080/fetch_sessions_by_domains \\
     -H "Content-Type: application/json" \\
     -d '{"domains": ["example.com"]}'
   \`\`\`

3. **Analyze Each Session**
   - For each session, follow Tutorial 1 steps 3-5
   - Compile dark pattern counts
   - Review law references

4. **Generate Report**
   - Combine all findings
   - Include priority descriptions
   - Export as JSON or format for presentation

### Tutorial 3: User Management

**Objective**: Set up and manage users

**Steps**:

1. **Register Admin User** (if first time)
   \`\`\`bash
   curl -X POST http://localhost:8080/register_user \\
     -H "Content-Type: application/json" \\
     -d '{
       "email": "admin@example.com",
       "password": "admin123",
       "first_name": "Admin",
       "last_name": "User",
       "role": "Admin",
       "domains": [],
       "sessions": []
     }'
   \`\`\`

2. **Register Client Users**
   \`\`\`bash
   curl -X POST http://localhost:8080/register_user \\
     -H "Content-Type: application/json" \\
     -d '{
       "email": "client@example.com",
       "password": "client123",
       "first_name": "Client",
       "last_name": "User",
       "role": "Client",
       "domains": ["example.com"],
       "sessions": []
     }'
   \`\`\`

3. **List All Users** (Admin only)
   \`\`\`bash
   curl -X POST http://localhost:8080/list_users \\
     -H "Content-Type: application/json" \\
     -d '{"user_id": "admin-user-id"}'
   \`\`\`

4. **Update User**
   \`\`\`bash
   curl -X PUT http://localhost:8080/update_user \\
     -H "Content-Type: application/json" \\
     -d '{
       "user_id": "client-user-id",
       "domains": ["example.com", "newdomain.com"]
     }'
   \`\`\`

---

## Troubleshooting & FAQs

### Common Issues

#### Issue 1: "Unauthorized access" Error

**Problem**: Getting 403 error on admin endpoints

**Solutions**:
1. Verify you're authenticated as Admin role
2. Check the \`role\` field in user data
3. Ensure you're using the correct user_id

**Example Fix**:
\`\`\`bash
# Check user role
curl -X GET http://localhost:8080/get_user_details/user123

# If role is "Client", you need Admin access
\`\`\`

#### Issue 2: "No session found" Error

**Problem**: Session ID not found in any collection

**Solutions**:
1. Verify the session ID is correct
2. Check if session exists in Firestore
3. Ensure session belongs to one of the three collections:
   - \`sessions\`
   - \`digital-sessions\`
   - \`influencer-marketing\`

**Debug Steps**:
\`\`\`bash
# List all sessions to find correct ID
curl -X GET http://localhost:8080/list_all_sessions_data
\`\`\`

#### Issue 3: Firestore Connection Errors

**Problem**: Cannot connect to Firestore

**Solutions**:
1. Verify \`GOOGLE_APPLICATION_CREDENTIALS_2\` is set correctly
2. Check service account has Firestore permissions
3. Verify network connectivity to GCP
4. Check Firestore database exists and is accessible

**Debug Steps**:
\`\`\`bash
# Test credentials
gcloud auth activate-service-account --key-file=service-account.json

# Test Firestore access
gcloud firestore databases list
\`\`\`

#### Issue 4: Airtable API Errors

**Problem**: Priority descriptions not loading

**Solutions**:
1. Verify \`AIRTABLE_API_KEY\` is set
2. Check \`BASE_ID\` is correct
3. Ensure session ID exists in Airtable
4. Verify API key has read permissions

**Debug Steps**:
\`\`\`bash
# Test Airtable connection
curl -X GET "https://api.airtable.com/v0/{BASE_ID}/tblevlgLvZNOyVW4E" \\
  -H "Authorization: Bearer $AIRTABLE_API_KEY"
\`\`\`

#### Issue 5: Empty Results

**Problem**: Endpoints return empty arrays

**Solutions**:
1. Verify data exists in Firestore collections
2. Check domain filters are correct
3. Ensure user has access to requested data
4. Verify collection names are correct

### Frequently Asked Questions

#### Q1: What's the difference between the three session collections?

**A**: 
- **sessions**: Main web session data with traditional user journeys
- **digital-sessions**: Digital violation detection sessions
- **influencer-marketing**: Video dark pattern analysis sessions

Each has different data structures but unified API access.

#### Q2: How are journey counts calculated?

**A**: Journey counts are calculated in parallel using ThreadPoolExecutor for performance. The system queries each session's \`userJourneys\` subcollection and counts documents.

#### Q3: Can I filter by multiple domains?

**A**: Yes, the \`/fetch_sessions_by_domains\` endpoint accepts an array of domains and returns sessions matching any of them.

#### Q4: How do I update my password?

**A**: Use the \`/update_user\` endpoint with the \`password\` field:
\`\`\`bash
curl -X PUT http://localhost:8080/update_user \\
  -H "Content-Type: application/json" \\
  -d '{
    "user_id": "your-user-id",
    "password": "new-password"
  }'
\`\`\`

#### Q5: What's the maximum number of sessions I can retrieve?

**A**: There's no hard limit in the API, but Firestore has query limits. For large datasets, consider pagination or domain filtering.

#### Q6: How do I handle video sessions differently?

**A**: Video sessions use the \`influencer-marketing\` collection and have a different structure. The API automatically handles the differences - you don't need special handling.

---

## Glossary of Terms

### Compliance & AI Concepts

- **Dark Pattern**: A user interface design choice that manipulates users into taking actions they might not otherwise take
- **Regulatory Violation**: A breach of legal or regulatory requirements (e.g., GDPR, CCPA)
- **Compliance Score**: A metric indicating how well a system adheres to regulatory requirements
- **Audit Trail**: A chronological record of system activities for compliance and security

### Application Terms

- **Session**: A collection of user journeys and images for a specific domain analysis
- **Journey**: A sequence of user interactions (e.g., checkout flow, signup process)
- **Image**: A screenshot or captured image from a user journey
- **Domain**: A website domain name (e.g., example.com)
- **Outcome**: A detected dark pattern or compliance issue
- **Law Reference**: A link to specific regulatory law or clause

### Technical Terms

- **Firestore**: Google Cloud's NoSQL document database
- **Collection**: A group of documents in Firestore (similar to a table in SQL)
- **ThreadPoolExecutor**: Python's parallel execution mechanism
- **API Endpoint**: A specific URL path that accepts requests and returns responses
- **JSON**: JavaScript Object Notation, the data format used for API requests/responses

---

## Accessibility & Localization

### Accessibility

#### Current Implementation
- API returns JSON data (screen-reader friendly)
- Error messages are clear and descriptive
- Consistent response structure

#### Recommendations
- Add language support in error messages
- Provide alternative formats for data export
- Include accessibility metadata in responses

### Localization

#### Current Status
- English-only interface
- Timestamps in ISO 8601 format (timezone-aware)
- No multi-language support

#### Future Enhancements
- Support for multiple languages in error messages
- Localized date/time formatting
- Regional compliance law references

### Internationalization Considerations
- Timezone handling: All timestamps use ISO 8601 format
- Character encoding: UTF-8 throughout
- Date formats: ISO 8601 standard (YYYY-MM-DDTHH:MM:SSZ)

---

## Versioning & Release Notes

### Version History

#### Version 1.0.0 (Current)
- Initial release
- Multi-collection support (sessions, digital-sessions, influencer-marketing)
- User authentication and role-based access
- Session and domain management
- Image metadata retrieval
- Priority descriptions from Airtable

### Release Notes Template

\`\`\`markdown
## Version X.Y.Z - Release Date

### Added
- New features and functionality

### Changed
- Modified existing features

### Fixed
- Bug fixes

### Deprecated
- Features scheduled for removal

### Security
- Security updates and patches

### Breaking Changes
- Changes that require code updates
\`\`\`

### Upgrade Guide

#### From Previous Versions
1. Review release notes for breaking changes
2. Update environment variables if needed
3. Test authentication flow
4. Verify Firestore collections exist
5. Update API client code if endpoints changed

### Backward Compatibility
- API endpoints maintain consistent structure
- Response formats remain stable
- New fields are added, not removed
- Deprecated endpoints are marked before removal

---

## Conclusion

This user manual provides comprehensive guidance for using the Report Application Backend. For technical details, refer to the [API Documentation](api-documentation.md) and [Architecture Documentation](architecture.html).

For support or questions, please refer to the troubleshooting section or contact your system administrator.

`;


// Documentation lookup object
export const documentationContent = {
  "/Documentation/3-auth.md": Doc3Auth,
  "/Documentation/7-security.md": Doc7Security,
  "/Documentation/DEPENDENCY_ALL_TABLES.md": DependencyAllTables,
  "/Documentation/Chat Agent Documentation/api-documentation.md": ChatAgentDocumentationApiDocumentation,
  "/Documentation/Chat Agent Documentation/architecture.md": ChatAgentDocumentationArchitecture,
  "/Documentation/Chat Agent Documentation/code-documentation.md": ChatAgentDocumentationCodeDocumentation,
  "/Documentation/Chat Agent Documentation/functional-flows.md": ChatAgentDocumentationFunctionalFlows,
  "/Documentation/Chat Agent Documentation/user-manual.md": ChatAgentDocumentationUserManual,
  "/Documentation/Chrome extension documentation/functional-summary.md": ChromeExtensionDocumentationFunctionalSummary,
  "/Documentation/Chrome extension documentation/api/api-versioning.md": ChromeExtensionDocumentationApiApiVersioning,
  "/Documentation/Chrome extension documentation/api/authentication.md": ChromeExtensionDocumentationApiAuthentication,
  "/Documentation/Chrome extension documentation/api/data-schemas.md": ChromeExtensionDocumentationApiDataSchemas,
  "/Documentation/Chrome extension documentation/api/endpoints.md": ChromeExtensionDocumentationApiEndpoints,
  "/Documentation/Chrome extension documentation/api/error-codes-responses.md": ChromeExtensionDocumentationApiErrorCodesResponses,
  "/Documentation/Chrome extension documentation/api/integration-examples.md": ChromeExtensionDocumentationApiIntegrationExamples,
  "/Documentation/Chrome extension documentation/api/rate-limits-quotas.md": ChromeExtensionDocumentationApiRateLimitsQuotas,
  "/Documentation/Chrome extension documentation/architecture/component-design.md": ChromeExtensionDocumentationArchitectureComponentDesign,
  "/Documentation/Chrome extension documentation/architecture/data-flow-storage.md": ChromeExtensionDocumentationArchitectureDataFlowStorage,
  "/Documentation/Chrome extension documentation/architecture/deployment-architecture.md": ChromeExtensionDocumentationArchitectureDeploymentArchitecture,
  "/Documentation/Chrome extension documentation/architecture/integration-points.md": ChromeExtensionDocumentationArchitectureIntegrationPoints,
  "/Documentation/Chrome extension documentation/architecture/monitoring-observability.md": ChromeExtensionDocumentationArchitectureMonitoringObservability,
  "/Documentation/Chrome extension documentation/architecture/scalability-resilience.md": ChromeExtensionDocumentationArchitectureScalabilityResilience,
  "/Documentation/Chrome extension documentation/architecture/security-privacy.md": ChromeExtensionDocumentationArchitectureSecurityPrivacy,
  "/Documentation/Chrome extension documentation/architecture/system-overview.md": ChromeExtensionDocumentationArchitectureSystemOverview,
  "/Documentation/Chrome extension documentation/code/code-examples.md": ChromeExtensionDocumentationCodeCodeExamples,
  "/Documentation/Chrome extension documentation/code/database-schema.md": ChromeExtensionDocumentationCodeDatabaseSchema,
  "/Documentation/Chrome extension documentation/code/deployment-environment.md": ChromeExtensionDocumentationCodeDeploymentEnvironment,
  "/Documentation/Chrome extension documentation/code/design-philosophy.md": ChromeExtensionDocumentationCodeDesignPhilosophy,
  "/Documentation/Chrome extension documentation/code/developer-experience.md": ChromeExtensionDocumentationCodeDeveloperExperience,
  "/Documentation/Chrome extension documentation/code/libraries-dependencies.md": ChromeExtensionDocumentationCodeLibrariesDependencies,
  "/Documentation/Chrome extension documentation/code/model-integrations.md": ChromeExtensionDocumentationCodeModelIntegrations,
  "/Documentation/Chrome extension documentation/code/operational-readiness.md": ChromeExtensionDocumentationCodeOperationalReadiness,
  "/Documentation/Chrome extension documentation/code/performance-benchmarking.md": ChromeExtensionDocumentationCodePerformanceBenchmarking,
  "/Documentation/Chrome extension documentation/code/security-compliance.md": ChromeExtensionDocumentationCodeSecurityCompliance,
  "/Documentation/Chrome extension documentation/code/testing-qa.md": ChromeExtensionDocumentationCodeTestingQa,
  "/Documentation/Chrome extension documentation/code/versioning-change-management.md": ChromeExtensionDocumentationCodeVersioningChangeManagement,
  "/Documentation/Chrome extension documentation/flows/compliance-audit-trail.md": ChromeExtensionDocumentationFlowsComplianceAuditTrail,
  "/Documentation/Chrome extension documentation/flows/end-to-end-workflows.md": ChromeExtensionDocumentationFlowsEndToEndWorkflows,
  "/Documentation/Chrome extension documentation/flows/error-handling-recovery.md": ChromeExtensionDocumentationFlowsErrorHandlingRecovery,
  "/Documentation/Chrome extension documentation/flows/interactive-flows.md": ChromeExtensionDocumentationFlowsInteractiveFlows,
  "/Documentation/Chrome extension documentation/flows/process-states.md": ChromeExtensionDocumentationFlowsProcessStates,
  "/Documentation/Chrome extension documentation/flows/role-based-access.md": ChromeExtensionDocumentationFlowsRoleBasedAccess,
  "/Documentation/Chrome extension documentation/flows/use-case-scenarios.md": ChromeExtensionDocumentationFlowsUseCaseScenarios,
  "/Documentation/Chrome extension documentation/user/accessibility-localization.md": ChromeExtensionDocumentationUserAccessibilityLocalization,
  "/Documentation/Chrome extension documentation/user/feature-walkthroughs.md": ChromeExtensionDocumentationUserFeatureWalkthroughs,
  "/Documentation/Chrome extension documentation/user/getting-started.md": ChromeExtensionDocumentationUserGettingStarted,
  "/Documentation/Chrome extension documentation/user/glossary.md": ChromeExtensionDocumentationUserGlossary,
  "/Documentation/Chrome extension documentation/user/interactive-tutorials.md": ChromeExtensionDocumentationUserInteractiveTutorials,
  "/Documentation/Chrome extension documentation/user/troubleshooting-faqs.md": ChromeExtensionDocumentationUserTroubleshootingFaqs,
  "/Documentation/Chrome extension documentation/user/user-versioning.md": ChromeExtensionDocumentationUserUserVersioning,
  "/Documentation/Cookie consent backend documentation/01-code-documentation/code-examples.md": CookieConsentBackendDocumentation01CodeDocumentationCodeExamples,
  "/Documentation/Cookie consent backend documentation/01-code-documentation/continuous-improvement.md": CookieConsentBackendDocumentation01CodeDocumentationContinuousImprovement,
  "/Documentation/Cookie consent backend documentation/01-code-documentation/core-design-philosophy.md": CookieConsentBackendDocumentation01CodeDocumentationCoreDesignPhilosophy,
  "/Documentation/Cookie consent backend documentation/01-code-documentation/database-schema.md": CookieConsentBackendDocumentation01CodeDocumentationDatabaseSchema,
  "/Documentation/Cookie consent backend documentation/01-code-documentation/deployment-environment.md": CookieConsentBackendDocumentation01CodeDocumentationDeploymentEnvironment,
  "/Documentation/Cookie consent backend documentation/01-code-documentation/developer-experience.md": CookieConsentBackendDocumentation01CodeDocumentationDeveloperExperience,
  "/Documentation/Cookie consent backend documentation/01-code-documentation/libraries-dependencies.md": CookieConsentBackendDocumentation01CodeDocumentationLibrariesDependencies,
  "/Documentation/Cookie consent backend documentation/01-code-documentation/model-integrations.md": CookieConsentBackendDocumentation01CodeDocumentationModelIntegrations,
  "/Documentation/Cookie consent backend documentation/01-code-documentation/operational-readiness.md": CookieConsentBackendDocumentation01CodeDocumentationOperationalReadiness,
  "/Documentation/Cookie consent backend documentation/01-code-documentation/performance-benchmarking.md": CookieConsentBackendDocumentation01CodeDocumentationPerformanceBenchmarking,
  "/Documentation/Cookie consent backend documentation/01-code-documentation/security-compliance.md": CookieConsentBackendDocumentation01CodeDocumentationSecurityCompliance,
  "/Documentation/Cookie consent backend documentation/01-code-documentation/testing-qa.md": CookieConsentBackendDocumentation01CodeDocumentationTestingQa,
  "/Documentation/Cookie consent backend documentation/01-code-documentation/versioning.md": CookieConsentBackendDocumentation01CodeDocumentationVersioning,
  "/Documentation/Cookie consent backend documentation/02-functional-flows/compliance-audit.md": CookieConsentBackendDocumentation02FunctionalFlowsComplianceAudit,
  "/Documentation/Cookie consent backend documentation/02-functional-flows/end-to-end-workflow.md": CookieConsentBackendDocumentation02FunctionalFlowsEndToEndWorkflow,
  "/Documentation/Cookie consent backend documentation/02-functional-flows/error-handling.md": CookieConsentBackendDocumentation02FunctionalFlowsErrorHandling,
  "/Documentation/Cookie consent backend documentation/02-functional-flows/interactive-flows.md": CookieConsentBackendDocumentation02FunctionalFlowsInteractiveFlows,
  "/Documentation/Cookie consent backend documentation/02-functional-flows/process-states.md": CookieConsentBackendDocumentation02FunctionalFlowsProcessStates,
  "/Documentation/Cookie consent backend documentation/02-functional-flows/role-based-access.md": CookieConsentBackendDocumentation02FunctionalFlowsRoleBasedAccess,
  "/Documentation/Cookie consent backend documentation/02-functional-flows/use-case-scenarios.md": CookieConsentBackendDocumentation02FunctionalFlowsUseCaseScenarios,
  "/Documentation/Cookie consent backend documentation/03-user-manual/accessibility.md": CookieConsentBackendDocumentation03UserManualAccessibility,
  "/Documentation/Cookie consent backend documentation/03-user-manual/feature-walkthroughs.md": CookieConsentBackendDocumentation03UserManualFeatureWalkthroughs,
  "/Documentation/Cookie consent backend documentation/03-user-manual/getting-started.md": CookieConsentBackendDocumentation03UserManualGettingStarted,
  "/Documentation/Cookie consent backend documentation/03-user-manual/glossary.md": CookieConsentBackendDocumentation03UserManualGlossary,
  "/Documentation/Cookie consent backend documentation/03-user-manual/interactive-tutorials.md": CookieConsentBackendDocumentation03UserManualInteractiveTutorials,
  "/Documentation/Cookie consent backend documentation/03-user-manual/release-notes.md": CookieConsentBackendDocumentation03UserManualReleaseNotes,
  "/Documentation/Cookie consent backend documentation/03-user-manual/troubleshooting.md": CookieConsentBackendDocumentation03UserManualTroubleshooting,
  "/Documentation/Cookie consent backend documentation/04-api-documentation/api-versioning.md": CookieConsentBackendDocumentation04ApiDocumentationApiVersioning,
  "/Documentation/Cookie consent backend documentation/04-api-documentation/authentication.md": CookieConsentBackendDocumentation04ApiDocumentationAuthentication,
  "/Documentation/Cookie consent backend documentation/04-api-documentation/data-schemas.md": CookieConsentBackendDocumentation04ApiDocumentationDataSchemas,
  "/Documentation/Cookie consent backend documentation/04-api-documentation/endpoints.md": CookieConsentBackendDocumentation04ApiDocumentationEndpoints,
  "/Documentation/Cookie consent backend documentation/04-api-documentation/error-codes.md": CookieConsentBackendDocumentation04ApiDocumentationErrorCodes,
  "/Documentation/Cookie consent backend documentation/04-api-documentation/integration-examples.md": CookieConsentBackendDocumentation04ApiDocumentationIntegrationExamples,
  "/Documentation/Cookie consent backend documentation/04-api-documentation/rate-limits.md": CookieConsentBackendDocumentation04ApiDocumentationRateLimits,
  "/Documentation/Cookie consent backend documentation/05-architecture/component-design.md": CookieConsentBackendDocumentation05ArchitectureComponentDesign,
  "/Documentation/Cookie consent backend documentation/05-architecture/data-flow-storage.md": CookieConsentBackendDocumentation05ArchitectureDataFlowStorage,
  "/Documentation/Cookie consent backend documentation/05-architecture/deployment-architecture.md": CookieConsentBackendDocumentation05ArchitectureDeploymentArchitecture,
  "/Documentation/Cookie consent backend documentation/05-architecture/integration-points.md": CookieConsentBackendDocumentation05ArchitectureIntegrationPoints,
  "/Documentation/Cookie consent backend documentation/05-architecture/monitoring-observability.md": CookieConsentBackendDocumentation05ArchitectureMonitoringObservability,
  "/Documentation/Cookie consent backend documentation/05-architecture/scalability-resilience.md": CookieConsentBackendDocumentation05ArchitectureScalabilityResilience,
  "/Documentation/Cookie consent backend documentation/05-architecture/security-privacy.md": CookieConsentBackendDocumentation05ArchitectureSecurityPrivacy,
  "/Documentation/Cookie consent backend documentation/05-architecture/system-overview.md": CookieConsentBackendDocumentation05ArchitectureSystemOverview,
  "/Documentation/Cookie consent frontend documentation/01-code-documentation/core-design-philosophy.md": CookieConsentFrontendDocumentation01CodeDocumentationCoreDesignPhilosophy,
  "/Documentation/Main Tool  documentation/api-documentation/api-versioning.md": MainToolDocumentationApiDocumentationApiVersioning,
  "/Documentation/Main Tool  documentation/api-documentation/authentication.md": MainToolDocumentationApiDocumentationAuthentication,
  "/Documentation/Main Tool  documentation/api-documentation/data-schemas.md": MainToolDocumentationApiDocumentationDataSchemas,
  "/Documentation/Main Tool  documentation/api-documentation/endpoints.md": MainToolDocumentationApiDocumentationEndpoints,
  "/Documentation/Main Tool  documentation/api-documentation/error-codes.md": MainToolDocumentationApiDocumentationErrorCodes,
  "/Documentation/Main Tool  documentation/api-documentation/integration-examples.md": MainToolDocumentationApiDocumentationIntegrationExamples,
  "/Documentation/Main Tool  documentation/api-documentation/rate-limits.md": MainToolDocumentationApiDocumentationRateLimits,
  "/Documentation/Main Tool  documentation/architecture/component-design.md": MainToolDocumentationArchitectureComponentDesign,
  "/Documentation/Main Tool  documentation/architecture/data-flow.md": MainToolDocumentationArchitectureDataFlow,
  "/Documentation/Main Tool  documentation/architecture/deployment-architecture.md": MainToolDocumentationArchitectureDeploymentArchitecture,
  "/Documentation/Main Tool  documentation/architecture/integrations.md": MainToolDocumentationArchitectureIntegrations,
  "/Documentation/Main Tool  documentation/architecture/monitoring.md": MainToolDocumentationArchitectureMonitoring,
  "/Documentation/Main Tool  documentation/architecture/scalability.md": MainToolDocumentationArchitectureScalability,
  "/Documentation/Main Tool  documentation/architecture/security-architecture.md": MainToolDocumentationArchitectureSecurityArchitecture,
  "/Documentation/Main Tool  documentation/architecture/system-overview.md": MainToolDocumentationArchitectureSystemOverview,
  "/Documentation/Main Tool  documentation/code-documentation/code-examples.md": MainToolDocumentationCodeDocumentationCodeExamples,
  "/Documentation/Main Tool  documentation/code-documentation/continuous-learning.md": MainToolDocumentationCodeDocumentationContinuousLearning,
  "/Documentation/Main Tool  documentation/code-documentation/database-schema.md": MainToolDocumentationCodeDocumentationDatabaseSchema,
  "/Documentation/Main Tool  documentation/code-documentation/deployment-environment.md": MainToolDocumentationCodeDocumentationDeploymentEnvironment,
  "/Documentation/Main Tool  documentation/code-documentation/design-philosophy.md": MainToolDocumentationCodeDocumentationDesignPhilosophy,
  "/Documentation/Main Tool  documentation/code-documentation/developer-experience.md": MainToolDocumentationCodeDocumentationDeveloperExperience,
  "/Documentation/Main Tool  documentation/code-documentation/libraries-dependencies.md": MainToolDocumentationCodeDocumentationLibrariesDependencies,
  "/Documentation/Main Tool  documentation/code-documentation/model-integrations.md": MainToolDocumentationCodeDocumentationModelIntegrations,
  "/Documentation/Main Tool  documentation/code-documentation/operational-readiness.md": MainToolDocumentationCodeDocumentationOperationalReadiness,
  "/Documentation/Main Tool  documentation/code-documentation/performance-benchmarking.md": MainToolDocumentationCodeDocumentationPerformanceBenchmarking,
  "/Documentation/Main Tool  documentation/code-documentation/security-compliance.md": MainToolDocumentationCodeDocumentationSecurityCompliance,
  "/Documentation/Main Tool  documentation/code-documentation/testing-qa.md": MainToolDocumentationCodeDocumentationTestingQa,
  "/Documentation/Main Tool  documentation/code-documentation/versioning.md": MainToolDocumentationCodeDocumentationVersioning,
  "/Documentation/Main Tool  documentation/functional-flows/compliance-integration.md": MainToolDocumentationFunctionalFlowsComplianceIntegration,
  "/Documentation/Main Tool  documentation/functional-flows/error-handling.md": MainToolDocumentationFunctionalFlowsErrorHandling,
  "/Documentation/Main Tool  documentation/functional-flows/interactive-flows.md": MainToolDocumentationFunctionalFlowsInteractiveFlows,
  "/Documentation/Main Tool  documentation/functional-flows/process-states.md": MainToolDocumentationFunctionalFlowsProcessStates,
  "/Documentation/Main Tool  documentation/functional-flows/role-based-access.md": MainToolDocumentationFunctionalFlowsRoleBasedAccess,
  "/Documentation/Main Tool  documentation/functional-flows/use-cases.md": MainToolDocumentationFunctionalFlowsUseCases,
  "/Documentation/Main Tool  documentation/functional-flows/workflow-diagrams.md": MainToolDocumentationFunctionalFlowsWorkflowDiagrams,
  "/Documentation/Main Tool  documentation/user-manual/accessibility.md": MainToolDocumentationUserManualAccessibility,
  "/Documentation/Main Tool  documentation/user-manual/feature-walkthroughs.md": MainToolDocumentationUserManualFeatureWalkthroughs,
  "/Documentation/Main Tool  documentation/user-manual/getting-started.md": MainToolDocumentationUserManualGettingStarted,
  "/Documentation/Main Tool  documentation/user-manual/glossary.md": MainToolDocumentationUserManualGlossary,
  "/Documentation/Main Tool  documentation/user-manual/troubleshooting.md": MainToolDocumentationUserManualTroubleshooting,
  "/Documentation/Main Tool  documentation/user-manual/tutorials.md": MainToolDocumentationUserManualTutorials,
  "/Documentation/Main Tool  documentation/user-manual/version-history.md": MainToolDocumentationUserManualVersionHistory,
  "/Documentation/Report documentation/api-documentation.md": ReportDocumentationApiDocumentation,
  "/Documentation/Report documentation/architecture.md": ReportDocumentationArchitecture,
  "/Documentation/Report documentation/code-documentation.md": ReportDocumentationCodeDocumentation,
  "/Documentation/Report documentation/functional-flow.md": ReportDocumentationFunctionalFlow,
  "/Documentation/Report documentation/README.md": ReportDocumentationReadme,
  "/Documentation/Report documentation/user-manual.md": ReportDocumentationUserManual,
};